import {createRemoteProposal,emailRemoteProposal} from './remoteProposal';
import {loadUnreadNotifications,readOwnNotification} from './notificationInbox';
import {placeFindingMarker,updateMarkerFinding,changeMarkerPhotos,regroupRepairs,saveRepairFinding,isFumigationFinding,repairLocationSummary} from './repairFindingGroups';
import {subscribeWorkspaceIdentity} from "./workspaceStartup";
import useScrollHeader from "./useScrollHeader";
import WorkflowTabs from "./WorkflowTabs";
import {InspectorRoster} from "./InspectorRoster";
import {loadInspectorRoster,reportInspector,assignableInspectors,withReportInspector} from "./inspectorRosterModel";
import {jsPDF} from 'jspdf';
import {pdfDiagramLayout} from './pdfDiagramLayout';
import {rotationHandle} from './rotationHandle';
import {createPDFReportLayout} from './pdfReportLayout';
import {normalizeReportStyle,reportRGB,reportTint,reportInk,reportTextColor} from './reportStyle';
import ReportStyleSettings from './ReportStyleSettings';
import VolumeEstimator from './VolumeEstimator';
import {CUFT_PER_M3} from './volumeEstimate';
import {followsDiagramArea,syncDiagramAreas} from './fumigationArea';
import './AppHeader.css';
import {preparePDFPhotos,addPDFPhoto} from './pdfPhotos';
import {evidencePhoto} from './walkthroughSession';
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { supabase } from "./supabaseClient";
import {WalkthroughSection, FindingEvidence, EvidenceViewer} from "./WalkthroughSection";
import {repairLabor} from "./walkthroughModel";
import {DiagramFindingDetails, findingsForMarker, updateRepairEstimate, replacementSummary} from "./DiagramFindingDetails";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const CANVAS_W = 1024;
const CANVAS_H = 760;                 // legacy height — existing jobs keep this
const NEW_CANVAS_H = 1280;            // taller 4:5 portrait area for new jobs (fills the phone screen)
// Satellite tile request dimensions — chosen to match the canvas aspect ratio
// so the image can be drawn in cover-mode (no black bars) at a uniform 1.6× scale.
const MAP_IMG_W = 640;
const MAP_IMG_H = Math.round(CANVAS_H * MAP_IMG_W / CANVAS_W); // = 400
// Each canvas pixel corresponds to MAP_IMG_W / CANVAS_W = 0.625 tile pixels.
// Equivalently, MAP_PIXEL_SCALE = CANVAS_W / MAP_IMG_W = 1.6.
const MAP_PIXEL_SCALE = CANVAS_W / MAP_IMG_W;

const WDO_CATEGORIES = [
  { num: 1, label: "Subterranean Termites", short: "Sub", color: "#b45309", section: "I" },
  { num: 2, label: "Drywood Termites",      short: "Dry", color: "#7c3aed", section: "I" },
  { num: 3, label: "Fungus / Dry Rot",      short: "Fun", color: "#065f46", section: "I" },
  { num: 4, label: "Other WDO",             short: "Oth", color: "#1e40af", section: "II" },
  { num: 5, label: "Further Inspection",    short: "FI",  color: "#6b7280", section: "FI" },
];

// Work / remediation lifecycle — tracked per job separately from the report
// build steps (FP/DM/FR/CT/CS/NC). Each stage carries one status.
const WORK_STAGES = [
  { key:"fume",      label:"Fumigation",   icon:"🔥" },
  { key:"repairs",   label:"Wood Repairs", icon:"🔨" },
  { key:"treatment", label:"Treatment",    icon:"🧪" },
  { key:"clearance", label:"Clearance",    icon:"✅" },
];
const WORK_STATUS = {
  na:          { label:"N/A",         color:"#475569" },
  pending:     { label:"Pending",     color:"#94a3b8" },
  scheduled:   { label:"Scheduled",   color:"#0ea5e9" },
  in_progress: { label:"In progress", color:"#f59e0b" },
  done:        { label:"Done",        color:"#22c55e" },
};
const WORK_STATUS_ORDER = ["na","pending","scheduled","in_progress","done"];
// Roll the per-stage statuses up into one headline status for cards/lists.
function jobOverallStatus(ws) {
  const active = WORK_STAGES.map(s=>(ws||{})[s.key]).filter(v=>v && v!=="na" && v!=="pending");
  if (active.length===0)                     return { label:"Awaiting", color:"#64748b" };
  if (active.every(v=>v==="done"))           return { label:"Complete", color:"#22c55e" };
  if (active.some(v=>v==="in_progress"))      return { label:"In progress", color:"#f59e0b" };
  if (active.some(v=>v==="scheduled"))        return { label:"Scheduled", color:"#0ea5e9" };
  return { label:"In progress", color:"#f59e0b" };
}
// Editable per-stage status list (readOnly renders chips instead of dropdowns).
function WorkStatus({ value, onChange, readOnly, light }) {
  const ws = value || {};
  const labelColor = light ? "#334155" : "#e2e8f0";
  const selBg = light ? "#fff" : "#0f172a";
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {WORK_STAGES.map(s=>{
        const cur = ws[s.key] || "pending";
        const meta = WORK_STATUS[cur] || WORK_STATUS.pending;
        return (
          <div key={s.key} style={{display:"flex",alignItems:"center",gap:10,justifyContent:"space-between"}}>
            <span style={{fontSize:13,fontWeight:600,color:labelColor}}>{s.icon} {s.label}</span>
            {readOnly ? (
              <span style={{fontSize:12,fontWeight:800,color:meta.color,background:meta.color+"22",border:`1px solid ${meta.color}`,borderRadius:999,padding:"2px 10px"}}>{meta.label}</span>
            ) : (
              <select value={cur} onChange={e=>onChange({...ws,[s.key]:e.target.value})}
                style={{background:selBg,color:meta.color,border:`1px solid ${meta.color}`,borderRadius:8,padding:"5px 10px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {WORK_STATUS_ORDER.map(k=><option key={k} value={k} style={{color:light?"#0f172a":"#fff",background:selBg}}>{WORK_STATUS[k].label}</option>)}
              </select>
            )}
          </div>
        );
      })}
    </div>
  );
}
// Compact read-only summary — only the stages that are actively moving.
function WorkStatusChips({ ws }) {
  const active = WORK_STAGES.filter(s=>{ const v=(ws||{})[s.key]; return v && v!=="na" && v!=="pending"; });
  if (active.length===0) return <span style={{fontSize:11,color:"#64748b"}}>Awaiting</span>;
  return <span style={{display:"inline-flex",gap:4,flexWrap:"wrap"}}>{active.map(s=>{ const meta=WORK_STATUS[ws[s.key]]; return <span key={s.key} style={{fontSize:10,fontWeight:700,color:meta.color,background:meta.color+"22",border:`1px solid ${meta.color}55`,borderRadius:6,padding:"1px 6px",whiteSpace:"nowrap"}}>{s.icon} {meta.label}</span>; })}</span>;
}

// Common structural components an inspector selects when repair/replacement is needed.
const REPAIR_COMPONENTS = ["Fascia board","Starter board","Rafter","Rafter tail","Sub-floor","Subfloor joist","Floor joist","Sill plate","Sole plate","Sheathing","Siding","Trim","Threshold","Door jamb","Window sill","Header","Beam","Post / column","Decking","Deck ledger","Eave","Soffit","Bargeboard","Stud","Mudsill","Stair stringer","Other"];

const REPORT_TYPES = ["Complete Report","Limited Report","Supplemental Report","Reinspection","Corrected Report","Separated Report"];
const INSPECTION_TAGS = ["Attic","Attic/Crawlspace","Basement","Closet","Crawl Space","Exterior","Exterior Closet","Exterior Shed","Fencing/Gates","Foundation","Garage","Roof","Subfloor","Decking","Retaining Walls"];


const DEFAULT_FUME_HEIGHTS = [
  {id:"fh1", label:'8 ft',  ft:8,  custom:false},
  {id:"fh2", label:'9 ft',  ft:9,  custom:false},
  {id:"fh3", label:'10 ft', ft:10, custom:false},
  {id:"fh4", label:'12 ft', ft:12, custom:false},
  {id:"fh5", label:'14 ft', ft:14, custom:false},
  {id:"fh6", label:'16 ft', ft:16, custom:false},
];

const DEFAULT_CODEBOOK = [
  { id:"cb1", cat:1, name:"1A-Subterranean", finding:"Evidence of subterranean termite activity noted in area(s) marked on diagram.", primaryRec:"Treat all active areas and potential entry points for subterranean termites. Apply soil treatment around foundation perimeter.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb2", cat:1, name:"1B-Sub Damage",   finding:"Subterranean termite damaged wood noted at area(s) marked on diagram.", primaryRec:"Repair or replace subterranean termite damaged wood at areas marked on the diagram.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb3", cat:2, name:"2A-Drywood",      finding:"Evidence of drywood termites noted in area(s) marked on diagram.", primaryRec:"Cover house and garage and fumigate for the elimination of drywood termites.", secondaryRec:"Locally treat area(s) marked on the diagram for the control of drywood termites.", defaultPrice:0, priceUnit:"" },
  { id:"cb4", cat:2, name:"2B-Dry Damage",   finding:"Termite damaged wood noted at area(s) marked on diagram.", primaryRec:"Repair or replace termite damaged wood at areas marked on the diagram.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb5", cat:3, name:"3A-Fungus",       finding:"Fungus damaged wood noted at area(s) marked on diagram.", primaryRec:"Repair or replace fungus damaged wood at areas marked on the diagram.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb6", cat:3, name:"3B-Conducive",    finding:"Conducive conditions to wood decay noted at area(s) marked on diagram.", primaryRec:"Correct conducive conditions noted. Repair and treat affected areas as needed.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb7", cat:4, name:"4A-Other WDO",    finding:"Evidence of other wood destroying organisms noted in area(s) marked on diagram.", primaryRec:"Treat area(s) marked on diagram for wood destroying organisms.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
  { id:"cb8", cat:5, name:"5A-Further Insp", finding:"Area(s) marked on diagram are inaccessible and require further inspection.", primaryRec:"Further inspection of area(s) marked on diagram is recommended.", secondaryRec:"", defaultPrice:0, priceUnit:"" },
];

const DEFAULT_FUME_HEIGHT_KEY = 'termitescope_fume_heights_v1';
function loadFumeHeights(){ try{ return JSON.parse(localStorage.getItem(DEFAULT_FUME_HEIGHT_KEY))||null; }catch{return null;} }
function saveFumeHeights(h){ try{ localStorage.setItem(DEFAULT_FUME_HEIGHT_KEY,JSON.stringify(h)); }catch{} }

// Settings that belong to the whole firm (the office sets these once; inspectors
// inherit them read-only). Personal/device keys — apiKey, mapboxKey, resendApiKey,
// emailWebhookUrl, inspectors roster — are intentionally NOT shared.
const FIRM_SETTING_KEYS = ["reportStyle","company","rates","codebook","disclaimers","customDescriptions","hiddenDescKeys","descOrder"];
function firmShared(s){ const o={}; FIRM_SETTING_KEYS.forEach(k=>{ if(s && k in s) o[k]=s[k]; }); return o; }

const DEFAULT_COMPANY = { name:"", license:"", branchOffice:"", address:"", city:"", state:"CA", zip:"", phone:"", email:"", website:"", logo:"" };
const DEFAULT_RATES = { fumigation: 0, fumigationUnit: "÷1,000 cu ft", fumigationLocked: false, localTreatment: 0, localTreatmentUnit: "job", localTreatmentLocked: false, preventiveTreatment: 0, preventiveTreatmentUnit: "job", preventiveTreatmentLocked: false, repairHourly: 0, repairHourlyUnit: "hour", repairHourlyLocked: false };

// Fumigation is priced per 1,000 cubic feet (US standard). Earlier builds defaulted
// to "cu m" (× 0.0283), which produced wildly inflated prices. Move any existing
// "cu m" setup to "÷1,000 cu ft" ONCE; after the flag is set the user may pick any
// unit (including cu m) and it sticks.
function migrateRates(saved) {
  const m = { ...DEFAULT_RATES, ...(saved || {}) };
  if (!m.fumUnitStd) {
    if (m.fumigationUnit === "cu m") m.fumigationUnit = "÷1,000 cu ft";
    m.fumUnitStd = true;
  }
  return m;
}

// ─── SPECTORA CSV FIELD MAPPINGS ─────────────────────────────────────────────
// Maps Spectora's known column headers → TermiteScope job fields
// Spectora Custom Export lets you pick any columns; these are the standard ones
const SPECTORA_MAP = {
  // Date / Report
  "inspection date":        (j,v)=>({...j, date: v.slice(0,10) || j.date }),
  "scheduled date":         (j,v)=>({...j, date: v.slice(0,10) || j.date }),
  "report number":          (j,v)=>({...j, reportNum: v }),
  "confirmation number":    (j,v)=>({...j, reportNum: v }),
  "order number":           (j,v)=>({...j, reportNum: v }),
  // Address
  "inspection address":     (j,v)=>({...j, address: v }),
  "property address":       (j,v)=>({...j, address: v }),
  "address":                (j,v)=>({...j, address: v }),
  "city":                   (j,v)=>({...j, city: v }),
  "state":                  (j,v)=>({...j, state: v }),
  "zip":                    (j,v)=>({...j, zip: v }),
  "zip code":               (j,v)=>({...j, zip: v }),
  "county":                 (j,v)=>({...j, county: v.replace(" County","") }),
  // Client → Ordered By
  "client name":            (j,v)=>({...j, orderedBy:{...j.orderedBy, name:v} }),
  "client first name":      (j,v)=>({...j, orderedBy:{...j.orderedBy, name:(j.orderedBy.name?j.orderedBy.name+" ":"")+v} }),
  "client last name":       (j,v)=>({...j, orderedBy:{...j.orderedBy, name:(j.orderedBy.name?j.orderedBy.name+" ":"")+v} }),
  "client email":           (j,v)=>({...j, orderedBy:{...j.orderedBy, email:v} }),
  "client phone":           (j,v)=>({...j, orderedBy:{...j.orderedBy, phone:v} }),
  // Agent → Party of Interest
  "agent name":             (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, name:v} }),
  "agent first name":       (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, name:(j.partyOfInterest.name?j.partyOfInterest.name+" ":"")+v} }),
  "agent last name":        (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, name:(j.partyOfInterest.name?j.partyOfInterest.name+" ":"")+v} }),
  "agent email":            (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, email:v} }),
  "agent company":          (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, company:v} }),
  "agent brokerage":        (j,v)=>({...j, partyOfInterest:{...j.partyOfInterest, company:v} }),
  "referral source":        (j,v)=>({...j, leadSource:v }),
  "lead source":            (j,v)=>({...j, leadSource:v }),
  // Inspector — matched by name to saved profiles
  "inspector":              (j,v,settings)=>{
    const match = (settings.inspectors||[]).find(i=>i.name.toLowerCase()===v.toLowerCase());
    return match ? {...j, inspectorIds:[match.id]} : j;
  },
  "inspector name":         (j,v,settings)=>{
    const match = (settings.inspectors||[]).find(i=>i.name.toLowerCase()===v.toLowerCase());
    return match ? {...j, inspectorIds:[match.id]} : j;
  },
};

function parseSpectoraCSV(csvText) {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h=>h.replace(/^"|"$/g,"").trim());
  return lines.slice(1).map(line=>{
    // Handle quoted fields with commas inside
    const vals = [];
    let cur = ""; let inQ = false;
    for (let i=0;i<line.length;i++) {
      if (line[i]==='"') { inQ=!inQ; }
      else if (line[i]==="," && !inQ) { vals.push(cur.trim()); cur=""; }
      else cur+=line[i];
    }
    vals.push(cur.trim());
    const row = {};
    headers.forEach((h,i)=>{ row[h]=vals[i]||""; });
    return row;
  });
}

function applySpectoraRow(row, baseJob, settings) {
  let j = {...baseJob};
  Object.entries(row).forEach(([col, val]) => {
    if (!val) return;
    const fn = SPECTORA_MAP[col.toLowerCase()];
    if (fn) j = fn(j, val, settings) || j;
  });
  return j;
}


const PROPERTY_DESCRIPTIONS = [
  // SFR
  { key:"sfr1",  label:"1-Story SFR · Furn · Occ",   text:"1 Story - Single Family Residence - Furnished - Occupied",          type:"sfr",   stories:1 },
  { key:"sfr2",  label:"2-Story SFR · Furn · Occ",   text:"2 Story - Single Family Residence - Furnished - Occupied",          type:"sfr",   stories:2 },
  { key:"sfr3",  label:"3-Story SFR · Furn · Occ",   text:"3 Story - Single Family Residence - Furnished - Occupied",          type:"sfr",   stories:3 },
  { key:"sfr1v", label:"1-Story SFR · Unfurn · Vac", text:"1 Story - Single Family Residence - Unfurnished - Vacant",          type:"sfr",   stories:1 },
  { key:"sfr2v", label:"2-Story SFR · Unfurn · Vac", text:"2 Story - Single Family Residence - Unfurnished - Vacant",          type:"sfr",   stories:2 },
  // Condo/TH
  { key:"co1",   label:"1-Story Condo · Furn · Occ", text:"1 Story - Condominium - Furnished - Occupied",                     type:"condo", stories:1 },
  { key:"co2",   label:"2-Story Condo · Furn · Occ", text:"2 Story - Condominium - Furnished - Occupied",                     type:"condo", stories:2 },
  { key:"th1",   label:"2-Story Townhouse · Furn",   text:"2 Story - Townhouse - Furnished - Occupied",                       type:"condo", stories:2 },
  { key:"th2",   label:"3-Story Townhouse · Furn",   text:"3 Story - Townhouse - Furnished - Occupied",                       type:"condo", stories:3 },
  // Multi-family
  { key:"dup",   label:"Duplex · Occ",               text:"Duplex - Occupied",                                                  type:"multi", stories:1 },
  { key:"tri",   label:"Triplex · Occ",              text:"Triplex - Occupied",                                                  type:"multi", stories:1 },
  { key:"apt",   label:"Apartment Bldg",             text:"Apartment Building - Multiple Units - Occupied",                    type:"multi", stories:2 },
  // Manufactured / mobile
  { key:"mfh",   label:"Manufactured Home",          text:"Manufactured Home - Single Family - Occupied",                     type:"mfh",   stories:1 },
  // Commercial
  { key:"com1",  label:"Commercial Bldg",            text:"Commercial Building - Occupied",                                    type:"com",   stories:1 },
  { key:"com2",  label:"Industrial / Warehouse",     text:"Industrial / Warehouse Building",                                   type:"com",   stories:1 },
];

// Map Google Places types → property description keys
function inferDescFromPlaceTypes(types, address_components) {
  const t = types || [];
  const isUnit = address_components && address_components.some(c=>c.types.includes("subpremise"));
  if (t.includes("lodging") || t.includes("establishment")) return ["com1"];
  if (isUnit) return ["co1","co2","th1"]; // has unit # → likely condo/TH
  if (t.includes("premise") || t.includes("street_address")) return ["sfr1","sfr2"];
  if (t.includes("residential")) return ["sfr1","sfr2"];
  return ["sfr1","sfr2"]; // safe default
}
// Suggest description keys based on address text hints
function inferDescFromAddress(address) {
  const a = (address||'').toLowerCase();
  const unitWords = ['unit','apt','ste','suite'];
  if (unitWords.some(w=>a.includes(w)) || a.includes('#')) return ['co1','co2','th1'];
  return ['sfr1','sfr2'];
}


const COUNTY_EMERGENCY = {
  "San Diego":   [{name:"Poison Control Center",phone:"(800) 222-1222"},{name:"Agricultural Department",phone:"(858) 694-2739"},{name:"Health Department",phone:"(866) 358-2966"},{name:"Structural Pest Control Board",phone:"(916) 561-8700",addr:"2005 Evergreen Street, Ste. 1500. Sacramento, CA 95815"}],
  "Los Angeles": [{name:"Poison Control Center",phone:"(800) 222-1222"},{name:"Agricultural Commissioner",phone:"(626) 575-5471"},{name:"Health Department",phone:"(213) 240-8117"},{name:"Structural Pest Control Board",phone:"(916) 561-8700",addr:"2005 Evergreen Street, Ste. 1500. Sacramento, CA 95815"}],
  "Orange":      [{name:"Poison Control Center",phone:"(800) 222-1222"},{name:"Agricultural Commissioner",phone:"(714) 447-7100"},{name:"Health Department",phone:"(714) 834-8180"},{name:"Structural Pest Control Board",phone:"(916) 561-8700",addr:"2005 Evergreen Street, Ste. 1500. Sacramento, CA 95815"}],
  "Riverside":   [{name:"Poison Control Center",phone:"(800) 222-1222"},{name:"Agricultural Commissioner",phone:"(951) 955-3045"},{name:"Health Department",phone:"(951) 955-8982"},{name:"Structural Pest Control Board",phone:"(916) 561-8700",addr:"2005 Evergreen Street, Ste. 1500. Sacramento, CA 95815"}],
  "default":     [{name:"Poison Control Center",phone:"(800) 222-1222"},{name:"Structural Pest Control Board",phone:"(916) 561-8700",addr:"2005 Evergreen Street, Ste. 1500. Sacramento, CA 95815"}],
};

const WORK_AUTH_LEGAL = `THE COMPANY AGREES\nTo guarantee all repair completed by this company for one year from date of completion except for caulking, grouting, or plumbing, which is guaranteed for a period of ninety (90) days. We assume no responsibility for work performed by others, to be bound to perform this work for the price quoted in our cost breakdown for a period not to exceed 30 days, to use reasonable care in the performance of our work but to assume no responsibility for damage to any hidden pipes, wiring, or other facilities or to any shrubs, plants, or roof.\n\nTHE OWNER OR OWNER'S AGENT AGREES\nTo pay for services rendered in any additional services requested upon completion of work to pay a service charge of one and one-half percent (1 1/2%) interest per month, or portion of any month, annual interest rate of eighteen percent (18%) on accounts exceeding the ten (10) day full payment schedule. The Owner grants to The Company a security interest in the property to secure payment sum for work and inspection fee completed. In case of non-payment by The owner, reasonable attorney fees and costs of collection shall be paid by owner, whether suit be filed or not.\n\nALL PARTIES AGREE\nIf any additional work is deemed necessary by the local building inspector, said work will not be performed without additional authorization from owner or owner's agent. This contract price does not include the charge of any Inspection Report fees.\n\nNOTICE TO OWNER\nUnder the California Mechanics Lien Law, any structural pest control company which contracts to do work for you, any contractor, subcontractor, laborer, supplier or other person who helps to improve your property, but is not paid for his or her work or supplies, has a right to enforce a claim against your property. This means that after a court hearing, your property could be sold by a court officer and the proceeds of the sale used to satisfy the indebtedness. This can happen even if you have paid your structural pest control company in full if the subcontractor, laborers or suppliers remain unpaid.`;

const PESTICIDE_CAUTION = `CAUTION - PESTICIDES ARE TOXIC CHEMICALS. Structural pest control companies are registered and regulated by the Structural Pest Control Board, and apply pesticides which are registered and approved for use by the California Department of Pesticide Regulation and the United States Environmental Protection Agency. Registration is granted when the state finds that based on scientific evidence, there are no appreciable risks weighted by the benefits. The degree of risk depends on the degree of exposure, so exposure should be minimized.\n\nIf within 24 hours following application, you experience symptoms similar to common seasonal illness comparable to the flu, contact your physician or poison control center and your pest control operator immediately.`;

const FR_TAIL = `Thank you for selecting us to perform a structural pest control inspection on your property. Should you have any questions regarding this report, please call us directly by the contact information provided on the first page of the inspection report.\n\nOur inspectors have determined that your property will benefit from a safe application of chemicals commonly used for structural pest control. In accordance with the laws and regulations of the State of California, we are required to provide you and your occupants with the following information prior to any application of chemicals to such property.`;


// ─── MEASUREMENT HELPERS ──────────────────────────────────────────────────────
// Mapbox GL Static API: at zoom Z, 1 tile = 512px = 78271.51696 * cos(lat*π/180) / 2^Z meters

// Convert a volume in cubic feet to the user's chosen pricing unit.
// Returns the multiplier: cuFt × factor = volume in target unit.
// Recognised metric aliases: cu m, m³, m3, cbm, cubic meter/metre
// Recognised yard aliases:   cu yd, yd³, yd3, cubic yard
// Everything else (cu ft, per job, linear ft …) → factor 1 (no conversion).
function cuFtConversionFactor(unit) {
  const u = (unit || "").toLowerCase().replace(/[\s·³(÷),]/g, "");
  // ÷1,000 billing unit — 1,000 cu ft = 1 billing unit (user's fumigation convention)
  // Accepts both old "m³ (÷1000)" name and new "÷1,000 cu ft" name for backward compat.
  if (["m31000","1000cuft","1000","per1000","div1000cuft","1kculft"].includes(u)) return 0.001;
  if (["cum","m3","cbm","cubicmeter","cubicmetre"].includes(u)) return 0.0283168;
  if (["cuyd","yd3","cubicyard"].includes(u)) return 1 / 27;
  return 1;
}
// Format a volume number nicely: integers for cu ft, 2 decimals for metric units.
function formatVolume(cuFt, unit) {
  const factor = cuFtConversionFactor(unit);
  const vol = cuFt * factor;
  return factor === 1 ? Math.round(vol).toLocaleString() : vol.toFixed(2);
}
// Map OSM building tags → a PROPERTY_DESCRIPTIONS key
function osmTagsToPropDesc(tags) {
  if (!tags) return null;
  const bldg   = (tags.building || '').toLowerCase();
  const levels = parseInt(tags['building:levels']) || null;
  const stories = levels || 1;

  const COM_TYPES = ['commercial','office','retail','industrial','warehouse','store','supermarket','hotel','motel'];
  const APT_TYPES = ['apartments','apartment','flat','flats','residential'];
  const TH_TYPES  = ['townhouse','terrace','semidetached_house'];
  const MFH_TYPES = ['mobile_home','manufactured'];

  if (COM_TYPES.includes(bldg)) return 'com1';
  if (MFH_TYPES.includes(bldg)) return 'mfh';
  if (APT_TYPES.includes(bldg)) return stories >= 2 ? 'apt' : 'dup';
  if (TH_TYPES.includes(bldg))  return stories >= 3 ? 'th2' : 'th1';

  // Default → SFR, use level count
  if (stories >= 3) return 'sfr3';
  if (stories === 2) return 'sfr2';
  return 'sfr1';
}

// Convert a lat/lng to canvas pixel coordinates given the map center, zoom, and rotation
// pixelScale: canvas pixels per tile world-pixel (default 1).
// With cover-mode rendering (MAP_PIXEL_SCALE = 1.6) pass MAP_PIXEL_SCALE so that
// overlay polygons align with the scaled-up satellite image.
function latLngToCanvasPx(lat, lng, centerLat, centerLng, zoom, canvasW, canvasH, rotDeg, pixelScale) {
  const ps = pixelScale || 1;
  // Mapbox GL uses 512-pixel tiles; use 512 as the base tile size so that
  // geographic offsets map to the correct canvas pixels on the Mapbox satellite image.
  const scale = 512 * Math.pow(2, zoom);
  function toWorld(la, lo) {
    const x = (lo + 180) / 360 * scale;
    const sinLat = Math.sin(la * Math.PI / 180);
    const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
    return [x, y];
  }
  const [cx, cy] = toWorld(centerLat, centerLng);
  const [px, py] = toWorld(lat, lng);
  let dx = px - cx, dy = py - cy;
  const r = ((rotDeg || 0) * Math.PI) / 180;
  const rdx = dx * Math.cos(r) - dy * Math.sin(r);
  const rdy = dx * Math.sin(r) + dy * Math.cos(r);
  return [canvasW / 2 + rdx * ps, canvasH / 2 + rdy * ps];
}
// Shoelace formula → area in canvas px²
function polygonAreaPx(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i][0] * pts[j][1];
    a -= pts[j][0] * pts[i][1];
  }
  return Math.abs(a) / 2;
}
// Canvas is 800px wide but displayed at ~full page width
// pts are in canvas coords (0–800), mpp = meters/px at this zoom
function canvasAreaSqFt(pts, mpp) {
  const areaPx = polygonAreaPx(pts);
  const areaM2 = areaPx * mpp * mpp;
  return areaM2 * 10.7639; // m² → ft²
}

// Inverse of latLngToCanvasPx — convert a canvas pixel back to lat/lng
function canvasPxToLatLng(x, y, centerLat, centerLng, zoom, canvasW, canvasH, rotDeg, pixelScale) {
  const ps = pixelScale || 1;
  const dx = (x - canvasW / 2) / ps;
  const dy = (y - canvasH / 2) / ps;
  // Undo rotation
  const r = -((rotDeg || 0) * Math.PI) / 180;
  const udx = dx * Math.cos(r) - dy * Math.sin(r);
  const udy = dx * Math.sin(r) + dy * Math.cos(r);
  // Mapbox GL uses 512-pixel tiles; use 512 as the base tile size (inverse of latLngToCanvasPx).
  const scale = 512 * Math.pow(2, zoom);
  const sinCLat = Math.sin(centerLat * Math.PI / 180);
  const cx = (centerLng + 180) / 360 * scale;
  const cy = (0.5 - Math.log((1 + sinCLat) / (1 - sinCLat)) / (4 * Math.PI)) * scale;
  const wx = cx + udx, wy = cy + udy;
  // Invert to lat/lng
  const lng = wx / scale * 360 - 180;
  const n = Math.PI - 2 * Math.PI * wy / scale;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return [lat, lng];
}

// Geodetically accurate area — projects each vertex back to lat/lng, then to
// local East-North metres from the centroid, then applies Shoelace.
// More accurate than canvas-px × mpp² because it accounts for the true
// position of every vertex rather than using a single centre-point scale.
function geoAreaSqFt(pts, mapCenter, zoom, rotDeg, canvasW, canvasH, pixelScale) {
  if (!mapCenter || !zoom || pts.length < 3) return 0;
  const cW = canvasW || CANVAS_W, cH = canvasH || CANVAS_H;
  const ll = pts.map(([x, y]) =>
    canvasPxToLatLng(x, y, mapCenter.lat, mapCenter.lng, zoom, cW, cH, rotDeg, pixelScale)
  );
  // Centroid of polygon vertices (local ENU reference point)
  const cLat = ll.reduce((s, p) => s + p[0], 0) / ll.length;
  const cLng = ll.reduce((s, p) => s + p[1], 0) / ll.length;
  const kLat = 111320;                                     // metres per degree latitude
  const kLng = 111320 * Math.cos(cLat * Math.PI / 180);   // metres per degree longitude
  // Project to local East-North metres
  const en = ll.map(([lat, lng]) => [(lng - cLng) * kLng, (lat - cLat) * kLat]);
  // Shoelace on metre coordinates
  let a = 0;
  for (let i = 0; i < en.length; i++) {
    const j = (i + 1) % en.length;
    a += en[i][0] * en[j][1] - en[j][0] * en[i][1];
  }
  return Math.abs(a) / 2 * 10.7639; // m² → ft²
}

// Ray-cast point-in-polygon for canvas pixel coords.
// Used to pick the polygon that contains the map center (correct building).
function ptInPoly(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (((yi > pt[1]) !== (yj > pt[1])) &&
        (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

// Geodesic area in ft² directly from a lat/lng ring [{lat,lon}...].
// Used when we have the actual building footprint coordinates (MS Buildings / OSM / Solar).
function latLngAreaSqFt(ring) {
  if (!ring || ring.length < 3) return 0;
  const cLat = ring.reduce((s, g) => s + g.lat, 0) / ring.length;
  const cLon = ring.reduce((s, g) => s + g.lon, 0) / ring.length;
  const kLat = 111320, kLng = 111320 * Math.cos(cLat * Math.PI / 180);
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    a += (ring[i].lon - cLon) * kLng * ((ring[j].lat - cLat) * kLat)
       - (ring[j].lon - cLon) * kLng * ((ring[i].lat - cLat) * kLat);
  }
  return Math.abs(a) / 2 * 10.7639; // m² → ft²
}

// ─── STORAGE ──────────────────────────────────────────────────────────────────
const LS_KEY = "termitescope_v3";
const SETTINGS_KEY = "termitescope_settings_v3";
const CONTACTS_KEY  = "termitescope_contacts_v1";
const CONTACT_TYPES = ["Agent","Buyer","Seller","Owner","Escrow","Other"];
// Wrapped localStorage write. On a quota failure (photo-heavy job overflowing the
// ~5MB cap) it fires a window event so the UI can warn instead of losing the write
// silently. Returns false when the write did not persist.
function lsSet(key, value){
  try{ localStorage.setItem(key, value); return true; }
  catch(e){
    const quota = e && (e.name==='QuotaExceededError' || e.code===22 || e.code===1014 || e.name==='NS_ERROR_DOM_QUOTA_REACHED');
    try{ window.dispatchEvent(new CustomEvent('ts-storage-error',{detail:{quota:!!quota}})); }catch{}
    return false;
  }
}
function loadContacts(){ try{ return JSON.parse(localStorage.getItem(CONTACTS_KEY))||[]; }catch{ return []; } }
function saveContacts(c){ return lsSet(CONTACTS_KEY,JSON.stringify(c)); }
function loadSettings() { try { return JSON.parse(localStorage.getItem(SETTINGS_KEY))||null; } catch { return null; } }
function saveSettings(s) { return lsSet(SETTINGS_KEY,JSON.stringify(s)); }
const JOBS_KEY = "termitescope_jobs_v3";
// Legacy localStorage readers — kept only to migrate old data into IndexedDB.
function loadJobLegacy() { try { return JSON.parse(localStorage.getItem(LS_KEY))||null; } catch { return null; } }
function loadJobsLegacy() { try { return JSON.parse(localStorage.getItem(JOBS_KEY))||[]; } catch { return []; } }

// ── IndexedDB key-value store ───────────────────────────────────────────────
// Photos and satellite images are base64 inside the job blob and overflow
// localStorage's ~5MB cap. IndexedDB holds far more and stores objects directly
// (structured clone — no JSON string bloat). The open job + jobs list live here.
const IDB_NAME = "termitescope", IDB_STORE = "kv";
let _idbPromise = null;
function idbOpen(){
  if(_idbPromise) return _idbPromise;
  _idbPromise = new Promise((resolve,reject)=>{
    try{
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = ()=>{ try{ req.result.createObjectStore(IDB_STORE); }catch{} };
      req.onsuccess = ()=>resolve(req.result);
      req.onerror   = ()=>reject(req.error);
    }catch(e){ reject(e); }
  });
  return _idbPromise;
}
async function idbGet(key){
  try{ const db=await idbOpen(); return await new Promise((res,rej)=>{ const r=db.transaction(IDB_STORE,"readonly").objectStore(IDB_STORE).get(key); r.onsuccess=()=>res(r.result); r.onerror=()=>rej(r.error); }); }
  catch{ return undefined; }
}
async function idbSet(key,val){
  try{ const db=await idbOpen(); return await new Promise((res,rej)=>{ const tx=db.transaction(IDB_STORE,"readwrite"); tx.objectStore(IDB_STORE).put(val,key); tx.oncomplete=()=>res(true); tx.onerror=()=>rej(tx.error); }); }
  catch{ try{ window.dispatchEvent(new CustomEvent('ts-storage-error',{detail:{quota:true}})); }catch{} return false; }
}
function saveJob(j)  { idbSet('job',  j);    return true; }
function saveJobs(js){ idbSet('jobs', js||[]); return true; }

// ─── SUPABASE CLOUD SYNC ──────────────────────────────────────────────────────
async function sbLoadSettings(userId){ try{ const {data}=await supabase.from("user_settings").select("data").eq("user_id",userId).maybeSingle(); return data?.data||null; }catch{ return null; } }
async function sbSaveSettings(userId,s){ try{ await supabase.from("user_settings").upsert({user_id:userId,data:s}); }catch{} }
async function sbLoadJob(userId){ try{ const {data}=await supabase.from("user_current_job").select("data").eq("user_id",userId).maybeSingle(); return data?.data||null; }catch{ return null; } }
async function sbSaveJob(userId,j){ try{ await supabase.from("user_current_job").upsert({user_id:userId,data:j,updated_at:new Date().toISOString()}); }catch{} }
async function sbLoadContacts(userId){ try{ const {data}=await supabase.from("user_contacts").select("data").eq("user_id",userId).maybeSingle(); return data?.data||[]; }catch{ return []; } }
async function sbSaveContacts(userId,c){ try{ await supabase.from("user_contacts").upsert({user_id:userId,data:c}); }catch{} }
async function sbLoadJobs(userId){ try{ const {data}=await supabase.from("user_jobs").select("data").eq("user_id",userId).maybeSingle(); return data?.data||null; }catch{ return null; } }
async function sbSaveJobs(userId,jobs){ try{ await supabase.from("user_jobs").upsert({user_id:userId,data:jobs,updated_at:new Date().toISOString()}); }catch{} }

// ─── Firm / role cloud helpers (return safe defaults if tables not set up) ───
async function sbLoadProfile(userId){ try{ const {data}=await supabase.from("firm_profiles").select("id,email,full_name,role,active").eq("id",userId).maybeSingle(); return data||null; }catch{ return null; } }
async function sbLoadProfiles(){ try{ const {data}=await supabase.from("firm_profiles").select("id,email,full_name,role,active"); return data||[]; }catch{ return []; } }
// Per-row shared jobs. RLS decides which rows a caller receives (staff: all;
// inspector: only rows assigned_to them). Returns null on any failure so the
// caller can fall back to legacy per-user storage.
async function sbLoadFirmJobs(){
  try{
    const {data,error}=await supabase.from("firm_jobs").select("id,data,assigned_to,updated_at");
    if(error||!data) return null;
    // `updatedAt` is the server revision, used for optimistic-lock conflict checks.
    return data.map(r=>({...(r.data||{}), id:r.id, assignedTo:r.assigned_to||null, updatedAt:r.updated_at}));
  }catch{ return null; }
}
// Save a job to firm_jobs. Returns {ok, rev}. Strips transient client-only keys.
// UPDATE-first, INSERT-only-if-new: inspectors have RLS to UPDATE their assigned
// job but NOT to INSERT, so a plain .upsert() (INSERT-on-conflict) would be
// rejected for them — breaking sync and the submit trigger. Update-first lets
// inspectors save their existing job; new jobs (staff) fall through to insert.
const firmWrites=new Map(),deletingFirmJobs=new Set();
function sbUpsertFirmJob(job,userId){
  const id=String(job.id);if(deletingFirmJobs.has(id))return Promise.resolve({ok:false});
  const pending=writeFirmJob(job,userId),writes=firmWrites.get(id)||new Set();writes.add(pending);firmWrites.set(id,writes);
  pending.finally(()=>{writes.delete(pending);if(!writes.size)firmWrites.delete(id);});return pending;
}
async function writeFirmJob(job,userId){
  try{
    const now=new Date().toISOString();
    const {updatedAt, _rev, ...clean}=job; // drop client-only revision markers
    const row={ data:clean, assigned_to:job.assignedTo||null, updated_at:now, updated_by:userId };
    const {data:upd,error:updErr}=await supabase.from("firm_jobs").update(row).eq("id",String(job.id)).select("id");
    if(updErr) return {ok:false};
    if(upd && upd.length>0) return {ok:true, rev:now};
    // No existing row updated → create it (staff insert; inspectors can't reach here normally).
    const {error:insErr}=await supabase.from("firm_jobs").insert({ id:String(job.id), ...row });
    return {ok:!insErr, rev:now};
  }catch{ return {ok:false}; }
}
async function sbDeleteFirmJob(id){
  id=String(id);deletingFirmJobs.add(id);await Promise.allSettled([...(firmWrites.get(id)||[])]);
  try{const {data,error}=await supabase.from("firm_jobs").delete().eq("id",id).select("id");if(error||!data?.length)throw Error("Could not delete this job from the cloud. Check your connection and permissions, then retry.");}
  catch(error){deletingFirmJobs.delete(id);throw error;}
}
async function sbUpdateProfile(id,patch){ try{ const {error}=await supabase.from("firm_profiles").update(patch).eq("id",id); return !error; }catch{ return false; } }
// Create an inspector login via the secure server function (needs the
// create-inspector Edge Function deployed). Returns {ok} or {error}.
async function sbCreateInspector(payload){
  try{
    // NOTE: Supabase auto-generated this function's slug as "bright-action"
    // (its display name is "create-inspector"). The app must call the real slug.
    const {data,error}=await supabase.functions.invoke("bright-action",{body:payload});
    if(error){
      let msg=error.message||"Request failed";
      try{ const ctx=await error.context?.json?.(); if(ctx?.error) msg=ctx.error; }catch{}
      return {error:msg};
    }
    return data||{ok:true};
  }catch(e){ return {error:String(e?.message||e)}; }
}
async function sbLoadNotifications(userId){ try{return await loadUnreadNotifications(supabase,userId);}catch{return null;} }
async function sbCreateNotification(userId,n){ try{ await supabase.from("notifications").insert({user_id:userId, ...n}); }catch{} }
// Firm-wide shared settings (single row). RLS: any member reads; only admin/operator writes.
async function sbLoadFirmSettings(){ try{ const {data,error}=await supabase.from("firm_settings").select("data").eq("id",1).maybeSingle(); if(error) return null; return data?.data||null; }catch{ return null; } }
async function sbSaveFirmSettings(data){ try{ const {error}=await supabase.from("firm_settings").upsert({id:1,data,updated_at:new Date().toISOString()}); return !error; }catch{ return false; } }

function newJob(reportNum) {
  const now = new Date();
  return {
    id: Date.now().toString(),
    reportNum: reportNum||"1", reportType:"Complete Report",
    date: now.toISOString().split("T")[0], time: now.toTimeString().slice(0,5),
    escrow:"", leadSource:"",
    orderedBy:  {name:"",company:"",phone:"",email:""},
    propOwner:  {name:"",company:"",phone:"",email:""},
    partyOfInterest:{name:"",company:"",email:""},
    address:"", city:"", state:"CA", zip:"", county:"", propDesc:"",
    inspectorIds:[], inspectionTags:[], customTags:"",
    drawings:[], markers:[], findings:[],
    mapBg:null, mapCenter:null, mapZoom:20, mapRotation:0, mapOffX:0, mapOffY:0, canvasH:NEW_CANVAS_H,
    osmLevels:1, clientSignature:"",
    stageProgress: { FP:false, DM:false, FR:false, CT:false, CS:false, NC:false },
    flag: false, noc: false, invoice: "",
    reportSentAt:"", reportSentTo:[],
    nocCompletionDate:"", nocSentTo:"",
    nocAutoSum:true, nocManualCost:0, nocInspectionFee:0, nocOtherFee:0,
    nocNotCompleted:"",
    nocCertification:"full", nocExceptText:"", nocRemarks:"",
    nocInspectorId:"", nocIncludeSignature:true,
  };
}

function nextLetter(findings, cat) {
  const used = findings.filter(f=>f.cat===cat).map(f=>f.letter);
  for (let i=0;i<26;i++) { const l=String.fromCharCode(65+i); if(!used.includes(l)) return l; }
  return "Z";
}
function sectionLabel(cat) {
  if (cat<=3) return "Section I"; if (cat===4) return "Section II"; return "Further Inspection";
}
function hexToRgb(hex) {
  return [parseInt(hex.slice(1,3),16),parseInt(hex.slice(3,5),16),parseInt(hex.slice(5,7),16)];
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  app: {fontFamily:"'DM Sans','Helvetica Neue',sans-serif",background:"#f0f2f5",minHeight:"100vh",color:"#1a1a2e"},
  header: {position:"sticky",top:0,zIndex:100,background:"#0f172a",borderBottom:"2px solid #22d3ee",display:"flex",alignItems:"center",gap:16,padding:"10px 20px"},
  logo: {fontWeight:800,fontSize:20,color:"#22d3ee",letterSpacing:-0.5,display:"flex",alignItems:"center",gap:8},
  progBar: {display:"flex",gap:4,flex:1,marginLeft:16,flexWrap:"wrap"},
  progStep: (active,done) => ({padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:done?"#22d3ee":active?"#1e40af":"#1e293b",color:done?"#0f172a":active?"#fff":"#64748b",cursor:"pointer",border:"none",transition:"all 0.2s"}),
  page: {maxWidth:1200,margin:"0 auto",padding:"24px 16px 80px"},
  card: {background:"#fff",borderRadius:12,padding:20,marginBottom:16,boxShadow:"0 1px 4px rgba(0,0,0,0.08)"},
  cardTitle: {fontSize:13,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:14},
  grid2: {display:"grid",gridTemplateColumns:"1fr 1fr",gap:12},
  grid3: {display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12},
  label: {display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4},
  input: {width:"100%",padding:"8px 10px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:14,background:"#f8fafc",outline:"none",boxSizing:"border-box"},
  select: {width:"100%",padding:"8px 10px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:14,background:"#f8fafc",outline:"none",boxSizing:"border-box"},
  textarea: {width:"100%",padding:"8px 10px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,background:"#f8fafc",resize:"vertical",outline:"none",boxSizing:"border-box"},
  btn: (color) => ({background:color||"#0f172a",color:"#fff",border:"none",borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}),
  btnOutline: {background:"transparent",border:"1px solid #e2e8f0",borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer",color:"#475569"},
  btnSm: (color) => ({background:color||"#0f172a",color:["transparent","#f1f5f9"].includes(color)?"#475569":"#fff",border:"none",borderRadius:6,padding:"5px 10px",fontSize:12,fontWeight:600,cursor:"pointer"}),
  btnSmOutline: {background:"transparent",border:"1px solid #cbd5e1",borderRadius:6,padding:"5px 10px",fontSize:12,cursor:"pointer",color:"#64748b"},
  chip: (active,color) => ({display:"inline-flex",alignItems:"center",padding:"4px 10px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",margin:"2px 3px",background:active?(color||"#0f172a"):"#f1f5f9",color:active?"#fff":"#475569",border:active?"none":"1px solid #e2e8f0"}),
  overlay: {position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"},
  modal: {background:"#fff",borderRadius:16,padding:16,width:"90%",maxWidth:640,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"},
  modalLg: {background:"#fff",borderRadius:16,padding:16,width:"95%",maxWidth:900,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"},
  findingCard: {border:"1px solid #e2e8f0",borderRadius:10,padding:14,marginBottom:10},
  findingBadge: (color) => ({display:"inline-flex",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:8,background:color,color:"#fff",fontWeight:800,fontSize:14,flexShrink:0}),
};

// ─── MAPBOX ───────────────────────────────────────────────────────────────────
const DEFAULT_MAPBOX_KEY = "pk.eyJ1Ijoic2NvdHR5YTcxIiwiYSI6ImNtbXF2cTE0aDE0Y2kyc29tbG1ieGRmdncifQ.VkjKieAKvuCkqdBMeBSbWQ";
async function geocodeAddress(address, apiKey) {
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${apiKey}&country=us&types=address&limit=1&proximity=ip`;
  let r, d;
  try { r = await fetch(url); d = await r.json(); }
  catch (e) { throw new Error("Geocoding network error: " + e.message); }
  if (!r.ok) throw new Error(`Mapbox API error ${r.status}: ${d.message || r.statusText}`);
  if (d.features && d.features.length > 0) {
    const feat = d.features[0];
    const [lng, lat] = feat.geometry.coordinates;
    const ctx = feat.context || [];
    let county = "";
    ctx.forEach(c => { if (c.id.startsWith("district.")) county = c.text.replace(" County",""); });
    // Mapbox doesn't return bounds for addresses; derive a small bbox
    return { lat, lng, county, bounds: null };
  }
  return null;
}
async function mapboxSuggest(query, apiKey) {
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${apiKey}&country=us&types=address&limit=5&proximity=ip`;
  const r = await fetch(url); const d = await r.json();
  if(!r.ok || d.message) { console.warn("Mapbox suggest error:", d.message||r.status); return []; }
  return d.features || [];
}
function staticMapUrl(lat, lng, zoom, w, h, apiKey, maptype="satellite") {
  const style = maptype === "hybrid" ? "satellite-streets-v12" : "satellite-v9";
  return `https://api.mapbox.com/styles/v1/mapbox/${style}/static/${lng},${lat},${zoom}/${w}x${h}?access_token=${apiKey}`;
}


// ─── DEFAULT CA DISCLAIMERS ───────────────────────────────────────────────────
const CA_DEFAULT_DISCLAIMERS = [
  {
    id:"disc_1", enabled:true, title:"Scope of Inspection",
    text:"This inspection is limited to the visible and accessible areas of the structure. Concealed areas, areas covered by insulation, furniture, stored goods, wall coverings, flooring, or other obstructions were not inspected. This report does not cover conditions that are not visible or accessible at the time of inspection."
  },
  {
    id:"disc_2", enabled:true, title:"Liability Limitation",
    text:"The liability of this company is limited to the cost of the inspection fee. This company shall not be liable for any consequential or incidental damages arising from the inspection or any work performed as a result thereof. This inspection report is not a guarantee or warranty of any kind."
  },
  {
    id:"disc_3", enabled:true, title:"CA DOSH Compliance",
    text:"This inspection was performed in accordance with the California Structural Pest Control Act (Business and Professions Code §8500 et seq.) and applicable regulations of the Structural Pest Control Board. This report is valid for 30 days from the date of inspection unless otherwise noted."
  },
  {
    id:"disc_4", enabled:true, title:"Wood Destroying Organisms",
    text:"Section I findings indicate active infestation or infection requiring corrective action. Section II findings indicate conditions present that are conducive to infestation or infection, or evidence of prior activity with no current evidence of activity. Further Inspection items indicate areas that were inaccessible and may require additional inspection before a complete report can be issued."
  },
  {
    id:"disc_5", enabled:false, title:"Roof Exclusion",
    text:"The roof covering and roof structure were not inspected as part of this report. A licensed roofing contractor should be consulted for a complete assessment of the roof condition."
  },
  {
    id:"disc_6", enabled:false, title:"Mold / Environmental",
    text:"This inspection is limited to wood destroying organisms and does not address mold, asbestos, lead paint, radon, or other environmental hazards. A qualified environmental inspector should be consulted for these concerns."
  },
];

// ─── PDF ──────────────────────────────────────────────────────────────────────
function generatePDF(job, settings) {
  const doc = new jsPDF({orientation:"portrait",unit:"pt",format:"letter"});
  const W=612, margin=48;
  const layout=createPDFReportLayout(doc,job,settings),style=layout.style;
  let y=layout.top,currentTitle="FINDINGS AND RECOMMENDATIONS";
  function addPage(){layout.y=y;layout.addPage(currentTitle);y=layout.y;}
  function checkY(n){if(y+n>layout.bottom)addPage();}
  function hdrBar(title){currentTitle=title;layout.setSection(title);y=layout.top+12;}
  function ln(x1,y1,x2,y2,rgb){doc.setDrawColor(...(rgb||[200,210,220]));doc.setLineWidth(0.5);doc.line(x1,y1,x2,y2);doc.setLineWidth(1);}
  function secTitle(txt,accentRgb){
    checkY(28);const rgb=accentRgb||reportRGB(reportTextColor(style.primary));
    doc.setFillColor(...reportTint(style.accent,.05));doc.rect(margin,y,W-margin*2,20,"F");
    doc.setFillColor(...rgb);doc.rect(margin,y,3,20,"F");
    doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(...rgb);doc.text(txt.toUpperCase(),margin+10,y+13);y+=26;
  }
  function renderDiagram(){
    const hasContent=(job.drawings||[]).length||(job.markers||[]).length||job.mapBg;
    if(!hasContent)return null;
    try{
        const CW=CANVAS_W, CH=job.canvasH||CANVAS_H;
        const printLayout=pdfDiagramLayout(job);
        const printH=printLayout?printLayout.height:CH;
        const dc=document.createElement("canvas"); dc.width=CW; dc.height=printH;
        const dctx=dc.getContext("2d");

        // Graph paper background — always clean white/grid for PDF
        dctx.fillStyle=style.diagramBackground==="tint"?`rgb(${reportTint(style.accent,.06).join(",")})`:"#ffffff"; dctx.fillRect(0,0,CW,printH);
        if(style.diagramBackground==="grid"){
        // Minor grid
        dctx.strokeStyle="rgba(180,200,225,0.6)"; dctx.lineWidth=0.5;
        for(let x=0;x<=CW;x+=20){dctx.beginPath();dctx.moveTo(x,0);dctx.lineTo(x,printH);dctx.stroke();}
        for(let yy=0;yy<=printH;yy+=20){dctx.beginPath();dctx.moveTo(0,yy);dctx.lineTo(CW,yy);dctx.stroke();}
        // Major grid
        dctx.strokeStyle="rgba(140,170,210,0.75)"; dctx.lineWidth=1;
        for(let x=0;x<=CW;x+=100){dctx.beginPath();dctx.moveTo(x,0);dctx.lineTo(x,printH);dctx.stroke();}
        for(let yy=0;yy<=printH;yy+=100){dctx.beginPath();dctx.moveTo(0,yy);dctx.lineTo(CW,yy);dctx.stroke();}
        }

        if(printLayout){dctx.save();dctx.translate(printLayout.x,printLayout.y);dctx.scale(printLayout.scale,printLayout.scale);}
        // Draw shapes
        (job.drawings||[]).forEach(d=>{
          if(!d.pts||d.pts.length<2) return;
          dctx.beginPath(); dctx.moveTo(d.pts[0][0],d.pts[0][1]);
          for(let i=1;i<d.pts.length;i++) dctx.lineTo(d.pts[i][0],d.pts[i][1]);
          if(d.closed) dctx.closePath();
          dctx.strokeStyle=style.primary; dctx.lineWidth=2.5;
          if(d.style==="dashed") dctx.setLineDash([10,6]);
          else if(d.style==="dotted") dctx.setLineDash([3,6]);
          else dctx.setLineDash([]);
          dctx.stroke(); dctx.setLineDash([]);
        });

        // Area labels on closed polygons
        if(job.mapCenter){
          (job.drawings||[]).filter(d=>d.closed&&d.pts&&d.pts.length>=3).forEach(d=>{
            const sqft=Math.round(geoAreaSqFt(d.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,CW,CH,MAP_PIXEL_SCALE));
            if(sqft<1)return;
            const cx2=d.pts.reduce((s,p)=>s+p[0],0)/d.pts.length;
            const cy2=d.pts.reduce((s,p)=>s+p[1],0)/d.pts.length;
            const lbl=sqft>=1000?(sqft/1000).toFixed(1)+"k sqft":sqft+" sqft";
            dctx.font="bold 13px sans-serif"; dctx.textAlign="center"; dctx.textBaseline="middle";
            const tw=dctx.measureText(lbl).width+14;
            dctx.fillStyle="rgba(15,23,42,0.75)";
            dctx.beginPath(); dctx.roundRect(cx2-tw/2,cy2-11,tw,22,4); dctx.fill();
            dctx.fillStyle="#fff"; dctx.fillText(lbl,cx2,cy2);
          });
        }

        // Draw WDO markers
        (job.markers||[]).forEach(m=>{
          if(m.type==="area"){
            dctx.font=`bold ${Math.max(m.fontSize||13,22)}px sans-serif`; dctx.textAlign="center"; dctx.textBaseline="middle";
            dctx.strokeStyle="rgba(255,255,255,0.9)"; dctx.lineWidth=3; dctx.lineJoin="round";
            dctx.strokeText(m.label,m.x,m.y);
            dctx.fillStyle="#0f172a"; dctx.fillText(m.label,m.x,m.y);
            return;
          }
          const dFs=Math.max(m.fontSize||14,22); const dLh=dFs*1.25;
          dctx.font=`bold ${dFs}px sans-serif`; dctx.textAlign="center"; dctx.textBaseline="middle";
          dctx.strokeStyle="rgba(255,255,255,0.95)"; dctx.lineWidth=3; dctx.lineJoin="round";
          const dParts=m.label.split("+");
          dParts.forEach((p,i)=>{ const ty=m.y+(i-(dParts.length-1)/2)*dLh; dctx.strokeText(p,m.x,ty); });
          dctx.fillStyle=style.primary; dParts.forEach((p,i)=>{ const ty=m.y+(i-(dParts.length-1)/2)*dLh; dctx.fillText(p,m.x,ty); });
        });

        if(printLayout)dctx.restore();
        // Crop tightly to the traced content so the shape sits centered.
        // The address label is drawn afterward INSIDE the crop, so it no longer
        // stretches the bounding box out to the top-left corner (which used to
        // push the shape off into a corner with empty grid space around it).
        const allContentPts=[
          ...(job.drawings||[]).flatMap(d=>d.pts||[]),
          ...(job.markers||[]).map(m=>[m.x,m.y]),
        ];
        let imgData, aspectRatio;
        if(printLayout){
          if(job.address){dctx.font="bold 14px sans-serif";dctx.fillStyle="#0f172a";dctx.textAlign="left";dctx.textBaseline="top";dctx.fillText(job.address+(job.city?", "+job.city:""),12,10);}
          imgData=dc.toDataURL("image/png");aspectRatio=printLayout.height/printLayout.width;
        } else if(allContentPts.length>0){
          const pad=60;
          const bx1=Math.max(0,Math.min(...allContentPts.map(p=>p[0]))-pad);
          const by1=Math.max(0,Math.min(...allContentPts.map(p=>p[1]))-pad);
          const bx2=Math.min(CW,Math.max(...allContentPts.map(p=>p[0]))+pad);
          const by2=Math.min(CH,Math.max(...allContentPts.map(p=>p[1]))+pad);
          const srcW=bx2-bx1, srcH=by2-by1;
          // Address label — top-left of the cropped region (in the padding band
          // above the shape, so it never overlaps the trace).
          if(job.address){
            dctx.font="bold 14px sans-serif"; dctx.fillStyle="#0f172a";
            dctx.textAlign="left"; dctx.textBaseline="top";
            dctx.fillText(job.address+(job.city?", "+job.city:""),bx1+10,by1+8);
          }
          aspectRatio=srcH/srcW;
          // 2× upscale for crisp print quality
          const S=2;
          const cc=document.createElement("canvas"); cc.width=srcW*S; cc.height=srcH*S;
          cc.getContext("2d").drawImage(dc,bx1,by1,srcW,srcH,0,0,srcW*S,srcH*S);
          imgData=cc.toDataURL("image/png");
        } else {
          // No traced content — fall back to the whole canvas with the address top-left.
          if(job.address){
            dctx.font="bold 14px sans-serif"; dctx.fillStyle="#0f172a";
            dctx.textAlign="left"; dctx.textBaseline="top";
            dctx.fillText(job.address+(job.city?", "+job.city:""),12,10);
          }
          imgData=dc.toDataURL("image/png");
          aspectRatio=CH/CW;
        }


      return {data:imgData,width:CW,height:CW*aspectRatio};
    }catch(error){throw Error("The property diagram could not be rendered. Reopen the diagram and retry exporting the report.");}
  }
  layout.cover(renderDiagram());
  layout.findings(WDO_CATEGORIES,replacementSummary);
  y=layout.y;
  addPage();hdrBar("GENERAL INFORMATION");secTitle("GENERAL INFORMATION");
  doc.setFont("helvetica","italic");doc.setFontSize(9);doc.setTextColor(80,80,80);
  const tailLns=doc.splitTextToSize(FR_TAIL,W-margin*2);
  tailLns.forEach(l=>{checkY(12);doc.text(l,margin,y);y+=12;});

  checkY(20);y+=10;ln(margin,y,W-margin,y);y+=14;
  doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(180,20,20);
  const pestLns=doc.splitTextToSize(PESTICIDE_CAUTION,W-margin*2);
  pestLns.forEach(l=>{checkY(12);doc.text(l,margin,y);y+=12;});

  const emergencyList=COUNTY_EMERGENCY[job.county]||COUNTY_EMERGENCY["default"];
  checkY(20);y+=10;ln(margin,y,W-margin,y);y+=14;
  doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(30,30,30);
  doc.text("For further information, contact any of the following agencies in your area:",margin,y);y+=16;
  emergencyList.forEach(e=>{
    const addressLines=e.addr?doc.splitTextToSize(String(e.addr),W-margin*2):[];
    checkY(16+addressLines.length*11);
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(30,30,30);
    doc.text(String(e.name),margin,y);doc.text(String(e.phone),W-margin,y,{align:"right"});y+=13;
    if(addressLines.length){doc.setFontSize(8);doc.setTextColor(100,116,139);doc.text(addressLines,margin,y);y+=addressLines.length*11;}
  });

  // Inspector credentials and signature are printed on the cover; client authorization follows.

  // PAGE 3: CONTRACT / PROPOSAL
  addPage();hdrBar("CONTRACT / PROPOSAL");
  doc.setFont("helvetica","bold");doc.setFontSize(16);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
  doc.text("CONTRACT / PROPOSAL",W/2,y,{align:"center"});y+=6;
  doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(100,116,139);
  doc.text(`Report # ${job.reportNum}`,W/2,y+14,{align:"center"});y+=30;
  secTitle("PROPERTY");
  doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(30,30,30);
  doc.text(`${job.address||""}, ${job.city||""}, ${job.state||"CA"} ${job.zip||""}`,margin,y);y+=20;
  ln(margin,y,W-margin,y);y+=14;
  secTitle("ITEMIZED QUOTE");
  doc.setFillColor(...reportRGB(style.primary));doc.rect(margin,y,W-margin*2,18,"F");
  doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(...reportRGB(reportInk(style.primary)));
  doc.setFontSize(8);
  doc.text("ITEM",margin+8,y+12);doc.text("SECTION",margin+280,y+12);
  doc.text("PRIMARY $",margin+370,y+12);doc.text("SECONDARY $",W-margin-8,y+12,{align:"right"});
  y+=22;

  let sI1=0,sI2=0,sII1=0,sII2=0;
  (job.findings||[]).forEach((f,idx)=>{
    checkY(20);
    const bg=idx%2===0?[248,250,252]:[255,255,255];
    doc.setFillColor(...bg);doc.rect(margin,y-10,W-margin*2,16,"F");
    const cat=WDO_CATEGORIES.find(c=>c.num===f.cat);
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(30,30,30);
    doc.text(`${f.cat}${f.letter} — ${cat?cat.label:""}`,margin+8,y);
    doc.text(sectionLabel(f.cat),margin+280,y);
    if(f.primaryPrice>0){
      doc.setFont("helvetica","bold");
      doc.text(`$${Number(f.primaryPrice).toLocaleString()}`,margin+370,y);
      doc.setFont("helvetica","normal");
      if(f.cat<=3)sI1+=Number(f.primaryPrice);
      else if(f.cat===4)sII1+=Number(f.primaryPrice);
    }
    if(f.hasSecondary&&f.secPrice>0){
      doc.text(`$${Number(f.secPrice).toLocaleString()}`,margin+460,y);
      if(f.cat<=3)sI2+=Number(f.secPrice);
      else if(f.cat===4)sII2+=Number(f.secPrice);
    }
    y+=16;
  });
  y+=8;ln(margin,y,W-margin,y);y+=16;
  doc.setFont("helvetica","bold");doc.setFontSize(10);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
  doc.text("Section I Subtotal (Primary):",margin+200,y);doc.text(`$${sI1.toLocaleString()}`,margin+370,y);y+=16;
  if(sI2>0){doc.text("Section I Subtotal (Secondary):",margin+200,y);doc.text(`$${sI2.toLocaleString()}`,margin+460,y);y+=16;}
  if(sII1>0){doc.text("Section II Subtotal:",margin+200,y);doc.text(`$${sII1.toLocaleString()}`,margin+370,y);y+=16;}
  y+=8;
  if(job.reportType==="Separated Report"){
    // Separated report: each section is its own authorizable total.
    doc.setFillColor(...reportRGB(style.primary));doc.rect(margin+180,y-12,W-margin*2-180,22,"F");
    doc.setTextColor(...reportRGB(reportInk(style.primary)));doc.setFontSize(11);
    doc.text("SECTION I TOTAL (PRIMARY):",margin+190,y+4);
    doc.text(`$${sI1.toLocaleString()}`,W-margin-10,y+4,{align:"right"});
    y+=26;
    doc.setFillColor(...reportRGB(style.primary));doc.rect(margin+180,y-12,W-margin*2-180,22,"F");
    doc.setTextColor(...reportRGB(reportInk(style.primary)));doc.setFontSize(11);
    doc.text("SECTION II TOTAL (PRIMARY):",margin+190,y+4);
    doc.text(`$${sII1.toLocaleString()}`,W-margin-10,y+4,{align:"right"});
    y+=28;
  } else {
    doc.setFillColor(...reportRGB(style.primary));doc.rect(margin+180,y-12,W-margin*2-180,22,"F");
    doc.setTextColor(...reportRGB(reportInk(style.primary)));doc.setFontSize(12);
    doc.text("GRAND TOTAL (PRIMARY):",margin+190,y+4);
    doc.text(`$${(sI1+sII1).toLocaleString()}`,W-margin-10,y+4,{align:"right"});
    y+=28;
  }
  if((sI2+sII2)>0){
    doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(100,116,139);
    doc.text(`Grand Total (Secondary): $${(sI2+sII2).toLocaleString()}`,margin+190,y);y+=20;
  }
  y+=14;ln(margin,y,W-margin,y);y+=20;
  doc.setFont("helvetica","italic");doc.setFontSize(9);doc.setTextColor(80,80,80);
  doc.text("No work will be performed until a signed copy of this agreement has been received.",margin,y);y+=16;
  doc.text("Payment due within 30 days from date of completion.",margin,y);y+=30;
  checkY(34);
  ln(margin,y,margin+156,y);ln(margin+180,y,margin+336,y);ln(margin+360,y,W-margin,y);
  y+=12;
  doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(100,116,139);
  doc.text("Property Owner / Date",margin,y);
  doc.text("Owner's Agent / Date",margin+180,y);
  doc.text("Inspected By / Date",margin+360,y);

  // PAGE 4: WORK AUTHORIZATION
  addPage();hdrBar("WORK AUTHORIZATION");
  doc.setFont("helvetica","bold");doc.setFontSize(16);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
  doc.text("WORK AUTHORIZATION",W/2,y,{align:"center"});y+=6;
  doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(100,116,139);
  doc.text(`Report # ${job.reportNum}`,W/2,y+14,{align:"center"});y+=30;
  const authTotal=(job.findings||[]).filter(f=>f.cat>=1&&f.cat<=4).reduce((s,f)=>s+(Number(f.primaryPrice)||0),0);
  const authTotalStr=authTotal>0?`$${authTotal.toFixed(2)}`:"$____________";
  const ackItems=[
    "No work will be performed until a signed copy of this agreement has been received.",
    `Address of Property: ${job.address||""}, ${job.city||""}, ${job.state||"CA"} ${job.zip||""}`,
    `The inspection report dated ${job.date} is incorporated herein by reference. The company is authorized to proceed with the work for a total sum of ${authTotalStr}. This total amount is due and payable within 30 days from the date of completion.`,
  ];
  ackItems.forEach(txt=>{
    checkY(50);
    doc.setDrawColor(180,180,180);doc.rect(margin,y,12,12);
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(30,30,30);
    const ls=doc.splitTextToSize(txt,W-margin*2-20);
    doc.text(ls,margin+18,y+10);
    y+=Math.max(24,ls.length*12+8);
  });
  y+=10;ln(margin,y,W-margin,y);y+=16;

  // Itemized findings table
  const authFindings = (job.findings||[]).filter(f=>f.cat>=1&&f.cat<=4);
  if(authFindings.length>0){
    checkY(30);
    // Table header
    const tCols=[30,60,250,70,70];
    const tX=[margin, margin+30, margin+90, margin+340, margin+410];
    doc.setFillColor(...reportRGB(style.primary));doc.rect(margin,y,W-margin*2,16,"F");
    doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(...reportRGB(reportInk(style.primary)));
    ["Item","Section","Description","Primary","Secondary"].forEach((h,i)=>{
      doc.text(h,tX[i]+3,y+11);
    });
    y+=16;
    authFindings.forEach((f,idx)=>{
      checkY(14);
      if(idx%2===0){doc.setFillColor(248,250,252);}else{doc.setFillColor(255,255,255);}
      doc.rect(margin,y,W-margin*2,14,"F");
      doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(30,30,30);
      const desc=(f.primaryRec||"").slice(0,45)+(f.primaryRec&&f.primaryRec.length>45?"…":"");
      doc.text(`${f.cat}${f.letter}`,tX[0]+3,y+10);
      doc.text(sectionLabel(f.cat),tX[1]+3,y+10);
      doc.text(desc,tX[2]+3,y+10);
      doc.text(Number(f.primaryPrice)>0?`$${Number(f.primaryPrice).toFixed(2)}`:"—",tX[3]+3,y+10);
      doc.text(f.hasSecondary&&Number(f.secPrice)>0?`$${Number(f.secPrice).toFixed(2)}`:"—",tX[4]+3,y+10);
      y+=14;
    });
    // Grand total row
    const grandTotal=(authFindings.reduce((s,f)=>s+(Number(f.primaryPrice)||0),0));
    doc.setFillColor(241,245,249);doc.rect(margin,y,W-margin*2,16,"F");
    doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
    doc.text("GRAND TOTAL (PRIMARY)",tX[2]+3,y+11);
    doc.text(`$${grandTotal.toFixed(2)}`,tX[3]+3,y+11);
    y+=16;
    y+=12;
    ln(margin,y,W-margin,y);y+=12;
  }

  WORK_AUTH_LEGAL.split("\n\n").forEach(para=>{
    checkY(30);
    const isBold=/^(THE COMPANY|THE OWNER|ALL PARTIES|NOTICE TO OWNER)/.test(para);
    if(isBold){
      const newlineIdx=para.indexOf("\n");
      const title=newlineIdx>-1?para.slice(0,newlineIdx):para;
      const body=newlineIdx>-1?para.slice(newlineIdx+1):"";
      doc.setFont("helvetica","bold");doc.setFontSize(10);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
      doc.text(title,W/2,y,{align:"center"});y+=14;
      if(body){
        doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(30,30,30);
        const bLns=doc.splitTextToSize(body,W-margin*2);
        bLns.forEach(l=>{checkY(12);doc.text(l,margin,y);y+=12;});
      }
    } else {
      doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(30,30,30);
      const pLns=doc.splitTextToSize(para,W-margin*2);
      pLns.forEach(l=>{checkY(12);doc.text(l,margin,y);y+=12;});
    }
    y+=10;
  });
  checkY(60);y+=20;
  ln(margin,y,margin+180,y);ln(W-margin-180,y,W-margin,y);
  y+=14;
  doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(100,116,139);
  doc.text("Property Owner / Date",margin,y);
  doc.text("Owner's Agent / Date",W-margin,y,{align:"right"});
  y+=30;ln(margin,y,margin+180,y);y+=14;
  doc.text("Inspector / Date",margin,y);

  // PAGE 5 (if any): DISCLAIMERS
  const activeDiscs = (settings.disclaimers||[]).filter(d=>d.enabled);
  if(activeDiscs.length>0){
    addPage(); hdrBar("DISCLAIMERS & LIMITATIONS");
    doc.setFont("helvetica","bold");doc.setFontSize(13);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
    doc.text("DISCLAIMERS & LIMITATIONS OF INSPECTION",W/2,y,{align:"center"});y+=8;
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(100,116,139);
    doc.text(`Report #${job.reportNum||""}  ·  ${job.address||""}  ·  ${job.date||""}`,W/2,y+12,{align:"center"});y+=30;
    ln(margin,y,W-margin,y);y+=16;

    activeDiscs.forEach((d,i)=>{
      checkY(50);
      // Title bar
      doc.setFillColor(241,245,249);
      doc.rect(margin,y,W-margin*2,18,"F");
      doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
      doc.text(`${i+1}. ${d.title.toUpperCase()}`,margin+8,y+12);
      y+=24;
      // Body
      doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(40,40,40);
      const dLines=doc.splitTextToSize(d.text,W-margin*2);
      dLines.forEach(l=>{checkY(12);doc.text(l,margin,y);y+=12;});
      y+=12;
    });

    // Signature acknowledgement at bottom
    checkY(60);y+=10;
    ln(margin,y,W-margin,y,[226,232,240]);y+=16;
    doc.setFont("helvetica","italic");doc.setFontSize(9);doc.setTextColor(100,116,139);
    doc.text("By signing below, all parties acknowledge receipt and understanding of the disclaimers stated above.",margin,y);
    y+=24;
    // Client signature — draw captured image if available, else blank line
    if(job.clientSignature){
      try{
        doc.addImage(job.clientSignature,"PNG",margin,y-14,180,40);
      }catch{}
    } else {
      ln(margin,y,margin+200,y);
    }
    ln(margin+260,y,margin+460,y);
    y+=12;
    doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(100,116,139);
    doc.text("Ordered By / Date",margin,y);
    doc.text("Inspector / Date",margin+260,y);
  } else if(job.clientSignature) {
    // No disclaimers page but we have a signature — add a compact sig page
    addPage(); hdrBar("ACKNOWLEDGEMENT & SIGNATURE");
    doc.setFont("helvetica","bold");doc.setFontSize(12);doc.setTextColor(...reportRGB(reportTextColor(style.primary)));
    doc.text("CLIENT ACKNOWLEDGEMENT",W/2,y,{align:"center"});y+=20;
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(60,60,60);
    const ackLines=doc.splitTextToSize("By signing below, the undersigned acknowledges receipt of this Wood Destroying Organisms Inspection Report and agrees to the terms and conditions set forth in the contract and proposal herein.",W-margin*2);
    ackLines.forEach(l=>{doc.text(l,margin,y);y+=12;});
    y+=16;
    try{ doc.addImage(job.clientSignature,"PNG",margin,y,200,50); }catch{}
    y+=60;
    ln(margin,y,margin+220,y);y+=12;
    doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(100,116,139);
    doc.text(`${job.orderedBy?.name||"Client"} — ${job.date||"Date"}`,margin,y);
  }

  // COMPLETION NOTICE — STANDARD NOTICE OF WORK COMPLETED AND NOT COMPLETED
  {
    addPage();
    const cW=W; const cM=margin;
    layout.noHeader.add(doc.internal.getCurrentPageInfo().pageNumber);y=48;
    // Title block (no report header on the prescribed notice).
    doc.setFillColor(255,255,255);
    doc.setFont("helvetica","bold");doc.setFontSize(11);doc.setTextColor(0,0,0);
    doc.text("STANDARD NOTICE OF WORK COMPLETED AND NOT COMPLETED",cW/2,y,{align:"center"});y+=13;
    doc.setFont("helvetica","italic");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
    doc.text("NOTICE - All recommendations may not have been completed - See below - Recommendations not completed.",cW/2,y,{align:"center"});y+=10;
    doc.text("This form is prescribed by the Structural Pest Control Board.",cW/2,y,{align:"center"});y+=8;
    ln(cM,y,cW-cM,y,[0,0,0]);y+=8;

    // Building info row
    const bCols=[60,180,120,60,96];
    const bX=[cM,cM+60,cM+240,cM+360,cM+420];
    doc.setFont("helvetica","bold");doc.setFontSize(7);doc.setTextColor(80,80,80);
    ["Building No.","Street","City","ZIP","Date of Completion"].forEach((l,i)=>doc.text(l,bX[i],y));
    y+=11;
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(0,0,0);
    const noticeAddress=String(job.address||"").match(/^(\d+\S*)\s+(.*)$/);
    const noticeValues=[noticeAddress?.[1]||"",noticeAddress?.[2]||String(job.address||""),String(job.city||""),String(job.zip||""),String(job.nocCompletionDate||job.date||"")];
    const noticeLines=noticeValues.map((v,i)=>doc.splitTextToSize(v,bCols[i]-8));
    noticeLines.forEach((lines,i)=>doc.text(lines,bX[i],y));
    y+=Math.max(...noticeLines.map(lines=>lines.length))*11-7;ln(cM,y,cW-cM,y);y+=8;

    // Company info + report meta (2-column)
    const compW=Math.round((cW-cM*2)*0.58); const metaX=cM+compW+12;
    const compBoxH=52;
    doc.setDrawColor(180,180,180);doc.setLineWidth(0.5);
    doc.rect(cM,y,compW,compBoxH,"S");
    doc.rect(metaX,y,cW-cM-metaX,compBoxH,"S");
    doc.setLineWidth(1);
    // Company logo + name
    let compY=y+10;
    if(settings.company.logo){
      try{doc.addImage(settings.company.logo,"PNG",cM+6,y+4,24,24);}catch{}
      doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(0,0,0);
      doc.text(settings.company.name||"",cM+36,compY);compY+=11;
    } else {
      doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(0,0,0);
      doc.text(settings.company.name||"",cM+6,compY);compY+=11;
    }
    doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
    const compLines=[settings.company.address,`${settings.company.city||""}${settings.company.city&&settings.company.state?", ":""}${settings.company.state||""}  ${settings.company.zip||""}`.trim(),settings.company.phone,settings.company.email].filter(Boolean);
    compLines.forEach(l=>{doc.text(l,cM+6,compY);compY+=9;});
    // Meta fields
    let mY=y+10;
    doc.setFont("helvetica","bold");doc.setFontSize(7.5);doc.setTextColor(80,80,80);
    doc.text("Report #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(job.reportNum||""),metaX+52,mY);mY+=11;
    doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
    doc.text("Registration #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(settings.company.license||""),metaX+62,mY);mY+=11;
    doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
    doc.text("Escrow #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(job.escrow||""),metaX+52,mY);mY+=11;
    // CORRECTED REPORT checkbox
    doc.setDrawColor(0,0,0);doc.setLineWidth(0.5);doc.rect(metaX+4,mY-8,8,8,"S");doc.setLineWidth(1);
    if(job.reportType==="Corrected Report"){
      doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(0,0,0);
      doc.text("✓",metaX+5,mY-1);
    }
    doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(0,0,0);
    doc.text("CORRECTED REPORT",metaX+14,mY-1);
    y+=compBoxH+8;

    // 3-column contact section
    const cColW=Math.floor((cW-cM*2)/3);
    doc.setFont("helvetica","bold");doc.setFontSize(7.5);doc.setTextColor(0,0,0);
    ["Ordered By:","Property Owner and/or Party of Interest:","Completion Sent To:"].forEach((h,i)=>doc.text(h,cM+i*cColW,y));
    y+=11;
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(30,30,30);
    const contacts=[
      [job.orderedBy?.name||"",job.orderedBy?.company||""],
      [job.propOwner?.name||"",job.propOwner?.company||""],
      [job.nocSentTo||job.partyOfInterest?.name||job.orderedBy?.name||"",""],
    ];
    contacts.forEach((lines,i)=>{
      let ccy=y;
      lines.filter(Boolean).forEach(l=>{doc.text(l,cM+i*cColW,ccy);ccy+=11;});
    });
    y+=28;
    ln(cM,y,cW-cM,y);y+=8;

    // "The following recommendations..." paragraph
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    const followPara=`The following recommendations on the above designated property, as outlined in Wood Destroying Pests and Organisms Inspection Report dated ${job.date||"___________"} have been and/or have not been completed.`;
    const followLines=doc.splitTextToSize(followPara,cW-cM*2);
    followLines.forEach(l=>{doc.text(l,cM,y);y+=11;});
    y+=4;

    // Completed primary recommendations box
    const sec1Findings=(job.findings||[]).filter(f=>f.cat>=1&&f.cat<=3);
    doc.setDrawColor(0,0,0);doc.setLineWidth(0.5);
    const box1H=Math.max(36,sec1Findings.length*11+20);
    doc.rect(cM,y,cW-cM*2,box1H,"S");doc.setLineWidth(1);
    doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
    doc.text("Recommendations completed by this firm that are in accordance with the Structural Pest Control Board's Rules and Regulations:",cM+4,y+10);
    let bY=y+20;
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    sec1Findings.forEach(f=>{
      const label=`${f.cat}${f.letter}  ${(f.primaryRec||"").slice(0,80)}${(f.primaryRec||"").length>80?"…":""}`;
      doc.text(label,cM+8,bY);bY+=11;
    });
    y+=box1H+6;

    // Completed secondary recommendations box
    const secFindings=(job.findings||[]).filter(f=>f.hasSecondary&&f.secPrice>0);
    const box2H=Math.max(36,secFindings.length*11+24);
    doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,box2H,"S");doc.setLineWidth(1);
    doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
    doc.text("Recommendations completed by this firm that are considered secondary and substandard measures under Section 1992 of the Structural Pest Control",cM+4,y+10);
    doc.text("Board's Rules and Regulations including person requesting secondary measure:",cM+4,y+18);
    let b2Y=y+28;
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    secFindings.forEach(f=>{
      const label=`${f.cat}${f.letter}  ${(f.secondaryRec||"").slice(0,80)}${(f.secondaryRec||"").length>80?"…":""}`;
      doc.text(label,cM+8,b2Y);b2Y+=11;
    });
    y+=box2H+6;

    // Cost of work completed
    checkY(40);
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text("Cost of work completed:",cM,y);y+=12;
    const autoWork=(job.findings||[]).filter(f=>f.cat>=1&&f.cat<=5).reduce((s,f)=>s+(Number(f.primaryPrice)||0),0);
    const workCost=job.nocAutoSum!==false?autoWork:Number(job.nocManualCost||0);
    const nocInspFee=Number(job.nocInspectionFee||0);
    const nocOtherFee=Number(job.nocOtherFee||0);
    const totalCost=workCost+nocInspFee+nocOtherFee;
    const costItems=[["Cost:",workCost],["Inspection Fee:",nocInspFee],["Other:",nocOtherFee],["Total:",totalCost]];
    costItems.forEach(([label,val])=>{
      doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
      doc.text(label,cW-cM-180,y);
      doc.text(`$${val.toFixed(2)}`,cW-cM,y,{align:"right"});
      ln(cW-cM-80,y+2,cW-cM,y+2);
      y+=13;
    });
    y+=4;

    // Recommendations not completed box
    checkY(60);
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text("Recommendations not completed by this firm:",cM,y);y+=4;
    const notCompletedH=Math.max(44, job.nocNotCompleted?(doc.splitTextToSize(job.nocNotCompleted,cW-cM*2-12).length*10+16):44);
    doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,notCompletedH,"S");doc.setLineWidth(1);
    if(job.nocNotCompleted){
      doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(30,30,30);
      doc.splitTextToSize(job.nocNotCompleted,cW-cM*2-12).forEach((l,i)=>doc.text(l,cM+6,y+12+(i*10)));
    }
    y+=notCompletedH+4;
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text("Estimated Cost: $",cW-cM-140,y);
    ln(cW-cM-70,y+2,cW-cM,y+2);
    y+=14;

    // Remarks / Certification box
    checkY(60);
    doc.setFont("helvetica","bold");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text("Remarks:",cM,y);y+=4;
    const certMap={none:"",full:"This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas.",except:`This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas except as follows: ${job.nocExceptText||""}`};
    const remarksText=(certMap[job.nocCertification||"full"]||certMap.full)+(job.nocRemarks?`\n${job.nocRemarks}`:"");
    const remarksLines=doc.splitTextToSize(remarksText,cW-cM*2-12);
    const remarksBoxH=Math.max(48,remarksLines.length*10+20);
    doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,remarksBoxH,"S");doc.setLineWidth(1);
    doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(30,30,30);
    remarksLines.forEach((l,i)=>{doc.text(l,cM+6,y+12+(i*10));});
    y+=remarksBoxH+12;

    // Inspector signature row
    checkY(40);
    const nocInspId=job.nocInspectorId||(job.inspectorIds||[])[0];
    const insp=(settings.inspectors||[]).find(i=>i.id===nocInspId)||(settings.inspectors||[])[0]||{name:"",license:""};
    doc.setFont("helvetica","bold");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text("Inspected By:",cM,y);
    doc.setFont("helvetica","normal");doc.text(insp.name||"",cM+60,y);
    doc.text("State License No.:",cM+230,y);
    doc.text(insp.license||"",cM+320,y);
    doc.text("Signature:",cM+390,y);
    if(insp.signature&&job.nocIncludeSignature!==false){
      try{doc.addImage(insp.signature,"PNG",cM+435,y-14,100,28);}catch{}
    } else {
      ln(cM+435,y,cM+535,y);
    }
    y+=18;

    // Legal footer
    doc.setFont("helvetica","normal");doc.setFontSize(6.5);doc.setTextColor(100,100,100);
    const footerText="You are entitled to obtain copies of all reports and completion notices on this property reported to the Board during the preceding two years upon payment of a search fee to: Structural Pest Control Board, 2005 Evergreen Street, Suite 1500, Sacramento, California, 95815. NOTE: Questions or problems concerning the above report should be directed to the manager of this company. Unresolved questions or problems with services performed may be directed to the Structural Pest Control Board at (916) 561-8700, (800) 737-8188 or www.pestboard.ca.gov. 43M-44 (Rev 10/01)";
    const footerLines=doc.splitTextToSize(footerText,cW-cM*2);
    footerLines.forEach(l=>{doc.text(l,cM,y);y+=8;});
  }

  layout.finalize();
  const fname=`WDO_Report_${job.reportNum||"draft"}_${(job.address||"report").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`;
  return {doc, fname};
}

async function downloadPDF(job, settings) {
  const {doc, fname} = generatePDF(await preparePDFPhotos(job,evidencePhoto), settings);
  await doc.save(fname,{returnPromise:true});
}

async function previewPDF(job, settings) {
  // Reserve the tab during the tap, before asynchronous photo decoding on iOS.
  const tab=window.open('', '_blank');
  if(!tab)throw Error('Allow pop-ups for SpecTermite to preview the report, or use Download PDF.');
  try{
    tab.document.title='Preparing inspection report';tab.document.body.textContent='Preparing report and photos…';
    const {doc}=generatePDF(await preparePDFPhotos(job,evidencePhoto),settings);
    const url=URL.createObjectURL(doc.output('blob'));tab.location.href=url;
    setTimeout(()=>URL.revokeObjectURL(url),300000);
  }catch(error){tab.close();throw error;}
}

function generateNocOnlyPDF(job, settings) {
  const doc = new jsPDF({orientation:"portrait",unit:"pt",format:"letter"});
  const W=612; const margin=48;
  let y=margin;
  function ln(x1,y1,x2,y2,rgb){doc.setDrawColor(...(rgb||[200,210,220]));doc.setLineWidth(0.5);doc.line(x1,y1,x2,y2);doc.setLineWidth(1);}
  function checkY(n){if(y+n>792-60){doc.addPage();y=margin;}}

  const cW=W; const cM=margin;
  doc.setFillColor(255,255,255);
  doc.setFont("helvetica","bold");doc.setFontSize(11);doc.setTextColor(0,0,0);
  doc.text("STANDARD NOTICE OF WORK COMPLETED AND NOT COMPLETED",cW/2,y,{align:"center"});y+=13;
  doc.setFont("helvetica","italic");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
  doc.text("NOTICE - All recommendations may not have been completed - See below - Recommendations not completed.",cW/2,y,{align:"center"});y+=10;
  doc.text("This form is prescribed by the Structural Pest Control Board.",cW/2,y,{align:"center"});y+=8;
  ln(cM,y,cW-cM,y,[0,0,0]);y+=8;

  const bCols=[60,180,120,60,96];
  const bX=[cM,cM+60,cM+240,cM+360,cM+420];
  doc.setFont("helvetica","bold");doc.setFontSize(7);doc.setTextColor(80,80,80);
  ["Building No.","Street","City","ZIP","Date of Completion"].forEach((l,i)=>doc.text(l,bX[i],y));
  y+=11;
  doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(0,0,0);
  [String(job.reportNum||""),String(job.address||""),String(job.city||""),String(job.zip||""),String(job.nocCompletionDate||job.date||"")].forEach((v,i)=>doc.text(v,bX[i],y));
  y+=4;ln(cM,y,cW-cM,y);y+=8;

  const compW=Math.round((cW-cM*2)*0.58); const metaX=cM+compW+12;
  const compBoxH=52;
  doc.setDrawColor(180,180,180);doc.setLineWidth(0.5);
  doc.rect(cM,y,compW,compBoxH,"S");
  doc.rect(metaX,y,cW-cM-metaX,compBoxH,"S");
  doc.setLineWidth(1);
  let compY=y+10;
  if(settings.company.logo){
    try{doc.addImage(settings.company.logo,"PNG",cM+6,y+4,24,24);}catch{}
    doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(0,0,0);
    doc.text(settings.company.name||"",cM+36,compY);compY+=11;
  } else {
    doc.setFont("helvetica","bold");doc.setFontSize(9);doc.setTextColor(0,0,0);
    doc.text(settings.company.name||"",cM+6,compY);compY+=11;
  }
  doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
  const compLines=[settings.company.address,`${settings.company.city||""}${settings.company.city&&settings.company.state?", ":""}${settings.company.state||""}  ${settings.company.zip||""}`.trim(),settings.company.phone,settings.company.email].filter(Boolean);
  compLines.forEach(l=>{doc.text(l,cM+6,compY);compY+=9;});
  let mY=y+10;
  doc.setFont("helvetica","bold");doc.setFontSize(7.5);doc.setTextColor(80,80,80);
  doc.text("Report #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(job.reportNum||""),metaX+52,mY);mY+=11;
  doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
  doc.text("Registration #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(settings.company.license||""),metaX+62,mY);mY+=11;
  doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
  doc.text("Escrow #:",metaX+4,mY);doc.setFont("helvetica","normal");doc.setTextColor(0,0,0);doc.text(String(job.escrow||""),metaX+52,mY);mY+=11;
  doc.setDrawColor(0,0,0);doc.setLineWidth(0.5);doc.rect(metaX+4,mY-8,8,8,"S");doc.setLineWidth(1);
  if(job.reportType==="Corrected Report"){
    doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(0,0,0);
    doc.text("✓",metaX+5,mY-1);
  }
  doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(0,0,0);
  doc.text("CORRECTED REPORT",metaX+14,mY-1);
  y+=compBoxH+8;

  const cColW=Math.floor((cW-cM*2)/3);
  doc.setFont("helvetica","bold");doc.setFontSize(7.5);doc.setTextColor(0,0,0);
  ["Ordered By:","Property Owner and/or Party of Interest:","Completion Sent To:"].forEach((h,i)=>doc.text(h,cM+i*cColW,y));
  y+=11;
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(30,30,30);
  const contacts=[
    [job.orderedBy?.name||"",job.orderedBy?.company||""],
    [job.propOwner?.name||"",job.propOwner?.company||""],
    [job.nocSentTo||job.partyOfInterest?.name||job.orderedBy?.name||"",""],
  ];
  contacts.forEach((lines,i)=>{
    let ccy=y;
    lines.filter(Boolean).forEach(l=>{doc.text(l,cM+i*cColW,ccy);ccy+=11;});
  });
  y+=28;
  ln(cM,y,cW-cM,y);y+=8;

  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  const followPara=`The following recommendations on the above designated property, as outlined in Wood Destroying Pests and Organisms Inspection Report dated ${job.date||"___________"} have been and/or have not been completed.`;
  const followLines=doc.splitTextToSize(followPara,cW-cM*2);
  followLines.forEach(l=>{doc.text(l,cM,y);y+=11;});
  y+=4;

  const sec1Findings=(job.findings||[]).filter(f=>f.cat>=1&&f.cat<=3);
  doc.setDrawColor(0,0,0);doc.setLineWidth(0.5);
  const box1H=Math.max(36,sec1Findings.length*11+20);
  doc.rect(cM,y,cW-cM*2,box1H,"S");doc.setLineWidth(1);
  doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
  doc.text("Recommendations completed by this firm that are in accordance with the Structural Pest Control Board's Rules and Regulations:",cM+4,y+10);
  let bY=y+20;
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  sec1Findings.forEach(f=>{
    const label=`${f.cat}${f.letter}  ${(f.primaryRec||"").slice(0,80)}${(f.primaryRec||"").length>80?"…":""}`;
    doc.text(label,cM+8,bY);bY+=11;
  });
  y+=box1H+6;

  const secFindings=(job.findings||[]).filter(f=>f.hasSecondary&&f.secPrice>0);
  const box2H=Math.max(36,secFindings.length*11+24);
  doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,box2H,"S");doc.setLineWidth(1);
  doc.setFont("helvetica","normal");doc.setFontSize(7.5);doc.setTextColor(60,60,60);
  doc.text("Recommendations completed by this firm that are considered secondary and substandard measures under Section 1992 of the Structural Pest Control",cM+4,y+10);
  doc.text("Board's Rules and Regulations including person requesting secondary measure:",cM+4,y+18);
  let b2Y=y+28;
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  secFindings.forEach(f=>{
    const label=`${f.cat}${f.letter}  ${(f.secondaryRec||"").slice(0,80)}${(f.secondaryRec||"").length>80?"…":""}`;
    doc.text(label,cM+8,b2Y);b2Y+=11;
  });
  y+=box2H+6;

  checkY(40);
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  doc.text("Cost of work completed:",cM,y);y+=12;
  const autoWork=(job.findings||[]).filter(f=>f.cat>=1&&f.cat<=5).reduce((s,f)=>s+(Number(f.primaryPrice)||0),0);
  const workCost=job.nocAutoSum!==false?autoWork:Number(job.nocManualCost||0);
  const nocInspFee=Number(job.nocInspectionFee||0);
  const nocOtherFee=Number(job.nocOtherFee||0);
  const totalCost=workCost+nocInspFee+nocOtherFee;
  const costItems=[["Cost:",workCost],["Inspection Fee:",nocInspFee],["Other:",nocOtherFee],["Total:",totalCost]];
  costItems.forEach(([label,val])=>{
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
    doc.text(label,cW-cM-180,y);
    doc.text(`$${val.toFixed(2)}`,cW-cM,y,{align:"right"});
    ln(cW-cM-80,y+2,cW-cM,y+2);
    y+=13;
  });
  y+=4;

  checkY(60);
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  doc.text("Recommendations not completed by this firm:",cM,y);y+=4;
  const notCompletedH=Math.max(44, job.nocNotCompleted?(doc.splitTextToSize(job.nocNotCompleted,cW-cM*2-12).length*10+16):44);
  doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,notCompletedH,"S");doc.setLineWidth(1);
  if(job.nocNotCompleted){
    doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(30,30,30);
    doc.splitTextToSize(job.nocNotCompleted,cW-cM*2-12).forEach((l,i)=>doc.text(l,cM+6,y+12+(i*10)));
  }
  y+=notCompletedH+4;
  doc.setFont("helvetica","normal");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  doc.text("Estimated Cost: $",cW-cM-140,y);
  ln(cW-cM-70,y+2,cW-cM,y+2);
  y+=14;

  checkY(60);
  doc.setFont("helvetica","bold");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  doc.text("Remarks:",cM,y);y+=4;
  const certMap={none:"",full:"This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas.",except:`This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas except as follows: ${job.nocExceptText||""}`};
  const remarksText=(certMap[job.nocCertification||"full"]||certMap.full)+(job.nocRemarks?`\n${job.nocRemarks}`:"");
  const remarksLines=doc.splitTextToSize(remarksText,cW-cM*2-12);
  const remarksBoxH=Math.max(48,remarksLines.length*10+20);
  doc.setLineWidth(0.5);doc.rect(cM,y,cW-cM*2,remarksBoxH,"S");doc.setLineWidth(1);
  doc.setFont("helvetica","normal");doc.setFontSize(8);doc.setTextColor(30,30,30);
  remarksLines.forEach((l,i)=>{doc.text(l,cM+6,y+12+(i*10));});
  y+=remarksBoxH+12;

  checkY(40);
  const nocInspId=job.nocInspectorId||(job.inspectorIds||[])[0];
  const insp=(settings.inspectors||[]).find(i=>i.id===nocInspId)||(settings.inspectors||[])[0]||{name:"",license:""};
  doc.setFont("helvetica","bold");doc.setFontSize(8.5);doc.setTextColor(0,0,0);
  doc.text("Inspected By:",cM,y);
  doc.setFont("helvetica","normal");doc.text(insp.name||"",cM+60,y);
  doc.text("State License No.:",cM+230,y);
  doc.text(insp.license||"",cM+320,y);
  doc.text("Signature:",cM+390,y);
  if(insp.signature&&job.nocIncludeSignature!==false){
    try{doc.addImage(insp.signature,"PNG",cM+435,y-14,100,28);}catch{}
  } else {
    ln(cM+435,y,cM+535,y);
  }
  y+=18;

  doc.setFont("helvetica","normal");doc.setFontSize(6.5);doc.setTextColor(100,100,100);
  const footerText="You are entitled to obtain copies of all reports and completion notices on this property reported to the Board during the preceding two years upon payment of a search fee to: Structural Pest Control Board, 2005 Evergreen Street, Suite 1500, Sacramento, California, 95815. NOTE: Questions or problems concerning the above report should be directed to the manager of this company. Unresolved questions or problems with services performed may be directed to the Structural Pest Control Board at (916) 561-8700, (800) 737-8188 or www.pestboard.ca.gov. 43M-44 (Rev 10/01)";
  const footerLines=doc.splitTextToSize(footerText,cW-cM*2);
  footerLines.forEach(l=>{doc.text(l,cM,y);y+=8;});

  const fname=`NOC_${job.reportNum||"draft"}_${(job.address||"noc").replace(/[^a-zA-Z0-9]/g,"_")}.pdf`;
  return {doc, fname};
}

async function emailPDF(job, settings) {
  const {doc, fname} = generatePDF(await preparePDFPhotos(job,evidencePhoto), settings);
  await doc.save(fname,{returnPromise:true});                              // auto-download so user has the file to attach
  const to      = job.orderedBy.email || "";
  const subject = encodeURIComponent(`WDO Inspection Report — ${job.address||"Property"}`);
  const body    = encodeURIComponent(
    `Hi ${job.orderedBy.name||""},\n\n` +
    `The WDO Inspection Report for ${job.address||"the property"} has been downloaded to your device as "${fname}".\n\n` +
    `Please attach that file to this email before sending.\n\n` +
    `Report #${job.reportNum||"—"} | Date: ${job.date||"—"}\n\n` +
    `If you have any questions, please don't hesitate to reach out.\n\n` +
    `${settings.company.name||""}\n${settings.company.phone||""}\n${settings.company.email||""}`
  );
  window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
}

// Send email via Resend API (requires REACT_APP_RESEND_API_KEY or settings.resendApiKey)
// Returns { ok:true } or { ok:false, error:string }
async function sendReportViaResend(job, settings, pdfBase64, fname, recipients) {
  const apiKey = settings.resendApiKey || process.env.REACT_APP_RESEND_API_KEY || "";
  if (!apiKey) return { ok:false, error:"No Resend API key configured. Add it in Settings → API Keys." };
  const to = (recipients && recipients.length ? recipients : [job.orderedBy.email]).filter(Boolean);
  if (!to.length) return { ok:false, error:"No recipient email selected. Add an email in Job Info or type one in." };
  try {
    const resp = await fetch("https://api.resend.com/emails", {
      method:"POST",
      headers: {"Authorization":`Bearer ${apiKey}`, "Content-Type":"application/json"},
      body: JSON.stringify({
        from: settings.company.email || "reports@spectermite.com",
        to,
        subject: `WDO Inspection Report — ${job.address||"Property"}`,
        html: `<p>Hi ${job.orderedBy.name||""},</p><p>Please find attached the Wood Destroying Organisms Inspection Report for <strong>${job.address||"the property"}</strong>.</p><p>Report #${job.reportNum||"—"} | Date: ${job.date||"—"}</p><p>${settings.company.name||""}<br/>${settings.company.phone||""}</p>`,
        attachments: [{ filename: fname, content: pdfBase64 }],
      }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(()=>({}));
      return { ok:false, error: err.message || `Resend error ${resp.status}` };
    }
    return { ok:true, to };
  } catch(e) {
    return { ok:false, error: e.message };
  }
}

// Send via Supabase Edge Function webhook (alternative to direct Resend)
async function sendReportViaWebhook(job, settings, pdfBase64, fname, recipients) {
  const webhookUrl = settings.emailWebhookUrl || process.env.REACT_APP_REPORT_EMAIL_WEBHOOK || "";
  if (!webhookUrl) return null; // not configured
  const to = (recipients && recipients.length ? recipients : [job.orderedBy.email]).filter(Boolean);
  try {
    const resp = await fetch(webhookUrl, {
      method:"POST",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify({ job, companyName: settings.company.name, pdfBase64, fname, to }),
    });
    return resp.ok ? { ok:true, to } : { ok:false, error:`Webhook error ${resp.status}` };
  } catch(e) {
    return { ok:false, error: e.message };
  }
}

// Send a plain (no-attachment) email via Resend — used for the e-sign link.
async function sendLinkViaResend(settings, { to, subject, html }) {
  const apiKey = settings.resendApiKey || process.env.REACT_APP_RESEND_API_KEY || "";
  if (!apiKey) return { ok:false, error:"No Resend API key configured. Add it in Settings → API Keys." };
  const rcpts = (Array.isArray(to)?to:[to]).filter(Boolean);
  if (!rcpts.length) return { ok:false, error:"No recipient email." };
  try {
    const resp = await fetch("https://api.resend.com/emails", {
      method:"POST",
      headers: {"Authorization":`Bearer ${apiKey}`, "Content-Type":"application/json"},
      body: JSON.stringify({ from: settings.company.email || "reports@spectermite.com", to:rcpts, subject, html }),
    });
    if (!resp.ok) { const err = await resp.json().catch(()=>({})); return { ok:false, error: err.message || `Resend error ${resp.status}` }; }
    return { ok:true, to:rcpts };
  } catch(e) { return { ok:false, error: e.message }; }
}

// Generate SMS body text for a report
function buildSMSBody(job, settings) {
  const addr = [job.address, job.city, job.state].filter(Boolean).join(", ");
  return encodeURIComponent(
    `WDO Inspection Report — Report #${job.reportNum||"—"}\n` +
    `Property: ${addr||"See report"}\n` +
    `Date: ${job.date||"—"}\n` +
    `From: ${settings.company.name||"Inspector"} ${settings.company.phone||""}\n` +
    `Please check your email for the PDF report.`
  );
}


// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── NOTIFICATIONS ───────────────────────────────────────────────────────────
function NotificationBell({ notifications, onOpenJob, onRead, closeSignal, onOpen }) {
  const [open, setOpen] = useState(false);
  const [busy,setBusy]=useState(null);
  const [error,setError]=useState('');
  async function openNotification(n){
    if(busy)return;
    setBusy(n.id);setError('');
    try{
      await onRead?.(n.id);
      if(n.job_id)onOpenJob?.(n.job_id);
      setOpen(false);
    }catch(e){setError(e.message||'Could not clear this notification. Please retry.');}
    finally{setBusy(null);}
  }
  useEffect(()=>{if(closeSignal)setOpen(false);},[closeSignal]);
  useEffect(()=>{if(!open)return;const close=e=>{if(e.key==='Escape')setOpen(false);};window.addEventListener('keydown',close);return()=>window.removeEventListener('keydown',close);},[open]);
  const inbox=notifications.filter(n=>!n.read);
  const unread = inbox.length;
  const fmt = ts => { try{ return new Date(ts).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}); }catch{ return ""; } };
  return (
    <div className="ts-notification-anchor" style={{position:"relative"}}>
      <button type="button" aria-label="Notifications" aria-expanded={open} aria-controls="ts-notifications-panel" onClick={()=>{ const willOpen=!open; setOpen(willOpen); if(willOpen)onOpen?.(); }} title="Notifications"
        style={{...S.btn("#1e293b"),fontSize:15,padding:"6px 10px",position:"relative"}}>
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z"/><path d="M10 21h4"/></svg>
        {unread>0 && <span style={{position:"absolute",top:-4,right:-4,background:"#ef4444",color:"#fff",fontSize:10,fontWeight:800,borderRadius:999,minWidth:16,height:16,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px"}}>{unread>9?"9+":unread}</span>}
      </button>
      {open && (<>
        <div onClick={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:2000}}/>
        <div id="ts-notifications-panel" className="ts-notifications-panel" aria-label="Notifications" style={{position:"absolute",right:0,top:"calc(100% + 6px)",width:320,maxHeight:440,overflowY:"auto",background:"#0f172a",border:"1px solid #334155",borderRadius:12,boxShadow:"0 16px 50px rgba(0,0,0,0.55)",zIndex:2001}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid #334155",fontWeight:800,fontSize:13,color:"#f1f5f9"}}>Notifications</div>
          {error&&<div role="alert" style={{padding:'10px 14px',color:'#fca5a5',fontSize:12}}>{error}</div>}
          {inbox.length===0 && <div style={{padding:24,textAlign:"center",color:"#64748b",fontSize:13}}>You're all caught up.</div>}
          {inbox.map(n=>(
            <button type="button" key={n.id} disabled={busy!==null} onClick={()=>openNotification(n)}
              aria-label={`Open notification: ${n.title}`} aria-busy={busy===n.id}
              style={{display:'block',width:'100%',textAlign:'left',border:0,minHeight:44,padding:"10px 14px",borderBottom:"1px solid rgba(51,65,85,0.5)",cursor:"pointer",background:"rgba(34,211,238,0.07)",opacity:busy===n.id?0.6:1}}>
              <div style={{fontSize:13,fontWeight:700,color:"#f1f5f9"}}>{n.title}</div>
              {n.body && <div style={{fontSize:12,color:"#94a3b8",marginTop:1}}>{n.body}</div>}
              <div style={{fontSize:10,color:"#64748b",marginTop:3}}>{fmt(n.created_at)}</div>
            </button>
          ))}
        </div>
      </>)}
    </div>
  );
}

// ─── TEAM MANAGEMENT (admin) ─────────────────────────────────────────────────
function TeamModal({ profiles, currentUserId, onClose, onChanged, onManageInspectors }) {
  const [list, setList] = useState(profiles || []);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email:"", password:"", full_name:"" });
  const [msg, setMsg] = useState(null); // {type:'ok'|'err', text}
  useEffect(()=>{ setList(profiles||[]); },[profiles]);

  const refresh = async ()=>{ const p = await sbLoadProfiles(); setList(p); onChanged?.(p); };

  const patch = async (id, p)=>{ setBusy(true); const ok = await sbUpdateProfile(id, p); setBusy(false);
    if(ok){ setList(l=>l.map(x=>x.id===id?{...x,...p}:x)); onChanged?.(); } else setMsg({type:'err',text:'Update failed — are you signed in as an admin?'}); };

  const addInspector = async ()=>{
    if(!form.email.trim() || form.password.length<6){ setMsg({type:'err',text:'Enter an email and a password of at least 6 characters.'}); return; }
    setBusy(true); setMsg(null);
    const res = await sbCreateInspector({ email:form.email.trim(), password:form.password, full_name:form.full_name.trim(), role:'inspector' });
    setBusy(false);
    if(res?.error){ setMsg({type:'err',text:res.error}); return; }
    setForm({email:"",password:"",full_name:""}); setAddOpen(false); setMsg({type:'ok',text:`Created ${form.email.trim()}.`});
    refresh();
  };

  const inp = {width:"100%",background:"#fff",color:"#0f172a",border:"1px solid #cbd5e1",borderRadius:8,padding:"8px 10px",fontSize:13};
  const roleColor = r => r==='admin'?'#dc2626':r==='operator'?'#0ea5e9':'#22c55e';

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(15,23,42,0.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#0f172a",border:"1px solid #334155",borderRadius:16,maxWidth:640,width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
        <div style={{position:"sticky",top:0,background:"#0f172a",borderBottom:"1px solid #334155",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontWeight:800,fontSize:16,color:"#f1f5f9"}}>👥 Team</span>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:"#94a3b8",lineHeight:1}}>×</button>
        </div>
        <div style={{padding:18}}>
          {msg && <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,fontSize:13,fontWeight:600,background:msg.type==='ok'?"rgba(34,197,94,0.15)":"rgba(220,38,38,0.15)",color:msg.type==='ok'?"#86efac":"#fca5a5",border:`1px solid ${msg.type==='ok'?'#166534':'#7f1d1d'}`}}>{msg.text}</div>}

          {!addOpen ? (
            <button onClick={()=>onManageInspectors?.()} style={{...S.btn("#0ea5e9"),fontSize:13,padding:"9px 16px",marginBottom:16}}>＋ Add inspector</button>
          ) : (
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:12,padding:14,marginBottom:16}}>
              <div style={{fontWeight:700,color:"#f1f5f9",marginBottom:10,fontSize:14}}>New inspector login</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <input style={inp} placeholder="Full name (e.g. Jane Doe)" value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))}/>
                <input style={inp} type="email" placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
                <input style={inp} type="text" placeholder="Temporary password (min 6 chars)" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))}/>
              </div>
              <div style={{fontSize:11,color:"#94a3b8",margin:"8px 0 10px"}}>They sign in at spectermite.com with this email + password (they can change it later). No email confirmation needed.</div>
              <div style={{display:"flex",gap:8}}>
                <button disabled={busy} onClick={addInspector} style={{...S.btn("#22c55e"),color:"#0f172a",fontWeight:800,fontSize:13,padding:"8px 16px",opacity:busy?0.6:1}}>{busy?"Creating…":"Create inspector"}</button>
                <button onClick={()=>{setAddOpen(false);setMsg(null);}} style={{...S.btn("#334155"),fontSize:13,padding:"8px 16px"}}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {list.length===0 && <div style={{color:"#64748b",fontSize:13}}>No team members yet.</div>}
            {[...list].sort((a,b)=>(a.full_name||a.email||"").localeCompare(b.full_name||b.email||"")).map(p=>{
              const isMe = p.id===currentUserId;
              return (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",background:"#1e293b",border:"1px solid #334155",borderRadius:10,padding:"10px 12px"}}>
                  <div style={{flex:"1 1 180px",minWidth:0}}>
                    <input style={{...inp,background:"transparent",border:"1px solid transparent",color:"#f1f5f9",fontWeight:700,padding:"4px 6px"}} value={p.full_name||""} placeholder={p.email||"(no name)"}
                      onChange={e=>setList(l=>l.map(x=>x.id===p.id?{...x,full_name:e.target.value}:x))}
                      onBlur={e=>patch(p.id,{full_name:e.target.value})}/>
                    <div style={{fontSize:11,color:"#64748b",padding:"0 6px"}}>{p.email}{isMe?" · you":""}</div>
                  </div>
                  <select disabled={isMe} value={p.role} onChange={e=>patch(p.id,{role:e.target.value})}
                    style={{background:"#0f172a",color:roleColor(p.role),border:`1px solid ${roleColor(p.role)}`,borderRadius:8,padding:"6px 10px",fontSize:12,fontWeight:800,cursor:isMe?"not-allowed":"pointer",opacity:isMe?0.6:1}}
                    title={isMe?"You can't change your own role":""}>
                    <option value="admin">admin</option>
                    <option value="operator">operator</option>
                    <option value="inspector">inspector</option>
                  </select>
                </div>
              );
            })}
          </div>
          <div style={{fontSize:11,color:"#64748b",marginTop:14,lineHeight:1.5}}>
            <b>admin</b> — full app + team management · <b>operator</b> — full app · <b>inspector</b> — sees only their assigned jobs.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const saved=loadSettings();
  const [settings,setSettings]=useState(saved ? {...saved, rates:migrateRates(saved.rates)} : {apiKey:"",mapboxKey:"",company:{...DEFAULT_COMPANY},rates:migrateRates(null),inspectors:[],codebook:[...DEFAULT_CODEBOOK],customDescriptions:[],hiddenDescKeys:[],descOrder:[],disclaimers:[...CA_DEFAULT_DISCLAIMERS]});
  // Job + jobs list now live in IndexedDB (see the hydrate effect below). Start
  // with a blank job; the effect fills it from IDB / migrates legacy localStorage.
  const [job,setJobState]=useState(()=>newJob("1"));
  const startupJobPatches=useRef(new Map());
  const startupNewJobs=useRef(new Map());
  const jobInteraction=useRef(0);
  const videoBusyRef=useRef(false);
  const setVideoBusy=useCallback(value=>{videoBusyRef.current=value;},[]);
  const setJob=(value)=>{
    if(typeof value!=="function"&&videoBusyRef.current&&String(value.id)!==String(job.id)){
      window.alert("Finish uploading or discard the walkthrough clip before changing inspections.");return;
    }
    jobInteraction.current++;
    setJobState(current=>{
      const next=typeof value==='function'?value(current):value;
      return checkingAccess||profile?.accessDenied?next:withReportInspector(next,settings.inspectors||[],user?.id);
    });
  };
  const hydratedRef = useRef(false);
  const evidenceParams=new URLSearchParams(window.location.search);
  const [activeSection,setActiveSection]=useState(evidenceParams.has("evidence")?"walkthrough":"jobs");
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  const headerRef=useRef(null);
  const [headerHeight,setHeaderHeight]=useState(0);
  const headerHidden=useScrollHeader(headerRef,activeSection);

  useEffect(()=>setMobileMenuOpen(false),[activeSection]);
  useEffect(()=>{if(!mobileMenuOpen)return;const close=e=>{if(e.key==='Escape')setMobileMenuOpen(false);};window.addEventListener('keydown',close);return()=>window.removeEventListener('keydown',close);},[mobileMenuOpen]);
  useEffect(()=>{ window._tsSetTab=setActiveSection; return()=>{ delete window._tsSetTab; }; },[setActiveSection]);
  const [showSettings,setShowSettings]=useState(false);
  const [settingsTab,setSettingsTab]=useState("company");
  const [checkingAccess,setCheckingAccess]=useState(true);
  const [cloudHydrating,setCloudHydrating]=useState(true);
  const [contacts,setContacts]=useState(()=>loadContacts());
  const [showContacts,setShowContacts]=useState(false);
  const [contactPickTarget,setContactPickTarget]=useState(null); // "orderedBy"|"propOwner"|"partyOfInterest"
  useEffect(()=>{
    clearTimeout(saveContactsTimerRef.current);
    saveContactsTimerRef.current = setTimeout(()=> saveContacts(contacts), 500);
    return ()=> clearTimeout(saveContactsTimerRef.current);
  },[contacts]);
  const jsPdfLoaded=true;
  const [savedJobs,setSavedJobs]=useState([]);
  const [showJobHistory,setShowJobHistory]=useState(false);

  // Hydrate job + jobs from IndexedDB on mount, migrating any legacy localStorage
  // data once. Guarded so it never clobbers an edit the user already started.
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      let idbJob = await idbGet('job');
      let idbJobs = await idbGet('jobs');
      if(idbJob===undefined){ const lj=loadJobLegacy(); if(lj){ idbJob = lj.id?lj:{...lj,id:Date.now().toString()}; await idbSet('job', idbJob); } }
      if(idbJobs===undefined){ const ljs=loadJobsLegacy(); if(ljs&&ljs.length){ idbJobs=ljs; await idbSet('jobs', ljs); } }
      try{ localStorage.removeItem(LS_KEY); localStorage.removeItem(JOBS_KEY); }catch{}
      if(cancelled) return;
      if(idbJob && idbJob.id){ setJobState(prev=> (prev && (prev.address || (prev.findings||[]).length || (prev.drawings||[]).length)) ? prev : idbJob); }
      if(idbJobs && idbJobs.length){ setSavedJobs(prev=> prev.length ? prev : idbJobs); }
      hydratedRef.current = true;
    })();
    return ()=>{ cancelled=true; };
  // eslint-disable-next-line
  },[]);

  // ─── Cloud Sync State ─────────────────────────────────────────────────────
  const [user, setUser] = useState(undefined);
  const [profile, setProfile] = useState(null);    // firm role row for the signed-in user
  const [profiles, setProfiles] = useState([]);     // all firm members (assignment + team)
  const [firmMode, setFirmMode] = useState(false);  // true once the shared firm tables exist
  const [notifications, setNotifications] = useState([]);
  const notificationAccount=useRef(null);
  notificationAccount.current=user?.id||null;
  const clearedNotificationIds=useRef(new Set());
  const applyNotifications=(uid,rows)=>{
    if(rows&&notificationAccount.current===uid)setNotifications(rows.filter(n=>!n.read&&!clearedNotificationIds.current.has(`${uid}:${n.id}`)));
  };
  const openOwnNotification=async id=>{
    const uid=user?.id;
    await readOwnNotification(supabase,uid,id);
    clearedNotificationIds.current.add(`${uid}:${id}`);
    if(notificationAccount.current!==uid)throw Error('Your account changed. Reopen notifications for the current account.');
    setNotifications(rows=>rows.filter(n=>n.id!==id));
  };
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle'|'syncing'|'synced'|'error'
  const [storageWarning, setStorageWarning] = useState(false); // localStorage quota hit
  const [installPrompt, setInstallPrompt] = useState(null); // deferred PWA install event
  const [isOffline, setIsOffline] = useState(typeof navigator!=='undefined' && navigator.onLine===false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const syncTimerRef = useRef(null);
  const saveJobTimerRef = useRef(null);
  const jobRevRef = useRef({}); // firm_jobs id -> server updated_at we last saw (optimistic-lock baseline)
  const saveSettingsTimerRef = useRef(null);
  const saveContactsTimerRef = useRef(null);

  // Auth notifications only update identity. Supabase data requests run from
  // the React effect, outside the auth callback and its session lock.
  useEffect(()=>subscribeWorkspaceIdentity(supabase.auth,setUser),[]);
  useEffect(()=>{
    if(user===undefined)return;
    let cancelled=false;
    const u=user;
    if(u){
      setNotifications([]);
      startupJobPatches.current.clear();
      const initialInteraction=jobInteraction.current;
      const mergeJob=j=>({...j,...startupJobPatches.current.get(String(j.id))});
      const mergeJobs=jobs=>[...jobs,...[...startupNewJobs.current.values()].filter(j=>!jobs.some(remote=>String(remote.id)===String(j.id)))].map(mergeJob);
      const restoreJob=(cloudJob,jobs)=>setJobState(current=>{
        const selected=jobInteraction.current!==initialInteraction?current:cloudJob;
        if(!selected?.id)return current;
        return mergeJob(jobs?jobs.find(j=>String(j.id)===String(selected.id))||selected:selected);
      });
      setCheckingAccess(true);
      setCloudHydrating(true);
        (async()=>{
          setSyncStatus('syncing');
          try{
            const prof = await sbLoadProfile(u.id);
            if(cancelled)return;
            if(!prof){setProfile({accessDenied:true,accessMessage:'This account has not been approved by the office. Ask an administrator to add you under Settings → Inspectors.'});setFirmMode(true);setSavedJobs([]);setJob(newJob());setSyncStatus('error');return;}
            let roster=null;
            if(prof){
              try{
                roster=await loadInspectorRoster(supabase);
                if(cancelled)return;
              }
              catch{if(cancelled)return;setProfile({...prof,accessDenied:true,accessMessage:'Unable to verify your inspector roster. Check your connection, then sign in again.'});setFirmMode(true);setSavedJobs([]);setJob(newJob());setSyncStatus('error');return;}
              if(!prof.active||(prof.role==='inspector'&&!roster.some(r=>r.user_id===u.id))){setProfile({...prof,accessDenied:true,accessMessage:'Your inspector account needs an active entry in the office roster. Contact your administrator.'});setFirmMode(true);setSavedJobs([]);setJob(newJob());setSyncStatus('error');return;}
              setSettings(s=>({...s,inspectors:roster.map(reportInspector)}));
            }
            setProfile(prof);
            setFirmMode(true);
            setCheckingAccess(false);
            // Open the shell after permission checks; large job/photo payloads
            // hydrate separately while editing and automatic writes are paused.
            const [cloudSettings, cloudJob, cloudContacts, firmJobs] = await Promise.all([
              sbLoadSettings(u.id), sbLoadJob(u.id), sbLoadContacts(u.id), sbLoadFirmJobs(),
            ]);
            if(cancelled)return;
            if(prof.active&&['admin','operator'].includes(prof.role)){
              const key='termitescope_roster_imported_v1_'+u.id;
              let imported=false;try{imported=localStorage.getItem(key)==='1';}catch{}
              if(!imported){
                roster=await loadInspectorRoster(supabase,[...(settings.inspectors||[]),...(cloudSettings?.inspectors||[])],true);
                if(cancelled)return;
                try{localStorage.setItem(key,'1');}catch{}
              }
            }
            if(cloudSettings) setSettings(s=>({...s,...cloudSettings,rates:migrateRates(cloudSettings.rates),inspectors:roster.map(reportInspector)}));
            if(cloudContacts?.length) setContacts(cloudContacts);
            sbLoadNotifications(u.id).then(value=>{if(!cancelled)applyNotifications(u.id,value);});
            if(firmJobs !== null){
              // Firm mode: the shared, role-secured job store is live.
              setFirmMode(true);
              firmJobs.forEach(j=>{ if(j.updatedAt) jobRevRef.current[String(j.id)]=j.updatedAt; }); // optimistic-lock baseline
              sbLoadProfiles().then(value=>{if(!cancelled)setProfiles(value);});
              // Firm-shared config (company/rates/codebook/disclaimers) overrides the
              // personal copy so every login uses the office's settings.
              sbLoadFirmSettings().then(fs=>{ if(!cancelled&&fs) setSettings(s=>({...s, ...firmShared(fs), ...(fs.rates?{rates:migrateRates(fs.rates)}:{})})); });
              const role = prof?.role;
              if(role === 'inspector'){
                setSavedJobs(firmJobs.map(mergeJob));           // RLS already limited these to their assignments
              } else {
                if(firmJobs.length === 0){
                  // Staff, empty firm store → seed once from this device's existing jobs.
                  const legacy = (await idbGet('jobs')) || loadJobsLegacy() || [];
                  if(cancelled)return;
                  const cloudLegacy = await sbLoadJobs(u.id);
                  if(cancelled)return;
                  const seed = (cloudLegacy && cloudLegacy.length) ? cloudLegacy : legacy;
                  if(seed.length){ await Promise.all(seed.map(j=>sbUpsertFirmJob(j,u.id))); if(cancelled)return;setSavedJobs(seed); }
                } else {
                  setSavedJobs(mergeJobs(firmJobs));
                }
                if(cloudJob && cloudJob.id) restoreJob(firmJobs.find(j=>String(j.id)===String(cloudJob.id))||newJob(),firmJobs);
              }
            } else {
              // Legacy mode (firm tables not created yet): behave exactly as before.
              const cloudJobs = await sbLoadJobs(u.id);
              if(cancelled)return;
              if(cloudJob && cloudJob.id) restoreJob(cloudJob);
              if(cloudJobs && cloudJobs.length) setSavedJobs(mergeJobs(cloudJobs));
            }
            setSyncStatus('synced');
          }catch{ if(cancelled)return;setSyncStatus('error');setProfile({accessDenied:true,accessMessage:'Could not verify your account. Check your connection and retry.'}); }finally{if(!cancelled){setCheckingAccess(false);setCloudHydrating(false);}}
        })();
    }else{
      startupNewJobs.current.clear();startupJobPatches.current.clear();
      setCheckingAccess(false);setCloudHydrating(false);setProfile(null);setProfiles([]);setFirmMode(false);setNotifications([]);
      setSyncStatus('idle');
    }
    return ()=>{cancelled=true;};
  // eslint-disable-next-line
  },[user]);

  // Debounced cloud sync — fires 2 s after any data change
  useEffect(()=>{
    if(!user||checkingAccess||cloudHydrating||profile?.accessDenied) return;
    clearTimeout(syncTimerRef.current);
    setSyncStatus('syncing');
    syncTimerRef.current = setTimeout(async()=>{
      try{
        const role = profile?.role;

        const tasks = [ sbSaveContacts(user.id, contacts) ];
        if(firmMode){
          // The open job goes through pushOpenJob (offline-queued).
          if(role === 'inspector'){
            if(job.assignedTo === user.id) await pushOpenJob();
          } else {
            await pushOpenJob();
            tasks.push(sbSaveSettings(user.id, settings), sbSaveJob(user.id, job));
            // Operators/admins own the firm-shared settings; push them for the firm.
            if(role==='admin'||role==='operator') tasks.push(sbSaveFirmSettings(firmShared(settings)));
          }
        } else {
          tasks.push(sbSaveJob(user.id, job), sbSaveSettings(user.id, settings), sbSaveJobs(user.id, savedJobs));
        }
        await Promise.all(tasks);
        setSyncStatus((typeof navigator!=='undefined' && navigator.onLine===false) ? 'error' : 'synced');
      }catch{ setSyncStatus('error'); }
    }, 2000);
    return ()=> clearTimeout(syncTimerRef.current);
  // eslint-disable-next-line
  },[job, settings, contacts, user, firmMode, profile, checkingAccess, cloudHydrating]);

  // Listen for background-sync event from service worker
  useEffect(()=>{
    const handler = ()=>{ if(user) setSyncStatus('syncing'); };
    window.addEventListener('ts-cloud-sync', handler);
    return ()=> window.removeEventListener('ts-cloud-sync', handler);
  },[user]);

  useEffect(()=>{
    clearTimeout(saveSettingsTimerRef.current);
    saveSettingsTimerRef.current = setTimeout(()=> saveSettings(settings), 500);
    return ()=> clearTimeout(saveSettingsTimerRef.current);
  },[settings]);
  useEffect(()=>{
    clearTimeout(saveJobTimerRef.current);
    saveJobTimerRef.current = setTimeout(()=>{
      if(!hydratedRef.current) return; // don't let the pre-hydrate blank job overwrite IDB
      saveJob(job);
      setSavedJobs(prev=>{
        const idx=prev.findIndex(j=>j.id===job.id);
        if(deletingFirmJobs.has(String(job.id))||(idx<0&&!job.address&&!(job.findings||[]).length&&!(job.drawings||[]).length))return prev;
        const next=idx>=0 ? prev.map((j,i)=>i===idx?job:j) : [job,...prev];
        saveJobs(next);
        return next;
      });
    }, 500);
    return ()=> clearTimeout(saveJobTimerRef.current);
  },[job]);



  // Accepts either a patch object or a functional updater (j => partial). The
  // functional form is essential for async writers (photo capture) so they read
  // the freshest job instead of a stale render closure.
  const updateJob=(patch)=>setJob(j=>{
    let changes=typeof patch==="function"?patch(j):patch;
    if(['drawings','mapCenter','mapZoom','mapRotation','mapPixelScale','canvasH','findings'].some(key=>key in changes)){
      const next={...j,...changes};
      const findings=syncJobDiagramAreas(next,settings,!(firmMode&&profile?.role==='inspector'));
      if(findings!==next.findings)changes={...changes,findings};
    }
    if(cloudHydrating)startupJobPatches.current.set(String(j.id),{...startupJobPatches.current.get(String(j.id)),...changes});
    return {...j,...changes};
  });

  // ── Role gating ───────────────────────────────────────────────────────────
  const role = profile?.role;
  useEffect(()=>{
    const header=headerRef.current;
    if(!header)return;
    const measure=()=>setHeaderHeight(header.getBoundingClientRect().height);
    measure();
    const observer=typeof ResizeObserver!=="undefined"?new ResizeObserver(measure):null;
    observer?.observe(header);
    window.addEventListener("resize",measure);
    return()=>{observer?.disconnect();window.removeEventListener("resize",measure);};
  },[checkingAccess,role,profile?.accessDenied,activeSection]);
  const isInspector = firmMode && role === 'inspector';
  const assignmentInspectors=assignableInspectors(profiles,settings.inspectors||[]);
  useEffect(()=>{ if(isInspector) setActiveSection('inspector'); },[isInspector]);
  // Office users start in Jobs. Role hydration must preserve a job they opened.
  // Once an inspector submits, their copy of the job locks read-only until the
  // office reopens it. All section edits route through this guarded updater.
  const jobLocked = isInspector && !!job.inspectionSubmitted;
  const guardedUpdateJob = jobLocked || checkingAccess ? (()=>{}) : updateJob;
  useEffect(()=>{
    if(checkingAccess||cloudHydrating||!user||profile?.accessDenied)return;
    setJobState(current=>withReportInspector(current,settings.inspectors||[],user.id));
  },[checkingAccess,cloudHydrating,user?.id,profile?.accessDenied,settings.inspectors,job.id,job.assignedTo]);

  // ── Firm-aware job list persistence (per-row in firm mode, blob in legacy) ──
  const stampRev = (id,r)=>{ if(r&&r.ok) jobRevRef.current[String(id)]=r.rev; };
  const persistNewJob = (j)=>{ if(cloudHydrating)startupNewJobs.current.set(String(j.id),j);setSavedJobs(prev=>{ const next=[j,...prev]; saveJobs(next); if(user&&!checkingAccess&&!cloudHydrating){ if(firmMode) sbUpsertFirmJob(j,user.id).then(r=>stampRev(j.id,r)); else sbSaveJobs(user.id,next); } return next; }); };
  const persistJobUpdate = (id,updates)=>{ setSavedJobs(prev=>{ const next=prev.map(j=>j.id===id?{...j,...updates}:j); saveJobs(next); if(user){ if(firmMode){ const uj=next.find(j=>j.id===id); if(uj) sbUpsertFirmJob(uj,user.id).then(r=>stampRev(id,r)); } else sbSaveJobs(user.id,next); } return next; }); };
  const persistJobDelete=async(id)=>{
    if(String(job.id)===String(id)&&videoBusyRef.current)throw Error("Finish uploading or discard the walkthrough clip before deleting this inspection.");
    const next=savedJobs.filter(j=>j.id!==id);
    if(user){if(firmMode)await sbDeleteFirmJob(id);else{const {error}=await supabase.from("user_jobs").upsert({user_id:user.id,data:next,updated_at:new Date().toISOString()});if(error)throw Error("Could not delete this job from the cloud. Retry when connected.");}}
    const pending=loadPending();delete pending[String(id)];savePending(pending);
    setSavedJobs(prev=>{const remaining=prev.filter(j=>j.id!==id);saveJobs(remaining);return remaining;});
    if(String(job.id)===String(id)){clearTimeout(syncTimerRef.current);clearTimeout(saveJobTimerRef.current);setJob(next[0]||newJob());}
  };

  const openNewJob=()=>{
    if(videoBusyRef.current){
      window.alert("Finish uploading or discard the walkthrough clip before changing inspections.");return;
    }
    const fresh=withReportInspector(newJob(String(Number(job.reportNum||0)+1)),settings.inspectors||[],user?.id);
    setJob(fresh);
    persistNewJob(fresh);
    setActiveSection("job");
    setMobileMenuOpen(false);
    window.scrollTo({top:0,behavior:"auto"});
  };

  // ── Offline write queue: firm jobs that failed to reach the cloud (offline or
  //    error) are parked in localStorage and flushed on reconnect. ─────────────
  const PENDING_KEY = "termitescope_pending_firmjobs_v1";
  const loadPending = ()=>{ try{ return JSON.parse(localStorage.getItem(PENDING_KEY))||{}; }catch{ return {}; } };
  const savePending = (m)=>{ lsSet(PENDING_KEY, JSON.stringify(m)); };
  const queueFirmJob = (j)=>{ if(deletingFirmJobs.has(String(j.id)))return; const m=loadPending(); m[String(j.id)]=j; savePending(m); };
  const pendingCount = ()=>{ try{ return Object.keys(loadPending()).length; }catch{ return 0; } };
  const flushQueue = async ()=>{
    if(!user || !firmMode || checkingAccess || cloudHydrating || profile?.accessDenied || (typeof navigator!=='undefined' && navigator.onLine===false)) return;
    const m=loadPending();
    for(const id of Object.keys(m)){
      const r=await sbUpsertFirmJob(m[id], user.id);
      if(r&&r.ok){ jobRevRef.current[id]=r.rev; delete m[id]; savePending(m); }
      else break; // still offline/erroring — stop, retry on next trigger
    }
  };
  // Push the currently-open firm job (offline writes are queued for retry).
  const pushOpenJob = async ()=>{
    const id=String(job.id);
    if(deletingFirmJobs.has(id)||(!savedJobs.some(j=>String(j.id)===id)&&!job.address&&!(job.findings||[]).length&&!(job.drawings||[]).length))return;
    if(typeof navigator!=='undefined' && navigator.onLine===false){ queueFirmJob(job); return; }
    const r=await sbUpsertFirmJob(job, user.id);
    if(r&&r.ok) jobRevRef.current[id]=r.rev; else queueFirmJob(job);
  };

  const prepareSignatureJob=async()=>{
    if(!user||!firmMode||!['admin','operator'].includes(role))throw Error('Sign in with an office account to send proposals.');
    const result=await sbUpsertFirmJob(job,user.id);
    if(!result?.ok)throw Error('The latest proposal could not be saved. Check your connection or reload the job before sending.');
    stampRev(job.id,result);
  };

  // Assign the open job to an inspector and notify them of the new assignment.
  const assignInspector = (uid)=>{
    const prev = job.assignedTo;
    updateJob({ assignedTo: uid||null });
    if(uid && uid!==prev){
      const body = ([job.address,job.city].filter(Boolean).join(", ") || `Report #${job.reportNum||"—"}`) + (job.date?` · ${job.date}`:"");
      sbCreateNotification(uid, { type:"assigned", title:"New job assigned", body, job_id:String(job.id) });
    }
  };
  // Assign any job (from the dashboard/calendar) to an inspector and notify them.
  const assignJob = (jobLike, uid)=>{
    const next=withReportInspector({...jobLike,assignedTo:uid||null},settings.inspectors||[],user?.id);
    const patch={assignedTo:uid||null,inspectorIds:next.inspectorIds||[],autoInspectorAccountId:next.autoInspectorAccountId,autoInspectorId:next.autoInspectorId};
    persistJobUpdate(jobLike.id,patch);
    if(String(job.id)===String(jobLike.id))updateJob(patch);
    if(uid && uid!==jobLike.assignedTo){
      const body = ([jobLike.address,jobLike.city].filter(Boolean).join(", ") || `Report #${jobLike.reportNum||"—"}`) + (jobLike.date?` · ${jobLike.date}`:"");
      sbCreateNotification(uid, { type:"assigned", title:"New job assigned", body, job_id:String(jobLike.id) });
    }
  };
  // Inspector submits the completed field inspection back to the office. Setting
  // the flag persists the job (via debounced sync + an immediate upsert); a
  // Supabase trigger on firm_jobs then notifies all operators/admins.
  const submitInspection = ()=>{
    if(!window.confirm("Submit this inspection to the office?\n\nThe office will be notified and your copy will lock. They can reopen it if changes are needed.")) return;
    const patch={ inspectionSubmitted:true, inspectionSubmittedAt:new Date().toISOString(), inspectionSubmittedBy:user?.id||null };
    const updated={...job,...patch};
    setJob(updated);
    if(user && firmMode && job.assignedTo===user.id) sbUpsertFirmJob(updated, user.id).then(r=>stampRev(updated.id,r));
  };
  // Office reopens a submitted job so the inspector can edit again.
  const reopenInspection = ()=>{
    const patch={ inspectionSubmitted:false, inspectionReopenedAt:new Date().toISOString() };
    setJob(j=>({...j,...patch}));
    persistJobUpdate(job.id, patch);
  };
  // Keep the notification bell live: refresh on focus, and poll every 20s while
  // the tab is visible (no Supabase realtime config needed).
  useEffect(()=>{
    if(!user) return undefined;
    const refresh = ()=> sbLoadNotifications(user.id).then(rows=>applyNotifications(user.id,rows));
    const onFocus = refresh;
    const onVisible = ()=>{ if(document.visibilityState==="visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const poll = setInterval(()=>{ if(document.visibilityState==="visible") refresh(); }, 20000);
    return ()=>{ window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVisible); clearInterval(poll); };
  },[user]);

  // Storage-quota + online/offline signals for the status banners.
  useEffect(()=>{
    const onStorage = ()=> setStorageWarning(true);
    const onOnline  = ()=>{ setIsOffline(false); flushQueue(); };
    const onOffline = ()=> setIsOffline(true);
    const onInstall = (e)=>{ e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = ()=> setInstallPrompt(null);
    window.addEventListener("ts-storage-error", onStorage);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstall);
    window.addEventListener("appinstalled", onInstalled);
    if(user && firmMode) flushQueue(); // drain anything parked from a previous offline session
    return ()=>{ window.removeEventListener("ts-storage-error", onStorage); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); window.removeEventListener("beforeinstallprompt", onInstall); window.removeEventListener("appinstalled", onInstalled); };
  // eslint-disable-next-line
  },[user, firmMode, checkingAccess, cloudHydrating, profile?.accessDenied]);

  const progress={
    job:!!(job.address&&job.orderedBy.name),
    map:!!(job.drawings.length>0||job.mapBg),
    findings:job.findings.length>0,
    contract:job.findings.some(f=>f.primaryPrice>0),
    report:false,
  };
  if(profile?.accessDenied)return <div style={{...S.app,padding:24,display:'grid',placeItems:'center'}}><div style={{...S.card,maxWidth:480}}><h2>Inspector access unavailable</h2><p style={{margin:'16px 0',lineHeight:1.6}}>{profile.accessMessage}</p><button style={S.btn('#0f172a')} onClick={()=>supabase.auth.signOut()}>Sign out</button><button style={{...S.btnOutline,marginLeft:8}} onClick={()=>window.location.reload()}>Retry</button></div></div>;
  const steps=[
    {key:"job",label:"Job Info",mobileLabel:"Job"},
    {key:"map",label:"Diagram"},
    {key:"walkthrough",label:"Video"},
    {key:"findings",label:"F&R",count:job.findings.length},
    {key:"contract",label:"Contract"},
    {key:"report",label:"PDF"},
    {key:"noc",label:"NOC"},
  ];

  return (
    <div style={{...S.app,"--ts-header-height":headerHidden?"0px":`${headerHeight}px`,"--ts-header-measured-height":`${headerHeight}px`}}>
      <style>{`
        input:focus, select:focus, textarea:focus {
          border-color: #22d3ee !important;
          box-shadow: 0 0 0 3px rgba(34,211,238,0.15) !important;
          outline: none !important;
        }
        input[type=number]::-webkit-inner-spin-button { opacity: 1; }
        button:active { opacity: 0.85; transform: scale(0.98); }
        * { box-sizing: border-box; }
        @media (max-width: 600px) {
          .grid2-mobile { grid-template-columns: 1fr !important; }
          .grid3-mobile { grid-template-columns: 1fr 1fr !important; }
          .grid3-mobile > div:first-child { grid-column: 1 / -1; }
          .grid3-mobile input[type="date"],
          .grid3-mobile input[type="time"] { font-size: 12px !important; padding: 6px 4px !important; text-align: center; }
        }
        .ts-mode-scroll { -ms-overflow-style:none; scrollbar-width:none; }
        .ts-mode-scroll::-webkit-scrollbar { display:none; }

      `}</style>
      <div ref={headerRef} className={`ts-header${headerHidden?" is-scroll-hidden":""}`} style={{...S.header}}>
        <div className="ts-logo" style={S.logo}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          SpecTermite
        </div>
        {["job","map","walkthrough","findings","contract","report","noc"].includes(activeSection) && <WorkflowTabs steps={steps} activeSection={activeSection} progress={progress} onSelect={setActiveSection} buttonStyle={S.progStep}/>}
        <div className="ts-header-status">
          {/* Cloud sync status */}
          {user ? (
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:syncStatus==='error'?"#f87171":syncStatus==='syncing'?"#fbbf24":"#34d399",cursor:"pointer",flexShrink:0}} title={`Signed in as ${user.email}`} onClick={()=>{ if(window.confirm(`Sign out of cloud sync?\n\n${user.email}`)) supabase.auth.signOut(); }}>
              {syncStatus==='syncing' && <span style={{display:"inline-block",animation:"spin 1s linear infinite"}}>⟳</span>}
              {syncStatus==='synced'  && "☁ Synced"}
              {syncStatus==='error'   && "⚠ Sync err"}
              {syncStatus==='idle'    && "☁"}
            </div>
          ) : (
            <button style={{...S.btn("#1e293b"),fontSize:11,padding:"5px 10px",border:"1px solid #334155"}} onClick={()=>setShowAuthModal(true)} title="Sign in to enable cloud sync across devices">
              ☁ Sign In
            </button>
          )}
          {user && firmMode && <NotificationBell closeSignal={mobileMenuOpen} onOpen={()=>setMobileMenuOpen(false)} notifications={notifications} onOpenJob={(jid)=>{ const j=savedJobs.find(x=>String(x.id)===String(jid)); if(j){ setJob(j); setActiveSection("job"); } }} onRead={openOwnNotification}/>}
        </div>
        <button type="button" className="ts-menu-toggle" aria-label="Main menu" aria-expanded={mobileMenuOpen} aria-controls="ts-header-actions" onClick={()=>setMobileMenuOpen(open=>!open)}><span aria-hidden="true">{mobileMenuOpen?'✕':'☰'}</span><span className="ts-menu-text">Menu</span></button>
        {mobileMenuOpen&&<button type="button" className="ts-menu-backdrop" aria-label="Close navigation menu" onClick={()=>setMobileMenuOpen(false)}/>}
        <div id="ts-header-actions" className={`ts-header-actions${mobileMenuOpen?' is-open':''}`} onClick={()=>setMobileMenuOpen(false)}>

          {!isInspector && firmMode && <button style={{...S.btn(activeSection==="dashboard"?"#0ea5e9":"#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setActiveSection("dashboard")} title="Office dashboard — pipeline & schedule">📊 Dashboard</button>}
          {!isInspector && <button style={{...S.btn(activeSection==="jobs"?"#0ea5e9":"#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setActiveSection("jobs")} title="All Jobs">📋 Jobs {savedJobs.length>0&&<span style={{background:"#22d3ee",color:"#0f172a",borderRadius:10,padding:"1px 6px",fontSize:11,marginLeft:4}}>{savedJobs.length}</span>}</button>}
          {!isInspector && firmMode && <button style={{...S.btn(activeSection==="calendar"?"#0ea5e9":"#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setActiveSection("calendar")} title="Team calendar — all inspectors, color-coded">📅 Calendar</button>}
          <button style={{...S.btn(activeSection==="inspector"?"#0ea5e9":"#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setActiveSection("inspector")} title="Inspector schedule & assigned jobs">🗓 {isInspector?"My Schedule":"Inspector"}</button>
          {!isInspector && <button style={{...S.btn("#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={openNewJob} title="New Job">＋ New</button>}
          {!isInspector && <button style={{...S.btn("#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setShowContacts(true)} title="Contacts"><span aria-hidden="true">👥</span><span className="ts-menu-label"> Contacts</span></button>}
          {role==='admin' && <button style={{...S.btn("#1e293b"),fontSize:12,padding:"6px 12px"}} onClick={()=>setShowTeam(true)} title="Manage team logins & roles">🛡 Team</button>}
          {installPrompt && <button style={{...S.btn("#0ea5e9"),fontSize:12,padding:"6px 12px"}} title="Install SpecTermite as an app" onClick={async()=>{ try{ installPrompt.prompt(); await installPrompt.userChoice; }catch{} setInstallPrompt(null); }}>⬇ Install</button>}
          {!isInspector && <button aria-label="Settings" style={{...S.btn("#1e293b"),fontSize:18,padding:"6px 12px"}} onClick={()=>{setSettingsTab("company");setShowSettings(true);}}><span aria-hidden="true">⚙</span><span className="ts-menu-label"> Settings</span></button>}
        </div>
      </div>
      <div className={`ts-page ts-section-${activeSection}`} style={{...S.page,...(activeSection==="map"&&{padding:"8px 4px 80px",maxWidth:"100%"})}}>
        {(isOffline || storageWarning) && (
          <div style={{margin:activeSection==="map"?"0 4px 8px":"0 0 12px",display:"flex",flexDirection:"column",gap:6}}>
            {isOffline && (
              <div style={{padding:"9px 14px",borderRadius:10,background:"#422006",border:"1px solid #b45309",color:"#fde68a",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                📴 Offline — your work is saved on this device{firmMode?` and will sync when you reconnect${pendingCount()?` (${pendingCount()} queued)`:""}`:""}.
              </div>
            )}
            {storageWarning && (
              <div style={{padding:"9px 14px",borderRadius:10,background:"#450a0a",border:"1px solid #dc2626",color:"#fecaca",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                <span>⚠️ This device's storage is full — new photos may not be saved locally. Stay online so they sync to the cloud, or remove some photos.</span>
                <button onClick={()=>setStorageWarning(false)} style={{background:"none",border:"none",color:"#fecaca",cursor:"pointer",fontSize:16,fontWeight:800}}>×</button>
              </div>
            )}
          </div>
        )}
        {["job","map","walkthrough","findings","contract","report","noc"].includes(activeSection) && firmMode && (isInspector || job.inspectionSubmitted) && (
          <div style={{margin:activeSection==="map"?"0 4px 8px":"0 0 12px",padding:"10px 14px",borderRadius:10,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",
            background:job.inspectionSubmitted?"#052e16":"#0c4a6e",border:`1px solid ${job.inspectionSubmitted?"#16a34a":"#0ea5e9"}`}}>
            {job.inspectionSubmitted ? (
              <>
                <span style={{fontSize:13,fontWeight:700,color:"#bbf7d0",flex:1,minWidth:180}}>✓ Inspection submitted{job.inspectionSubmittedAt?` · ${new Date(job.inspectionSubmittedAt).toLocaleString()}`:""}{isInspector?" — locked. The office has been notified.":""}</span>
                {!isInspector && <button onClick={reopenInspection} style={{...S.btn("#f59e0b"),fontSize:13,fontWeight:800,padding:"8px 16px"}}>↩ Reopen for inspector</button>}
              </>
            ) : (
              <>
                <span style={{fontSize:13,fontWeight:700,color:"#e0f2fe",flex:1,minWidth:180}}>📋 Inspecting: {job.address||`Report #${job.reportNum||"—"}`}</span>
                <button onClick={submitInspection} style={{...S.btn("#16a34a"),fontSize:13,fontWeight:800,padding:"8px 18px"}}>✓ Submit to office</button>
              </>
            )}
          </div>
        )}
        {!isInspector && firmMode && activeSection==="dashboard" && <OperatorDashboard jobs={savedJobs} people={assignmentInspectors} settings={settings} onOpen={(j,tab)=>{setJob(j);setActiveSection(tab||"job");}} onGoJobs={()=>setActiveSection("jobs")} onNew={openNewJob} onAssign={assignJob}/>}
        {!isInspector && activeSection==="jobs"     && <JobsDashboard jobs={savedJobs} currentJobId={job.id} settings={settings} firmMode={firmMode} people={assignmentInspectors} onAssign={assignJob} onOpen={(j,tab)=>{setJob(j);setActiveSection(tab||"job");}} onNew={openNewJob} onUpdateJob={persistJobUpdate} onDelete={persistJobDelete}/>}
        {!isInspector && activeSection==="calendar" && <OperatorCalendar jobs={savedJobs} people={assignmentInspectors} onOpen={(j,tab)=>{setJob(j);setActiveSection(tab||"job");}} onAssign={assignJob} onPatchJob={persistJobUpdate}/>}
        {activeSection==="inspector"&& <InspectorDashboard jobs={savedJobs} settings={settings} firmMode={firmMode} people={firmMode ? assignmentInspectors : (settings.inspectors||[]).map(i=>({id:i.id,full_name:i.name,license:i.license}))} lockedId={isInspector ? user?.id : null} onPatchJob={persistJobUpdate} onOpen={(j,tab)=>{setJob(j);setActiveSection(tab||"job");}}/>}
        {activeSection==="job"      && <JobSection job={job} updateJob={guardedUpdateJob} settings={settings} setSettings={setSettings} firmMode={firmMode && !isInspector} isInspector={isInspector} people={assignmentInspectors} onAssignInspector={assignInspector} onPickContact={(target)=>{setContactPickTarget(target);setShowContacts(true);}}/>}
        {activeSection==="map"      && <MapSection job={job} updateJob={guardedUpdateJob} settings={settings} setSettings={setSettings} readOnly={jobLocked}/>}
        <div style={{display:activeSection==="walkthrough"?"block":"none"}}>
          {evidenceParams.has("evidence")?<EvidenceViewer path={evidenceParams.get("evidence")} start={Number(evidenceParams.get("start"))||0} end={Number(evidenceParams.get("end"))||300} user={user} onSignIn={()=>setShowAuthModal(true)}/>:<WalkthroughSection key={`${job.id}:${user?.id||"signed-out"}`} job={job} updateJob={guardedUpdateJob} settings={settings} user={user} onSignIn={()=>setShowAuthModal(true)} inspectorMode={isInspector} readOnly={jobLocked} onActivityChange={setVideoBusy}/>}
        </div>
        {activeSection==="findings" && <FindingsSection job={job} updateJob={guardedUpdateJob} settings={settings} inspectorMode={isInspector}/>}
        {activeSection==="contract" && <ContractSection job={job} updateJob={guardedUpdateJob} settings={settings} isInspector={isInspector} onPrepareSignature={prepareSignatureJob}/>}
        {activeSection==="noc"      && <NocSection job={job} updateJob={guardedUpdateJob} settings={settings}/>}
        {activeSection==="report"   && <ReportSection job={job} settings={settings} jsPdfLoaded={jsPdfLoaded} updateJob={updateJob}/>}
      </div>
      {showSettings&&<SettingsModal onSignOut={user?async()=>{const {error}=await supabase.auth.signOut();if(error)throw error;}:undefined} initialTab={settingsTab} profile={profile} profiles={profiles} onAccountsChanged={async()=>setProfiles(await sbLoadProfiles())} settings={settings} setSettings={setSettings} onClose={()=>setShowSettings(false)}/>}
      {showJobHistory&&<JobHistoryModal jobs={savedJobs} currentJobId={job.id} onOpen={(j)=>{setJob(j);setShowJobHistory(false);setActiveSection("job");}} onDelete={persistJobDelete} onClose={()=>setShowJobHistory(false)} onUpdateJob={persistJobUpdate}/>}
      {showAuthModal&&<CloudAuthModal onClose={()=>setShowAuthModal(false)} user={user}/>}
      {showTeam&&<TeamModal onManageInspectors={()=>{setShowTeam(false);setSettingsTab("inspectors");setShowSettings(true);}} profiles={profiles} currentUserId={user?.id} onClose={()=>setShowTeam(false)} onChanged={(p)=>{ if(Array.isArray(p)) setProfiles(p); else sbLoadProfiles().then(setProfiles); }}/>}
      {showContacts&&<ContactsModal contacts={contacts} setContacts={setContacts} onClose={()=>setShowContacts(false)} onPick={contactPickTarget?(c)=>{
        if(contactPickTarget==="orderedBy") updateJob({orderedBy:{name:c.name,company:c.company||"",phone:c.phone||"",email:c.email||""}});
        else if(contactPickTarget==="propOwner") updateJob({propOwner:{name:c.name,company:c.company||"",phone:c.phone||"",email:c.email||""}});
        else if(contactPickTarget==="partyOfInterest") updateJob({partyOfInterest:{name:c.name,company:c.company||"",email:c.email||""}});
        setContactPickTarget(null); setShowContacts(false);
      }:null}/>}
    </div>
  );
}

// ─── CLOUD AUTH MODAL ─────────────────────────────────────────────────────────
function CloudAuthModal({onClose, user}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('signin'); // 'signin'|'signup'|'reset'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email || (!password && mode !== 'reset')) { setError('Enter email and password.'); return; }
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { error: err } = await supabase.auth.signUp({ email, password });
        if (err) throw err;
        setInfo('Check your email for a confirmation link, then sign in.');
        setMode('signin');
      } else if (mode === 'reset') {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
        if (err) throw err;
        setInfo('Reset link sent — check your email.');
        setMode('signin');
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        onClose();
      }
    } catch(err) { setError(err.message || 'Something went wrong.'); }
    finally { setLoading(false); }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{...S.modal, maxWidth:380, background:'#1e293b'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontWeight:800,fontSize:16,color:'#22d3ee',display:'flex',alignItems:'center',gap:8}}>
            ☁ Cloud Sync
          </div>
          <button style={{...S.btnSmOutline,borderColor:'#334155',color:'#94a3b8'}} onClick={onClose}>✕</button>
        </div>
        {user ? (
          <div>
            <div style={{color:'#f1f5f9',fontSize:14,marginBottom:12}}>Signed in as <strong>{user.email}</strong></div>
            <div style={{color:'#94a3b8',fontSize:12,marginBottom:20,lineHeight:1.6}}>Your jobs, settings, and contacts sync automatically across all your devices.</div>
            <button style={{...S.btn('#dc2626'),width:'100%'}} onClick={()=>{ supabase.auth.signOut(); onClose(); }}>Sign Out</button>
          </div>
        ) : (
          <div>
            <div style={{color:'#94a3b8',fontSize:12,marginBottom:16,lineHeight:1.6}}>
              Sign in to sync jobs, findings, and settings across all your devices. Works offline — syncs when connected.
            </div>
            {error && <div style={{background:'#7f1d1d',color:'#fca5a5',borderRadius:8,padding:'8px 12px',fontSize:12,marginBottom:12}}>{error}</div>}
            {info  && <div style={{background:'#052e16',color:'#86efac',borderRadius:8,padding:'8px 12px',fontSize:12,marginBottom:12}}>{info}</div>}
            <form onSubmit={handleSubmit}>
              <label style={{...S.label,color:'#94a3b8'}}>Email</label>
              <input style={{...S.input,background:'#fff',color:'#0f172a',WebkitTextFillColor:'#0f172a',caretColor:'#0f172a',boxShadow:'inset 0 0 0 1000px #fff',borderColor:'#334155',marginBottom:12}} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" autoFocus/>
              {mode !== 'reset' && (
                <>
                  <label style={{...S.label,color:'#94a3b8'}}>Password</label>
                  <input style={{...S.input,background:'#fff',color:'#0f172a',WebkitTextFillColor:'#0f172a',caretColor:'#0f172a',boxShadow:'inset 0 0 0 1000px #fff',borderColor:'#334155',marginBottom:16}} type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"/>
                </>
              )}
              <button style={{...S.btn('#22d3ee'),color:'#0f172a',width:'100%',marginBottom:10}} type="submit" disabled={loading}>
                {loading ? 'Please wait…' : mode==='signup' ? 'Create Account' : mode==='reset' ? 'Send Reset Link' : 'Sign In'}
              </button>
            </form>
            <div style={{textAlign:'center',fontSize:12,color:'#64748b'}}>
              {mode==='signin' && <><span style={{cursor:'pointer',color:'#22d3ee'}} onClick={()=>setMode('signup')}>Create account</span> · <span style={{cursor:'pointer',color:'#22d3ee'}} onClick={()=>setMode('reset')}>Forgot password?</span></>}
              {mode==='signup' && <span style={{cursor:'pointer',color:'#22d3ee'}} onClick={()=>setMode('signin')}>Back to sign in</span>}
              {mode==='reset' && <span style={{cursor:'pointer',color:'#22d3ee'}} onClick={()=>setMode('signin')}>Back to sign in</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PHOTO UPLOAD COMPONENT ───────────────────────────────────────────────────
function PhotoUpload({ photos, onChange, label }) {
  const inputRef = useRef(null);
  const camRef = useRef(null);

  function addPhotos(files) {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = ev => {
        // Resize to max 900px wide before storing
        const img = new window.Image();
        img.onload = () => {
          const maxW = 900;
          const scale = img.width > maxW ? maxW / img.width : 1;
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
          onChange(prev => [...prev, { id: Date.now().toString() + Math.random(), data: dataUrl, caption: '' }]);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  return (
    <div style={{marginTop:12}}>
      <label style={{...S.label,marginBottom:6}}>{label || 'Photos'} <span style={{fontWeight:400,color:'#94a3b8'}}>(optional)</span></label>
      <input ref={inputRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={e=>{ addPhotos(e.target.files); e.target.value=''; }}/>
      <input ref={camRef} type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={e=>{ addPhotos(e.target.files); e.target.value=''; }}/>
      <div style={{display:'flex',gap:8,marginBottom:8}}>
        <button type="button" onClick={()=>camRef.current&&camRef.current.click()}
          style={{...S.btn('#1e40af'),flex:1,fontSize:13,padding:'9px 12px'}}>📷 Take Photo</button>
        <button type="button" onClick={()=>inputRef.current&&inputRef.current.click()}
          style={{...S.btnOutline,flex:1,fontSize:13,padding:'9px 12px'}}>🖼 Choose File</button>
      </div>
      <div
        style={{border:'2px dashed #cbd5e1',borderRadius:10,padding:photos.length?'10px':'28px',textAlign:'center',cursor:'pointer',background:'#f8fafc'}}
        onClick={()=>inputRef.current.click()}
        onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor='#22d3ee';}}
        onDragLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';}}
        onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor='#cbd5e1';addPhotos(e.dataTransfer.files);}}>
        {photos.length === 0 ? (
          <>
            <div style={{fontSize:28,marginBottom:6}}>📸</div>
            <div style={{fontSize:13,color:'#64748b'}}>Tap to add photos <span style={{fontSize:11,color:'#94a3b8'}}>(or drag & drop)</span></div>
          </>
        ) : (
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-start'}}>
            {photos.map((ph,i)=>(
              <div key={ph.id} style={{position:'relative',flexShrink:0}}>
                <img src={ph.data} alt="" style={{width:80,height:64,objectFit:'cover',borderRadius:6,border:'1px solid #e2e8f0',display:'block'}}/>
                <button
                  style={{position:'absolute',top:-6,right:-6,width:18,height:18,borderRadius:'50%',background:'#dc2626',color:'#fff',border:'none',cursor:'pointer',fontSize:10,padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}
                  onClick={ev=>{ev.stopPropagation();onChange(prev=>prev.filter(p=>p.id!==ph.id));}}>✕</button>
              </div>
            ))}
            <div style={{width:80,height:64,border:'2px dashed #cbd5e1',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',background:'#f1f5f9',fontSize:24,color:'#94a3b8'}}>+</div>
          </div>
        )}
      </div>
      {photos.length > 0 && (
        <div style={{fontSize:11,color:'#94a3b8',marginTop:4}}>{photos.length} photo{photos.length!==1?'s':''} — tap × to remove</div>
      )}
    </div>
  );
}

// ─── CLIENT SIGNATURE PANEL ───────────────────────────────────────────────────
function ClientSignaturePanel({ signature, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(!!signature);

  useEffect(()=>{
    if (!signature) return;
    const c = canvasRef.current;
    if (!c) return;
    const img = new window.Image();
    img.onload = () => { c.getContext('2d').drawImage(img, 0, 0); };
    img.src = signature;
  }, []);

  function getPos(e) {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const scX = c.width / r.width, scY = c.height / r.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return [(cx - r.left) * scX, (cy - r.top) * scY];
  }
  function down(e) { e.preventDefault(); const c = canvasRef.current; const [x,y]=getPos(e); c.getContext('2d').beginPath(); c.getContext('2d').moveTo(x,y); setDrawing(true); }
  function move(e) { e.preventDefault(); if(!drawing) return; const c = canvasRef.current; const ctx=c.getContext('2d'); const [x,y]=getPos(e); ctx.lineTo(x,y); ctx.strokeStyle='#1a1a2e'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke(); }
  function up(e) { e.preventDefault(); setDrawing(false); setHasSig(true); onChange(canvasRef.current.toDataURL('image/png')); }
  function clear() { const c = canvasRef.current; if(c) c.getContext('2d').clearRect(0,0,c.width,c.height); setHasSig(false); onChange(''); }

  return (
    <div style={{marginTop:8}}>
      <div style={{border:'1px solid #e2e8f0',borderRadius:8,overflow:'hidden',background:'#fafafa',touchAction:'none'}}>
        <canvas ref={canvasRef} width={500} height={100} style={{width:'100%',display:'block',cursor:'crosshair'}}
          onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
          onTouchStart={down} onTouchMove={move} onTouchEnd={up}/>
      </div>
      <div style={{display:'flex',gap:8,marginTop:6,alignItems:'center'}}>
        <span style={{fontSize:11,color:'#94a3b8',flex:1}}>{hasSig ? '✓ Signature captured' : 'Sign above'}</span>
        {hasSig && <button style={{...S.btnSmOutline,fontSize:11}} onClick={clear}>Clear</button>}
      </div>
    </div>
  );
}

// ─── JOB INFO ─────────────────────────────────────────────────────────────────
function JobSection({job,updateJob,settings,setSettings,onPickContact,firmMode,people,isInspector,onAssignInspector}){
  const [addressInput,setAddressInput]=useState(job.address||"");
  const inputRef=useRef(null);
  const acTimerRef=useRef(null);
  const [suggestions,setSuggestions]=useState([]);
  const [showSuggestions,setShowSuggestions]=useState(false);

  function fillTestData(){
    setSettings(s=>({...s,
      company:{...s.company,
        name:"Pacific Pest & Termite, Inc.",
        license:"PR 8432",
        address:"4891 Convoy St, Suite 201",
        city:"San Diego",
        state:"CA",
        zip:"92111",
        phone:"(619) 555-0182",
        email:"info@pacificpesttermite.com",
        website:"www.pacificpesttermite.com",
      },
      rates:{...DEFAULT_RATES,...(s.rates||{}),
        fumigation:0.35,
        localTreatment:295,
        preventiveTreatment:450,
      },
    }));
    updateJob({
      orderedBy:{name:"Jennifer Martinez",company:"Coldwell Banker Realty",phone:"(619) 555-4821",email:"j.martinez@cbr.com"},
      propOwner:{name:"David & Sarah Nguyen",company:"",phone:"(619) 555-9374",email:"david.nguyen@gmail.com"},
      partyOfInterest:{name:"Michael Torres",company:"Torres Escrow Services",email:"m.torres@torresescrow.com"},
      escrow:"ESC-2024-08821",
      leadSource:"Referral",
    });
  }
  function fetchSuggestions(value){
    const key = process.env.REACT_APP_MAPBOX_KEY || settings.mapboxKey || DEFAULT_MAPBOX_KEY;
    if(!key||value.length<3){ setSuggestions([]); return; }
    clearTimeout(acTimerRef.current);
    acTimerRef.current=setTimeout(async()=>{
      try{
        const feats = await mapboxSuggest(value, key);
        setSuggestions(feats);
        setShowSuggestions(feats.length>0);
      }catch{ setSuggestions([]); }
    },300);
  }
  function selectSuggestion(feat){
    const [lng,lat]=feat.geometry.coordinates;
    const ctx=feat.context||[];
    let city="",state="CA",zip="",county="";
    ctx.forEach(c=>{
      if(c.id.startsWith("place."))    city=c.text;
      if(c.id.startsWith("region."))   state=(c.short_code||c.text).replace("US-","");
      if(c.id.startsWith("postcode.")) zip=c.text;
      if(c.id.startsWith("district.")) county=c.text.replace(" County","");
    });
    const addr=(feat.place_name||"").split(",")[0].trim()||`${feat.address||""} ${feat.text||""}`.trim();
    setAddressInput(addr);
    setSuggestions([]); setShowSuggestions(false);
    const suggestedKeys=inferDescFromAddress(addr);
    const match=PROPERTY_DESCRIPTIONS.find(d=>d.key===suggestedKeys[0]);
    const updates={address:addr,city,state,zip,county,mapBg:null,mapCenter:{lat,lng},markers:[],autoLoad:true};
    if(match&&!job.propDesc) updates.propDesc=match.text;
    updateJob(updates);
    if(match) setSuggestedDesc(suggestedKeys);
  }

  const [showSpectora,setShowSpectora]=useState(false);
  const [suggestedDesc,setSuggestedDesc]=useState([]);
  const uf=(field)=>(e)=>updateJob({[field]:e.target.value});
  const uC=(role,field)=>(e)=>updateJob({[role]:{...job[role],[field]:e.target.value}});
  const toggleTag=(tag)=>{
    const tags=job.inspectionTags||[];
    updateJob({inspectionTags:tags.includes(tag)?tags.filter(t=>t!==tag):[...tags,tag]});
  };
  const toggleInspector=(id)=>{
    const ids=job.inspectorIds||[];
    updateJob({inspectorIds:ids.includes(id)?ids.filter(i=>i!==id):[...ids,id]});
  };

  return(
    <div className="ts-job-form">
      <details className="ts-job-helpers" open={window.innerWidth>=600}>
        <summary>Import &amp; setup tools</summary>
      {/* Spectora Import Banner */}
      <div style={{...S.card,background:"linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)",border:"none",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px"}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:"#22d3ee"}}>📥 Import from Spectora</div>
          <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>Export a job CSV from Spectora Reporting → drop it here to auto-fill</div>
        </div>
        <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:700}} onClick={()=>setShowSpectora(true)}>Import CSV</button>
      </div>

      {/* Dev shortcut — fill placeholder data for testing */}
      <div style={{...S.card,background:"#fffbeb",border:"1px dashed #f59e0b",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px"}}>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#92400e"}}>🧪 Dev: Fill Test Data</div>
          <div style={{fontSize:11,color:"#b45309",marginTop:2}}>Populate company, client, agent &amp; escrow fields with sample data for testing</div>
        </div>
        <button style={{...S.btn("#f59e0b"),color:"#78350f",fontWeight:700,fontSize:12}} onClick={fillTestData}>Fill Sample Data</button>
      </div>

      </details>

      <div className="ts-form-card" style={S.card}>
        <div className="ts-card-title" style={S.cardTitle}>Report Info</div>
        <div style={S.grid3} className="grid3-mobile">
          <div><label style={S.label}>Report #</label><input style={S.input} value={job.reportNum} onChange={uf("reportNum")}/></div>
          <div><label style={S.label}>Date of Inspection</label><input style={S.input} type="date" value={job.date} onChange={uf("date")}/></div>
          <div><label style={S.label}>Time</label><input style={{...S.input,minHeight:44}} type="time" value={job.time} onChange={uf("time")}/></div>
        </div>
        <div style={{...S.grid2,marginTop:12}} className="grid2-mobile">
          <div><label style={S.label}>Report Type</label>
            <select style={S.select} value={job.reportType} onChange={uf("reportType")}>
              {REPORT_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div><label style={S.label}>Escrow #</label><input style={S.input} value={job.escrow} onChange={uf("escrow")} placeholder="Optional"/></div>
        </div>
      </div>

      <div className="ts-form-card" style={S.card}>
        <div className="ts-card-title" style={S.cardTitle}>Property Inspected</div>
        <div style={{marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
            <label style={{...S.label,marginBottom:0}}>Street Address {process.env.REACT_APP_MAPBOX_KEY||settings.mapboxKey||DEFAULT_MAPBOX_KEY?"":"— autocomplete needs Mapbox API key"}</label>
            {(() => {
              const dest = [job.address, job.city, job.state, job.zip].filter(Boolean).join(", ");
              if (!dest.trim()) return null;
              const url = "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(dest);
              return (
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{...S.btnSm("#1e40af"),fontSize:11,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4,whiteSpace:"nowrap"}}
                  title={"Directions to "+dest}>
                  🧭 Map It
                </a>
              );
            })()}
          </div>
          <div style={{position:"relative"}}>
          <input ref={inputRef} style={S.input} value={addressInput}
            onChange={e=>{setAddressInput(e.target.value);updateJob({address:e.target.value,mapBg:null,mapCenter:null,markers:[]});fetchSuggestions(e.target.value);}}
            onBlur={e=>{setTimeout(()=>setShowSuggestions(false),150);if(e.target.value.trim()) updateJob({autoLoad:true});}}
            onFocus={()=>{if(suggestions.length>0)setShowSuggestions(true);}}
            placeholder="Street address..." autoComplete="off"/>
          {showSuggestions&&suggestions.length>0&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #cbd5e1",borderRadius:6,boxShadow:"0 4px 12px rgba(0,0,0,0.12)",zIndex:999,maxHeight:220,overflowY:"auto"}}>
              {suggestions.map((feat,i)=>(
                <div key={i} onMouseDown={()=>selectSuggestion(feat)}
                  style={{padding:"8px 12px",fontSize:13,cursor:"pointer",borderBottom:i<suggestions.length-1?"1px solid #f1f5f9":"none"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                  {feat.place_name}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
        <div style={S.grid3} className="grid3-mobile">
          <div><label style={S.label}>City</label><input style={S.input} value={job.city} onChange={uf("city")}/></div>
          <div><label style={S.label}>State</label><input style={S.input} value={job.state} onChange={uf("state")}/></div>
          <div><label style={S.label}>Zip</label><input style={S.input} value={job.zip} onChange={uf("zip")}/></div>
        </div>
        <div style={{...S.grid2,marginTop:12}} className="grid2-mobile">
          <div><label style={S.label}>County</label><input style={S.input} value={job.county} onChange={uf("county")}/></div>
          <div><label style={S.label}>Lead Source</label><input style={S.input} value={job.leadSource} onChange={uf("leadSource")} placeholder="Yelp, Referral..."/></div>
        </div>
      </div>

      <DescriptionCard job={job} updateJob={updateJob} settings={settings} setSettings={setSettings} suggestedKeys={suggestedDesc}/>

      <div className="ts-form-card" style={S.card}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div className="ts-card-title" style={S.cardTitle}>Ordered By</div>
          {onPickContact&&<button style={{...S.btnSm("#1e40af"),fontSize:11}} onClick={()=>onPickContact("orderedBy")}>📇 Pick from Contacts</button>}
        </div>
        <div style={S.grid2}>
          <div><label style={S.label}>Name</label><input style={S.input} value={job.orderedBy.name} onChange={uC("orderedBy","name")} placeholder="Full name" autoComplete="name"/></div>
          <div><label style={S.label}>Company</label><input style={S.input} value={job.orderedBy.company} onChange={uC("orderedBy","company")} placeholder="Brokerage / company" autoComplete="organization"/></div>
          <div><label style={S.label}>Phone</label><input style={S.input} value={job.orderedBy.phone} placeholder="(619) 555-0000" type="tel" autoComplete="tel" onChange={e=>updateJob({orderedBy:{...job.orderedBy,phone:formatPhone(e.target.value)}})}/></div>
          <div><label style={S.label}>Email</label><input style={S.input} value={job.orderedBy.email} onChange={uC("orderedBy","email")} type="email" autoComplete="email"/></div>
        </div>
      </div>

      <div className="ts-form-card" style={S.card}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1}}>Property Owner</div>
          <div style={{display:"flex",gap:6}}>
            {onPickContact&&<button style={{...S.btnSm("#1e40af"),fontSize:11}} onClick={()=>onPickContact("propOwner")}>📇 Pick</button>}
            <button style={{...S.btnSmOutline,fontSize:11,color:"#1e40af",borderColor:"#bfdbfe"}} onClick={()=>updateJob({propOwner:{...job.orderedBy}})}>↙ Same as Ordered By</button>
          </div>
        </div>
        <div style={S.grid2}>
          <div><label style={S.label}>Name</label><input style={S.input} value={job.propOwner.name} onChange={uC("propOwner","name")} placeholder="Full name" autoComplete="name"/></div>
          <div><label style={S.label}>Company</label><input style={S.input} value={job.propOwner.company} onChange={uC("propOwner","company")} placeholder="Optional"/></div>
          <div><label style={S.label}>Phone</label><input style={S.input} value={job.propOwner.phone} placeholder="(619) 555-0000" type="tel" autoComplete="tel" onChange={e=>updateJob({propOwner:{...job.propOwner,phone:formatPhone(e.target.value)}})}/></div>
          <div><label style={S.label}>Email</label><input style={S.input} value={job.propOwner.email} onChange={uC("propOwner","email")} placeholder="owner@email.com" type="email" autoComplete="email"/></div>
        </div>
      </div>

      <div className="ts-form-card" style={S.card}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div className="ts-card-title" style={S.cardTitle}>Party of Interest <span style={{fontSize:11,fontWeight:400,color:"#94a3b8"}}>(optional)</span></div>
          {onPickContact&&<button style={{...S.btnSm("#1e40af"),fontSize:11}} onClick={()=>onPickContact("partyOfInterest")}>📇 Pick</button>}
        </div>
        <div style={S.grid3}>
          <div><label style={S.label}>Name</label><input style={S.input} value={job.partyOfInterest.name} onChange={uC("partyOfInterest","name")}/></div>
          <div><label style={S.label}>Company</label><input style={S.input} value={job.partyOfInterest.company} onChange={uC("partyOfInterest","company")}/></div>
          <div><label style={S.label}>Email</label><input style={S.input} value={job.partyOfInterest.email} onChange={uC("partyOfInterest","email")}/></div>
        </div>
      </div>

      {firmMode && (
        <div className="ts-form-card" style={S.card}>
          <div className="ts-card-title" style={S.cardTitle}>Assigned inspector (login)</div>
          <div style={{fontSize:12,color:"#64748b",marginBottom:8}}>Controls who sees this job in their Inspector schedule. Only the assigned inspector (plus admins/operators) can open it.</div>
          <select aria-label="Assigned inspector" style={S.input} value={job.assignedTo||""} onChange={e=>(onAssignInspector?onAssignInspector(e.target.value||null):updateJob({assignedTo:e.target.value||null}))}>
            <option value="">— Unassigned —</option>
            {(people||[]).map(p=><option key={p.id} value={p.id}>{p.full_name||p.email||"(unnamed)"}</option>)}
            {(settings.inspectors||[]).filter(ins=>!(people||[]).some(p=>p.id===ins.userId)).map(ins=><option key={`roster:${ins.id}`} value={`unavailable:${ins.id}`} disabled>{ins.name} — {ins.userId?"Login unavailable":"No login linked"}</option>)}
          </select>
          <div style={{fontSize:12,color:"#64748b",marginTop:6}}>Inspector logins are managed in Settings → Inspectors. An active linked login is required for job access.</div>
        </div>
      )}

      <div className="ts-form-card" style={S.card}>
        <div className="ts-card-title" style={S.cardTitle}>Work status</div>
        <div style={{fontSize:12,color:"#64748b",marginBottom:10}}>{isInspector?"Set by the office — this is read-only for you.":"Track the remediation lifecycle — this is what inspectors see on their schedule."}</div>
        <WorkStatus value={job.workStatus} onChange={ws=>updateJob({workStatus:ws})} readOnly={isInspector} light/>
      </div>

      {settings.inspectors.length>0&&(
        <div className="ts-form-card" style={S.card}>
          <div className="ts-card-title" style={S.cardTitle}>Inspector(s) — report credentials</div>
          {job.autoInspectorId&&<div style={{fontSize:12,color:'#64748b',marginBottom:10}}>Primary inspector selected automatically from the assigned account, or your account when unassigned. Additional inspectors can be added below.</div>}
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {settings.inspectors.map(ins=>(
              <button type="button" key={ins.id} aria-pressed={(job.inspectorIds||[]).includes(ins.id)} disabled={ins.id===job.autoInspectorId} title={ins.id===job.autoInspectorId?'Selected automatically from the assigned account or your account':undefined} style={S.chip((job.inspectorIds||[]).includes(ins.id),"#1e40af")} onClick={()=>toggleInspector(ins.id)}>
                {ins.name} — {ins.license}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ts-form-card" style={S.card}>
        <div className="ts-card-title" style={S.cardTitle}>Inspection Tag Posted</div>
        <div style={{display:"flex",flexWrap:"wrap"}}>
          {INSPECTION_TAGS.map(tag=>(
            <button key={tag} style={S.chip((job.inspectionTags||[]).includes(tag),"#065f46")} onClick={()=>toggleTag(tag)}>{tag}</button>
          ))}
        </div>
        <div style={{marginTop:10}}>
          <label style={S.label}>Other / Custom Tags</label>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input style={{...S.input,flex:1}} value={job.customTags||""}
              autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false}
              onChange={e=>updateJob({customTags:e.target.value})}
              onBlur={e=>updateJob({customTags:e.target.value})}
              placeholder="e.g. Pool house, Workshop…"/>
            {(job.customTags||"").trim()&&(
              <span style={{fontSize:11,color:"#065f46",fontWeight:700,whiteSpace:"nowrap"}}>✓ Saved</span>
            )}
          </div>
        </div>
      </div>
      {/* ── Smart Next Step Banner ── */}
      {(()=>{
        const missing=[];
        if(!job.address) missing.push("property address");
        if(!job.orderedBy.name) missing.push("client name");
        if(!job.propDesc) missing.push("property description");
        if(!(job.inspectorIds||[]).length && settings.inspectors.length>0) missing.push("inspector");
        if(!(job.inspectionTags||[]).length && !(job.customTags||"").trim()) missing.push("inspection tags");
        const readyForDiagram = job.address && job.orderedBy.name;
        return (
          <div style={{
            ...S.card,
            background: readyForDiagram && !missing.length
              ? "linear-gradient(135deg,#065f46,#047857)"
              : "linear-gradient(135deg,#1e3a5f,#1e40af)",
            border:"none", padding:"16px 20px"
          }}>
            {readyForDiagram && !missing.length ? (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontWeight:800,fontSize:15,color:"#fff"}}>✓ Job Info Complete</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:2}}>Head to Diagram to load satellite &amp; trace the property</div>
                </div>
                <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:800,fontSize:14,padding:"10px 20px"}}
                  onClick={()=>window._tsSetTab&&window._tsSetTab("map")}>
                  → Go to Diagram
                </button>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <div style={{fontSize:20}}>📋</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13,color:"#fff",marginBottom:3}}>Still needed:</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.8)"}}>
                    {missing.map((m,i)=>(
                      <span key={m}>{i>0?" · ":""}<span style={{color:"#fde68a",fontWeight:600}}>{m}</span></span>
                    ))}
                  </div>
                </div>
                {readyForDiagram && (
                  <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:700,fontSize:13}}
                    onClick={()=>window._tsSetTab&&window._tsSetTab("map")}>
                    Skip to Diagram →
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })()}
      {showSpectora&&<SpectoraImportModal settings={settings} onImport={(imported)=>{updateJob(imported);setShowSpectora(false);}} onClose={()=>setShowSpectora(false)}/>}
    </div>
  );
}



// ─── DESCRIPTION CARD ────────────────────────────────────────────────────────
function DescriptionCard({job, updateJob, settings, setSettings, suggestedKeys}) {
  const [editMode, setEditMode] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newText,  setNewText]  = useState("");
  const [editingKey,  setEditingKey]  = useState(null);
  const [editLabel,   setEditLabel]   = useState("");
  const [editText,    setEditText]    = useState("");

  const hiddenKeys = settings.hiddenDescKeys || [];
  const descOrder  = settings.descOrder      || [];

  // Build ordered, filtered list
  const allRaw = [
    ...PROPERTY_DESCRIPTIONS,
    ...(settings.customDescriptions||[]).map(d=>({...d, custom:true})),
  ].filter(d => !hiddenKeys.includes(d.key));

  const allDescs = [...allRaw].sort((a,b)=>{
    const ai=descOrder.indexOf(a.key), bi=descOrder.indexOf(b.key);
    if(ai===-1&&bi===-1) return 0;
    if(ai===-1) return 1;
    if(bi===-1) return -1;
    return ai-bi;
  });

  const isCustomText = job.propDesc && !allDescs.find(d=>d.text===job.propDesc);

  function handleChipClick(d){
    if(editMode) return;
    if(job.propDesc===d.text) updateJob({propDesc:""}); else updateJob({propDesc:d.text});
  }

  function saveToCustom(){
    if(!job.propDesc||!job.propDesc.trim()) return;
    const label=job.propDesc.slice(0,40).trim()+(job.propDesc.length>40?"…":"");
    const key="custom_"+Date.now();
    setSettings(s=>({...s, customDescriptions:[...(s.customDescriptions||[]),{key,label,text:job.propDesc,custom:true}]}));
  }

  function deleteDesc(key){
    const isCustom=(settings.customDescriptions||[]).some(d=>d.key===key);
    if(isCustom){
      setSettings(s=>({...s,
        customDescriptions:(s.customDescriptions||[]).filter(d=>d.key!==key),
        descOrder:(s.descOrder||[]).filter(k=>k!==key),
      }));
    } else {
      setSettings(s=>({...s,
        hiddenDescKeys:[...(s.hiddenDescKeys||[]),key],
        descOrder:(s.descOrder||[]).filter(k=>k!==key),
      }));
    }
  }

  function moveDesc(key,dir){
    const keys=allDescs.map(d=>d.key);
    const idx=keys.indexOf(key);
    const newIdx=idx+dir;
    if(newIdx<0||newIdx>=keys.length) return;
    const next=[...keys];
    [next[idx],next[newIdx]]=[next[newIdx],next[idx]];
    setSettings(s=>({...s,descOrder:next}));
  }

  function addNew(){
    if(!newLabel.trim()||!newText.trim()) return;
    const key="custom_"+Date.now();
    setSettings(s=>({...s,
      customDescriptions:[...(s.customDescriptions||[]),{key,label:newLabel.trim(),text:newText.trim(),custom:true}],
      descOrder:[...(s.descOrder||[]),key],
    }));
    setNewLabel(""); setNewText("");
  }

  function saveEdit(key){
    if(!editLabel.trim()||!editText.trim()) return;
    setSettings(s=>({...s,
      customDescriptions:(s.customDescriptions||[]).map(d=>d.key===key?{...d,label:editLabel.trim(),text:editText.trim()}:d),
    }));
    setEditingKey(null);
  }

  const arrowBtn={background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:10,padding:"1px 3px",lineHeight:1,display:"block"};
  const iconBtn={background:"none",border:"none",cursor:"pointer",fontSize:11,padding:"0 3px",lineHeight:1};

  return (
    <div style={S.card}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={S.cardTitle}>General Description of Property</div>
        <button
          style={{...S.btnSm(editMode?"#1e40af":"#f1f5f9"),fontSize:11,color:editMode?"#fff":"#64748b",padding:"5px 12px"}}
          onClick={()=>{ setEditMode(m=>!m); setEditingKey(null); }}>
          {editMode ? "✓ Done" : "✏️ Edit"}
        </button>
      </div>

      {!editMode && (suggestedKeys||[]).length>0 && !job.propDesc && (
        <div style={{fontSize:11,color:"#0e7490",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",gap:6,background:"#ecfeff",border:"1px solid #a5f3fc",borderRadius:6,padding:"6px 10px"}}>
          <span>✦ Auto-suggested from address — tap the highlighted option to confirm</span>
        </div>
      )}

      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
        {allDescs.map((d,idx)=>{
          const isSuggested = !editMode && (suggestedKeys||[]).includes(d.key);
          const isActive    = !editMode && job.propDesc===d.text;

          // Inline edit form for this chip
          if(editMode && editingKey===d.key){
            return (
              <div key={d.key} style={{display:"flex",flexDirection:"column",gap:6,background:"#f0f9ff",border:"1px solid #38bdf8",borderRadius:10,padding:"10px 12px",minWidth:260}}>
                <input value={editLabel} onChange={e=>setEditLabel(e.target.value)}
                  placeholder="Short label" style={{...S.input,fontSize:12}}/>
                <textarea value={editText} onChange={e=>setEditText(e.target.value)}
                  placeholder="Full description text" style={{...S.textarea,fontSize:12,minHeight:44}}/>
                <div style={{display:"flex",gap:6}}>
                  <button style={{...S.btnSm("#1e40af"),fontSize:11}} onClick={()=>saveEdit(d.key)}>Save</button>
                  <button style={{...S.btnSm("#f1f5f9"),fontSize:11,color:"#64748b"}} onClick={()=>setEditingKey(null)}>Cancel</button>
                </div>
              </div>
            );
          }

          let chipStyle;
          if(isActive){
            chipStyle={display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",background:"linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",color:"#fff",border:"2px solid #1e3a8a",boxShadow:"0 0 0 3px rgba(30,64,175,0.35), 0 4px 12px rgba(30,64,175,0.5)",letterSpacing:"0.2px",transform:"scale(1.03)"};
          } else if(isSuggested){
            chipStyle={display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:10,fontSize:12,fontWeight:600,cursor:"pointer",background:"#f0f9ff",color:"#0369a1",border:"1px dashed #7dd3fc",letterSpacing:"0.2px"};
          } else if(editMode){
            chipStyle={display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:10,fontSize:12,fontWeight:500,cursor:"default",background:"#f1f5f9",color:"#475569",border:"1px solid #cbd5e1",letterSpacing:"0.2px"};
          } else {
            chipStyle={display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:10,fontSize:12,fontWeight:500,cursor:"pointer",background:"#f8fafc",color:"#64748b",border:"1px solid #e2e8f0",transition:"border-color 0.15s, color 0.15s",letterSpacing:"0.2px"};
          }

          return (
            <div key={d.key} style={{display:"inline-flex",alignItems:"center",gap:2}}>
              {editMode && (
                <div style={{display:"flex",flexDirection:"column"}}>
                  <button style={arrowBtn} onClick={()=>moveDesc(d.key,-1)} disabled={idx===0} title="Move left">▲</button>
                  <button style={arrowBtn} onClick={()=>moveDesc(d.key,1)} disabled={idx===allDescs.length-1} title="Move right">▼</button>
                </div>
              )}
              <button style={chipStyle} onClick={()=>handleChipClick(d)}>
                {isActive && <span style={{fontSize:11}}>✓</span>}
                {isSuggested && !isActive && <span style={{fontSize:10,color:"#0ea5e9"}}>✦</span>}
                {d.label}
                {isActive && <span style={{fontSize:10,opacity:0.7,marginLeft:2}}>✕</span>}
              </button>
              {editMode && (
                <>
                  {d.custom && (
                    <button style={{...iconBtn,color:"#3b82f6"}} title="Edit"
                      onClick={()=>{ setEditingKey(d.key); setEditLabel(d.label); setEditText(d.text); }}>✏️</button>
                  )}
                  <button style={{...iconBtn,color:"#ef4444"}} title="Remove"
                    onClick={()=>deleteDesc(d.key)}>✕</button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit mode: add new button form */}
      {editMode && (
        <div style={{borderTop:"1px solid #e2e8f0",paddingTop:14,marginTop:4}}>
          <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>+ Create New Button</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>CHIP LABEL</label>
              <input value={newLabel} onChange={e=>setNewLabel(e.target.value)}
                placeholder="e.g. 4-Story SFR · Furn" style={{...S.input,fontSize:12,width:200}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>FULL DESCRIPTION TEXT</label>
              <input value={newText} onChange={e=>setNewText(e.target.value)}
                placeholder="e.g. 4 Story – Single Family Residence…" style={{...S.input,fontSize:12,width:290}}/>
            </div>
            <button style={{...S.btnSm("#1e40af"),fontSize:12}} onClick={addNew}
              disabled={!newLabel.trim()||!newText.trim()}>+ Add</button>
          </div>
          {hiddenKeys.length>0 && (
            <button style={{marginTop:10,fontSize:11,color:"#6366f1",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}
              onClick={()=>setSettings(s=>({...s,hiddenDescKeys:[],descOrder:[]}))}>
              ↺ Restore {hiddenKeys.length} hidden default{hiddenKeys.length>1?"s":""}
            </button>
          )}
        </div>
      )}

      {/* Normal mode: textarea + save-to-catalog */}
      {!editMode && (
        <>
          <textarea
            style={{...S.textarea, minHeight:60}}
            value={job.propDesc}
            onChange={e=>updateJob({propDesc:e.target.value})}
            placeholder="Type a custom description or select one above..."/>
          {isCustomText && job.propDesc.trim().length>5 && (
            <button style={{...S.btnSm("#065f46"),marginTop:8,fontSize:12}} onClick={saveToCustom}>
              + Save to catalog
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── SPECTORA IMPORT MODAL ────────────────────────────────────────────────────
function SpectoraImportModal({settings, onImport, onClose}) {
  const [mode, setMode] = useState(null); // null | "image" | "csv"
  const [step, setStep] = useState("upload"); // upload | map | preview
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [fieldMap, setFieldMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const TS_FIELDS = [
    {key:"date",               label:"Inspection Date"},
    {key:"reportNum",          label:"Report Number"},
    {key:"address",            label:"Property Address"},
    {key:"city",               label:"City"},
    {key:"state",              label:"State"},
    {key:"zip",                label:"Zip"},
    {key:"county",             label:"County"},
    {key:"orderedBy.name",     label:"Ordered By — Name"},
    {key:"orderedBy.email",    label:"Ordered By — Email"},
    {key:"orderedBy.phone",    label:"Ordered By — Phone"},
    {key:"orderedBy.company",  label:"Ordered By — Company"},
    {key:"propOwner.name",     label:"Property Owner — Name"},
    {key:"propOwner.email",    label:"Property Owner — Email"},
    {key:"partyOfInterest.name",   label:"Agent — Name"},
    {key:"partyOfInterest.email",  label:"Agent — Email"},
    {key:"partyOfInterest.company",label:"Agent — Company/Brokerage"},
    {key:"leadSource",         label:"Lead Source"},
    {key:"escrow",             label:"Escrow Number"},
    {key:"INSPECTOR",          label:"Inspector (auto-match)"},
    {key:"SKIP",               label:"— Skip this column —"},
  ];

  // ── CSV path ──────────────────────────────────────────────────────────────
  function handleCSV(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = parseSpectoraCSV(ev.target.result);
        if (!parsed.length) { setError("No data rows found in CSV"); return; }
        const hdrs = Object.keys(parsed[0]);
        setRows(parsed); setHeaders(hdrs);
        const autoMap = {};
        hdrs.forEach(h => { autoMap[h] = SPECTORA_MAP[h.toLowerCase()] ? h.toLowerCase() : "SKIP"; });
        setFieldMap(autoMap);
        setError(""); setStep("map");
      } catch(err) { setError("Failed to parse CSV: " + err.message); }
    };
    reader.readAsText(file);
  }

  // ── Image path ────────────────────────────────────────────────────────────
  async function handleImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setExtracting(true); setError("");
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = ev => res(ev.target.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const mediaType = file.type || "image/jpeg";

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: [
              {
                type: "image",
                source: { type: "base64", media_type: mediaType, data: base64 }
              },
              {
                type: "text",
                text: `You are extracting inspection job data from a Spectora screenshot. 
Extract every visible field and return ONLY a valid JSON object with these exact keys (use empty string if not found):
{
  "date": "YYYY-MM-DD",
  "reportNum": "",
  "address": "",
  "city": "",
  "state": "",
  "zip": "",
  "county": "",
  "escrow": "",
  "leadSource": "",
  "orderedByName": "",
  "orderedByEmail": "",
  "orderedByPhone": "",
  "orderedByCompany": "",
  "propOwnerName": "",
  "propOwnerEmail": "",
  "agentName": "",
  "agentEmail": "",
  "agentCompany": "",
  "inspector": ""
}
Return ONLY the JSON, no explanation, no markdown.`
              }
            ]
          }]
        })
      });

      const data = await response.json();
      if (data.error) { setError("AI error: " + data.error.message); setExtracting(false); return; }

      const text = (data.content||[]).map(b=>b.text||"").join("").trim();
      const clean = text.replace(/^```json|^```|```$/gm,"").trim();
      const extracted = JSON.parse(clean);

      // Map extracted fields to job preview directly
      const j = {
        reportNum: extracted.reportNum||"",
        date: extracted.date||"",
        address: extracted.address||"",
        city: extracted.city||"",
        state: extracted.state||"CA",
        zip: extracted.zip||"",
        county: (extracted.county||"").replace(" County",""),
        escrow: extracted.escrow||"",
        leadSource: extracted.leadSource||"",
        orderedBy: {
          name: extracted.orderedByName||"",
          email: extracted.orderedByEmail||"",
          phone: extracted.orderedByPhone||"",
          company: extracted.orderedByCompany||"",
        },
        propOwner: {
          name: extracted.propOwnerName||"",
          email: extracted.propOwnerEmail||"",
          phone: "",
          company: "",
        },
        partyOfInterest: {
          name: extracted.agentName||"",
          email: extracted.agentEmail||"",
          company: extracted.agentCompany||"",
        },
        inspectorIds: [],
      };
      // Try to match inspector by name
      if (extracted.inspector) {
        const match = (settings.inspectors||[]).find(i=>i.name.toLowerCase()===extracted.inspector.toLowerCase());
        if (match) j.inspectorIds = [match.id];
      }
      setPreview(j);
      setStep("preview");
    } catch(err) {
      setError("Extraction failed: " + err.message);
    }
    setExtracting(false);
  }

  function buildPreview() {
    const row = rows[selectedRow];
    let j = {
      reportNum:"",date:"",address:"",city:"",state:"CA",zip:"",county:"",
      escrow:"",leadSource:"",
      orderedBy:{name:"",company:"",phone:"",email:""},
      propOwner:{name:"",company:"",phone:"",email:""},
      partyOfInterest:{name:"",company:"",email:""},
      inspectorIds:[],
    };
    headers.forEach(h => {
      const mapped = fieldMap[h];
      if (!mapped || mapped==="SKIP") return;
      const val = row[h] || "";
      if (!val) return;
      if (mapped.includes(".")) {
        const [parent, child] = mapped.split(".");
        j[parent] = {...(j[parent]||{}), [child]: val};
      } else if (mapped === "INSPECTOR") {
        const match = (settings.inspectors||[]).find(i=>i.name.toLowerCase()===val.toLowerCase());
        if (match) j.inspectorIds = [match.id];
      } else if (mapped === "date") {
        const d = new Date(val);
        if (!isNaN(d)) j.date = d.toISOString().split("T")[0];
      } else {
        j[mapped] = val;
      }
    });
    setPreview(j);
    setStep("preview");
  }

  const autoMappedCount = headers.filter(h=>fieldMap[h]&&fieldMap[h]!=="SKIP").length;

  return (
    <div style={S.overlay}>
      <div style={{...S.modalLg, maxWidth:700}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <h3 style={{margin:0,fontSize:18}}>Import from Spectora</h3>
            <div style={{fontSize:12,color:"#64748b",marginTop:2}}>
              {!mode && "Choose how to import"}
              {mode==="image" && step==="upload" && "Screenshot → AI extracts all fields automatically"}
              {mode==="csv" && step==="upload" && "Step 1 — Upload CSV export"}
              {step==="map" && `Step 2 — Map Fields (${autoMappedCount} of ${headers.length} auto-matched)`}
              {step==="preview" && "Review & Import"}
            </div>
          </div>
          <button style={S.btnSmOutline} onClick={onClose}>✕</button>
        </div>

        {error && <div style={{color:"#dc2626",fontSize:13,marginBottom:12,padding:"8px 12px",background:"#fef2f2",borderRadius:8}}>{error}</div>}

        {/* MODE PICKER */}
        {!mode && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginTop:8}}>
            {/* Image option */}
            <div style={{border:"2px solid #e2e8f0",borderRadius:14,padding:28,textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
              onClick={()=>setMode("image")}
              onMouseEnter={el=>{if(el.currentTarget)el.currentTarget.style.borderColor="#22d3ee";}}
              onMouseLeave={el=>{if(el.currentTarget)el.currentTarget.style.borderColor="#e2e8f0";}}>
              <div style={{fontSize:36,marginBottom:10}}>📸</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>Screenshot</div>
              <div style={{fontSize:12,color:"#64748b",lineHeight:1.6}}>Take a screenshot of any Spectora job page. AI reads it and fills everything in automatically.</div>
              <div style={{marginTop:12,display:"inline-block",background:"#f0fdf4",color:"#15803d",fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20}}>✦ Recommended</div>
            </div>
            {/* CSV option */}
            <div style={{border:"2px solid #e2e8f0",borderRadius:14,padding:28,textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
              onClick={()=>setMode("csv")}
              onMouseEnter={el=>{if(el.currentTarget)el.currentTarget.style.borderColor="#22d3ee";}}
              onMouseLeave={el=>{if(el.currentTarget)el.currentTarget.style.borderColor="#e2e8f0";}}>
              <div style={{fontSize:36,marginBottom:10}}>📊</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>CSV Export</div>
              <div style={{fontSize:12,color:"#64748b",lineHeight:1.6}}>Export a job from Spectora Reporting, upload the CSV file, and map the columns.</div>
            </div>
          </div>
        )}

        {/* IMAGE UPLOAD */}
        {mode==="image" && step==="upload" && (
          <div>
            <input ref={imgRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleImage}/>
            {extracting ? (
              <div style={{textAlign:"center",padding:48}}>
                <div style={{fontSize:36,marginBottom:16}}>🤖</div>
                <div style={{fontWeight:700,fontSize:16,color:"#0f172a",marginBottom:8}}>Reading your screenshot...</div>
                <div style={{fontSize:13,color:"#64748b"}}>Claude is extracting all job details from the image</div>
                <div style={{marginTop:20,height:4,background:"#f1f5f9",borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",width:"60%",background:"linear-gradient(90deg,#22d3ee,#3b82f6)",borderRadius:2,animation:"none"}}/>
                </div>
              </div>
            ) : (
              <div>
                <div
                  style={{background:"#f8fafc",border:"2px dashed #cbd5e1",borderRadius:12,padding:40,textAlign:"center",marginBottom:16,cursor:"pointer"}}
                  onClick={()=>imgRef.current.click()}>
                  <div style={{fontSize:40,marginBottom:12}}>📸</div>
                  <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>Upload Spectora Screenshot</div>
                  <div style={{fontSize:13,color:"#64748b",marginBottom:20,lineHeight:1.6}}>
                    Screenshot any Spectora job page — the job details screen, order confirmation, or summary. Claude will read the image and extract all the data.
                  </div>
                  <button style={S.btn("#0f172a")} onClick={e=>{e.stopPropagation();imgRef.current.click();}}>Choose Image</button>
                </div>
                <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:12,fontSize:12,color:"#3730a3",lineHeight:1.7}}>
                  <strong>Works with any Spectora screen:</strong> Job details · Order confirmation email · Client summary · Scheduling page
                </div>
                <button style={{...S.btnSmOutline,marginTop:14}} onClick={()=>setMode(null)}>← Back</button>
              </div>
            )}
          </div>
        )}

        {/* CSV UPLOAD */}
        {mode==="csv" && step==="upload" && (
          <div>
            <div style={{background:"#f8fafc",border:"2px dashed #cbd5e1",borderRadius:12,padding:36,textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:32,marginBottom:12}}>📂</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>Upload Spectora CSV Export</div>
              <div style={{fontSize:13,color:"#64748b",marginBottom:20}}>
                In Spectora: <strong>Reporting → Custom Export</strong> → select job → download CSV
              </div>
              <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={handleCSV}/>
              <button style={S.btn("#0f172a")} onClick={()=>fileRef.current.click()}>Choose CSV File</button>
            </div>
            <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:14,marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:12,color:"#1e40af",marginBottom:6}}>RECOMMENDED EXPORT COLUMNS</div>
              <div style={{fontSize:12,color:"#3730a3",lineHeight:1.8}}>
                Inspection Date · Report Number · Inspection Address · City · State · Zip · County · Client Name · Client Email · Client Phone · Agent Name · Agent Email · Agent Brokerage · Inspector · Referral Source
              </div>
            </div>
            <button style={S.btnSmOutline} onClick={()=>setMode(null)}>← Back</button>
          </div>
        )}

        {/* FIELD MAPPER (CSV only) */}
        {step==="map" && (
          <div>
            {rows.length > 1 && (
              <div style={{marginBottom:14}}>
                <label style={S.label}>Which row to import? (CSV has {rows.length} jobs)</label>
                <select style={{...S.select,maxWidth:400}} value={selectedRow} onChange={e=>setSelectedRow(Number(e.target.value))}>
                  {rows.map((r,i)=>(
                    <option key={i} value={i}>
                      Row {i+1}: {r[headers.find(h=>h.toLowerCase().includes("address"))||headers[0]]||"(no address)"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{maxHeight:320,overflowY:"auto",border:"1px solid #e2e8f0",borderRadius:8}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"#f1f5f9",position:"sticky",top:0}}>
                    <th style={{padding:"8px 12px",textAlign:"left",fontWeight:700,color:"#475569"}}>Spectora Column</th>
                    <th style={{padding:"8px 12px",textAlign:"left",fontWeight:700,color:"#475569"}}>Sample Value</th>
                    <th style={{padding:"8px 12px",textAlign:"left",fontWeight:700,color:"#475569"}}>Maps To</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((h,i)=>{
                    const isAuto = SPECTORA_MAP[h.toLowerCase()];
                    const val = rows[selectedRow]?.[h]||"";
                    return (
                      <tr key={h} style={{background:i%2===0?"#fff":"#f8fafc"}}>
                        <td style={{padding:"6px 12px",fontWeight:600}}>{h}</td>
                        <td style={{padding:"6px 12px",color:"#64748b",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val||"—"}</td>
                        <td style={{padding:"6px 12px"}}>
                          <select
                            style={{...S.select,fontSize:12,padding:"4px 8px",background:isAuto?"#f0fdf4":"#f8fafc",borderColor:isAuto?"#86efac":"#e2e8f0"}}
                            value={fieldMap[h]||"SKIP"}
                            onChange={e=>setFieldMap(m=>({...m,[h]:e.target.value}))}>
                            {TS_FIELDS.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{display:"flex",gap:10,marginTop:16}}>
              <button style={S.btn("#0f172a")} onClick={buildPreview}>Preview Import →</button>
              <button style={S.btnOutline} onClick={()=>setStep("upload")}>← Back</button>
            </div>
          </div>
        )}

        {/* PREVIEW */}
        {step==="preview" && preview && (
          <div>
            <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:14,marginBottom:16}}>
              <div style={{fontWeight:700,color:"#15803d",marginBottom:4}}>✓ Ready to import</div>
              <div style={{fontSize:12,color:"#166534"}}>Review the data below before importing. This will overwrite Job Info fields.</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13}}>
              {[
                ["Report #",        preview.reportNum],
                ["Date",            preview.date],
                ["Address",         preview.address],
                ["City / State / Zip", [preview.city,preview.state,preview.zip].filter(Boolean).join(", ")],
                ["County",          preview.county],
                ["Lead Source",     preview.leadSource],
                ["Ordered By",      preview.orderedBy?.name],
                ["Ordered By Email",preview.orderedBy?.email],
                ["Ordered By Phone",preview.orderedBy?.phone],
                ["Agent",           preview.partyOfInterest?.name],
                ["Agent Company",   preview.partyOfInterest?.company],
                ["Inspector",       preview.inspectorIds?.length>0?(settings.inspectors.find(i=>i.id===preview.inspectorIds[0])?.name||"matched"):"none found"],
              ].filter(r=>r[1]).map(([label,val])=>(
                <div key={label} style={{background:"#f8fafc",borderRadius:6,padding:"8px 10px"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#64748b"}}>{label}</div>
                  <div style={{fontWeight:600,color:"#1a1a2e",marginTop:2}}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button style={S.btn("#22d3ee")} onClick={()=>onImport(preview)}>✓ Import This Job</button>
              <button style={S.btnOutline} onClick={()=>{ if(mode==="image"){setStep("upload");}else{setStep("map");} }}>← Back</button>
              <button style={{...S.btnSmOutline,marginLeft:"auto"}} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function formatPhone(val) {
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3)  return digits.length ? `(${digits}` : "";
  if (digits.length <= 6)  return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

// Calculator glyph — body, screen, and a 3×2 grid of buttons so it reads
// unmistakably as a calculator regardless of emoji font support.
function CalculatorIcon({size=15}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
      <rect x="4" y="2" width="16" height="20" rx="2.5"/>
      <rect x="7.5" y="5.5" width="9" height="3.5" rx="0.6" fill="currentColor" stroke="none"/>
      <circle cx="8.5"  cy="13" r="1" fill="currentColor" stroke="none"/>
      <circle cx="12"   cy="13" r="1" fill="currentColor" stroke="none"/>
      <circle cx="15.5" cy="13" r="1" fill="currentColor" stroke="none"/>
      <circle cx="8.5"  cy="17" r="1" fill="currentColor" stroke="none"/>
      <circle cx="12"   cy="17" r="1" fill="currentColor" stroke="none"/>
      <circle cx="15.5" cy="17" r="1" fill="currentColor" stroke="none"/>
    </svg>
  );
}

// ─── MAP / DIAGRAM ────────────────────────────────────────────────────────────
function DiagramToolIcon({tool}){
  const paths={trace:<><path d="m4 16-1 5 5-1L20 8l-4-4Z"/><path d="m13 7 4 4"/></>,mark:<><path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="10" r="2"/></>,measure:<><path d="m3 16 13-13 5 5L8 21Z"/><path d="m7 12 2 2m2-6 2 2m2-6 2 2"/></>,edit:<><path d="m12 3 8 5v8l-8 5-8-5V8Z"/><path d="m9 14 6-6 2 2-6 6H9Z"/></>,erase:<><path d="m3 14 9-11 9 8-8 10H9Z"/><path d="m7 9 10 9M9 21h12"/></>,text:<><path d="M4 5h16M12 5v15M8 20h8M4 5v3m16-3v3"/></>};
  return <svg className="ts-tool-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[tool]}</svg>;
}
function MapSection({job,updateJob:_updateJob,settings,setSettings,readOnly=false}){
  const canvasRef    = useRef(null);
  const liveCanvasRef= useRef(null);
  const [mode,       setMode]        = useState(() => window.innerWidth < 520 ? null : "trace");
  const [fullscreen, setFullscreen]  = useState(false);
  // Per-job canvas height: existing jobs (no canvasH) stay 760 so their saved
  // traces remain aligned; new jobs use the taller portrait area that fills the screen.
  const CH  = job.canvasH || CANVAS_H;
  const MIH = Math.round(CH * MAP_IMG_W / CANVAS_W); // satellite tile height matching CH
  const [tracePts,   setTracePts]    = useState([]);
  const [traceStyle, setTraceStyle]  = useState("solid");
  const mousePosRef = useRef(null);   // live cursor for preview line (ref avoids re-render on every mouse move)
  const [selectedCat,setSelectedCat] = useState(2);
  const [markerLabel, setMarkerLabel] = useState("2A"); // e.g. "2A", "2B"
  const [markerSize, setMarkerSize]  = useState(() => window.innerWidth < 520 ? 11 : 8);
  const [pendingPhotoMarkerId, setPendingPhotoMarkerId] = useState(null);
  const [photoTargetId,setPhotoTargetId]=useState(null);
  useEffect(()=>setPhotoTargetId(null),[pendingPhotoMarkerId]);
  const [mobSetupOpen, setMobSetupOpen] = useState(false);
  const [loadingMap, setLoadingMap]  = useState(false);
  const [mapError,   setMapError]    = useState("");
  const [mapInfo,    setMapInfo]     = useState("");
  const [pinchZooming, setPinchZooming] = useState(false);
  const [viewScale,    setViewScale]    = useState(1);   // persistent CSS zoom (1 = no extra zoom)
  const [showGrid,     setShowGrid]     = useState(false);
  const pinchRef   = useRef(null);
  const mapWrapRef = useRef(null);   // direct DOM ref for CSS-transform pinch feedback
  const lastTapRef = useRef(0);      // timestamp of last touchend tap (double-tap detection)
  const isTouchRef = useRef(false);  // true once any touch event fires
  const zoomTimerRef = useRef(null);
  // Edit mode state
  const [selectedShapeIdx, setSelectedShapeIdx] = useState(null); // index in job.drawings
  const [dragPtIdx,        setDragPtIdx]         = useState(null); // vertex being dragged
  const [isDragging,       setIsDragging]         = useState(false);
  const [isDraggingShape,  setIsDraggingShape]    = useState(false);
  const dragShapeRef = useRef(null); // { startPos, origPts } for whole-shape drag
  const [isRotatingShape,  setIsRotatingShape]    = useState(false);
  const rotateShapeRef = useRef(null); // { centroid, startAngle, origPts }
  const dragPtRef     = useRef(null);  // vertex index being dragged (sync companion to dragPtIdx state)
  const panDragRef    = useRef(null);  // { startX, startY, dx, dy } during active pan drag
  const [autoTracing,      setAutoTracing]        = useState(false);
  const [labelTargetIdx,   setLabelTargetIdx]     = useState(null); // shape selected in text mode
  const [pendingAreaLabel, setPendingAreaLabel]   = useState(""); // label queued for map placement
  const [areaLabelSize,    setAreaLabelSize]      = useState(13); // font size for next text placement
  const [customLabelInput, setCustomLabelInput]  = useState(null); // null = hidden, "" = editing
  const [hoveredTool,      setHoveredTool]        = useState(null);
  const [nodePopup,        setNodePopup]          = useState(null); // { x, y, shapeIdx, ptIdx } in canvas px
  const CLOSE_DIST = 28;  // px — tap-to-close radius (larger = easier on mobile)
  const SNAP_DIST  = 10; // px — snap-to-existing-vertex radius in trace mode
  const [snap90, setSnap90] = useState(false);
  const [measureStart, setMeasureStart] = useState(null); // [x,y] of first dropped pin, or null
  const [volCalcOpen, setVolCalcOpen] = useState(false);  // standalone volume calculator panel

  // ── Undo / Redo history ────────────────────────────────────────────────────
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [undoLen, setUndoLen] = useState(0);
  const [redoLen, setRedoLen] = useState(0);

  // Wraps _updateJob: auto-saves a snapshot before any drawings/markers change.
  function updateJob(updates) {
    if (typeof updates !== 'function' && ('drawings' in updates || 'markers' in updates)) {
      undoStack.current.push({
        drawings: JSON.parse(JSON.stringify(job.drawings || [])),
        markers:  JSON.parse(JSON.stringify(job.markers  || [])),
      });
      if (undoStack.current.length > 50) undoStack.current.shift();
      redoStack.current = [];
      setUndoLen(undoStack.current.length);
      setRedoLen(0);
    }
    _updateJob(updates);
  }

  function handleUndo() {
    if (!undoStack.current.length) return;
    redoStack.current.push({
      drawings: JSON.parse(JSON.stringify(job.drawings || [])),
      markers:  JSON.parse(JSON.stringify(job.markers  || [])),
    });
    const prev = undoStack.current.pop();
    setUndoLen(undoStack.current.length);
    setRedoLen(redoStack.current.length);
    _updateJob({ drawings: prev.drawings, markers: prev.markers });
  }

  function handleRedo() {
    if (!redoStack.current.length) return;
    undoStack.current.push({
      drawings: JSON.parse(JSON.stringify(job.drawings || [])),
      markers:  JSON.parse(JSON.stringify(job.markers  || [])),
    });
    const next = redoStack.current.pop();
    setUndoLen(undoStack.current.length);
    setRedoLen(redoStack.current.length);
    _updateJob({ drawings: next.drawings, markers: next.markers });
  }
  // ──────────────────────────────────────────────────────────────────────────

  // Returns the nearest existing vertex [x,y] within threshold, or null
  function snapToExisting(pos, excludeOwnFirst){
    const drawings = job.drawings || [];
    const threshold = isTouchRef.current ? SNAP_DIST * 2 : SNAP_DIST;
    let best = null, bestD = threshold;
    drawings.forEach(d=>{
      d.pts.forEach((p,pi)=>{
        if(excludeOwnFirst && pi===0) return;
        const dist = Math.hypot(p[0]-pos[0], p[1]-pos[1]);
        if(dist < bestD){ bestD=dist; best=p; }
      });
    });
    return best;
  }

  // Returns nearest point on segment [a,b] to point p
  function nearestPointOnSegment(p, a, b){
    const dx=b[0]-a[0], dy=b[1]-a[1];
    const len2=dx*dx+dy*dy;
    if(len2===0) return a;
    const t=Math.max(0,Math.min(1,((p[0]-a[0])*dx+(p[1]-a[1])*dy)/len2));
    return [a[0]+t*dx, a[1]+t*dy];
  }

  // Snaps pos to nearest 90° angle from the last traced point when snap90 is on
  function applySnap90(pos) {
    if (!snap90 || tracePts.length === 0) return pos;
    const last = tracePts[tracePts.length - 1];
    const dx = pos[0] - last[0];
    const dy = pos[1] - last[1];
    const angle = Math.atan2(dy, dx);
    const snappedAngle = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
    const dist = Math.hypot(dx, dy);
    return [last[0] + dist * Math.cos(snappedAngle), last[1] + dist * Math.sin(snappedAngle)];
  }

  // Returns straight-line distance in feet between two canvas pixel points
  function edgeLengthFt(p1, p2) {
    if (!job.mapCenter || !job.mapZoom) return 0;
    const [lat1, lng1] = canvasPxToLatLng(p1[0], p1[1], job.mapCenter.lat, job.mapCenter.lng, job.mapZoom, CANVAS_W, CH, job.mapRotation||0, job.mapPixelScale||MAP_PIXEL_SCALE);
    const [lat2, lng2] = canvasPxToLatLng(p2[0], p2[1], job.mapCenter.lat, job.mapCenter.lng, job.mapZoom, CANVAS_W, CH, job.mapRotation||0, job.mapPixelScale||MAP_PIXEL_SCALE);
    const R = 6371000;
    const phi = (lat1 + lat2) / 2 * Math.PI / 180;
    const dx = (lng2 - lng1) * Math.PI / 180 * R * Math.cos(phi);
    const dy = (lat2 - lat1) * Math.PI / 180 * R;
    return Math.sqrt(dx*dx + dy*dy) * 3.28084;
  }

  // Returns {pos:[x,y], shapeIdx, edgeAfterIdx} for nearest edge within threshold, or null
  function snapToEdge(pos){
    const drawings = job.drawings || [];
    const threshold = isTouchRef.current ? SNAP_DIST*2 : SNAP_DIST;
    let best=null, bestD=threshold, bestInfo=null;
    drawings.forEach((d,di)=>{
      const n=d.pts.length;
      const loopN=d.closed?n:n-1;
      for(let i=0;i<loopN;i++){
        const a=d.pts[i], b=d.pts[(i+1)%n];
        const np=nearestPointOnSegment(pos,a,b);
        const dist=Math.hypot(np[0]-pos[0],np[1]-pos[1]);
        if(dist<bestD){ bestD=dist; best=np; bestInfo={shapeIdx:di,edgeAfterIdx:i}; }
      }
    });
    return best ? {pos:best,...bestInfo} : null;
  }

  async function autoTraceBuilding(centerOverride) {
    const mapCenter = centerOverride || job.mapCenter;
    if (!mapCenter) return;
    const { lat, lng } = mapCenter;

    // If the user placed a marker pin on the map, use its lat/lng as the
    // building-selection reference — it is a more authoritative location hint
    // than the geocoded address, which often lands between buildings or on the street.
    const markers = job.markers || [];
    const pinMarker = markers.find(m => m.type === "pin") || (markers.length > 0 ? markers[0] : null);
    let selLat = lat, selLng = lng;
    if (pinMarker && job.mapZoom) {
      const ll = canvasPxToLatLng(pinMarker.x, pinMarker.y, lat, lng, job.mapZoom, CANVAS_W, CH, job.mapRotation||0, job.mapPixelScale||MAP_PIXEL_SCALE);
      selLat = ll[0]; selLng = ll[1];
    }
    setAutoTracing(true);
    setMapError(""); setMapInfo("");
    try {
      // Kick off Solar API immediately in parallel — used for authoritative sqft
      // and as a fallback building bounding box if all OSM sources fail.
      const gKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || settings.apiKey;
      const solarPromise = gKey ? fetch(
        `https://solar.googleapis.com/v1/buildingInsights:findClosest?location.latitude=${lat}&location.longitude=${lng}&requiredQuality=LOW&key=${gKey}`,
        { signal: AbortSignal.timeout(8000) }
      ).then(r => r.ok ? r.json() : null).catch(() => null) : Promise.resolve(null);

      // Route Overpass queries through the server-side proxy (/api/overpass)
      // to avoid CORS blocks and mirror restrictions in the browser.
      async function fetchOverpass(query) {
        const r = await fetch('/api/overpass', {
          method: 'POST',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify({ query }),
        });
        if (!r.ok) throw new Error('overpass proxy error');
        return r.json();
      }
      // Fire is_in + radius + MS Buildings in parallel for best quality.
      // Use selLat/selLng (marker position if placed, else geocoded center) so
      // building discovery is centered on the correct building, not the geocoded address.
      const isInQ = `[out:json][timeout:25];is_in(${selLat},${selLng})->.a;way(pivot.a)["building"];out geom;`;
      const radii = [150, 250, 400];
      const [isInResult, ...radiiResults] = await Promise.all([
        fetchOverpass(isInQ).catch(() => null),
        ...radii.map(radius => {
          const q = `[out:json][timeout:25];way["building"](around:${radius},${selLat},${selLng});out geom;`;
          return fetchOverpass(q).catch(() => null);
        }),
      ]);
      // Fire MS Buildings in parallel with OSM so both are ready at the same time
      const msPromise = fetch(`/api/buildings?lat=${selLat}&lng=${selLng}`, {
        signal: AbortSignal.timeout(12000),
      }).then(r => r.ok ? r.json() : null).catch(() => null);

      let ways = [];
      // Prefer is_in (exact containment) over radius results
      if (isInResult) {
        const found = (isInResult.elements || []).filter(el => el.type === "way" && el.geometry && el.geometry.length >= 3);
        if (found.length > 0) ways = found;
      }
      if (ways.length === 0) {
        for (const data of radiiResults) {
          if (!data) continue;
          const found = (data.elements || []).filter(el => el.type === "way" && el.geometry && el.geometry.length >= 3);
          if (found.length > 0) { ways = found; break; }
        }
      }

      // ── Microsoft Global ML Building Footprints (via /api/buildings) ────────────
      // Used when OSM has no data, OR when OSM only returned a coarse rectangle
      // (≤8 vertices). MS Buildings has 20-40 ML-derived vertices for most US
      // buildings. Well-mapped OSM buildings (>8 verts) are kept as-is.
      {
        const msData = await msPromise;
        if (msData?.features?.length > 0) {
          const msWays = msData.features.map(ring => ({
            geometry: ring.map(([ln, la]) => ({ lat: la, lon: ln })),
            tags: { _msBuildings: true },
          }));
          // Pick the MS building nearest the selection reference (marker pin if placed,
          // otherwise geocoded center). Using vertex count previously caused the largest
          // nearby building to win; using geocoded lat caused wrong-building picks when
          // the marker is on a different building than the geocoded address.
          const msCentroid = g => [g.reduce((s,p)=>s+p.lat,0)/g.length, g.reduce((s,p)=>s+p.lon,0)/g.length];
          const msDist = w => { const [cLa,cLn]=msCentroid(w.geometry); return Math.hypot((cLa-selLat)*111320,(cLn-selLng)*111320*Math.cos(selLat*Math.PI/180)); };
          const msBest = msWays.reduce((b,w) => msDist(w) < msDist(b) ? w : b, msWays[0]);
          const bboxA = g => { const lts=g.map(p=>p.lat),lns=g.map(p=>p.lon); return (Math.max(...lts)-Math.min(...lts))*(Math.max(...lns)-Math.min(...lns)); };
          if (ways.length === 0) {
            ways = [msBest];
          } else {
            const osmBest = ways.reduce((b,w)=>w.geometry.length>b.geometry.length?w:b, ways[0]);
            const areaRatio = bboxA(msBest.geometry) / Math.max(bboxA(osmBest.geometry), 1e-12);
            // Replace OSM with nearest MS building only when OSM has a coarse rectangle (≤8 verts)
            // and areas are within 3× — keeps well-mapped OSM polygons (>8 verts).
            // Use [msBest] (single building) not msWays to avoid contaminating the selection pool.
            if (osmBest.geometry.length <= 8 && msBest.geometry.length > osmBest.geometry.length && areaRatio > 0.3 && areaRatio < 3) {
              ways = [msBest];
            }
          }
        }
      }
      // ── Fallback B: Mapbox Tilequery — building polygons via Mapbox Streets ──────
      // Queries Mapbox's pre-rendered vector tiles for building polygons at the pin.
      // Uses the always-available Mapbox key (hardcoded default). Catches buildings
      // that are in Mapbox's dataset but not returned by Overpass.
      if (ways.length === 0) {
        try {
          const mbKey = process.env.REACT_APP_MAPBOX_KEY || settings.mapboxKey || DEFAULT_MAPBOX_KEY;
          const tqUrl = `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery/${selLng},${selLat}.json?layers=building&radius=100&limit=10&access_token=${mbKey}`;
          const tqData = await fetch(tqUrl, { signal: AbortSignal.timeout(8000) })
            .then(r => r.ok ? r.json() : null).catch(() => null);
          const bldFeats = (tqData?.features || []).filter(f => f.geometry?.type === 'Polygon');
          if (bldFeats.length > 0) {
            ways = bldFeats.map(f => ({
              geometry: f.geometry.coordinates[0].map(([ln, la]) => ({ lat: la, lon: ln })),
              tags: {}
            }));
          }
        } catch { /* fall through */ }
      }
      // Fallback C: homes mapped with address tags but no building= tag
      // (very common in gated/master-planned communities like Palm Desert)
      if (ways.length === 0) {
        try {
          const addrQ = `[out:json][timeout:15];way["addr:housenumber"](around:40,${selLat},${selLng});out geom;`;
          const addrResult = await fetchOverpass(addrQ).catch(() => null);
          if (addrResult) {
            const found = (addrResult.elements || []).filter(el => el.type === "way" && el.geometry && el.geometry.length >= 3);
            if (found.length > 0) ways = found;
          }
        } catch { /* continue */ }
      }
      // Fallback D: any closed way containing the pin that isn't a road/water/
      // natural feature — catches untagged building outlines, filtered to
      // plausible building size (bounding box < 0.004° ≈ 400 m in any dimension)
      if (ways.length === 0) {
        try {
          const anyQ = `[out:json][timeout:15];is_in(${selLat},${selLng})->.a;way(pivot.a)[!"highway"][!"railway"][!"waterway"][!"natural"][!"boundary"][!"landuse"];out geom;`;
          const anyResult = await fetchOverpass(anyQ).catch(() => null);
          if (anyResult) {
            const found = (anyResult.elements || []).filter(el => {
              if (el.type !== "way" || !el.geometry || el.geometry.length < 3) return false;
              const lats = el.geometry.map(g => g.lat), lons = el.geometry.map(g => g.lon);
              const spanLat = Math.max(...lats) - Math.min(...lats);
              const spanLon = Math.max(...lons) - Math.min(...lons);
              return spanLat < 0.004 && spanLon < 0.004; // exclude large area polygons
            });
            if (found.length > 0) ways = found;
          }
        } catch { /* continue */ }
      }
      // Fallback E (Nominatim): address search — covers addresses not yet in OSM
      // building index but present as named places with polygon geometry
      if (ways.length === 0) {
        try {
          const addrQ = [job.address, job.city, job.state].filter(Boolean).join(', ');
          const nomUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addrQ)}&format=json&polygon_geojson=1&limit=5`;
          const nomData = await fetch(nomUrl, { headers: { 'Accept-Language': 'en' } })
            .then(r => r.ok ? r.json() : []).catch(() => []);
          const polyHit = (nomData || []).find(r =>
            r.geojson && (r.geojson.type === 'Polygon' || r.geojson.type === 'MultiPolygon')
          );
          if (polyHit) {
            const ring = polyHit.geojson.type === 'Polygon'
              ? polyHit.geojson.coordinates[0]
              : polyHit.geojson.coordinates[0][0];
            ways = [{ geometry: ring.map(([ln, la]) => ({ lat: la, lon: ln })), tags: polyHit.extratags || {} }];
          }
        } catch { /* Nominatim unavailable — fall through to no-building error */ }
      }
      // ── Fallback E: Google Solar API — building-specific bounding box ─────────
      // Solar API analyzes individual buildings (for solar potential) and returns a
      // building-level bounding box — far more precise than geocoding bounds which
      // represent the parcel or street block, not the structure itself.
      // Not marked _estimated because this is ML-detected building data, not a guess.
      if (ways.length === 0) {
        try {
          const solarData = await solarPromise;
          if (solarData?.boundingBox) {
            const { ne, sw } = solarData.boundingBox;
            ways = [{ geometry: [
              { lat: ne.latitude, lon: ne.longitude },
              { lat: ne.latitude, lon: sw.longitude },
              { lat: sw.latitude, lon: sw.longitude },
              { lat: sw.latitude, lon: ne.longitude },
            ], tags: {} }];
          }
        } catch { /* fall through to Google geocoding bounds */ }
      }
      // ── Fallback F: Google geocoding bounds rectangle ─────────────────────────
      // If all OSM sources failed, create an estimated rectangle from the bounds
      // that Google returned when the map was geocoded. This covers every address
      // Google can geocode (essentially all US addresses), giving the user a
      // starting shape to refine instead of a blank error.
      if (ways.length === 0) {
        const allFailed = !isInResult && radiiResults.every(r => r === null);
        // allFailed: Overpass unavailable — fall through to estimated rectangle below

        // Place a ~15 m × 25 m rectangle at the geocoded centre as a starting point.
        // (Geocoding bounds represent the parcel/address viewport, not the building
        //  footprint, so they are not used here — they produce misleadingly large shapes.)
        const dLat = 0.000135;  // ≈ 15 m
        const dLng = 0.000250;  // ≈ 25 m at mid-US latitudes
        const corners = [
          { lat: lat + dLat, lon: lng + dLng },
          { lat: lat + dLat, lon: lng - dLng },
          { lat: lat - dLat, lon: lng - dLng },
          { lat: lat - dLat, lon: lng + dLng },
        ];
        ways = [{ geometry: corners, tags: {}, _estimated: true }];
      }
      // ── Collect ALL structures on this property ────────────────────────────────
      function ptInPolyLL(la, ln, geom) {
        let inside = false;
        for (let i = 0, j = geom.length - 1; i < geom.length; j = i++) {
          const xi = geom[i].lon, yi = geom[i].lat;
          const xj = geom[j].lon, yj = geom[j].lat;
          if (((yi > la) !== (yj > la)) && (ln < (xj - xi) * (la - yi) / (yj - yi) + xi))
            inside = !inside;
        }
        return inside;
      }
      function centroid(geom) {
        return [geom.reduce((s, g) => s + g.lat, 0) / geom.length,
                geom.reduce((s, g) => s + g.lon, 0) / geom.length];
      }
      function distM(la1, ln1, la2, ln2) {
        return Math.hypot((la2-la1)*111320, (ln2-ln1)*111320*Math.cos(la1*Math.PI/180));
      }

      const containing = ways.filter(w => ptInPolyLL(selLat, selLng, w.geometry));
      let propertyWays = [];

      // Bounding-box area in (deg²) — used to prefer the most specific building
      // when multiple OSM polygons contain the pin (e.g. a unit inside a large mall).
      const bboxArea = geom => {
        const lts = geom.map(g => g.lat), lns = geom.map(g => g.lon);
        return (Math.max(...lts) - Math.min(...lts)) * (Math.max(...lns) - Math.min(...lns));
      };

      if (containing.length > 0) {
        // Pin is inside one or more buildings — pick the SMALLEST (most specific).
        // Large enclosing polygons (shopping centres, campuses) are deprioritised.
        const sorted = [...containing].sort((a, b) => bboxArea(a.geometry) - bboxArea(b.geometry));
        propertyWays = [sorted[0]];
      } else {
        // Pin is outside every found building — pick by nearest centroid distance.
        // Exclude tiny structures (sheds, enclosures) under ~20 m² bbox, but otherwise
        // prefer proximity — the closest building to the geocoded pin is almost always
        // correct. Area-weighted scoring caused large neighbouring buildings to win.
        const MIN_BBOX = 1e-8; // ≈ 20 m² — filters out fences, small sheds
        const candidates = ways.filter(w => {
          const d = distM(selLat, selLng, ...centroid(w.geometry));
          return d <= 150 && bboxArea(w.geometry) >= MIN_BBOX;
        });
        let bestWay = null, bestDist = Infinity;
        candidates.forEach(w => {
          const d = distM(selLat, selLng, ...centroid(w.geometry));
          if (d < bestDist) { bestDist = d; bestWay = w; }
        });
        if (bestWay) {
          propertyWays = [bestWay];
        } else {
          // No suitable OSM building — place an estimated rectangle centred at pin.
          const dLat = 0.000135, dLng = 0.000250;
          propertyWays = [{ geometry: [
            { lat: lat + dLat, lon: lng + dLng },
            { lat: lat + dLat, lon: lng - dLng },
            { lat: lat - dLat, lon: lng - dLng },
            { lat: lat - dLat, lon: lng + dLng },
          ], tags: {}, _estimated: true }];
        }
      }

      // Expand: include detached garages / ADUs on the same parcel.
      // Only expand when the primary building has OSM tags (building=yes, etc.).
      // Microsoft Buildings data has tags:{} — expanding with untagged neighbours
      // would include every house on the block.
      const primaryHasOSMTags = Object.keys(propertyWays[0].tags || {}).some(k => k !== '_msBuildings');
      if (primaryHasOSMTags) {
        const PARCEL_RADIUS_M = 25; // metres from primary building centroid
        const primaryHouseNum = propertyWays[0].tags?.['addr:housenumber'] || null;
        const [primCLat, primCLng] = centroid(propertyWays[0].geometry);
        const extraWays = ways.filter(w => {
          if (propertyWays.includes(w)) return false;
          if (distM(primCLat, primCLng, ...centroid(w.geometry)) > PARCEL_RADIUS_M) return false;
          const wHouseNum = w.tags?.['addr:housenumber'];
          if (wHouseNum && (!primaryHouseNum || wHouseNum !== primaryHouseNum)) return false;
          return true;
        });
        if (extraWays.length > 0) propertyWays = [...propertyWays, ...extraWays];
      }

      const rot      = job.mapRotation || 0;
      const origZoom = (centerOverride && centerOverride.zoom) || job.mapZoom || 19;
      const MARGIN   = 40;  // px padding from canvas edge
      const MIN_ZOOM = 17;  // allow zoom-out for large properties

      // Convert one way's geometry to canvas points, removing OSM closing duplicate.
      // MAP_PIXEL_SCALE (1.6) accounts for the cover-mode stretch (640→1024 px).
      // latLngToCanvasPx uses 512-tile world scale to match Mapbox GL zoom levels.
      function buildWayPts(w, z) {
        let p = w.geometry.map(g =>
          latLngToCanvasPx(g.lat, g.lon, lat, lng, z, CANVAS_W, CH, rot, MAP_PIXEL_SCALE)
        );
        if (p.length > 1 && Math.hypot(p[0][0]-p[p.length-1][0], p[0][1]-p[p.length-1][1]) < 2)
          p = p.slice(0, -1);
        return p;
      }

      // Step zoom down until ALL building polygons fit inside the canvas with margin
      let zoom = origZoom;
      let allPts = propertyWays.map(w => buildWayPts(w, zoom));
      while (zoom > MIN_ZOOM) {
        const allFit = allPts.every(pts =>
          pts.every(([x,y]) => x>=MARGIN && x<=CANVAS_W-MARGIN && y>=MARGIN && y<=CH-MARGIN)
        );
        if (allFit) break;
        zoom--;
        // eslint-disable-next-line no-loop-func
        allPts = propertyWays.map(w => buildWayPts(w, zoom));
      }

      // Reload satellite at new zoom if we had to step down
      if (zoom < origZoom) {
        await reloadAt(zoom);
      }

      // Compute authoritative sqft: footprint × floor count (osmLevels from OSM building:levels tag)
      const solarData = await solarPromise;
      const solarSqFt = solarData?.solarPotential?.wholeRoofStats?.areaMeters2
        ? Math.round(solarData.solarPotential.wholeRoofStats.areaMeters2 * 10.7639) : 0;
      const polygonSqFt = Math.round(latLngAreaSqFt(propertyWays[0]?.geometry || []));
      const primaryWay  = propertyWays[0];
      const osmLevels   = parseInt(primaryWay?.tags?.['building:levels']) || 1;
      const authSqFt = Math.round((solarSqFt || polygonSqFt) * osmLevels);

      // Color: orange for property buildings, amber for estimated outlines
      const newDrawings = propertyWays.map((w, i) => ({
        pts: allPts[i],
        style: "solid",
        closed: true,
        color: w._estimated ? "#fbbf24" : "#f97316",
      }));
      const hasEstimated = propertyWays.some(w => w._estimated);
      const structCount  = propertyWays.length;
      const jobUpdates  = { drawings: newDrawings, osmLevels };

      if (hasEstimated) {
        setMapInfo("Estimated outline placed — no building data found. Refine with ✏️ Trace or Edit.");
      } else {
        const descKey   = osmTagsToPropDesc(primaryWay.tags);
        const descMatch = descKey && PROPERTY_DESCRIPTIONS.find(d => d.key === descKey);
        if (descMatch && !job.propDesc) {
          jobUpdates.propDesc = descMatch.text;
          setMapInfo(`${structCount > 1 ? structCount + " structures traced — " : ""}Detected: ${descMatch.label}`);
        } else if (structCount > 1) {
          setMapInfo(`${structCount} structures traced`);
        } else {
          setMapInfo("");
        }
      }
      updateJob(jobUpdates);
      setMode("edit");
      setSelectedShapeIdx(null);
    } catch (e) {
      // Ultimate safety net — never show a red error to the inspector.
      // Place an amber estimated rectangle centred on the pin instead.
      try {
        const dLat = 0.000135, dLng = 0.000250;
        const estGeom = [
          { lat: lat + dLat, lon: lng + dLng },
          { lat: lat + dLat, lon: lng - dLng },
          { lat: lat - dLat, lon: lng - dLng },
          { lat: lat - dLat, lon: lng + dLng },
        ];
        const estPts = estGeom.map(g =>
          latLngToCanvasPx(g.lat, g.lon, lat, lng, job.mapZoom || 19,
            CANVAS_W, CH, job.mapRotation || 0, MAP_PIXEL_SCALE)
        );
        updateJob({ drawings: [{ pts: estPts, style: "solid", closed: true, color: "#fbbf24" }] });
        setMapInfo("Estimated outline placed — no building data found. Refine with ✏️ Trace or Edit.");
      } catch {
        setMapInfo("Tracing unavailable — check your connection.");
      }
    } finally {
      setAutoTracing(false);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function getPos(e){
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CH / rect.height;
    // changedTouches[0] works on touchend (touches list is empty there); touches[0] for touchmove/start
    const src = e.changedTouches?.[0] ?? e.touches?.[0] ?? e;
    const x = (src.clientX - rect.left) * scaleX;
    const y = (src.clientY - rect.top)  * scaleY;
    return [x, y];
  }

  function drawMarkers(ctx){
    // Draw property center pin if satellite loaded
    if(job.mapBg && job.mapCenter){
      const cx=CANVAS_W/2, cy=CH/2;
      // Drop shadow
      ctx.save();
      ctx.shadowColor="rgba(0,0,0,0.4)";ctx.shadowBlur=6;ctx.shadowOffsetY=2;
      // Pin body
      ctx.beginPath();
      ctx.arc(cx, cy-16, 10, 0, Math.PI*2);
      ctx.fillStyle="#dc2626"; ctx.fill();
      ctx.strokeStyle="#fff"; ctx.lineWidth=2; ctx.stroke();
      // Pin stem
      ctx.beginPath();ctx.moveTo(cx,cy-6);ctx.lineTo(cx,cy);
      ctx.strokeStyle="#dc2626";ctx.lineWidth=2.5;ctx.stroke();
      // Inner dot
      ctx.beginPath();ctx.arc(cx,cy-16,4,0,Math.PI*2);
      ctx.fillStyle="#fff";ctx.fill();
      ctx.restore();
    }
    // When CSS-zoomed, markers live in the HTML overlay so they're never clipped
    if(viewScale > 1){ return; }
    (job.markers||[]).forEach(m=>{
      if(m.type==="area"){
        ctx.save();
        ctx.font = `bold ${m.fontSize||13}px sans-serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.lineWidth = 3; ctx.lineJoin = "round";
        ctx.strokeText(m.label, m.x, m.y);
        ctx.fillStyle = "#0f172a";
        ctx.fillText(m.label, m.x, m.y);
        ctx.restore();
        return;
      }
      const cat   = WDO_CATEGORIES.find(c=>c.num===Number(m.label[0]));
      const color = cat ? cat.color : "#1a1a2e";
      const fontSize = m.fontSize || (markerSize + 4);
      ctx.font         = `bold ${fontSize}px sans-serif`;
      ctx.textAlign    = "center";
      ctx.textBaseline = "middle";
      ctx.strokeStyle  = "rgba(255,255,255,0.95)";
      ctx.lineWidth    = 2;
      ctx.lineJoin     = "round";
      const mParts = m.label.split("+");
      const mLh = fontSize * 1.25;
      mParts.forEach((part,i)=>{ const ty=m.y+(i-(mParts.length-1)/2)*mLh; ctx.strokeText(part,m.x,ty); });
      ctx.fillStyle = color;
      mParts.forEach((part,i)=>{ const ty=m.y+(i-(mParts.length-1)/2)*mLh; ctx.fillText(part,m.x,ty); });
      // Photo badge: green dot + count when this pin's finding has photos.
      const photoCount=(m.photos||[]).length+(job.findings||[]).filter(f=>f.markerId===m.id).reduce((s,f)=>s+((f.photos||[]).length),0);
      if(photoCount>0){
        const bw=ctx.measureText(mParts[0]).width;
        const bx=m.x+bw/2+9, by=m.y-mLh/2-1;
        ctx.beginPath(); ctx.arc(bx,by,7.5,0,Math.PI*2);
        ctx.fillStyle="#16a34a"; ctx.fill();
        ctx.lineWidth=1.5; ctx.strokeStyle="#fff"; ctx.stroke();
        ctx.fillStyle="#fff"; ctx.font="bold 9px sans-serif";
        ctx.fillText(String(photoCount),bx,by+0.5);
      }
    });
  }

  // Draw sq ft area labels on closed polygons
  function drawAreaLabels(ctx, drawings){
    if(!job.mapCenter) return;
    (drawings||[]).filter(d=>d.closed&&d.pts.length>=3).forEach(d=>{
      const sqft = Math.round(geoAreaSqFt(d.pts, job.mapCenter, job.mapZoom, job.mapRotation||0, undefined, undefined, job.mapPixelScale||MAP_PIXEL_SCALE));
      // centroid
      const cx = d.pts.reduce((s,p)=>s+p[0],0)/d.pts.length;
      const cy = d.pts.reduce((s,p)=>s+p[1],0)/d.pts.length;

      // Custom text label badge
      if(d.label){
        ctx.save();
        ctx.font = "bold 13px sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        const lw = ctx.measureText(d.label).width + 14;
        const ly = sqft >= 1 ? cy - 28 : cy;
        ctx.fillStyle = "rgba(15,23,42,0.82)";
        ctx.beginPath(); ctx.roundRect(cx-lw/2, ly-11, lw, 22, 5); ctx.fill();
        ctx.fillStyle = "#f8fafc";
        ctx.fillText(d.label, cx, ly);
        ctx.restore();
      }

      if(sqft < 1) return;
      const label = sqft >= 1000 ? (sqft/1000).toFixed(1)+"k sqft" : sqft+" sqft";
      ctx.save();
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      const tw = ctx.measureText(label).width + 12;
      ctx.fillStyle = "rgba(0,0,0,0.65)";
      ctx.beginPath();
      ctx.roundRect(cx-tw/2, cy-10, tw, 20, 4);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.fillText(label, cx, cy);
      ctx.restore();
    });
  }

  // Draw a Google-Maps-style measurement: pin dots at each end + a distance pill
  function drawMeasureLine(ctx, a, b, live){
    const ft = edgeLengthFt(a, b);
    // Connecting line
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]);
    ctx.strokeStyle = live ? "#22d3ee" : "#0891b2";
    ctx.lineWidth = 2.5;
    ctx.setLineDash(live ? [7,5] : []);
    ctx.shadowColor = "rgba(0,0,0,0.45)"; ctx.shadowBlur = 3;
    ctx.stroke();
    ctx.restore();
    // Endpoint pins
    [a, b].forEach(p=>{
      ctx.save();
      ctx.beginPath(); ctx.arc(p[0], p[1], 6, 0, Math.PI*2);
      ctx.fillStyle = "#fff"; ctx.fill();
      ctx.beginPath(); ctx.arc(p[0], p[1], 4, 0, Math.PI*2);
      ctx.fillStyle = "#0891b2"; ctx.fill();
      ctx.restore();
    });
    // Distance pill at midpoint
    if(ft >= 0.5){
      const label = ft >= 1000 ? (ft/1000).toFixed(1)+"k ft" : Math.round(ft)+" ft";
      const mx = (a[0]+b[0])/2, my = (a[1]+b[1])/2;
      ctx.save();
      ctx.font = "bold 13px sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      const tw = ctx.measureText(label).width + 16;
      ctx.fillStyle = live ? "rgba(34,211,238,0.95)" : "rgba(8,145,178,0.95)";
      ctx.beginPath(); ctx.roundRect(mx-tw/2, my-12, tw, 24, 6); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.fillText(label, mx, my);
      ctx.restore();
    }
  }

  // Draw all committed measurements stored on the job
  function drawMeasurements(ctx){
    (job.measurements||[]).forEach(m=>{
      if(m && m.a && m.b) drawMeasureLine(ctx, m.a, m.b, false);
    });
  }

  function drawScaleBar(ctx){
    if(!job.mapCenter || !job.mapZoom) return;
    // Derive feet-per-pixel from the SAME conversion the measurement tool uses, so
    // the scale bar and on-screen measurements can never disagree.
    const ftPerPx = edgeLengthFt([0, CH/2], [100, CH/2]) / 100;
    if(!ftPerPx) return;
    // Pick largest "nice" distance that fits in ~120px
    const nicesFt = [10,20,50,100,200,500,1000];
    let barFt = 10;
    for(const n of nicesFt){ if(n / ftPerPx <= 120) barFt = n; }
    const barPx = barFt / ftPerPx;
    const x = 18, y = CH - 22, h = 5;
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.beginPath(); ctx.roundRect(x-6, y-10, barPx+12, 22, 4); ctx.fill();
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, y+h); ctx.lineTo(x, y); ctx.lineTo(x+barPx, y); ctx.lineTo(x+barPx, y+h);
    ctx.stroke();
    ctx.font = "bold 11px sans-serif"; ctx.fillStyle = "#fff";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(barFt >= 1000 ? (barFt/1000)+"k ft" : barFt+" ft", x + barPx/2, y - 2);
    ctx.restore();
  }

  function drawSelectionOverlay(ctx, drawings){
    if(selectedShapeIdx===null || mode!=="edit") return;
    const d = (drawings||[])[selectedShapeIdx];
    if(!d||!d.pts) return;
    // Highlight the selected shape outline
    ctx.save();
    ctx.strokeStyle="#22d3ee"; ctx.lineWidth=3;
    ctx.setLineDash([6,3]);
    ctx.beginPath();
    ctx.moveTo(d.pts[0][0], d.pts[0][1]);
    for(let i=1;i<d.pts.length;i++) ctx.lineTo(d.pts[i][0], d.pts[i][1]);
    if(d.closed) ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    // Draw midpoint "add point" handles on each edge
    const segCount = d.closed ? d.pts.length : d.pts.length - 1;
    for (let i = 0; i < segCount; i++) {
      const a = d.pts[i], b = d.pts[(i + 1) % d.pts.length];
      const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
      ctx.beginPath(); ctx.arc(mx, my, 6, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(34,211,238,0.18)";
      ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1.5;
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#22d3ee"; ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("+", mx, my);
    }
    // Draw draggable vertex handles — larger radius for easier touch
    d.pts.forEach((p,i)=>{
      const isActive = dragPtIdx===i;
      const r = isTouchRef.current ? (isActive?14:10) : (isActive?10:7);
      ctx.beginPath();
      ctx.arc(p[0], p[1], r, 0, Math.PI*2);
      ctx.fillStyle = isActive ? "#22d3ee" : "#fff";
      ctx.strokeStyle = "#22d3ee";
      ctx.lineWidth = 2.5;
      ctx.fill();
      ctx.stroke();
      // Index label
      ctx.fillStyle="#0f172a"; ctx.font=`bold ${isTouchRef.current?10:9}px sans-serif`;
      ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText(i+1, p[0], p[1]);
    });
    // Rotation handle (any shape with 3+ points, open or closed)
    if(d.pts.length>=3){
      const [cx,cy]=shapeCentroid(d.pts);
      const {x:hx,y:hy,radius,fontSize,lineWidth}=rotationControl(d.pts);
      ctx.beginPath(); ctx.setLineDash([4,3]);
      ctx.moveTo(cx,cy); ctx.lineTo(hx,hy);
      ctx.strokeStyle="#f59e0b"; ctx.lineWidth=1.5; ctx.stroke();
      ctx.setLineDash([]);
      const active=isRotatingShape;
      ctx.beginPath(); ctx.arc(hx,hy,radius,0,Math.PI*2);
      ctx.fillStyle=active?"#fbbf24":"#f59e0b";
      ctx.strokeStyle="#fff"; ctx.lineWidth=lineWidth;
      ctx.fill(); ctx.stroke();
      ctx.fillStyle="#fff"; ctx.font=`bold ${fontSize}px sans-serif`;
      ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText("↻",hx,hy);
    }
    ctx.restore();
  }

  function drawShapes(ctx, drawings){
    (drawings||[]).forEach(d=>{
      if(!d.pts||d.pts.length<2) return;
      const color = d.color || "#1a1a2e";
      // Subtle glow / shadow for better visibility on satellite
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur  = 4;
      ctx.beginPath();
      ctx.moveTo(d.pts[0][0], d.pts[0][1]);
      for(let i=1;i<d.pts.length;i++) ctx.lineTo(d.pts[i][0], d.pts[i][1]);
      if(d.closed) ctx.closePath();
      ctx.strokeStyle = color;
      ctx.lineWidth   = 1.75;
      if(d.style==="dashed")      ctx.setLineDash([12,6]);
      else if(d.style==="dotted") ctx.setLineDash([3,7]);
      else                         ctx.setLineDash([]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      if(mode==="edit"){
        d.pts.forEach(p=>{
          ctx.beginPath();
          ctx.arc(p[0], p[1], 4, 0, Math.PI*2);
          ctx.fillStyle = "#fff";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p[0], p[1], 3, 0, Math.PI*2);
          ctx.fillStyle = color;
          ctx.fill();
        });
      }
    });
  }

  // ── Base redraw ─────────────────────────────────────────────────────────────
  const bgImgRef = useRef(null);
  const bgSrcRef = useRef(null);

  const redraw = useCallback(()=>{
    const canvas = canvasRef.current;
    if(!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    if(canvas.width !== CANVAS_W * dpr){ canvas.width = CANVAS_W * dpr; canvas.height = CH * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0,0,CANVAS_W,CH);

    function drawGridPaper(){
      ctx.fillStyle="#f7f9fc"; ctx.fillRect(0,0,CANVAS_W,CH);
      ctx.strokeStyle="rgba(180,200,225,0.55)"; ctx.lineWidth=0.5;
      for(let x=0;x<=CANVAS_W;x+=20){ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,CH);ctx.stroke();}
      for(let y=0;y<=CH;y+=20){ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(CANVAS_W,y);ctx.stroke();}
      ctx.strokeStyle="rgba(140,170,210,0.7)"; ctx.lineWidth=1;
      for(let x=0;x<=CANVAS_W;x+=100){ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,CH);ctx.stroke();}
      for(let y=0;y<=CH;y+=100){ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(CANVAS_W,y);ctx.stroke();}
    }

    function drawGridOverlay(){
      ctx.save();
      ctx.strokeStyle="rgba(255,255,255,0.25)"; ctx.lineWidth=0.5;
      for(let x=0;x<=CANVAS_W;x+=20){ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,CH);ctx.stroke();}
      for(let y=0;y<=CH;y+=20){ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(CANVAS_W,y);ctx.stroke();}
      ctx.strokeStyle="rgba(255,255,255,0.55)"; ctx.lineWidth=1;
      for(let x=0;x<=CANVAS_W;x+=100){ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,CH);ctx.stroke();}
      for(let y=0;y<=CH;y+=100){ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(CANVAS_W,y);ctx.stroke();}
      ctx.restore();
    }

    if(job.mapBg){
      // Cache the background image so we don't reload on every redraw
      function doDraw(img){
        const rot = ((job.mapRotation||0) * Math.PI) / 180;
        ctx.save();
        if(rot !== 0){
          ctx.translate(CANVAS_W/2, CH/2);
          ctx.rotate(rot);
          const scale = Math.abs(Math.cos(rot)) + Math.abs(Math.sin(rot));
          ctx.drawImage(img, -CANVAS_W*scale/2, -CH*scale/2, CANVAS_W*scale, CH*scale);
        } else {
          ctx.drawImage(img, 0, 0, CANVAS_W, CH);
        }
        ctx.restore();
        if(showGrid) drawGridOverlay();
        drawShapes(ctx, job.drawings);
        drawSelectionOverlay(ctx, job.drawings);
        drawAreaLabels(ctx, job.drawings);
        drawMeasurements(ctx);
        drawScaleBar(ctx);
        drawMarkers(ctx);
      }
      if(bgSrcRef.current === job.mapBg && bgImgRef.current){
        doDraw(bgImgRef.current);
      } else {
        const img = new Image();
        img.onload = ()=>{ bgImgRef.current=img; bgSrcRef.current=job.mapBg; doDraw(img); };
        img.src = job.mapBg;
      }
    } else {
      // No satellite yet — graph paper placeholder + shapes
      drawGridPaper();
      drawShapes(ctx, job.drawings);
      drawSelectionOverlay(ctx, job.drawings);
      drawAreaLabels(ctx, job.drawings);
      drawMeasurements(ctx);
      drawScaleBar(ctx);
      drawMarkers(ctx);
    }
  // eslint-disable-next-line
  },[job.drawings, job.markers, job.measurements, job.mapBg, job.mapRotation, markerSize, selectedShapeIdx, dragPtIdx, mode, viewScale, showGrid]);

  // ── Live overlay: trace preview + mouse rubber-band line ───────────────────
  const redrawLive = useCallback(()=>{
    const lc = liveCanvasRef.current;
    if(!lc) return;
    const ctx = lc.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    if(lc.width !== CANVAS_W * dpr){ lc.width = CANVAS_W * dpr; lc.height = CH * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0,0,CANVAS_W,CH);

    // MEASURE mode — live "pull" preview from the dropped pin to the cursor
    if(mode==="measure" && measureStart){
      const mp = mousePosRef.current;
      if(mp){
        // Snap the cursor to a nearby traced corner so wall ends are easy to hit
        const snap = snapToExisting(mp, false);
        const end = snap || mp;
        drawMeasureLine(ctx, measureStart, end, true);
      } else {
        // Just the dropped pin, waiting for the pull
        ctx.save();
        ctx.beginPath(); ctx.arc(measureStart[0], measureStart[1], 6, 0, Math.PI*2);
        ctx.fillStyle = "#fff"; ctx.fill();
        ctx.beginPath(); ctx.arc(measureStart[0], measureStart[1], 4, 0, Math.PI*2);
        ctx.fillStyle = "#0891b2"; ctx.fill();
        ctx.restore();
      }
      return;
    }

    if(tracePts.length === 0) return;

    const mousePos = mousePosRef.current;
    const isNearFirst = tracePts.length >= 3 && mousePos &&
      Math.hypot(mousePos[0]-tracePts[0][0], mousePos[1]-tracePts[0][1]) <= CLOSE_DIST;

    // Snap to existing vertex or edge for rubber-band preview
    const vertexSnap = mousePos && !isNearFirst ? snapToExisting(mousePos, false) : null;
    const edgeSnapPrev = !vertexSnap && mousePos && !isNearFirst ? snapToEdge(mousePos) : null;
    const snapTarget = vertexSnap || (edgeSnapPrev ? edgeSnapPrev.pos : null);
    const effectiveMouse = snapTarget || mousePos;

    // Committed lines (with shadow for satellite visibility)
    ctx.save();
    ctx.shadowColor = "rgba(255,255,255,0.7)";
    ctx.shadowBlur  = 5;
    ctx.beginPath();
    ctx.moveTo(tracePts[0][0], tracePts[0][1]);
    for(let i=1;i<tracePts.length;i++) ctx.lineTo(tracePts[i][0], tracePts[i][1]);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth   = 3;
    ctx.stroke();
    ctx.restore();

    // Rubber-band preview to cursor (snapped if near existing vertex)
    if(effectiveMouse && tracePts.length >= 1){
      ctx.beginPath();
      ctx.moveTo(tracePts[tracePts.length-1][0], tracePts[tracePts.length-1][1]);
      ctx.lineTo(effectiveMouse[0], effectiveMouse[1]);
      ctx.strokeStyle = isNearFirst ? "#22d3ee" : snapTarget ? "#f59e0b" : "rgba(15,23,42,0.4)";
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([6,4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Point dots
    tracePts.forEach((p,i)=>{
      const isFirst = i===0;
      const r = isFirst ? 7 : 4;
      ctx.beginPath();
      ctx.arc(p[0], p[1], r, 0, Math.PI*2);
      ctx.fillStyle   = isFirst ? (isNearFirst?"#22d3ee":"#0f172a") : "#fff";
      ctx.strokeStyle = isFirst ? (isNearFirst?"#22d3ee":"#0f172a") : "#0f172a";
      ctx.lineWidth   = 2;
      ctx.fill();
      ctx.stroke();
      // Label first point with "close" hint
      if(isFirst && tracePts.length>=3){
        ctx.font = "bold 10px sans-serif";
        ctx.fillStyle = isNearFirst ? "#22d3ee" : "#64748b";
        ctx.textAlign = "center";
        ctx.fillText("close", p[0], p[1]-12);
      }
    });

    // Snap-to-close highlight ring
    if(isNearFirst){
      ctx.beginPath();
      ctx.arc(tracePts[0][0], tracePts[0][1], CLOSE_DIST, 0, Math.PI*2);
      ctx.strokeStyle = "rgba(34,211,238,0.5)";
      ctx.lineWidth   = 2;
      ctx.setLineDash([4,4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // Snap-to-existing-vertex highlight (amber ring + dot)
    if(snapTarget){
      ctx.beginPath();
      ctx.arc(snapTarget[0], snapTarget[1], SNAP_DIST, 0, Math.PI*2);
      ctx.strokeStyle = "rgba(245,158,11,0.5)";
      ctx.lineWidth   = 2;
      ctx.setLineDash([4,4]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(snapTarget[0], snapTarget[1], 6, 0, Math.PI*2);
      ctx.fillStyle = "#f59e0b";
      ctx.fill();
      ctx.font = "bold 10px sans-serif";
      ctx.fillStyle = "#f59e0b";
      ctx.textAlign = "center";
      ctx.fillText("snap", snapTarget[0], snapTarget[1]-14);
    }

    // Live area badge (shown when ≥3 points placed, before closing)
    if(tracePts.length >= 3 && job.mapCenter){
      const liveArea = Math.round(geoAreaSqFt(
        tracePts, job.mapCenter, job.mapZoom, job.mapRotation||0,
        undefined, undefined, job.mapPixelScale||MAP_PIXEL_SCALE
      ));
      if(liveArea > 0){
        const cx = tracePts.reduce((s,p)=>s+p[0],0)/tracePts.length;
        const cy = tracePts.reduce((s,p)=>s+p[1],0)/tracePts.length;
        const aLabel = liveArea >= 1000 ? (liveArea/1000).toFixed(1)+"k sqft" : liveArea+" sqft";
        ctx.save();
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        const tw = ctx.measureText(aLabel).width + 14;
        ctx.fillStyle = "rgba(59,130,246,0.82)";
        ctx.beginPath(); ctx.roundRect(cx-tw/2, cy-11, tw, 22, 5); ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.fillText(aLabel, cx, cy);
        ctx.restore();
      }
    }
  },[tracePts, job.drawings, mode, measureStart]);

  useEffect(()=>{ redraw(); },[redraw]);
  useEffect(()=>{ redrawLive(); },[redrawLive]);
  // Arrow-key nudge: selected shape (single) or ALL shapes (when none selected = align trace to image)
  useEffect(()=>{
    function onKey(e){
      if(mode!=="edit") return;
      const dirs = {ArrowLeft:[-1,0],ArrowRight:[1,0],ArrowUp:[0,-1],ArrowDown:[0,1]};
      const d = dirs[e.key];
      if(!d) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const dx = d[0]*step, dy = d[1]*step;
      if(selectedShapeIdx !== null){
        // Nudge only the selected shape
        updateJob({drawings:(job.drawings||[]).map((dr,i)=>
          i!==selectedShapeIdx ? dr : {...dr, pts:dr.pts.map(p=>[p[0]+dx, p[1]+dy])}
        )});
      } else if((job.drawings||[]).length > 0){
        // No shape selected — nudge ALL shapes together (align trace to satellite)
        updateJob({drawings:(job.drawings||[]).map(dr=>({...dr, pts:dr.pts.map(p=>[p[0]+dx, p[1]+dy])}))});
      }
    }
    window.addEventListener("keydown", onKey);
    return ()=>window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line
  },[mode, selectedShapeIdx, job.drawings]);

  // Auto-load satellite + auto-trace when flagged from Job Info address blur
  useEffect(()=>{
    if(job.autoLoad && job.address && !loadingMap){
      updateJob({autoLoad:false});
      autoLoadAndTrace();
    }
  // eslint-disable-next-line
  },[job.autoLoad]);

  // ── Rotation helpers ──────────────────────────────────────────────────────────
  function shapeCentroid(pts){
    const cx=pts.reduce((s,p)=>s+p[0],0)/pts.length;
    const cy=pts.reduce((s,p)=>s+p[1],0)/pts.length;
    return [cx,cy];
  }
  function rotationControl(pts){
    return rotationHandle(pts,CANVAS_W,CH,canvasRef.current?.getBoundingClientRect().width);
  }
  function rotatePts(pts,cx,cy,angle){
    const cos=Math.cos(angle),sin=Math.sin(angle);
    return pts.map(([x,y])=>[cx+cos*(x-cx)-sin*(y-cy), cy+sin*(x-cx)+cos*(y-cy)]);
  }
  // "True up" a hand-traced rectilinear footprint so every corner becomes 90°,
  // aligned to the building's own dominant wall direction (not the screen axes).
  function squareUpShape(pts){
    const n = pts.length;
    if(n < 4) return pts;
    const [cx,cy] = shapeCentroid(pts);
    // 1. Dominant wall angle θ via length-weighted circular mean (90°-periodic → ×4)
    let S=0, C=0, wTot=0;
    for(let i=0;i<n;i++){
      const a=pts[i], b=pts[(i+1)%n];
      const dx=b[0]-a[0], dy=b[1]-a[1];
      const len=Math.hypot(dx,dy);
      if(len<1e-6) continue;
      const ang=Math.atan2(dy,dx);
      S += len*Math.sin(4*ang); C += len*Math.cos(4*ang); wTot += len;
    }
    if(wTot < 1e-6) return pts;
    const theta = Math.atan2(S,C)/4;
    // 2. Rotate into axis-aligned frame
    const R = rotatePts(pts, cx, cy, -theta);
    // 3. Classify edges + union-find on X (vertical edges) and Y (horizontal edges)
    const px=Array.from({length:n},(_,i)=>i), py=Array.from({length:n},(_,i)=>i);
    function find(arr,i){ while(arr[i]!==i){ arr[i]=arr[arr[i]]; i=arr[i]; } return i; }
    function union(arr,a,b){ arr[find(arr,a)]=find(arr,b); }
    for(let i=0;i<n;i++){
      const j=(i+1)%n;
      const dx=R[j][0]-R[i][0], dy=R[j][1]-R[i][1];
      if(Math.abs(dx) >= Math.abs(dy)) union(py,i,j); // horizontal → share Y
      else                              union(px,i,j); // vertical   → share X
    }
    // 4. Average each coordinate class
    const xs={}, ys={};
    for(let i=0;i<n;i++){
      const rx=find(px,i), ry=find(py,i);
      (xs[rx]=xs[rx]||[]).push(R[i][0]);
      (ys[ry]=ys[ry]||[]).push(R[i][1]);
    }
    const xMean={}, yMean={};
    for(const k in xs) xMean[k]=xs[k].reduce((s,v)=>s+v,0)/xs[k].length;
    for(const k in ys) yMean[k]=ys[k].reduce((s,v)=>s+v,0)/ys[k].length;
    const Rsq = R.map((_,i)=>[xMean[find(px,i)], yMean[find(py,i)]]);
    // 5. Degenerate guard — bail if the result collapsed to a line
    const bx=Rsq.map(p=>p[0]), by=Rsq.map(p=>p[1]);
    if((Math.max(...bx)-Math.min(...bx) < 1) || (Math.max(...by)-Math.min(...by) < 1)) return pts;
    // 6. Rotate back
    return rotatePts(Rsq, cx, cy, theta);
  }
  function allShapesCentroid(){
    const allPts=(job.drawings||[]).flatMap(d=>d.pts);
    if(allPts.length===0) return [CANVAS_W/2, CH/2];
    return [allPts.reduce((s,p)=>s+p[0],0)/allPts.length, allPts.reduce((s,p)=>s+p[1],0)/allPts.length];
  }
  function rotateAllShapes(angleDeg){
    const drawings=job.drawings||[];
    if(drawings.length===0) return;
    const [cx,cy]=allShapesCentroid();
    const angle=angleDeg*Math.PI/180;
    const cos=Math.cos(angle),sin=Math.sin(angle);
    const newDrawings=drawings.map(d=>({...d,pts:rotatePts(d.pts,cx,cy,angle)}));
    const markers=(job.markers||[]).map(m=>({...m,x:cx+cos*(m.x-cx)-sin*(m.y-cy),y:cy+sin*(m.x-cx)+cos*(m.y-cy)}));
    updateJob({drawings:newDrawings,markers});
  }
  function scaleAllShapes(factor){
    const drawings=job.drawings||[];
    if(drawings.length===0) return;
    const [cx,cy]=allShapesCentroid();
    const newDrawings=drawings.map(d=>({...d,pts:d.pts.map(([x,y])=>[cx+(x-cx)*factor,cy+(y-cy)*factor])}));
    const markers=(job.markers||[]).map(m=>({...m,x:cx+(m.x-cx)*factor,y:cy+(m.y-cy)*factor}));
    updateJob({drawings:newDrawings,markers});
  }

  function pointInPolygon(pt, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i], [xj, yj] = poly[j];
      if (((yi > pt[1]) !== (yj > pt[1])) &&
          (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  function handleMouseMove(e){
    if(panDragRef.current && !isDragging && !isDraggingShape && !isRotatingShape && !rotateShapeRef.current && !dragShapeRef.current){
      const dx = e.clientX - panDragRef.current.startX;
      const dy = e.clientY - panDragRef.current.startY;
      panDragRef.current.dx = dx; panDragRef.current.dy = dy;
      const wrap = mapWrapRef.current;
      if(wrap) wrap.style.transform = `translate(${dx}px,${dy}px)${viewScale!==1?` scale(${viewScale})`:''}`;
      return;
    }
    if(mode==="trace" && tracePts.length>0){
      mousePosRef.current = applySnap90(getPos(e));
      redrawLive();
    }
    if(mode==="measure" && measureStart){
      mousePosRef.current = getPos(e);
      redrawLive();
    }
    if(mode==="edit" && isDragging && dragPtIdx!==null && selectedShapeIdx!==null){
      const pos = getPos(e);
      const drawings = (job.drawings||[]).map((d,di)=>{
        if(di!==selectedShapeIdx) return d;
        const newPts = d.pts.map((p,pi)=>pi===dragPtIdx?pos:p);
        return {...d, pts:newPts};
      });
      updateJob({drawings});
    }
    if(mode==="edit" && isDraggingShape && dragShapeRef.current && selectedShapeIdx!==null){
      const pos = getPos(e);
      const { startPos, origDrawings, origMarkers, origMeasurements } = dragShapeRef.current;
      const dx = pos[0] - startPos[0], dy = pos[1] - startPos[1];
      // Move the whole diagram together — every shape, marker and measurement.
      const drawings = origDrawings.map(d=>({...d, pts: d.pts.map(p=>[p[0]+dx, p[1]+dy])}));
      const markers = origMarkers.map(m=>({...m, x:m.x+dx, y:m.y+dy}));
      const measurements = origMeasurements.map(mm=>({...mm, a:[mm.a[0]+dx, mm.a[1]+dy], b:[mm.b[0]+dx, mm.b[1]+dy]}));
      updateJob({drawings, markers, measurements});
    }
    if(mode==="edit" && isRotatingShape && rotateShapeRef.current && selectedShapeIdx!==null){
      const pos = getPos(e);
      const {centroid:[cx,cy], startAngle, origDrawings, origMarkers, origMeasurements} = rotateShapeRef.current;
      const angle = Math.atan2(pos[1]-cy,pos[0]-cx) - startAngle;
      const cos=Math.cos(angle), sin=Math.sin(angle);
      const rot = (x,y) => [cx + cos*(x-cx) - sin*(y-cy), cy + sin*(x-cx) + cos*(y-cy)];
      // Rotate the whole diagram together around the selected shape's centre.
      const drawings = origDrawings.map(d=>({...d, pts: d.pts.map(p=>rot(p[0],p[1]))}));
      const markers = origMarkers.map(m=>{ const [nx,ny]=rot(m.x,m.y); return {...m, x:nx, y:ny}; });
      const measurements = origMeasurements.map(mm=>{ const [ax,ay]=rot(mm.a[0],mm.a[1]); const [bx,by]=rot(mm.b[0],mm.b[1]); return {...mm, a:[ax,ay], b:[bx,by]}; });
      updateJob({drawings, markers, measurements});
    }
  }
  function handleMouseLeave(){
    mousePosRef.current = null;
    redrawLive();
  }

  // ── Pinch-to-zoom handlers ──────────────────────────────────────────────────
  function pinchDist(e){ return Math.hypot(e.touches[0].clientX-e.touches[1].clientX, e.touches[0].clientY-e.touches[1].clientY); }

  function handleTouchStart(e){
    isTouchRef.current = true;
    if(e.touches.length===2){
      e.preventDefault();
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      pinchRef.current = {startDist: pinchDist(e), startZoom: job.mapZoom||21, midX, midY};
      setPinchZooming(true);
    } else {
      // Always track drag start — drag > 8px pans, tap fires mode action on touchend.
      const t=e.touches[0];
      panDragRef.current={startX:t.clientX,startY:t.clientY,dx:0,dy:0,vx:0,vy:0,lastT:Date.now()};
      if(mode==="edit"){
        // Drag setup must happen on finger-down so touchmove can act immediately.
        handleTap(e);
        // If handleTap started a rotation or shape-drag, cancel pan immediately
        // (isRotatingShape/isDraggingShape are async state; refs are sync)
        if(rotateShapeRef.current || dragShapeRef.current || dragPtRef.current !== null) panDragRef.current=null;
      }
    }
  }

  function handleTouchMove(e){
    if(e.touches.length===2 && pinchRef.current){
      e.preventDefault();
      const ratio = pinchDist(e) / pinchRef.current.startDist;
      pinchRef.current.currentRatio = ratio;
      const effective = Math.max(1, viewScale * ratio);
      const wrap = mapWrapRef.current;
      if(wrap){
        // Use actual pinch midpoint as transform origin for natural feel
        const rect = wrap.getBoundingClientRect();
        const ox = ((pinchRef.current.midX - rect.left) / rect.width * 100).toFixed(1);
        const oy = ((pinchRef.current.midY - rect.top)  / rect.height * 100).toFixed(1);
        wrap.style.transformOrigin = `${ox}% ${oy}%`;
        wrap.style.transform       = `scale(${effective.toFixed(4)})`;
      }
    } else if(e.touches.length===1){
      if(panDragRef.current && !isDragging && !isDraggingShape && !isRotatingShape && !rotateShapeRef.current && !dragShapeRef.current && dragPtRef.current === null){
        const t=e.touches[0];
        const dx=t.clientX-panDragRef.current.startX, dy=t.clientY-panDragRef.current.startY;
        // Track velocity for momentum
        const now=Date.now();
        const dt=now-(panDragRef.current.lastT||now);
        if(dt>0){
          panDragRef.current.vx=(dx-panDragRef.current.dx)/dt*16;
          panDragRef.current.vy=(dy-panDragRef.current.dy)/dt*16;
        }
        panDragRef.current.dx=dx; panDragRef.current.dy=dy; panDragRef.current.lastT=now;
        const wrap=mapWrapRef.current;
        if(wrap) wrap.style.transform=`translate(${dx}px,${dy}px)${viewScale!==1?` scale(${viewScale})`:''}`;
        e.preventDefault();
      } else {
        handleMouseMove(e);
      }
    }
  }

  function handleTouchEnd(e){
    // ── Pinch gesture ended (any 2-finger touch, with or without movement) ──────
    if(pinchRef.current){
      const ratio = pinchRef.current.currentRatio;
      pinchRef.current = null;
      setPinchZooming(false);
      if(ratio !== undefined){
        const combinedScale = viewScale * ratio;
        // If the gesture is large enough, step to the next tile zoom level for sharp detail
        const curZoom = job.mapZoom || 20;
        if (combinedScale >= 1.4 && curZoom < 22) {
          setViewScale(1);
          if(mapWrapRef.current){ mapWrapRef.current.style.transform=''; mapWrapRef.current.style.transformOrigin=''; }
          changeZoom(1);
          return;
        }
        if (combinedScale <= 0.60 && curZoom > 17) {
          setViewScale(1);
          if(mapWrapRef.current){ mapWrapRef.current.style.transform=''; mapWrapRef.current.style.transformOrigin=''; }
          changeZoom(-1);
          return;
        }
        // Moderate pinch — persist CSS scale for fine-grained inspection (max 4×)
        const newScale = Math.max(1, Math.min(4, combinedScale));
        const wrap = mapWrapRef.current;
        if(wrap){
          wrap.style.transformOrigin = '50% 50%';
          wrap.style.transform       = `scale(${newScale})`;
        }
        setViewScale(newScale);
      }
      // Always return after any pinch — never let it register as a tap
      return;
    }

    // ── Capture vertex-drag state before cleanup ───────────────────────────────
    const wasVertexDrag = isDragging || isDraggingShape || isRotatingShape;

    // ── Clean up drag states (set up by handleTouchStart in edit mode) ─────────
    if(isDragging){ setIsDragging(false); setDragPtIdx(null); dragPtRef.current=null; }
    if(isDraggingShape){ setIsDraggingShape(false); dragShapeRef.current=null; }
    if(isRotatingShape){ setIsRotatingShape(false); rotateShapeRef.current=null; }

    // ── Drag-vs-tap dispatch (all modes) ────────────────────────────────────────
    if(panDragRef.current){
      const {dx,dy,vx=0,vy=0}=panDragRef.current; panDragRef.current=null;
      const wrap=mapWrapRef.current;
      if(wrap) wrap.style.transform=viewScale!==1?`scale(${viewScale})`:'';
      if(!wasVertexDrag && (Math.abs(dx)>8||Math.abs(dy)>8)){
        // Momentum: if finger lifted with velocity, animate inertia then commit once
        const capScale=viewScale;
        if(Math.abs(vx)>1.5||Math.abs(vy)>1.5){
          let ix=dx, iy=dy, cvx=vx, cvy=vy;
          const FRICTION=0.88;
          function inertiaStep(){
            cvx*=FRICTION; cvy*=FRICTION;
            ix+=cvx; iy+=cvy;
            if(wrap) wrap.style.transform=`translate(${ix}px,${iy}px)${capScale!==1?` scale(${capScale})`:''}`;
            if(Math.abs(cvx)>0.5||Math.abs(cvy)>0.5){
              requestAnimationFrame(inertiaStep);
            } else {
              if(wrap) wrap.style.transform=capScale!==1?`scale(${capScale})`:'';
              commitPan(ix,iy);
              if(mode==="trace"){ mousePosRef.current=null; redrawLive(); }
            }
          }
          requestAnimationFrame(inertiaStep);
        } else {
          commitPan(dx,dy);
          if(mode==="trace"){ mousePosRef.current=null; redrawLive(); }
        }
        return;
      }
    }

    // ── Tap actions (drag was small or was a vertex drag) ───────────────────────
    if(mode===null) handleTap(e);
    if(mode==="trace" || mode==="mark" || mode==="erase" || mode==="text" || mode==="measure"){
      // Double-tap detection: two taps within 300 ms = finish trace
      const now = Date.now();
      if(now - lastTapRef.current < 300){
        lastTapRef.current = 0;
        handleDblTap(e);
      } else {
        lastTapRef.current = now;
        handleTap(e);
      }
    }
    // Clear rubber-band preview when finger lifts
    if(mode==="trace"){ mousePosRef.current = null; redrawLive(); }
  }

  // ── Tap handler ─────────────────────────────────────────────────────────────
  function handleTap(e){
    if(e.type==="touchend"||e.type==="touchstart") e.preventDefault();
    e.preventDefault();

    // Dismiss node popup on any tap elsewhere
    if(nodePopup){ setNodePopup(null); return; }

    // All modes — start pan tracking on mousedown (drag>5px commits pan in mouseup)
    if(e.type==="mousedown"){
      panDragRef.current = {startX:e.clientX, startY:e.clientY, dx:0, dy:0};
      // Don't return — let mode action fire below
    }

    const pos = getPos(e);

    // Existing findings reopen in mobile browse, Mark, and Edit modes.
    // Trace, measurement, text placement, and erase retain their own gestures.
    if(mode===null||mode==="mark"||mode==="edit"){
      const hitMarker=(job.markers||[]).find(m=>m.type!=="area"&&Math.hypot((m.x||0)-pos[0],(m.y||0)-pos[1])<26);
      if(hitMarker){ panDragRef.current=null; setPendingPhotoMarkerId(hitMarker.id); return; }
    }

    // EDIT mode — click shape to select, then drag vertices
    if(mode==="edit"){
      const drawings = job.drawings||[];

      // Helper: find the best shape index for a given tap position
      function findShapeAt(tapPos, excludeIdx){
        // 1. Prefer closed polygon the tap is inside
        for(let di=0;di<drawings.length;di++){
          if(di===excludeIdx) continue;
          const d=drawings[di];
          if(d.closed && d.pts.length>=3 && pointInPolygon(tapPos, d.pts)) return di;
        }
        // 2. Closest edge within 20px
        let bestIdx=-1, bestDist=20;
        drawings.forEach((d,di)=>{
          if(di===excludeIdx) return;
          const segs = d.pts.length;
          const limit = d.closed ? segs : segs - 1;
          for(let i=0;i<limit;i++){
            const [ax,ay]=d.pts[i], [bx,by]=d.pts[(i+1)%segs];
            const denom=(Math.pow(bx-ax,2)+Math.pow(by-ay,2))||1;
            const t=Math.max(0,Math.min(1,((tapPos[0]-ax)*(bx-ax)+(tapPos[1]-ay)*(by-ay))/denom));
            const dist=Math.hypot(tapPos[0]-(ax+t*(bx-ax)), tapPos[1]-(ay+t*(by-ay)));
            if(dist<bestDist){ bestDist=dist; bestIdx=di; }
          }
        });
        return bestIdx;
      }

      // If no shape selected, pick the best shape at tap point
      if(selectedShapeIdx===null){
        const idx = findShapeAt(pos, -1);
        setSelectedShapeIdx(idx >= 0 ? idx : null);
        return;
      }

      // Shape is selected — check vertex handles, then midpoint handles, then inside drag
      const sel = drawings[selectedShapeIdx];
      if(sel){
        // 0. Rotation handle hit? (any shape with 3+ points)
        if(sel.pts.length>=3){
          const {x:hx,y:hy,hitRadius}=rotationControl(sel.pts);
          if(Math.hypot(pos[0]-hx,pos[1]-hy)<=hitRadius){
            const [cx,cy]=shapeCentroid(sel.pts);
            rotateShapeRef.current={centroid:[cx,cy],startAngle:Math.atan2(pos[1]-cy,pos[0]-cx),origDrawings:job.drawings||[],origMarkers:job.markers||[],origMeasurements:job.measurements||[]};
            setIsRotatingShape(true);
            return;
          }
        }
        // 1. Vertex handle hit?
        let hitPt=-1;
        sel.pts.forEach((p,pi)=>{ if(Math.hypot(p[0]-pos[0],p[1]-pos[1])<16) hitPt=pi; });
        if(hitPt>=0){
          dragPtRef.current = hitPt;
          setDragPtIdx(hitPt);
          setIsDragging(true);
          return;
        }
        // 2. Midpoint handle hit? → insert new vertex and immediately drag it
        const segCount = sel.closed ? sel.pts.length : sel.pts.length - 1;
        let hitMid=-1;
        for(let i=0;i<segCount;i++){
          const a=sel.pts[i], b=sel.pts[(i+1)%sel.pts.length];
          const mx=(a[0]+b[0])/2, my=(a[1]+b[1])/2;
          if(Math.hypot(pos[0]-mx, pos[1]-my)<14){ hitMid=i; break; }
        }
        if(hitMid>=0){
          const newPts=[...sel.pts.slice(0,hitMid+1), pos, ...sel.pts.slice(hitMid+1)];
          updateJob({drawings:drawings.map((dd,i)=>i===selectedShapeIdx?{...dd,pts:newPts}:dd)});
          dragPtRef.current = hitMid+1;
          setDragPtIdx(hitMid+1);
          setIsDragging(true);
          return;
        }
        // 3. Inside the shape's footprint (open or closed) → drag whole shape
        if(sel.pts.length>=3 && pointInPolygon(pos, sel.pts)){
          dragShapeRef.current = { startPos: pos, origDrawings: job.drawings||[], origMarkers: job.markers||[], origMeasurements: job.measurements||[] };
          setIsDraggingShape(true);
          return;
        }
        // 4. Near any edge of the shape → drag whole shape
        {
          const limit = sel.closed ? sel.pts.length : sel.pts.length - 1;
          for(let i=0;i<limit;i++){
            const [ax,ay]=sel.pts[i], [bx,by]=sel.pts[(i+1)%sel.pts.length];
            const denom=(Math.pow(bx-ax,2)+Math.pow(by-ay,2))||1;
            const t=Math.max(0,Math.min(1,((pos[0]-ax)*(bx-ax)+(pos[1]-ay)*(by-ay))/denom));
            if(Math.hypot(pos[0]-(ax+t*(bx-ax)), pos[1]-(ay+t*(by-ay)))<20){
              dragShapeRef.current = { startPos: pos, origDrawings: job.drawings||[], origMarkers: job.markers||[], origMeasurements: job.measurements||[] };
              setIsDraggingShape(true);
              return;
            }
          }
        }
        // 5. Did the user click a different shape? Switch to it
        const otherIdx = findShapeAt(pos, selectedShapeIdx);
        if(otherIdx >= 0){ setSelectedShapeIdx(otherIdx); setDragPtIdx(null); return; }
        // 6. Empty space → deselect
        setSelectedShapeIdx(null); setDragPtIdx(null);
      }
      return;
    }

    // MARK mode
    if(mode==="mark"){
      const newMarkerId=Date.now().toString();
      const newMarker={id:newMarkerId, x:pos[0], y:pos[1], label:markerLabel, fontSize:markerSize+4, photos:[]};
      updateJob(placeFindingMarker(job,newMarker,settings.codebook||DEFAULT_CODEBOOK,settings));
      setPendingPhotoMarkerId(newMarkerId);
      return;
    }

    // ERASE mode
    if(mode==="erase"){
      const markers  = job.markers||[];
      const close    = markers.find(m=>Math.hypot(m.x-pos[0],m.y-pos[1])<30);
      if(close){ updateJob({markers:markers.filter(m=>m.id!==close.id)}); return; }
      // Remove a measurement if tapped near one
      const meas = job.measurements||[];
      let measIdx=-1, measDist=30;
      meas.forEach((m,mi)=>{
        if(!m||!m.a||!m.b) return;
        const [ax,ay]=m.a,[bx,by]=m.b;
        const t=Math.max(0,Math.min(1,((pos[0]-ax)*(bx-ax)+(pos[1]-ay)*(by-ay))/(Math.pow(bx-ax,2)+Math.pow(by-ay,2)||1)));
        const dist=Math.hypot(pos[0]-(ax+t*(bx-ax)), pos[1]-(ay+t*(by-ay)));
        if(dist<measDist){ measDist=dist; measIdx=mi; }
      });
      if(measIdx>=0){ updateJob({measurements:meas.filter((_,i)=>i!==measIdx)}); return; }
      const drawings = job.drawings||[];
      // Find closest shape by checking proximity to any segment
      let closestIdx = -1; let closestDist = 30;
      drawings.forEach((d,di)=>{
        const segCount = d.closed ? d.pts.length : d.pts.length - 1;
        for(let i=0;i<segCount;i++){
          const [ax,ay]=d.pts[i]; const [bx,by]=d.pts[(i+1)%d.pts.length];
          const t=Math.max(0,Math.min(1,((pos[0]-ax)*(bx-ax)+(pos[1]-ay)*(by-ay))/(Math.pow(bx-ax,2)+Math.pow(by-ay,2)||1)));
          const dist=Math.hypot(pos[0]-(ax+t*(bx-ax)), pos[1]-(ay+t*(by-ay)));
          if(dist<closestDist){ closestDist=dist; closestIdx=di; }
        }
      });
      if(closestIdx>=0){ updateJob({drawings:drawings.filter((_,i)=>i!==closestIdx)}); return; }
      // fallback: remove last
      if(drawings.length>0) updateJob({drawings:drawings.slice(0,-1)});
      return;
    }

    // TEXT mode — place area label if one is pending, else tap a shape to select it
    if(mode==="text"){
      if(pendingAreaLabel){
        updateJob({markers:[...(job.markers||[]),{id:Date.now().toString(),x:pos[0],y:pos[1],label:pendingAreaLabel,type:"area",fontSize:areaLabelSize}]});
        return;
      }
      const drawings = job.drawings||[];
      for(let di=0;di<drawings.length;di++){
        const d=drawings[di];
        if(d.closed && d.pts.length>=3 && pointInPolygon(pos, d.pts)){
          setLabelTargetIdx(di); return;
        }
      }
      let bestIdx=-1, bestDist=20;
      drawings.forEach((d,di)=>{
        const segs=d.pts.length, limit=d.closed?segs:segs-1;
        for(let i=0;i<limit;i++){
          const [ax,ay]=d.pts[i],[bx,by]=d.pts[(i+1)%segs];
          const denom=(Math.pow(bx-ax,2)+Math.pow(by-ay,2))||1;
          const t=Math.max(0,Math.min(1,((pos[0]-ax)*(bx-ax)+(pos[1]-ay)*(by-ay))/denom));
          const dist=Math.hypot(pos[0]-(ax+t*(bx-ax)),pos[1]-(ay+t*(by-ay)));
          if(dist<bestDist){ bestDist=dist; bestIdx=di; }
        }
      });
      if(bestIdx>=0) setLabelTargetIdx(bestIdx);
      return;
    }

    // MEASURE mode — drop a pin, pull to the next point, tap to lock the distance
    if(mode==="measure"){
      const snap = snapToExisting(pos, false);
      const p = snap || pos;
      if(!measureStart){
        // First tap drops the starting pin
        setMeasureStart(p);
        mousePosRef.current = p;
      } else {
        // Second tap locks in the measurement
        updateJob({measurements:[...(job.measurements||[]), {a:measureStart, b:p}]});
        setMeasureStart(null);
        mousePosRef.current = null;
      }
      redrawLive();
      return;
    }

    // TRACE mode — tap-to-place points
    if(mode==="trace"){
      // 1. Check close-to-own-first-point (close the polygon)
      // Use a larger hit target on touch devices (fingers are ~44px; internal canvas is 800px wide)
      const closeD = isTouchRef.current ? CLOSE_DIST * 2 : CLOSE_DIST;
      if(tracePts.length >= 3){
        if(Math.hypot(pos[0]-tracePts[0][0], pos[1]-tracePts[0][1]) <= closeD){
          updateJob({drawings:[...(job.drawings||[]), {pts:tracePts, style:traceStyle, closed:true}]});
          setTracePts([]);
          return;
        }
      }
      // 2. Apply 90° snap, then vertex/edge snap on top
      const snapped90 = applySnap90(pos);
      const snapped   = snapToExisting(snapped90, false);
      const edgeSnap  = !snapped ? snapToEdge(snapped90) : null;
      const finalPos  = snapped || (edgeSnap ? edgeSnap.pos : snapped90);

      // 3. Vertex snap — auto-finish if trace has ≥2 pts
      if(snapped && tracePts.length >= 2){
        updateJob({drawings:[...(job.drawings||[]), {pts:[...tracePts, snapped], style:traceStyle, closed:false}]});
        setTracePts([]);
        return;
      }

      // 4. Edge snap — insert a shared vertex into the existing shape, then place/finish
      if(edgeSnap){
        const {pos:snapPos, shapeIdx, edgeAfterIdx} = edgeSnap;
        const updatedDrawings = (job.drawings||[]).map((d,di)=>{
          if(di!==shapeIdx) return d;
          const newPts=[...d.pts];
          newPts.splice(edgeAfterIdx+1, 0, snapPos);
          return {...d, pts:newPts};
        });
        if(tracePts.length >= 2){
          // Finish trace, connecting to this edge
          updateJob({drawings:[...updatedDrawings, {pts:[...tracePts, snapPos], style:traceStyle, closed:false}]});
          setTracePts([]);
          return;
        }
        // First (or intermediate) point — insert vertex and continue
        updateJob({drawings:updatedDrawings});
        setTracePts(pts=>[...pts, snapPos]);
        return;
      }

      setTracePts(pts=>[...pts, finalPos]);
    }
  }

  function handleDblTap(e){
    e.preventDefault();
    const pos = getPos(e);
    const HIT = 22; // px — touch-friendly vertex hit radius

    // Check all shape vertices first (works in any mode)
    const drawings = job.drawings || [];
    for(let si = drawings.length - 1; si >= 0; si--){
      const d = drawings[si];
      for(let pi = 0; pi < d.pts.length; pi++){
        const p = d.pts[pi];
        if(Math.hypot(p[0]-pos[0], p[1]-pos[1]) < HIT){
          // Switch to edit mode and open node popup
          setMode("edit");
          setSelectedShapeIdx(si);
          setDragPtIdx(pi);
          setNodePopup({ x: p[0], y: p[1], shapeIdx: si, ptIdx: pi });
          return;
        }
      }
    }

    // Default: finish open trace line
    if(mode==="trace" && tracePts.length>=2){
      updateJob({drawings:[...(job.drawings||[]), {pts:tracePts, style:traceStyle, closed:false}]});
      setTracePts([]);
    }
  }

  function expandPolygon(pts, px){
    if(pts.length < 3) return pts;
    const cx = pts.reduce((s,p)=>s+p[0],0)/pts.length;
    const cy = pts.reduce((s,p)=>s+p[1],0)/pts.length;
    return pts.map(([x,y])=>{ const dx=x-cx, dy=y-cy, len=Math.hypot(dx,dy)||1; return [x+dx/len*px, y+dy/len*px]; });
  }
  function cancelTrace(){ setTracePts([]); mousePosRef.current = null; redrawLive(); }
  function undoLastPoint(){ setTracePts(pts=>{ const n=pts.slice(0,-1); if(n.length===0){ mousePosRef.current = null; redrawLive(); } return n; }); }

  // Core loader — accepts explicit zoom; defaults to 19, retries at 18 if no imagery
  async function reloadAt(zoom){
    // Reloading replaces the background, so if there's no trace to misalign we
    // adopt the taller canvas here (matching a fresh new job). Traced jobs keep
    // their original height. These shadow the component-level CH/MIH.
    const emptyTrace = !((job.drawings||[]).length) && !((job.markers||[]).length);
    const CH  = job.canvasH || (emptyTrace ? NEW_CANVAS_H : CANVAS_H);
    const MIH = Math.round(CH * MAP_IMG_W / CANVAS_W);
    const key = process.env.REACT_APP_MAPBOX_KEY || settings.mapboxKey || DEFAULT_MAPBOX_KEY;
    if(!key){ setMapError("Mapbox API key not configured"); return; }
    if(!job.address){ setMapError("Add property address in Job Info first"); return; }
    if(typeof navigator!=='undefined' && navigator.onLine===false){ setMapInfo("You're offline — connect to load satellite imagery. You can still trace, mark findings, and take photos on the current view."); return null; }
    setLoadingMap(true); setMapError("");
    try{
      const center = job.mapCenter;
      let lat, lng, county, mapBounds=null;
      if(center){ lat=center.lat; lng=center.lng; county=job.county; mapBounds=job.mapBounds||null; }
      else {
        const geo = await geocodeAddress(`${job.address} ${job.city} ${job.state}`, key);
        if(!geo){ setMapError("Address not found"); setLoadingMap(false); return; }
        lat=geo.lat; lng=geo.lng; county=geo.county; mapBounds=geo.bounds||null;
      }
      const tryLoad = (z) => new Promise((resolve,reject)=>{
        // Request tile at MAP_IMG_W × MIH (640×400, matching the canvas 8:5 ratio).
        // The tile is drawn in cover-mode (stretched to fill CANVAS_W × CH) so
        // there are no black bars and the full canvas area shows satellite imagery.
        const url = staticMapUrl(lat, lng, z, MAP_IMG_W, MIH, key, "satellite");
        const img = new Image(); img.crossOrigin="anonymous";
        img.onload = ()=>{
          try {
            const c=document.createElement("canvas"); c.width=CANVAS_W; c.height=CH;
            const ctx2=c.getContext("2d");
            ctx2.drawImage(img, 0, 0, CANVAS_W, CH);
            // Sample 5 patches spread across the image — a road/roof only at
            // centre won't falsely reject a valid tile.
            const checkPts=[[CANVAS_W*.25,CH*.25],[CANVAS_W*.75,CH*.25],[CANVAS_W*.25,CH*.75],[CANVAS_W*.75,CH*.75],[CANVAS_W*.5,CH*.5]];
            let totalGrey=0,totalPx=0;
            for(const [sx,sy] of checkPts){const pd=ctx2.getImageData(Math.round(sx-6),Math.round(sy-6),12,12).data;for(let i=0;i<pd.length;i+=4){const d=Math.max(pd[i],pd[i+1],pd[i+2])-Math.min(pd[i],pd[i+1],pd[i+2]);if(d<15)totalGrey++;totalPx++;}}
            if(totalGrey/totalPx>0.99 && z>17){ reject('grey'); }
            else resolve({dataUrl:c.toDataURL("image/jpeg",0.92), actualZoom:z});
          } catch(e) { reject(e); }
        };
        img.onerror = ()=>reject(new Error("Satellite image failed to load — check Mapbox API key or network"));
        img.src = url;
      });
      let result;
      let tryZoom = zoom;
      let netAttempts = 0;
      while (!result) {
        try {
          result = await tryLoad(tryZoom);
        } catch(e) {
          if (e === 'grey' && tryZoom > 17) { tryZoom--; continue; }
          if (e instanceof Error && netAttempts < 2) {
            netAttempts++;
            const delay = 1000 * netAttempts;
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          throw e instanceof Error ? e : new Error("Satellite image failed to load");
        }
      }
      updateJob({mapBg:result.dataUrl, mapCenter:{lat,lng}, mapZoom:result.actualZoom,
                 mapPixelScale: MAP_PIXEL_SCALE, mapRotation: 0, canvasH: CH,
                 county:county||job.county, ...(mapBounds?{mapBounds}:{})});
      bgImgRef.current=null; bgSrcRef.current=null;
      setLoadingMap(false);
      return {lat, lng, zoom: result.actualZoom};
    } catch(e) { setMapError(e?.message || "Error loading map"); setLoadingMap(false); return null; }
  }
  function loadSatellite(){ return reloadAt(job.mapZoom||19); }
  async function autoLoadAndTrace(){
    const result = await reloadAt(job.mapZoom||19);
    if(result) await autoTraceBuilding(result);
  }
  async function changeZoom(delta){
    if(typeof navigator!=='undefined' && navigator.onLine===false){ setMapInfo("Offline — pinch to zoom the current image; connect to load a sharper tile."); return; }
    const cur = job.mapZoom||19;
    const next = Math.min(22, Math.max(17, cur+delta));
    if(next===cur) return;
    // Reset CSS visual zoom when changing tile zoom — keeps both systems independent
    setViewScale(1);
    if(mapWrapRef.current){ mapWrapRef.current.style.transform=''; mapWrapRef.current.style.transformOrigin=''; }
    const result = await reloadAt(next);
    if(!result) return;
    const actualDelta = result.zoom - cur;
    if(actualDelta === 0) return;
    const scale = Math.pow(2, actualDelta);
    const cx = CANVAS_W/2, cy = CH/2;
    const scalePt = ([x,y]) => [cx + (x-cx)*scale, cy + (y-cy)*scale];
    const drawings = (job.drawings||[]).map(d => ({...d, pts: d.pts.map(scalePt)}));
    const markers  = (job.markers||[]).map(m => ({...m, x: cx+(m.x-cx)*scale, y: cy+(m.y-cy)*scale}));
    updateJob({drawings, markers});
  }

  async function commitPan(dxCSS, dyCSS){
    if(!job.mapBg || !job.mapCenter || loadingMap) return;
    if(typeof navigator!=='undefined' && navigator.onLine===false){
      // Snap the live CSS pan back so canvas coordinates stay consistent; imagery
      // can't be recentered without a connection, but tracing/marking is unaffected.
      if(mapWrapRef.current){ mapWrapRef.current.style.transform=''; mapWrapRef.current.style.transformOrigin=''; }
      setMapInfo("Offline — panning needs a connection to refresh imagery. Pinch-zoom still works on the current view.");
      return;
    }
    const canvas = canvasRef.current;
    if(!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const panPxX = dxCSS * (CANVAS_W / rect.width);
    const panPxY = dyCSS * (CH / rect.height);
    const zoom = job.mapZoom || 19;
    // Find the lat/lng now at the canvas center after the pan
    const [newLat, newLng] = canvasPxToLatLng(
      CANVAS_W/2 - panPxX, CH/2 - panPxY,
      job.mapCenter.lat, job.mapCenter.lng,
      zoom, CANVAS_W, CH, job.mapRotation||0, MAP_PIXEL_SCALE
    );
    // Shift all drawings and markers so they stay geographically aligned
    const drawings = (job.drawings||[]).map(d=>({...d, pts:d.pts.map(([x,y])=>[x+panPxX,y+panPxY])}));
    const markers  = (job.markers||[]).map(m=>({...m, x:m.x+panPxX, y:m.y+panPxY}));
    // Load new satellite tile at new center
    const key = process.env.REACT_APP_MAPBOX_KEY || settings.mapboxKey || DEFAULT_MAPBOX_KEY;
    if(!key){ setMapError("Mapbox API key not configured"); return; }
    setLoadingMap(true); setMapError("");
    try {
      const url = staticMapUrl(newLat, newLng, zoom, MAP_IMG_W, MIH, key, "satellite");
      const mapBg = await new Promise((resolve, reject)=>{
        const img = new Image(); img.crossOrigin="anonymous";
        img.onload = ()=>{
          const c=document.createElement("canvas"); c.width=CANVAS_W; c.height=CH;
          c.getContext("2d").drawImage(img,0,0,CANVAS_W,CH);
          resolve(c.toDataURL("image/jpeg",0.92));
        };
        img.onerror = ()=>reject(new Error("Satellite image failed to load"));
        img.src = url;
      });
      bgImgRef.current=null; bgSrcRef.current=null;
      updateJob({mapBg, mapCenter:{lat:newLat,lng:newLng}, mapZoom:zoom, drawings, markers});
      setLoadingMap(false);
    } catch(err){ setMapError(err?.message||"Pan failed"); setLoadingMap(false); }
  }

  function addQuickPhoto(files){
    const mid=pendingPhotoMarkerId;
    const candidates=findingsForMarker(job,(job.markers||[]).find(m=>m.id===mid)||{id:mid});
    const target=candidates.find(f=>f.id===photoTargetId)||candidates.find(f=>f.kind==='repair')||candidates[0];
    Array.from(files).forEach(file=>{
      if(!file.type.startsWith("image/")) return;
      // Trigger iOS share sheet immediately while still inside the user-gesture chain.
      // navigator.canShare gating is intentionally skipped — it returns false on many iOS
      // configs even when sharing works. The try/catch handles unsupported cases.
      if(navigator.share){
        try{
          const sf=new File([file],`inspection-${Date.now()}.jpg`,{type:file.type||'image/jpeg'});
          navigator.share({files:[sf]}).catch(()=>{});
        }catch(e){}
      }
      const reader=new FileReader();
      reader.onload=ev=>{
        const img=new window.Image();
        img.onload=()=>{
          const maxW=900, scale=img.width>maxW?maxW/img.width:1;
          const c=document.createElement("canvas");
          c.width=Math.round(img.width*scale); c.height=Math.round(img.height*scale);
          c.getContext("2d").drawImage(img,0,0,c.width,c.height);
          const data=c.toDataURL("image/jpeg",0.82);
          const newPhoto={id:Date.now().toString()+Math.random(),data,caption:""};
          // Attach the photo to the finding(s) this marker created, so it is
          // structurally bound to that finding (2A, 2B…) and shows everywhere the
          // finding does. Fall back to the marker only when no finding is linked.
          // Functional updater reads the freshest job — bursts of photos no longer
          // clobber each other via a stale render closure.
          updateJob(j=>changeMarkerPhotos(j,mid,list=>[...list,newPhoto],target?.id));
        };
        img.src=ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  const traceActive = mode==="trace" && tracePts.length>0;
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Re-evaluate the mobile layout when the viewport changes (rotation, split view).
  const [, setViewportTick] = useState(0);
  useEffect(() => {
    const onResize = () => setViewportTick(t => t + 1);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("orientationchange", onResize); };
  }, []);

  const mob = window.innerWidth < 520;
  const overlayPos = mob
    ? {position:"absolute",top:"calc(100% + 62px)",right:0,width:70,maxHeight:"min(55dvh,440px)",zIndex:50,overflowY:"auto",borderRadius:12}
    : {position:"absolute",left:"calc(100% + 8px)",top:0,zIndex:20};

  return(
    <div>
      {volCalcOpen && (() => {
        const meas = job.measurements || [];
        const setMeta = (si, patch) => {
          const next = [...(job.volStructures || [])];
          while (next.length <= si) next.push({});
          next[si] = { ...next[si], ...patch };
          updateJob({ volStructures: next });
        };
        const heightPresets = (loadFumeHeights() || DEFAULT_FUME_HEIGHTS).map(h => h.ft);
        const structures = [];
        for (let i = 0; i < meas.length; i += 2) {
          const si = i / 2;
          structures.push({
            si,
            L: meas[i]   ? Math.round(edgeLengthFt(meas[i].a,   meas[i].b))   : 0,
            W: meas[i+1] ? Math.round(edgeLengthFt(meas[i+1].a, meas[i+1].b)) : 0,
            meta: (job.volStructures || [])[si] || {},
          });
        }
        const volOf = s => { const h = Number(s.meta.heightFt) || 0; return (s.W > 0 && h > 0) ? s.L * s.W * h : 0; };
        const total = structures.reduce((sum, s) => sum + volOf(s), 0);
        return (
          <div onClick={() => setVolCalcOpen(false)} style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:460,width:"100%",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.35)"}}>
              <div style={{position:"sticky",top:0,background:"#fff",borderBottom:"1px solid #eef2f7",padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:"16px 16px 0 0"}}>
                <span style={{display:"flex",alignItems:"center",gap:8,fontWeight:800,fontSize:16,color:"#0f172a"}}><CalculatorIcon size={18}/> Volume Calculator</span>
                <button onClick={() => setVolCalcOpen(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:"#94a3b8",lineHeight:1}}>×</button>
              </div>
              <div style={{padding:"12px 16px 16px"}}>
                <div style={{fontSize:12,color:"#64748b",marginBottom:12,lineHeight:1.4}}>
                  Auto-paired from your {meas.length} measurements — 1st = length, 2nd = width of each structure. Add a height to get cubic feet.
                </div>
                {structures.map(s => {
                  const h = Number(s.meta.heightFt) || 0;
                  const vol = volOf(s);
                  return (
                    <div key={s.si} style={{border:"1px solid #e2e8f0",borderRadius:12,padding:12,marginBottom:10,background:"#f8fafc"}}>
                      <input value={s.meta.name || ""} placeholder={`Structure ${s.si + 1}`} onChange={e => setMeta(s.si, {name:e.target.value})}
                        style={{width:"100%",border:"none",background:"transparent",fontWeight:800,fontSize:14,color:"#0f172a",marginBottom:8,outline:"none"}}/>
                      {s.W <= 0 ? (
                        <div style={{fontSize:12,color:"#b45309",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"6px 8px"}}>
                          Only one side measured ({s.L} ft). Measure a second side (width) to complete this structure.
                        </div>
                      ) : (
                        <>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:14,color:"#334155",fontWeight:600}}>
                            <span>{s.L} ft</span><span style={{color:"#94a3b8"}}>×</span>
                            <span>{s.W} ft</span><span style={{color:"#94a3b8"}}>×</span>
                            <input type="number" inputMode="numeric" value={s.meta.heightFt || ""} placeholder="H"
                              onChange={e => setMeta(s.si, {heightFt:e.target.value})}
                              style={{width:56,padding:"6px 8px",border:"1px solid #cbd5e1",borderRadius:8,fontSize:14,fontWeight:700,textAlign:"center"}}/>
                            <span style={{color:"#64748b"}}>ft H</span>
                          </div>
                          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
                            {heightPresets.map(ft => (
                              <button key={ft} onClick={() => setMeta(s.si, {heightFt:String(ft)})}
                                style={{padding:"4px 9px",borderRadius:999,border:"1px solid",borderColor:h===ft?"#2563eb":"#e2e8f0",background:h===ft?"#2563eb":"#fff",color:h===ft?"#fff":"#475569",fontSize:12,fontWeight:700,cursor:"pointer"}}>{ft}′</button>
                            ))}
                          </div>
                          <div style={{marginTop:10,textAlign:"right",fontWeight:800,fontSize:15,color:vol>0?"#1d4ed8":"#94a3b8"}}>
                            {vol>0 ? vol.toLocaleString()+" cu ft" : "← set a height"}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:6,padding:"14px",background:"#0f172a",borderRadius:12,color:"#fff"}}>
                  <span style={{fontWeight:700,fontSize:14}}>Total volume</span>
                  <span style={{fontWeight:900,fontSize:20}}>{total.toLocaleString()} <span style={{fontSize:13,fontWeight:700,opacity:0.8}}>cu ft</span></span>
                </div>
                <div style={{fontSize:11,color:"#94a3b8",marginTop:8,textAlign:"center"}}>
                  {meas.length} measurement{meas.length!==1?"s":""} → {structures.length} structure{structures.length!==1?"s":""}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <div style={fullscreen ? {...S.card,padding:"8px",position:"fixed",inset:0,zIndex:1000,margin:0,borderRadius:0,maxWidth:"none",overflowY:"auto"} : {...S.card,padding:"10px 8px"}} className={`ts-map-card${fullscreen?" is-fullscreen":""}`}>

        {/* ── Two-column layout: sidebar controls left, canvas right ── */}
        <div style={{display:"flex",gap:6,alignItems:mob?"stretch":"flex-start",position:"relative",flexDirection:mob?"column":"row"}}>

          {/* ── LEFT SIDEBAR — 82px icon+label strip (full-width top bar on mobile) ── */}
          <div className="ts-diagram-tools" style={{width:mob?"100%":82,flexShrink:0,display:"flex",flexDirection:mob?"row":"column",flexWrap:mob?"wrap":"nowrap",gap:mob?4:8,alignItems:mob?"stretch":"stretch",...(!mob?{position:"sticky",top:60,alignSelf:"flex-start",zIndex:10,background:"#fff",borderRadius:20,border:"1.5px solid #e2e8f0",boxShadow:"0 2px 14px rgba(0,0,0,0.08)",padding:"10px 6px"}:{})}}>
            {/* Auto-Trace — hidden on mobile when setup row is collapsed */}
            {job.mapCenter && (!mob || mobSetupOpen) && (
              <button
                className="ts-map-setting" style={{...S.btn("#f97316"),fontSize:12,padding:"8px 4px",fontWeight:700,opacity:autoTracing?0.65:1,lineHeight:1.2,...(mob?{flex:"1 1 45%"}:{})}}
                onClick={()=>autoTraceBuilding()} disabled={autoTracing}
                title={autoTracing?"Tracing…":"Auto-Trace All"}>
                {autoTracing ? "⏳ Tracing…" : "✦ Trace All"}
              </button>
            )}

            {/* Reload / Load Map — hidden on mobile when setup row is collapsed */}
            {(!mob || mobSetupOpen) && (
              <button className="ts-map-setting" style={{...S.btn("#22d3ee"),fontSize:12,padding:"8px 4px",fontWeight:700,opacity:(!job.address&&!loadingMap)?0.55:1,lineHeight:1.2,...(mob?{flex:1}:{})}}
                onClick={loadSatellite} disabled={loadingMap}
                title={!job.address?"Enter address in Job Info":"Load satellite imagery"}>
                {loadingMap ? "⏳ Loading…" : job.mapBg ? "↺ Reload" : "🛰 Load Map"}
              </button>
            )}

            {/* Zoom + / level / − (desktop only — canvas has floating +/- on mobile) */}
            {job.mapBg && !mob && (
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <button style={{...S.btnSm("#0f172a"),fontSize:18,lineHeight:1,padding:"9px 0",fontWeight:700,width:"100%"}}
                  onClick={()=>changeZoom(0.25)} disabled={loadingMap||(job.mapZoom||19)>=22} title="Zoom in">+</button>
                <span style={{fontSize:11,fontWeight:700,color:"#1e40af",textAlign:"center",padding:"2px 0"}}>{parseFloat((job.mapZoom||19).toFixed(2))}</span>
                <button style={{...S.btnSm("#0f172a"),fontSize:18,lineHeight:1,padding:"9px 0",fontWeight:700,width:"100%"}}
                  onClick={()=>changeZoom(-0.25)} disabled={loadingMap||(job.mapZoom||19)<=17} title="Zoom out">−</button>
                {viewScale > 1 && (
                  <button
                    style={{...S.btnSm("#7c3aed"),fontSize:10,padding:"3px 0",fontWeight:700,width:"100%"}}
                    onClick={()=>{ setViewScale(1); if(mapWrapRef.current){ mapWrapRef.current.style.transform=''; mapWrapRef.current.style.transformOrigin=''; } }}
                    title="Reset pinch zoom">×{viewScale.toFixed(1)}</button>
                )}
              </div>
            )}

            {/* Mode buttons — wrapped in relative div so overlays anchor here */}
            <div className="ts-diagram-modes" style={{position:"relative",...(mob?{flex:"1 1 0%"}:{})}}>              <div className="ts-diagram-mode-row" role="group" aria-label="Drawing tools" style={{display:"flex",flexDirection:mob?"row":"column",gap:2,background:mob?"#f1f5f9":"transparent",borderRadius:8,padding:mob?3:0,overflowX:"visible",flexWrap:"nowrap",justifyContent:mob?"space-around":"flex-start"}}>
                {[["trace","✏️","Trace"],["mark","📍","Mark"],["measure","📏","Measure"],["edit","⬡","Edit"],["erase","⌫","Erase"],["text","🏷","Text"]].map(([k,icon,label])=>(
                  <button key={k} type="button" aria-label={label} aria-pressed={mode===k}
                    style={{...S.btnSm(mode===k?"#0f172a":"transparent"),
                      color:mode===k?"#fff":"#64748b",
                      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                      gap:2,padding:mob?"5px 4px":"10px 4px",width:mob?"auto":"100%",flex:mob?"1 1 0":undefined,flexShrink:0,minWidth:0,lineHeight:1.1,minHeight:mob?36:46,
                      position:"relative"}}
                    onMouseEnter={()=>!mob&&setHoveredTool(k)}
                    onMouseLeave={()=>setHoveredTool(null)}
                    onClick={()=>{ setMode(k); if(k!=="trace") setTracePts([]); if(k!=="measure") setMeasureStart(null); if(k!=="edit"){ setSelectedShapeIdx(null); setDragPtIdx(null); } if(k!=="text"){ setLabelTargetIdx(null); setPendingAreaLabel(""); } if(mob) setMobSetupOpen(false); }}>
                    <span style={{fontSize:mob?18:22,display:"flex",alignItems:"center"}}>{mob?<DiagramToolIcon tool={k}/>:icon}</span>
                    {mob && <span style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.03em"}}>{label}</span>}
                    {!mob && hoveredTool===k && (
                      <div style={{position:"absolute",left:"calc(100% + 10px)",top:"50%",transform:"translateY(-50%)",
                        background:"rgba(15,23,42,0.95)",color:"#f8fafc",
                        fontSize:12,fontWeight:600,whiteSpace:"nowrap",
                        padding:"5px 10px",borderRadius:8,
                        border:"1px solid rgba(255,255,255,0.12)",
                        boxShadow:"0 4px 14px rgba(0,0,0,0.45)",
                        pointerEvents:"none",zIndex:999}}>
                        {label}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Category picker — Mark mode */}
              {mode==="mark" && (
                <div style={{
                  ...overlayPos,
                  background:"rgba(15,23,42,0.95)",borderRadius:mob?undefined:10,
                  padding:mob?"6px 4px":"8px 8px",
                  border:"1px solid rgba(255,255,255,0.12)",
                  boxShadow:"0 4px 20px rgba(0,0,0,0.5)",
                  display:"flex",flexDirection:"column",gap:mob?3:6,minWidth:mob?0:148,
                }}>
                  {/* Title + close */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2}}>
                    <div style={{fontSize:mob?9:10,color:WDO_CATEGORIES.find(c=>c.num===selectedCat)?.color||"#22d3ee",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.03em",flex:1,textAlign:"center",lineHeight:1.2}}>
                      {mob ? markerLabel : `Place ${markerLabel}`}
                    </div>
                    {mob && <button onClick={()=>setMode(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#94a3b8",padding:0,lineHeight:1,flexShrink:0}}>×</button>}
                  </div>
                  {/* Category numbers — single column on mobile, two rows on desktop */}
                  {mob ? (
                    <div style={{display:"flex",flexDirection:"column",gap:3}}>
                      {WDO_CATEGORIES.map(c=>(
                        <button key={c.num}
                          style={{...S.btnSm(selectedCat===c.num?c.color:"transparent"),
                            height:30,width:"100%",padding:0,
                            color:selectedCat===c.num?"#fff":"#94a3b8",
                            fontWeight:700,fontSize:13,
                            border:selectedCat===c.num?"2px solid rgba(255,255,255,0.6)":"1px solid rgba(255,255,255,0.18)",
                            borderRadius:7,
                            boxShadow:selectedCat===c.num?"0 0 0 2px rgba(255,255,255,0.15)":"none"}}
                          onClick={()=>{ setSelectedCat(c.num); setMarkerLabel(String(c.num)+"A"); }}
                          title={c.label}>{c.num}</button>
                      ))}
                    </div>
                  ) : (
                    [WDO_CATEGORIES.slice(0,3),WDO_CATEGORIES.slice(3)].map((row,ri)=>(
                      <div key={ri} style={{display:"flex",gap:4}}>
                        {row.map(c=>(
                          <button key={c.num}
                            style={{...S.btnSm(selectedCat===c.num?c.color:"transparent"),
                              flex:1,height:40,padding:0,
                              color:selectedCat===c.num?"#fff":"#94a3b8",
                              fontWeight:700,fontSize:16,
                              border:selectedCat===c.num?"2px solid rgba(255,255,255,0.6)":"1px solid rgba(255,255,255,0.18)",
                              borderRadius:8,
                              boxShadow:selectedCat===c.num?"0 0 0 2px rgba(255,255,255,0.15)":"none"}}
                            onClick={()=>{ setSelectedCat(c.num); setMarkerLabel(String(c.num)+"A"); }}
                            title={c.label}>{c.num}</button>
                        ))}
                        {ri===1 && <div style={{flex:1}}/>}
                      </div>
                    ))
                  )}
                  {/* Sub-category toggles — single column on mobile */}
                  <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"repeat(3,1fr)",gap:mob?3:4}}>
                    {["A","B","C"].map(sub=>{
                      const lbl=selectedCat+sub;
                      const parts=markerLabel?markerLabel.split("+"):[];
                      const isActive=parts.includes(lbl);
                      const catColor=WDO_CATEGORIES.find(c=>c.num===selectedCat)?.color||"#94a3b8";
                      return (
                        <button key={sub}
                          style={{...S.btnSm(isActive?"#fff":"transparent"),
                            height:mob?28:40,padding:0,
                            color:isActive?"#0f172a":catColor,
                            fontWeight:800,fontSize:mob?11:14,
                            border:isActive?"2px solid rgba(255,255,255,0.8)":`1px solid ${catColor}44`,
                            borderRadius:mob?7:8}}
                          onClick={()=>{
                            const cur=markerLabel?markerLabel.split("+"):[];
                            const next=isActive?cur.filter(p=>p!==lbl):[...cur,lbl].sort();
                            if(next.length>0) setMarkerLabel(next.join("+"));
                          }}
                          title={"Toggle "+lbl}>{lbl}</button>
                      );
                    })}
                  </div>
                  {/* Size control — vertical slider on mobile, horizontal on desktop */}
                  {mob ? (
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flex:1,justifyContent:"flex-end",paddingBottom:6,paddingTop:4}}>
                      <span style={{fontSize:9,color:"#64748b",fontWeight:700,lineHeight:1}}>A</span>
                      <input type="range" min={2} max={18} value={markerSize}
                        onChange={e=>{const sz=Number(e.target.value);setMarkerSize(sz);updateJob({markers:(job.markers||[]).map(m=>m.type!=='area'?{...m,fontSize:sz+4}:m)});}}
                        style={{
                          WebkitAppearance:"slider-vertical",
                          writingMode:"vertical-lr",
                          direction:"rtl",
                          height:90,width:28,
                          accentColor:"#22d3ee",cursor:"pointer"
                        }}
                        title="Marker size"/>
                      <span style={{fontSize:7,color:"#64748b",fontWeight:700,lineHeight:1}}>a</span>
                    </div>
                  ) : (
                    <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                      <span style={{fontSize:11,color:"#64748b",fontWeight:700,textTransform:"uppercase",flexShrink:0}}>Size</span>
                      <input type="range" min={2} max={18} value={markerSize}
                        onChange={e=>{const sz=Number(e.target.value);setMarkerSize(sz);updateJob({markers:(job.markers||[]).map(m=>m.type!=='area'?{...m,fontSize:sz+4}:m)});}}
                        style={{flex:1,minWidth:0,width:0,accentColor:"#22d3ee",cursor:"pointer"}}
                        title="Marker size"/>
                    </div>
                  )}
                </div>
              )}

              {/* Line style — Trace mode */}
              {mode==="trace" && (
                <div style={{
                  ...overlayPos,
                  background:"rgba(248,250,252,0.97)",borderRadius:10,
                  padding:"8px 6px",
                  border:"1px solid #e2e8f0",
                  boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
                  display:"flex",flexDirection:"column",gap:5,...(mob?{}:{minWidth:140}),
                }}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:mob?9:11,color:"#64748b",fontWeight:700}}>{mob?"Style":"Line style"}</span>
                    {mob && <button onClick={()=>setMode(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#94a3b8",padding:"0 2px",lineHeight:1}}>×</button>}
                  </div>
                  {[["solid","—","—— Solid"],["dashed","- -","- - Dashed"],["dotted","···","··· Dotted"]].map(([s,icon,lbl])=>{
                    const active=traceStyle===s;
                    return <button key={s}
                      style={{...S.btnSm(active?"#1e40af":"#f1f5f9"),fontSize:mob?11:12,padding:mob?"6px 4px":"8px 10px",color:active?"#fff":"#64748b"}}
                      onClick={()=>setTraceStyle(s)}>
                      {mob?icon:lbl}
                    </button>;
                  })}
                  <div style={{height:1,background:"#e2e8f0",margin:"2px 0"}}/>
                  <span style={{fontSize:mob?9:11,color:"#64748b",fontWeight:700}}>Snap</span>
                  <button
                    style={{
                      ...S.btnSm(snap90?"#22d3ee":"#f1f5f9"),
                      fontSize:mob?11:13,
                      padding:mob?"8px 4px":"10px 10px",
                      color:snap90?"#0f172a":"#475569",
                      fontWeight:700,
                      border: snap90?"none":"2px dashed #cbd5e1",
                    }}
                    onClick={()=>setSnap90(s=>!s)}
                    title="Force right angles between points">
                    ⊿ 90° Snap{snap90?" ON":""}
                  </button>
                </div>
              )}

              {/* Measure tool — Measure mode */}
              {mode==="measure" && (
                <div style={{
                  ...overlayPos,
                  background:"rgba(248,250,252,0.97)",borderRadius:10,
                  padding:"8px 8px",
                  border:"1px solid #e2e8f0",
                  boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
                  display:"flex",flexDirection:"column",gap:6,...(mob?{}:{minWidth:160}),
                }}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:mob?9:11,color:"#64748b",fontWeight:700}}>📏 Measure</span>
                    {mob && <button onClick={()=>setMode(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#94a3b8",padding:"0 2px",lineHeight:1}}>×</button>}
                  </div>
                  <div style={{fontSize:11,color:"#64748b",lineHeight:1.4}}>
                    {measureStart ? "Tap the end point to lock the distance." : "Tap a corner to drop a pin, then drag to the next corner."}
                  </div>
                  {measureStart && (
                    <button
                      style={{...S.btnSm("#f1f5f9"),fontSize:12,padding:"8px 10px",color:"#475569",fontWeight:700}}
                      onClick={()=>{ setMeasureStart(null); mousePosRef.current=null; redrawLive(); }}>
                      ✕ Cancel pin
                    </button>
                  )}
                  {(job.measurements||[]).length >= 2 && (
                    <button
                      style={{...S.btnSm("#eff6ff"),fontSize:12,padding:"8px 10px",color:"#1d4ed8",fontWeight:800,border:"1px solid #bfdbfe",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
                      onClick={()=>setVolCalcOpen(true)} title="Calculate fumigation volume from your measurements">
                      <CalculatorIcon/> Calculate volume
                    </button>
                  )}
                  {(job.measurements||[]).length > 0 && (
                    <button
                      style={{...S.btnSm("#fef2f2"),fontSize:12,padding:"8px 10px",color:"#dc2626",fontWeight:700,border:"1px solid #fecaca"}}
                      onClick={()=>{ setMeasureStart(null); mousePosRef.current=null; updateJob({measurements:[], volStructures:[]}); }}>
                      🗑 Clear {(job.measurements||[]).length} measurement{(job.measurements||[]).length>1?"s":""}
                    </button>
                  )}
                </div>
              )}

              {/* Text labels — Text mode */}
              {mode==="text" && (
                <div style={{
                  ...overlayPos,
                  background:"rgba(248,250,252,0.97)",borderRadius:10,
                  padding:"8px 6px",
                  border:"1px solid #e2e8f0",
                  boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
                  display:"flex",flexDirection:"column",gap:5,
                  ...(mob?{}:{minWidth:140,maxHeight:"calc(100vh - 180px)",overflowY:"auto"}),
                }}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:11,color:"#64748b",fontWeight:700}}>Label</span>
                    {mob && <button onClick={()=>setMode(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#94a3b8",padding:"0 2px",lineHeight:1}}>×</button>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr":"1fr",gap:4}}>
                    {["Garage","Deck","Patio Cover","Shed","Fence","Carport","Pool","Steps"].map(lbl=>{
                      const active=pendingAreaLabel===lbl;
                      return <button key={lbl}
                        style={{...S.btnSm(active?"#0f172a":"#f1f5f9"),fontSize:12,padding:"8px 10px",color:active?"#fff":"#64748b"}}
                        onClick={()=>setPendingAreaLabel(active?"":lbl)}>
                        {lbl}
                      </button>;
                    })}
                    {(settings.customAreaLabels||[]).map(lbl=>{
                      const active=pendingAreaLabel===lbl;
                      return <div key={lbl} style={{position:"relative",display:"flex"}}>
                        <button
                          style={{...S.btnSm(active?"#0f172a":"#f1f5f9"),fontSize:12,padding:"8px 10px",color:active?"#fff":"#64748b",flex:1,paddingRight:22}}
                          onClick={()=>setPendingAreaLabel(active?"":lbl)}>
                          {lbl}
                        </button>
                        <button onClick={e=>{e.stopPropagation();setSettings(s=>({...s,customAreaLabels:(s.customAreaLabels||[]).filter(x=>x!==lbl)}));if(pendingAreaLabel===lbl)setPendingAreaLabel("");}}
                          style={{position:"absolute",right:3,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:11,color:active?"#94a3b8":"#94a3b8",lineHeight:1,padding:"1px 3px"}}>×</button>
                      </div>;
                    })}
                  </div>
                  {customLabelInput===null
                    ? <button
                        style={{...S.btnSm("#6366f1"),fontSize:12,padding:"8px 10px"}}
                        onClick={()=>setCustomLabelInput(pendingAreaLabel||"")}>
                        ✎ Custom…
                      </button>
                    : <div style={{display:"flex",gap:4,alignItems:"center"}}>
                        <input
                          autoFocus
                          style={{...S.input,flex:1,fontSize:13,padding:"6px 8px"}}
                          placeholder="Enter label…"
                          value={customLabelInput}
                          onChange={e=>setCustomLabelInput(e.target.value)}
                          onKeyDown={e=>{
                            if(e.key==="Enter"){
                              const trimmed=customLabelInput.trim();
                              if(trimmed){
                                setPendingAreaLabel(trimmed);
                                const builtIn=["Garage","Deck","Patio Cover","Shed","Fence","Carport","Pool","Steps"];
                                const saved=settings.customAreaLabels||[];
                                if(!builtIn.includes(trimmed)&&!saved.includes(trimmed))
                                  setSettings(s=>({...s,customAreaLabels:[...(s.customAreaLabels||[]),trimmed]}));
                              }
                              setCustomLabelInput(null);
                            }
                            if(e.key==="Escape") setCustomLabelInput(null);
                          }}/>
                        <button style={{...S.btnSm("#6366f1"),padding:"6px 10px",fontSize:13,flexShrink:0}}
                          onClick={()=>{
                            const trimmed=customLabelInput.trim();
                            if(trimmed){
                              setPendingAreaLabel(trimmed);
                              const builtIn=["Garage","Deck","Patio Cover","Shed","Fence","Carport","Pool","Steps"];
                              const saved=settings.customAreaLabels||[];
                              if(!builtIn.includes(trimmed)&&!saved.includes(trimmed))
                                setSettings(s=>({...s,customAreaLabels:[...(s.customAreaLabels||[]),trimmed]}));
                            }
                            setCustomLabelInput(null);
                          }}>✓</button>
                        <button style={{...S.btnSm("transparent"),color:"#94a3b8",padding:"6px 8px",fontSize:13,flexShrink:0}}
                          onClick={()=>setCustomLabelInput(null)}>✕</button>
                      </div>
                  }
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                    <span style={{fontSize:10,color:"#94a3b8",flexShrink:0}}>Size</span>
                    <input type="range" min={8} max={28} value={areaLabelSize}
                      onChange={e=>{
                        const sz=Number(e.target.value);
                        setAreaLabelSize(sz);
                        const updated=(job.markers||[]).map(m=>m.type==="area"?{...m,fontSize:sz}:m);
                        updateJob({markers:updated});
                      }}
                      style={{flex:1,cursor:"pointer",accentColor:"#0f172a"}}/>
                    <span style={{fontSize:10,color:"#64748b",minWidth:18,textAlign:"right"}}>{areaLabelSize}</span>
                  </div>
                  {pendingAreaLabel
                    ? <span style={{fontSize:11,color:"#22d3ee",fontStyle:"italic",fontWeight:600,marginTop:2}}>Tap map to place "{pendingAreaLabel}"</span>
                    : <span style={{fontSize:11,color:"#94a3b8",fontStyle:"italic",marginTop:2}}>Select label, then tap map</span>
                  }
                </div>
              )}

              {/* Edit mode popout — shape actions */}
              {mode==="edit" && (
                <div style={{
                  ...overlayPos,
                  background:"rgba(248,250,252,0.97)",borderRadius:10,
                  padding:"8px 6px",
                  border:"1px solid #e2e8f0",
                  boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
                  display:"flex",flexDirection:"column",gap:5,
                  ...(mob?{}:{minWidth:140}),
                }}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:mob?9:11,color:"#64748b",fontWeight:700}}>⬡ Edit</span>
                    {mob && <button onClick={()=>setMode(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#94a3b8",padding:"0 2px",lineHeight:1}}>×</button>}
                  </div>
                  {selectedShapeIdx===null ? (
                    <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.5,padding:"4px 2px"}}>
                      Tap any shape to select it.
                    </div>
                  ) : (<>
                    <button
                      style={{...S.btnSm((job.drawings[selectedShapeIdx]||{}).closed?"#64748b":"#16a34a"),fontSize:mob?11:12,padding:mob?"8px 4px":"10px 10px",color:"#fff",fontWeight:700}}
                      title="Connect the last point back to the first so the whole shape becomes one closed object you can move and rotate together"
                      disabled={(((job.drawings[selectedShapeIdx]||{}).pts)||[]).length<3}
                      onClick={()=>{
                        updateJob({drawings:job.drawings.map((dd,i)=>i===selectedShapeIdx?{...dd,closed:!dd.closed,sqft:undefined}:dd)});
                      }}>
                      {(job.drawings[selectedShapeIdx]||{}).closed?"✂ Open Ends":"🔗 Connect Ends"}
                    </button>
                    <button
                      style={{...S.btnSm("#0891b2"),fontSize:mob?11:12,padding:mob?"8px 4px":"10px 10px",color:"#fff",fontWeight:700}}
                      title="Snap all corners to 90° following the building's orientation"
                      disabled={!(()=>{const s=job.drawings[selectedShapeIdx];return s&&s.closed&&s.pts&&s.pts.length>=4;})()}
                      onClick={()=>{
                        const d=job.drawings[selectedShapeIdx];
                        const newPts=squareUpShape(d.pts);
                        updateJob({drawings:job.drawings.map((dd,i)=>i===selectedShapeIdx?{...dd,pts:newPts,sqft:undefined}:dd)});
                      }}>
                      ▱ Square Up
                    </button>
                    <button
                      style={{...S.btnSm("#1e40af"),fontSize:mob?11:12,padding:mob?"8px 4px":"10px 10px",fontWeight:600}}
                      disabled={dragPtIdx===null||((job.drawings[selectedShapeIdx]||{}).pts||[]).length<=3}
                      onClick={()=>{
                        const d=job.drawings[selectedShapeIdx];
                        if(dragPtIdx!==null&&d&&d.pts.length>3){
                          updateJob({drawings:job.drawings.map((dd,i)=>i===selectedShapeIdx?{...dd,pts:d.pts.filter((_,pi)=>pi!==dragPtIdx)}:dd)});
                          setDragPtIdx(null);
                        }
                      }}>
                      ✕ Del Point
                    </button>
                    <button
                      style={{...S.btnSm("#dc2626"),fontSize:mob?11:12,padding:mob?"8px 4px":"10px 10px"}}
                      onClick={()=>{
                        updateJob({drawings:job.drawings.filter((_,i)=>i!==selectedShapeIdx)});
                        setSelectedShapeIdx(null); setDragPtIdx(null);
                      }}>
                      🗑 Del Shape
                    </button>
                    <button
                      style={{...S.btnSmOutline,fontSize:mob?11:12,padding:mob?"8px 4px":"10px 10px"}}
                      onClick={()=>{setSelectedShapeIdx(null);setDragPtIdx(null);}}>
                      ✓ Done
                    </button>
                  </>)}
                </div>
              )}

            </div>

            {/* Grid overlay toggle */}
            {job.mapBg && !mob && (
              <button
                style={{...S.btnSm(showGrid?"#1e40af":"#374151"),fontSize:mob?13:14,padding:mob?"5px 8px":"9px 0",fontWeight:700,...(mob?{flexShrink:0}:{width:"100%"})}}
                onClick={()=>setShowGrid(g=>!g)}
                title={showGrid?"Hide grid overlay":"Show grid overlay"}>
                ▦
              </button>
            )}

            {/* Undo / Redo + setup toggle + quick photo (mobile) */}
            <div className="ts-diagram-utilities" role="group" aria-label="Map and drawing actions" style={{display:"flex",gap:2,...(mob?{flexShrink:0}:{})}}>
              {mob && (
                <button type="button" aria-label="Map settings" aria-expanded={mobSetupOpen} style={{...S.btnSm(mobSetupOpen?"#1e40af":"#374151"),fontSize:11,padding:"6px 6px",fontWeight:700,flexShrink:0}}
                  onClick={()=>setMobSetupOpen(o=>!o)}
                  title={mobSetupOpen?"Hide map settings":"Show map settings"}>
                  Map {mobSetupOpen?"▴":"▾"}
                </button>
              )}
              {mob && job.mapBg && <button type="button" aria-label="Grid overlay" aria-pressed={showGrid} style={{...S.btnSm(showGrid?"#1e40af":"#374151")}} onClick={()=>setShowGrid(g=>!g)} title="Grid overlay">▦</button>}
              <button style={{...S.btnSm("#374151"),fontSize:14,...(mob?{padding:"5px 8px"}:{flex:1,padding:"9px 0"}),opacity:undoLen>0?1:0.35}}
                onClick={handleUndo} disabled={undoLen===0} title="Undo">↩</button>
              <button style={{...S.btnSm("#374151"),fontSize:14,...(mob?{padding:"5px 8px"}:{flex:1,padding:"9px 0"}),opacity:redoLen>0?1:0.35}}
                onClick={handleRedo} disabled={redoLen===0} title="Redo">↪</button>
              {mob && (()=>{
                const lastMarker=(job.markers||[]).at(-1);
                const hasPhoto=!!pendingPhotoMarkerId;
                return (
                  <button
                    style={{...S.btnSm(hasPhoto?"#1e40af":"#374151"),fontSize:14,padding:"6px 8px",flexShrink:0,opacity:lastMarker?1:0.35}}
                    disabled={!lastMarker}
                    onClick={()=>setPendingPhotoMarkerId(hasPhoto?null:lastMarker?.id)}
                    aria-pressed={hasPhoto} title="Add photo to last marker"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h4l2-3h4l2 3h4v14H4Z"/><circle cx="12" cy="13" r="4"/></svg></button>
                );
              })()}
            </div>

          </div>{/* end sidebar */}

          {/* ── MAIN AREA: trace status bar + errors + canvas ── */}
          <div style={{flex:1,minWidth:0}}>

            {/* Trace action bar — hidden on mobile when idle (saves ~46px) */}
            <div className="ts-trace-bar" style={{
              display: mob && !traceActive ? "none" : "flex",
              alignItems:"center",gap:8,marginBottom:8,
              padding:"10px 14px",borderRadius:10,
              background: traceActive ? "#0f172a" : "#f8fafc",
              border: traceActive ? "none" : "1px dashed #e2e8f0",
              transition:"all 0.2s",minHeight:46,flexWrap:"wrap"
            }}>
              {traceActive ? (<>
                <span style={{fontSize:12,color:"#22d3ee",fontWeight:700,minWidth:48}}>
                  {tracePts.length} pts
                </span>
                <span className="ts-trace-hint" style={{fontSize:11,color:"#94a3b8",flex:1,minWidth:80}}>
                  {tracePts.length<3?"Tap corners to outline the structure":"Tap ● first point to close"}
                </span>
                {tracePts.length >= 3 && (
                  <button style={{...S.btn("#f97316"),fontSize:12,padding:"6px 14px",fontWeight:700}}
                    onClick={()=>{
                      updateJob({drawings:[...(job.drawings||[]),{pts:tracePts,style:traceStyle,closed:true}]});
                      setTracePts([]); mousePosRef.current = null; redrawLive();
                    }}>
                    ⬡ Close Shape
                  </button>
                )}
                <button style={{...S.btnSm("#374151"),fontSize:12,padding:"5px 10px"}}
                  onClick={undoLastPoint} disabled={tracePts.length===0}>↩ Undo</button>
                <button style={{...S.btnSm("#1e40af"),fontSize:12,padding:"5px 10px"}}
                  onClick={()=>{ if(tracePts.length>=2){updateJob({drawings:[...(job.drawings||[]),{pts:tracePts,style:traceStyle,closed:false}]});setTracePts([]); mousePosRef.current = null; redrawLive();} }}
                  disabled={tracePts.length<2}>
                  ✓ Open
                </button>
                <button style={{...S.btnSm("#dc2626"),fontSize:12,padding:"5px 10px"}}
                  onClick={cancelTrace}>✕</button>
              </>) : (<>
                <span style={{fontSize:12,color:"#94a3b8",lineHeight:1.4,flex:1}}>
                  {mode==="trace" && "Tap corners to outline structure · Tap first point (●) to close"}
                  {mode==="measure" && (measureStart ? "Tap the end point to lock the distance" : "Tap a corner, then drag to the next to measure the distance")}
                  {mode===null && "Tap a finding label to edit its details"}
                  {mode==="mark"  && `Tap map to place ${markerLabel} · Tap a finding label to edit`}
                  {mode==="edit"  && (selectedShapeIdx===null
                    ? "Tap a finding to edit · Tap a shape to select"
                    : "Drag ○ handles · ⊕ adds vertex · Drag inside to move · Tap outside to deselect")}
                  {mode==="erase" && "Tap any marker or traced line to remove it"}
                  {mode==="text" && (pendingAreaLabel
                    ? `Tap map to place "${pendingAreaLabel}"`
                    : "Choose a label below, then tap the map to place it")}
                </span>
              </>)}
            </div>

            {/* Errors / hints */}
            {mapError && <div style={{color:"#dc2626",fontSize:13,marginBottom:8,padding:"8px 12px",background:"#fef2f2",borderRadius:8}}>{mapError}</div>}
            {mapInfo  && <div style={{color:"#065f46",fontSize:13,marginBottom:8,padding:"8px 12px",background:"#d1fae5",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <span>{mapInfo}</span>
              <button onClick={()=>setMapInfo("")} style={{marginLeft:8,background:"none",border:"none",cursor:"pointer",color:"#065f46",fontSize:16,lineHeight:1,padding:"0 2px"}}>×</button>
            </div>}
            {!job.mapBg && !mapError && (
              <div style={{fontSize:12,color:"#94a3b8",marginBottom:8,padding:"8px 12px",background:"#f8fafc",borderRadius:8,border:"1px dashed #e2e8f0"}}>
                💡 {job.address ? `Click Satellite to load ${job.address}` : "Enter a property address in Job Info first, then load satellite."}
              </div>
            )}

            {/* ── Canvas stack: outer container not scaled — hosts CSS-scaled wrapper
                 and HTML marker overlay so markers are never clipped by zoom ── */}
            <div style={{position:"relative",width:"100%",...(fullscreen?{maxWidth:`min(100%, calc((100svh - 120px) * ${CANVAS_W} / ${CH}))`,margin:"0 auto"}:{})}}>
              <button
                onClick={()=>setFullscreen(v=>!v)}
                style={{position:"absolute",top:8,right:8,zIndex:30,border:"none",borderRadius:8,cursor:"pointer",padding:"6px 10px",fontSize:12,fontWeight:700,background:"rgba(15,23,42,0.82)",color:"#fff"}}
                title={fullscreen?"Exit fullscreen (Esc)":"Expand the map to full screen for easier tracing"}>
                {fullscreen?"✕ Exit":"⛶ Expand"}
              </button>
        <div ref={mapWrapRef} style={{position:"relative",border:"2px solid #e2e8f0",borderRadius:10,overflow:"hidden",touchAction:"none",width:"100%",
          transform: viewScale !== 1 ? `scale(${viewScale})` : undefined,
          transformOrigin: '50% 50%',
        }}>
          <canvas ref={canvasRef} width={CANVAS_W} height={CH}
            style={{width:"100%",display:"block",height:"auto",aspectRatio:`${CANVAS_W} / ${CH}`}}/>
          <canvas ref={liveCanvasRef} width={CANVAS_W} height={CH}
            style={{position:"absolute",top:0,left:0,width:"100%",height:"auto",aspectRatio:`${CANVAS_W} / ${CH}`,
              cursor:mode==="trace"?"crosshair":mode==="measure"?"crosshair":mode==="mark"?"cell":isRotatingShape?"grabbing":isDraggingShape?"grabbing":panDragRef.current?"grabbing":"pointer"}}
            onMouseDown={handleTap}
            onMouseUp={(e)=>{ if(panDragRef.current){ const {dx,dy}=panDragRef.current; panDragRef.current=null; const wrap=mapWrapRef.current; if(wrap) wrap.style.transform=viewScale!==1?`scale(${viewScale})`:''; if(!isDragging&&!isDraggingShape&&(Math.abs(dx)>5||Math.abs(dy)>5)){ commitPan(dx,dy); return; } } if(isDragging){ setIsDragging(false); setDragPtIdx(null); } if(isDraggingShape){ setIsDraggingShape(false); dragShapeRef.current=null; } if(isRotatingShape){ setIsRotatingShape(false); rotateShapeRef.current=null; } }}
            onMouseMove={handleMouseMove}
            onMouseLeave={(e)=>{ handleMouseLeave(); if(panDragRef.current){ const {dx,dy}=panDragRef.current; panDragRef.current=null; const wrap=mapWrapRef.current; if(wrap) wrap.style.transform=viewScale!==1?`scale(${viewScale})`:''; if(!isDragging&&!isDraggingShape&&(Math.abs(dx)>5||Math.abs(dy)>5)){ commitPan(dx,dy); return; } } if(isDragging){ setIsDragging(false); setDragPtIdx(null); } if(isDraggingShape){ setIsDraggingShape(false); dragShapeRef.current=null; } if(isRotatingShape){ setIsRotatingShape(false); rotateShapeRef.current=null; } }}
            onDoubleClick={handleDblTap}
            onTouchEnd={handleTouchEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}/>
          {/* Auto-tracing in-progress callout */}
          {autoTracing && (
            <div style={{
              position:"absolute",top:14,left:"50%",
              transform:"translateX(-50%)",
              pointerEvents:"none",
              background:"rgba(124,58,237,0.92)",
              color:"#fff",
              fontSize:13,
              fontWeight:700,
              borderRadius:20,
              padding:"7px 18px",
              letterSpacing:"0.03em",
              whiteSpace:"nowrap",
              boxShadow:"0 2px 12px rgba(0,0,0,0.25)",
              display:"flex",alignItems:"center",gap:8,
            }}>
              <span style={{display:"inline-block",animation:"spin 1s linear infinite"}}>⏳</span>
              Tracing all structures…
            </div>
          )}
          {/* Loading indicator overlay */}
          {loadingMap && (
            <div style={{
              position:"absolute",inset:0,
              background:"rgba(15,23,42,0.55)",
              display:"flex",alignItems:"center",justifyContent:"center",
              borderRadius:8,zIndex:20,pointerEvents:"none",
            }}>
              <div style={{background:"rgba(15,23,42,0.92)",color:"#22d3ee",fontWeight:700,
                fontSize:14,padding:"10px 22px",borderRadius:20,
                boxShadow:"0 4px 16px rgba(0,0,0,0.4)"}}>
                ⏳ Loading satellite…
              </div>
            </div>
          )}
          {/* ── Marker bar — shown below the canvas when satellite map is loaded ── */}
          {/* (removed from inside canvas wrapper; rendered as flow element below) */}
        </div>{/* end canvas stack (CSS-scaled) */}

        {/* ── Floating zoom controls — outside scaled/overflow wrapper so iOS touch always lands here ── */}
        {job.mapBg && mob && (
          <div style={{
            position:"absolute",bottom:12,right:12,
            display:"flex",flexDirection:"column",
            zIndex:30,opacity:loadingMap?0.5:1,
            borderRadius:10,overflow:"hidden",
            boxShadow:"0 2px 14px rgba(0,0,0,0.32)",
            border:"1.5px solid rgba(0,0,0,0.16)",
          }}>
            {viewScale > 1 && (
              <button
                style={{width:48,height:40,background:"rgba(124,58,237,0.92)",
                  color:"#fff",border:"none",borderBottom:"1px solid rgba(0,0,0,0.15)",
                  fontSize:11,lineHeight:1.2,fontWeight:700,
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                onClick={()=>{setViewScale(1);if(mapWrapRef.current){mapWrapRef.current.style.transform='';mapWrapRef.current.style.transformOrigin='';}}}
                title="Reset pinch zoom">×{viewScale.toFixed(1)}</button>
            )}
            <button
              style={{width:38,height:36,background:"rgba(255,255,255,0.97)",
                color:"#1a202c",border:"none",borderBottom:"1px solid rgba(0,0,0,0.1)",
                fontSize:22,lineHeight:1,fontWeight:300,
                cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onClick={()=>{if(!loadingMap&&(job.mapZoom||19)<22)changeZoom(0.25);}}
              title="Zoom in">+</button>
            <button
              style={{width:38,height:36,background:"rgba(255,255,255,0.97)",
                color:"#1a202c",border:"none",
                fontSize:22,lineHeight:1,fontWeight:300,
                cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onClick={()=>{if(!loadingMap&&(job.mapZoom||19)>17)changeZoom(-0.25);}}
              title="Zoom out">−</button>
          </div>
        )}

        {/* ── Node popup: shown on double-tap of a shape vertex ── */}
        {nodePopup && (()=>{
          const wW = mapWrapRef.current?.clientWidth  || 600;
          const wH = mapWrapRef.current?.clientHeight || 400;
          const px = (nodePopup.x / CANVAS_W) * wW;
          const py = (nodePopup.y / CH) * wH;
          const popW = 152, popH = 110;
          const clampedLeft = Math.max(popW/2, Math.min(wW - popW/2, px));
          const flipDown = py < popH;
          const shape = (job.drawings||[])[nodePopup.shapeIdx];
          const canDelete = shape && shape.pts.length > 3;
          return (
            <div style={{
              position:"absolute",
              left: clampedLeft, top: py,
              transform: flipDown ? "translate(-50%, 15px)" : "translate(-50%,-120%)",
              background:"#1e293b",
              border:"1px solid rgba(255,255,255,0.15)",
              borderRadius:10,
              boxShadow:"0 4px 18px rgba(0,0,0,0.55)",
              padding:"10px 12px",
              zIndex:30,
              display:"flex", flexDirection:"column", gap:7,
              minWidth:140,
            }}>
              <div style={{fontSize:11,color:"#94a3b8",marginBottom:2,textAlign:"center"}}>Vertex options</div>
              <button
                style={{
                  background: canDelete ? "#dc2626" : "#475569",
                  color:"#fff", border:"none", borderRadius:6,
                  padding:"6px 10px", fontSize:13, fontWeight:600,
                  cursor: canDelete ? "pointer" : "not-allowed",
                  opacity: canDelete ? 1 : 0.5,
                }}
                disabled={!canDelete}
                onClick={()=>{
                  if(!canDelete) return;
                  const newPts = shape.pts.filter((_,i)=>i!==nodePopup.ptIdx);
                  updateJob({drawings:(job.drawings||[]).map((d,i)=>i===nodePopup.shapeIdx?{...d,pts:newPts}:d)});
                  setNodePopup(null);
                  setDragPtIdx(null);
                }}>
                ✕ Delete node
              </button>
              <button
                style={{
                  background:"#334155", color:"#e2e8f0", border:"none", borderRadius:6,
                  padding:"6px 10px", fontSize:13, cursor:"pointer",
                }}
                onClick={()=>setNodePopup(null)}>
                Cancel
              </button>
            </div>
          );
        })()}

        {/* ── HTML marker overlay: renders WDO markers outside the CSS-scaled wrapper
             so they are never clipped by overflow:hidden. Visible at any zoom level.
             Off-screen markers clamp to the nearest edge of the visible map area. ── */}
        {viewScale > 1 && (job.markers||[]).length > 0 && (()=>{
          const wW = mapWrapRef.current?.clientWidth  || 600;
          const wH = mapWrapRef.current?.clientHeight || 400;
          const PAD = 18;
          return (
            <div style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"hidden",zIndex:8}}>
              {(job.markers||[]).map((m,i)=>{
                const rawX   = (m.x / CANVAS_W) * wW;
                const rawY   = (m.y / CH) * wH;
                const scaledX = wW/2 + (rawX - wW/2) * viewScale;
                const scaledY = wH/2 + (rawY - wH/2) * viewScale;
                const px = Math.max(PAD, Math.min(wW - PAD, scaledX));
                const py = Math.max(PAD, Math.min(wH - PAD, scaledY));
                if(m.type==="area"){
                  return (
                    <div key={i} style={{
                      position:"absolute", left:px, top:py,
                      transform:"translate(-50%,-50%)",
                      color:"#0f172a", fontSize:12, fontWeight:700,
                      whiteSpace:"nowrap",
                      textShadow:"0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff",
                    }}>{m.label}</div>
                  );
                }
                const cat = WDO_CATEGORIES.find(c=>c.num===Number(m.label[0]));
                return (
                  <button type="button" key={m.id} aria-label={`Edit finding ${m.label}`}
                    onClick={()=>setPendingPhotoMarkerId(m.id)}
                    style={{pointerEvents:mode===null||mode==="mark"||mode==="edit"?"auto":"none",cursor:"pointer",minWidth:44,minHeight:44,
                    position:"absolute", left:px, top:py,
                    transform:"translate(-50%,-50%)",
                    background: cat?.color || "#1e293b",
                    color:"#fff", fontSize:12, fontWeight:800,
                    padding:"2px 7px", borderRadius:5,
                    border:"2px solid rgba(255,255,255,0.9)",
                    boxShadow:"0 2px 8px rgba(0,0,0,0.55)",
                    letterSpacing:"0.01em",
                    display:"flex",flexDirection:"column",alignItems:"center",gap:1,lineHeight:1,
                  }}>{m.label.split("+").map((p,pi)=><span key={pi}>{p}</span>)}</button>
                );
              })}
            </div>
          );
        })()}

            </div>{/* end outer non-scaled container */}

          </div>{/* end main area */}

        </div>{/* end two-column layout */}



        {/* ── Footer: legend + stats + undo last shape ── */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginTop:10,flexWrap:"wrap"}}>
          {WDO_CATEGORIES.map(c=>(
            <span key={c.num} style={{fontSize:11,display:"inline-flex",alignItems:"center",gap:4}}>
              <span style={{display:"inline-block",width:12,height:12,borderRadius:3,background:c.color}}/>
              {c.num}
            </span>
          ))}
          <span style={{fontSize:12,color:"#64748b",marginLeft:"auto"}}>
            {(job.drawings||[]).length} shapes · {(job.markers||[]).length} markers
            {job.mapCenter && (job.drawings||[]).some(d=>d.closed) && (() => {
              const footprint = (job.drawings||[]).filter(d=>d.closed&&d.pts.length>=3)
                .reduce((s,d)=>s+(d.sqft||geoAreaSqFt(d.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,undefined,undefined,job.mapPixelScale||MAP_PIXEL_SCALE)),0);
              if (footprint <= 0) return null;
              return <span style={{color:"#1e40af",fontWeight:700,marginLeft:8}}>
                ≈ {Math.round(footprint).toLocaleString()} sqft
              </span>;
            })()}
          </span>
          {mode!=="edit" && (job.drawings||[]).length>0 &&(
            <button style={{...S.btnSmOutline,fontSize:11,color:"#b45309",borderColor:"#fde68a"}}
              onClick={()=>{ updateJob({drawings:(job.drawings||[]).slice(0,-1)}); }}
              title="Remove the last traced shape">
              ↩ Undo Shape
            </button>
          )}
          {mode!=="edit" && ((job.drawings||[]).length>0||(job.markers||[]).length>0)&&(
            <button style={{...S.btnSmOutline,fontSize:11,color:"#dc2626",borderColor:"#fca5a5"}}
              onClick={()=>{ if(window.confirm("Clear all shapes and markers?"))updateJob({drawings:[],markers:[]}); }}>
              🗑 Clear All
            </button>
          )}
        </div>
      </div>
      {/* Smart next step */}
      <div style={{...S.card,background:"linear-gradient(135deg,#1e3a5f,#1e40af)",border:"none",padding:mob?"8px 12px":"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:mob?"nowrap":"wrap"}}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontWeight:700,fontSize:mob?12:13,color:"#fff",...(mob?{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}:{})}}>
            {(job.drawings||[]).length>0||(job.markers||[]).length>0 ? "Diagram saved — ready for F&R" : "Draw outline, then add F&R findings"}
          </div>
          {!mob&&<div style={{fontSize:11,color:"rgba(255,255,255,0.65)",marginTop:2}}>
            {(job.drawings||[]).length} shapes · {(job.markers||[]).length} markers placed
          </div>}
        </div>
        <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:800,fontSize:mob?12:13,padding:mob?"6px 12px":"9px 18px",whiteSpace:"nowrap",flexShrink:0}}
          onClick={()=>window._tsSetTab&&window._tsSetTab("findings")}>
          {mob?"→ F&R":"→ Go to F&R"}
        </button>
      </div>

      {/* Finding details and photos — opens immediately after placing a marker */}
      {pendingPhotoMarkerId && (()=>{
        const pm=(job.markers||[]).find(m=>m.id===pendingPhotoMarkerId);
        if(!pm) return null;
        const detailFindings=findingsForMarker(job,pm);
        const updateDetails=(id,patch)=>updateJob(current=>updateMarkerFinding(current,pm.id,id,patch,settings,(f,p)=>updateRepairEstimate(f,p,settings)));
        const photoFindings=detailFindings.some(f=>f.kind==='repair')?detailFindings.filter(f=>f.kind==='repair'):detailFindings;
        const photoTarget=photoFindings.find(f=>f.id===photoTargetId)||photoFindings[0];
        const photos=Array.from(new Map((photoTarget?[photoTarget]:[]).flatMap(f=>(f.photos||[]).map(p=>[p.id,p]))).values());
        const removePhoto=pid=>updateJob(current=>changeMarkerPhotos(current,pm.id,list=>list.filter(p=>p.id!==pid),photoTarget?.id));
        return (
          <div className="ts-finding-popup-layer" style={{position:"fixed",top:0,left:0,right:0,height:"100dvh",zIndex:1200,pointerEvents:"none",boxSizing:"border-box",padding:"max(12px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left))",display:"flex",alignItems:mob?"center":"flex-end",justifyContent:"center"}}>
            <div role="dialog" aria-label="Diagram finding details" aria-modal="false" onKeyDown={event=>{if(event.key==="Escape")setPendingPhotoMarkerId(null);}} style={{background:"#0f172a",borderRadius:14,border:"1px solid rgba(255,255,255,0.15)",boxShadow:"0 8px 32px rgba(0,0,0,0.5)",display:"flex",flexDirection:"column",alignItems:"stretch",width:mob?"100%":440,maxWidth:460,maxHeight:"100%",overflow:"hidden",pointerEvents:"auto"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexShrink:0,minWidth:0,padding:"8px 14px",borderBottom:"1px solid #334155"}}>
                <span style={{color:"#22d3ee",fontWeight:700,fontSize:mob?11:12,...(mob?{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}:{textTransform:"uppercase",letterSpacing:"0.05em"})}}>
                  📍 {pm.label} · Finding details
                </span>
                <button aria-label="Close finding popup" onClick={()=>setPendingPhotoMarkerId(null)} style={{background:"#1e293b",border:"1px solid #475569",borderRadius:8,cursor:"pointer",color:"#e2e8f0",fontSize:24,width:44,height:44,flexShrink:0,lineHeight:1}}>×</button>
              </div>
              <div className="ts-finding-popup-content" tabIndex={0} role="region" aria-label="Finding details and photos" style={{overflowY:"auto",minHeight:0,overscrollBehavior:"contain",WebkitOverflowScrolling:"touch",padding:"12px 14px 14px"}}>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <DiagramFindingDetails findings={detailFindings} components={REPAIR_COMPONENTS} settings={settings} onChange={updateDetails} readOnly={readOnly}/>
              {photoFindings.length>1&&<label style={{color:'#e2e8f0',fontSize:13}}>Photos for
                <select aria-label="Finding for photos" value={photoTarget?.id||''} onChange={event=>setPhotoTargetId(event.target.value)} style={{width:'100%',fontSize:16,padding:10,borderRadius:8,marginTop:6}}>
                  {photoFindings.map(f=><option key={f.id} value={f.id}>{f.cat}{f.letter} · {f.component||'Finding'} · {f.repairAction||''}</option>)}
                </select>
              </label>}
              {photos.length>0&&(
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {photos.map(ph=>(
                    <div key={ph.id} style={{position:"relative"}}>
                      <img src={ph.data} alt="" style={{width:mob?48:64,height:mob?40:52,objectFit:"cover",borderRadius:6,border:"1px solid rgba(255,255,255,0.2)"}}/>
                      <button onClick={()=>removePhoto(ph.id)}
                        style={{position:"absolute",top:-4,right:-4,width:16,height:16,borderRadius:"50%",background:"#dc2626",color:"#fff",border:"none",cursor:"pointer",fontSize:9,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label style={{background:"#1e40af",color:"#fff",borderRadius:8,padding:mob?"9px":"9px 14px",fontWeight:700,fontSize:13,cursor:"pointer",textAlign:"center",border:"1px solid rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",gap:6,whiteSpace:"nowrap"}}>
                {mob?"📷":`📷 ${photos.length>0?"Add Another Photo":"Take / Add Photo"}`}
                <input type="file" accept="image/*" capture="environment" style={{display:"none"}}
                  onChange={e=>{addQuickPhoto(Array.from(e.target.files));e.target.value="";}}/>
              </label>
              {photos.length>0 && navigator.share && (
                <button
                  onClick={async()=>{
                    try{
                      const res=await fetch(photos[photos.length-1].data);
                      const blob=await res.blob();
                      await navigator.share({files:[new File([blob],`inspection-${Date.now()}.jpg`,{type:'image/jpeg'})]});
                    }catch(e){}
                  }}
                  style={{background:"#0f4c81",color:"#7dd3fc",border:"1px solid #0ea5e9",borderRadius:8,padding:mob?"9px":"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",textAlign:"center"}}>
                  {mob?"📲":"📲 Save to Camera Roll"}
                </button>
              )}
              <button onClick={()=>setPendingPhotoMarkerId(null)} style={{background:"#1e293b",color:"#e2e8f0",border:"1px solid #475569",borderRadius:8,padding:"9px",fontWeight:600,fontSize:13,cursor:"pointer"}}>
                Done
              </button>
              </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// ─── QUICK ADD TOOLBAR ────────────────────────────────────────────────────────
const QUICK_LAST_KEY='termitescope_quick_last_cat_v1';
function loadQuickLast(){ try{ return JSON.parse(localStorage.getItem(QUICK_LAST_KEY))||{}; }catch{ return {}; } }
function saveQuickLast(cat,id){ try{ const m=loadQuickLast(); m[cat]=id; localStorage.setItem(QUICK_LAST_KEY,JSON.stringify(m)); }catch{} }

function QuickAddToolbar({categories, codebook, findings, onSelect, onBlank, onRepeat, onCodebook}) {
  const [openPop, setOpenPop] = useState(null); // cat number or null

  // The one-click default for a category: the entry last used for it, else the first.
  function defaultEntry(cat){
    const entries = codebook.filter(e=>e.cat===cat.num);
    if(!entries.length) return null;
    const last = loadQuickLast()[cat.num];
    return entries.find(e=>e.id===last) || entries[0];
  }
  // Main button = add the default in one click. The ▾ caret opens the full list.
  function handleCatClick(cat) {
    const e = defaultEntry(cat);
    if(!e){ onBlank(cat.num); setOpenPop(null); return; }
    onSelect(e); saveQuickLast(cat.num, e.id); setOpenPop(null);
  }
  function pick(cat, e){ onSelect(e); saveQuickLast(cat, e.id); setOpenPop(null); }

  return (
    <div style={{...S.card, padding:"12px 16px"}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <button style={S.btn("#22d3ee")} onClick={()=>{setOpenPop(null);onCodebook();}}>
          📋 Codebook
        </button>
        {findings.length>0 && (
          <button style={{...S.btn("#0f172a"),fontWeight:800}} onClick={onRepeat} title="Add another finding just like the last one">
            ↺ Repeat Last
          </button>
        )}

        {categories.map(cat=>{
          const entries = codebook.filter(e=>e.cat===cat.num);
          const isOpen  = openPop===cat.num;
          return (
            <div key={cat.num} style={{position:"relative"}}>
              <button
                style={{
                  ...S.btnSm(cat.color),
                  minWidth:44,
                  outline: isOpen ? `2px solid ${cat.color}` : "none",
                  outlineOffset:2
                }}
                onClick={()=>handleCatClick(cat)}
                title={(()=>{const d=defaultEntry(cat);return d?`Add ${d.name}${entries.length>1?"  ·  click ▾ for other entries":""}`:`Start a blank ${cat.label} finding`;})()}>
                + {cat.num}
                {entries.length > 1 && <span onClick={e=>{e.stopPropagation(); setOpenPop(isOpen?null:cat.num);}} style={{fontSize:11,marginLeft:5,opacity:0.9,padding:"0 3px",cursor:"pointer"}} title="Other entries">▾</span>}
              </button>

              {/* Popover */}
              {isOpen && (
                <div style={{
                  position:"absolute",top:"calc(100% + 6px)",left:0,zIndex:200,
                  background:"#fff",borderRadius:10,
                  boxShadow:"0 8px 30px rgba(0,0,0,0.15)",
                  border:`2px solid ${cat.color}`,
                  minWidth:260,maxWidth:340,
                }}>
                  <div style={{padding:"8px 12px",background:cat.color,borderRadius:"8px 8px 0 0"}}>
                    <div style={{fontWeight:700,fontSize:12,color:"#fff"}}>{cat.label}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.8)",marginTop:2}}>Choose an entry or start blank</div>
                  </div>
                  <div style={{maxHeight:260,overflowY:"auto"}}>
                    {entries.map(e=>(
                      <button key={e.id}
                        style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",
                          borderBottom:"1px solid #f1f5f9",padding:"10px 14px",cursor:"pointer"}}
                        onMouseEnter={el=>{if(el.currentTarget)el.currentTarget.style.background="#f8fafc";}}
                        onMouseLeave={el=>{if(el.currentTarget)el.currentTarget.style.background="none";}}
                        onClick={()=>pick(cat.num,e)}>
                        <div style={{fontWeight:700,fontSize:13,color:"#1a1a2e",marginBottom:2}}>{e.name}</div>
                        <div style={{fontSize:11,color:"#64748b",lineHeight:1.4}}>
                          {e.finding.slice(0,70)}{e.finding.length>70?"…":""}
                        </div>
                        {e.defaultPrice>0&&(
                          <div style={{fontSize:11,fontWeight:700,color:cat.color,marginTop:3}}>
                            ${e.defaultPrice.toLocaleString()}{e.priceUnit?<span style={{fontWeight:400,color:"#64748b"}}> / {e.priceUnit}</span>:null}
                          </div>
                        )}
                      </button>
                    ))}
                    <button
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",
                        padding:"10px 14px",cursor:"pointer",color:"#64748b",fontSize:12}}
                      onMouseEnter={el=>{if(el.currentTarget)el.currentTarget.style.background="#f8fafc";}}
                      onMouseLeave={el=>{if(el.currentTarget)el.currentTarget.style.background="none";}}
                      onClick={()=>{onBlank(cat.num);setOpenPop(null);}}>
                      + Start with blank entry
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      </div>

      {/* Click outside to close */}
      {openPop!==null && (
        <div style={{position:"fixed",inset:0,zIndex:199}} onClick={()=>setOpenPop(null)}/>
      )}
    </div>
  );
}

// ─── FINDINGS & RECOMMENDATIONS ───────────────────────────────────────────────
function FindingsSection({job,updateJob,settings,inspectorMode}){
  useEffect(()=>{
    const findings=syncJobDiagramAreas(job,settings,!inspectorMode);
    if(findings!==job.findings)updateJob({findings});
  },[job,settings,inspectorMode,updateJob]);
  const [showCodebook,setShowCodebook]=useState(false);
  const [editingId,setEditingId]=useState(null);
  const [newFinding,setNewFinding]=useState(null);

  function addFromCodebook(entry){
    const letter=nextLetter(job.findings,entry.cat);
    setNewFinding({id:Date.now().toString(),markerId:null,cat:entry.cat,letter,finding:entry.finding,primaryRec:entry.primaryRec,secondaryRec:entry.secondaryRec,hasSecondary:!!entry.secondaryRec,primaryPrice:entry.defaultPrice,priceUnit:entry.priceUnit||"",secPrice:0,notes:"",fumeSqft:"",fumeHeightFt:0,fumeCubicFt:0,bidProgress:{FP:false,DM:false,FR:false,CT:false,CS:false,NC:false}});
    setShowCodebook(false);
  }
  function addBlank(cat){
    const letter=nextLetter(job.findings,cat);
    setNewFinding({id:Date.now().toString(),markerId:null,cat,letter,finding:"",primaryRec:"",secondaryRec:"",hasSecondary:false,primaryPrice:0,priceUnit:"",secPrice:0,notes:"",fumeSqft:"",fumeHeightFt:0,fumeCubicFt:0,bidProgress:{FP:false,DM:false,FR:false,CT:false,CS:false,NC:false}});
  }
  function saveNew(f){updateJob(saveRepairFinding(job,f,settings));setNewFinding(null);}
  function saveFinding(f){updateJob(saveRepairFinding(job,f,settings));setEditingId(null);}
  function deleteFinding(id){const f=job.findings.find(x=>x.id===id);const nm=f?`${f.cat}${f.letter}`:"this finding";if(!window.confirm(`Delete finding ${nm}? This can't be undone.`))return;updateJob({findings:job.findings.filter(f=>f.id!==id)});}
  function repeatLast(){
    if(!job.findings.length)return;
    const last=job.findings[job.findings.length-1];
    const letter=nextLetter(job.findings,last.cat);
    setNewFinding({...last,id:Date.now().toString(),letter,markerId:null,sourceId:undefined,evidence:undefined,reviewedAt:undefined,repairLocations:undefined,photos:[]});
  }

  const secI=job.findings.filter(f=>f.cat<=3);
  const secII=job.findings.filter(f=>f.cat===4);
  const secFI=job.findings.filter(f=>f.cat===5);

  return(
    <div>
      <QuickAddToolbar
        categories={WDO_CATEGORIES}
        codebook={settings.codebook}
        findings={job.findings}
        onSelect={addFromCodebook}
        onBlank={addBlank}
        onRepeat={repeatLast}
        onCodebook={()=>setShowCodebook(true)}
      />
      {inspectorMode && <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:10,padding:"10px 14px",margin:"10px 0",fontSize:12,color:"#1e40af",fontWeight:600}}>💲 Pricing is set by the office. You can place findings, add photos, and set the repair component.</div>}
      {newFinding&&<FindingEditor finding={newFinding} markers={job.markers} job={job} settings={settings} onSave={saveNew} onCancel={()=>setNewFinding(null)} isNew priceLocked={inspectorMode}/>}

      {secI.length>0&&(
        <div style={S.card}>
          <div style={{...S.cardTitle,color:"#b45309"}}>Section I — Active Infestation / Infection</div>
          {secI.map(f=>editingId===f.id
            ?<FindingEditor key={f.id} finding={f} markers={job.markers} job={job} settings={settings} onSave={saveFinding} onCancel={()=>setEditingId(null)} priceLocked={inspectorMode}/>
            :<FindingRow key={f.id} finding={f} job={job} settings={settings} onEdit={()=>setEditingId(f.id)} onDelete={()=>deleteFinding(f.id)} onUpdate={inspectorMode?undefined:u=>updateJob({findings:job.findings.map(x=>x.id===u.id?u:x)})}/>
          )}
        </div>
      )}
      {secII.length>0&&(
        <div style={S.card}>
          <div style={{...S.cardTitle,color:"#1e40af"}}>Section II — Further Infestation / Conditions</div>
          {secII.map(f=>editingId===f.id
            ?<FindingEditor key={f.id} finding={f} markers={job.markers} job={job} settings={settings} onSave={saveFinding} onCancel={()=>setEditingId(null)} priceLocked={inspectorMode}/>
            :<FindingRow key={f.id} finding={f} job={job} settings={settings} onEdit={()=>setEditingId(f.id)} onDelete={()=>deleteFinding(f.id)} onUpdate={inspectorMode?undefined:u=>updateJob({findings:job.findings.map(x=>x.id===u.id?u:x)})}/>
          )}
        </div>
      )}
      {secFI.length>0&&(
        <div style={S.card}>
          <div style={{...S.cardTitle,color:"#6b7280"}}>Further Inspection</div>
          {secFI.map(f=>editingId===f.id
            ?<FindingEditor key={f.id} finding={f} markers={job.markers} job={job} settings={settings} onSave={saveFinding} onCancel={()=>setEditingId(null)} priceLocked={inspectorMode}/>
            :<FindingRow key={f.id} finding={f} job={job} settings={settings} onEdit={()=>setEditingId(f.id)} onDelete={()=>deleteFinding(f.id)} onUpdate={inspectorMode?undefined:u=>updateJob({findings:job.findings.map(x=>x.id===u.id?u:x)})}/>
          )}
        </div>
      )}
      {!job.findings.length&&!newFinding&&(
        <div style={{...S.card,textAlign:"center",color:"#94a3b8",padding:40}}>
          No findings yet. Tap <strong>Codebook</strong> to add from pre-written entries, or use the <strong>+ category</strong> buttons for a blank entry.
        </div>
      )}
      {showCodebook&&<CodebookModal codebook={settings.codebook} onSelect={addFromCodebook} onClose={()=>setShowCodebook(false)}/>}
      {job.findings.length>0&&(
        <div style={{...S.card,background:"linear-gradient(135deg,#065f46,#047857)",border:"none",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:"#fff"}}>{job.findings.length} finding{job.findings.length!==1?"s":""} added</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",marginTop:2}}>Review the contract &amp; proposal next</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={{...S.btnSmOutline,borderColor:"rgba(255,255,255,0.3)",color:"#fff",fontSize:12}}
              onClick={()=>window._tsSetTab&&window._tsSetTab("map")}>← Diagram</button>
            <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:800,fontSize:13,padding:"9px 18px"}}
              onClick={()=>window._tsSetTab&&window._tsSetTab("contract")}>→ Contract</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function diagramFootprintSqft(job){
    if(!job||!job.mapCenter||!(job.drawings||[]).length) return 0;
    const closed=(job.drawings||[]).filter(d=>d.closed&&d.pts&&d.pts.length>=3);
    if(!closed.length) return 0;
    const center=[CANVAS_W/2, (job.canvasH||CANVAS_H)/2];
    const atCenter=closed.find(d=>ptInPoly(center,d.pts));
    const best=atCenter||closed.reduce((a,b)=>{
      const aA=geoAreaSqFt(a.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,CANVAS_W,job.canvasH||CANVAS_H,job.mapPixelScale||MAP_PIXEL_SCALE);
      const bA=geoAreaSqFt(b.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,CANVAS_W,job.canvasH||CANVAS_H,job.mapPixelScale||MAP_PIXEL_SCALE);
      return bA>aA?b:a;
    });
    return Math.round(geoAreaSqFt(best.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,CANVAS_W,job.canvasH||CANVAS_H,job.mapPixelScale||MAP_PIXEL_SCALE));
}

export function syncJobDiagramAreas(job,settings,canPrice=true){
  if(!job.mapCenter||!(job.drawings||[]).some(d=>d.closed&&d.pts?.length>=3))return job.findings;
  const rates=settings?.rates||{};
  return syncDiagramAreas(job.findings,diagramFootprintSqft(job),canPrice?(f,cu)=>{
    const rate=Number(rates.fumigationLocked?rates.fumigation:(f.fumeRate||rates.fumigation))||0;
    return rate>0?Math.round(cu*cuFtConversionFactor(rates.fumigationUnit||"÷1,000 cu ft")*rate)+(f.evidence?repairLabor(f):0):f.primaryPrice;
  }:null);
}

function volumeFootprints(job){
  return (job?.drawings||[]).map((d,i)=>{
    if(!d.closed||d.pts?.length<3)return null;
    const sqft=job.mapCenter?geoAreaSqFt(d.pts,job.mapCenter,job.mapZoom,job.mapRotation||0,CANVAS_W,job.canvasH||CANVAS_H,job.mapPixelScale||MAP_PIXEL_SCALE):Number(d.sqft)||0;
    return sqft>0?{id:d.id||`drawing-${i}`,label:d.name||`Drawing ${i+1}`,sqft:Math.round(sqft)}:null;
  }).filter(Boolean);
}

export function FindingRow({finding,onEdit,onDelete,onUpdate,job,settings}){
  const cat=WDO_CATEGORIES.find(c=>c.num===finding.cat);

  // Mini volume calculator state (cat===2 only)
  const heights = loadFumeHeights() || DEFAULT_FUME_HEIGHTS;
  const initHt = finding.fumeHeightFt
    ? (heights.find(h=>h.ft===finding.fumeHeightFt)||null)
    : null;
  const diagramSqft = useMemo(()=>diagramFootprintSqft(job),[job]);
  const [sqftInput,setSqft]=useState(finding.fumeSqft||'');
  const sqft=followsDiagramArea(finding)&&diagramSqft>0?String(diagramSqft):sqftInput;
  const [selHt,setSelHt]=useState(initHt?initHt.id:null);
  const [customHeight,setCustomHeight]=useState(!initHt&&finding.fumeHeightFt?String(finding.fumeHeightFt):"");

  const activeFt=selHt?(heights.find(h=>h.id===selHt)?.ft||0):(Number(customHeight)>0?Number(customHeight):0);
  const sqftNum=Number(sqft)||0;
  const cubicFt=sqftNum&&activeFt?Math.round(sqftNum*activeFt):(Number(finding.fumeCubicFt)||0);
  const rateLocked=!!(settings?.rates?.fumigationLocked);
  const settingsRateVal=parseFloat(settings?.rates?.fumigation)||0;
  const rate=rateLocked?settingsRateVal:parseFloat(finding.fumeRate||settingsRateVal||0);
  const fumeUnit=settings?.rates?.fumigationUnit||"÷1,000 cu ft";
  const convFactor=cuFtConversionFactor(fumeUnit);
  const calcPrice=cubicFt&&rate?Math.round(cubicFt*convFactor*rate):0;

  // One-time self-heal: fumigation prices saved under the old "cu m" formula were
  // ~28× too high. Recompute the stored price from the saved volume using the
  // current unit factor — once per finding (fumePriceFixed flag), so any manual
  // edit made afterward is preserved.
  useEffect(()=>{
    if(!onUpdate || !isFumigationFinding(finding) || finding.fumePriceFixed || (followsDiagramArea(finding)&&diagramSqft>0)) return;
    const cu = Number(finding.fumeCubicFt) || (Number(finding.fumeSqft)*Number(finding.fumeHeightFt)) || 0;
    if(cu>0 && rate>0){
      const correct = Math.round(cu*convFactor*rate)+(finding.evidence?repairLabor(finding):0);
      if(correct>0 && correct!==finding.primaryPrice){
        onUpdate({...finding, primaryPrice:correct, fumePriceFixed:true});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[finding.id, rate, convFactor]);

  function applyVolume(newSqft,ft,source=followsDiagramArea(finding)?'diagram':'manual'){
    if(!onUpdate || !Number.isFinite(ft) || ft<0) return;
    const cu=ft&&Number(newSqft)?Math.round(Number(newSqft)*ft):0;
    const pr=cu&&rate?Math.round(cu*convFactor*rate)+(finding.evidence?repairLabor(finding):0):finding.primaryPrice;
    onUpdate({...finding,fumeSqft:newSqft,fumeSqftSource:source,fumeHeightFt:ft,fumeCubicFt:cu,fumeVolumeEstimate:null,fumeMeasuredVal:"",primaryPrice:pr||finding.primaryPrice});
  }

  return(
    <div style={S.findingCard}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
        <div style={S.findingBadge(cat.color)}>{finding.cat}{finding.letter}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:13,color:"#1a1a2e",marginBottom:3}}>{cat.label}{finding.component?<span style={{fontWeight:600,color:"#475569"}}> · 🔧 {finding.component}</span>:null}{(finding.repairHours!==""&&finding.repairHours!=null)?<span style={{fontWeight:600,color:"#b45309"}}> · ⏱ {finding.repairHours} hr{Number(finding.repairHours)===1?"":"s"}</span>:null}</div>
          <div style={{fontSize:12,color:"#475569",marginBottom:4}}>{finding.finding}</div>
          <div style={{fontSize:12,color:"#0f172a"}}><strong>Rec:</strong> {finding.primaryRec}</div>
          {finding.hasSecondary&&finding.secondaryRec&&(
            <div style={{fontSize:12,color:"#64748b",marginTop:2}}><strong>Alt:</strong> {finding.secondaryRec}</div>
          )}
          {finding.repairAction&&<div style={{fontSize:12,color:"#475569",marginTop:4}}>{replacementSummary(finding)}</div>}
          {finding.repairLocations?.length>0&&<div style={{marginTop:8,display:'grid',gap:4}}>{finding.repairLocations.map((l,i)=><div key={l.id} style={{fontSize:12,color:'#475569'}}>{repairLocationSummary(l,i)}{l.notes?` · ${l.notes}`:''}</div>)}</div>}
          <FindingEvidence finding={finding}/>
          {finding.evidence&&finding.cat===2&&Number(finding.repairHours)>0&&<div style={{fontSize:11,color:'#64748b',marginTop:4}}>Wood repair: {finding.repairHours} hours{finding.woodRepairRate>0?` × $${finding.woodRepairRate}/hour = $${repairLabor(finding)} (included)`:" · office pricing required"}</div>}
          {finding.notes&&<div style={{fontSize:11,color:"#94a3b8",marginTop:3}}>Notes: {finding.notes}</div>}
          {/* Photo thumbnails */}
          {(finding.photos||[]).length>0&&(
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
              {finding.photos.map(ph=>(
                <img key={ph.id} src={ph.data} alt="" style={{width:52,height:40,objectFit:"cover",borderRadius:4,border:"1px solid #e2e8f0",cursor:"pointer"}} onClick={()=>window.open(ph.data,'_blank')}/>
              ))}
            </div>
          )}

          {/* ── Inline volume calculator (Drywood / cat 2 only) ── */}
          {isFumigationFinding(finding)&&onUpdate&&(
            <div style={{marginTop:8,padding:"8px 10px",background:"#eff6ff",borderRadius:8,border:"1px solid #bfdbfe"}}>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"#1e40af",fontWeight:600,whiteSpace:"nowrap"}}>sqft</span>
                <input
                  type="number" inputMode="decimal"
                  value={sqft}
                  onChange={e=>{setSqft(e.target.value); applyVolume(e.target.value,activeFt,"manual");}}
                  style={{width:72,padding:"2px 6px",fontSize:12,fontWeight:700,border:"1px solid #bfdbfe",borderRadius:6,textAlign:"center"}}
                  placeholder={diagramSqft?String(diagramSqft):"sqft"}/>
                {!followsDiagramArea(finding)&&diagramSqft>0&&<button style={S.btnSmOutline} onClick={()=>{setSqft(String(diagramSqft));applyVolume(String(diagramSqft),activeFt,'diagram');}}>Use diagram: {diagramSqft.toLocaleString()} sqft</button>}
                <span style={{fontSize:11,color:"#94a3b8"}}>×</span>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {heights.map(h=>(
                    <button key={h.id}
                      style={{padding:"2px 8px",fontSize:11,fontWeight:600,borderRadius:20,border:"1px solid",
                        borderColor:selHt===h.id?"#3b82f6":"#bfdbfe",
                        background:selHt===h.id?"#3b82f6":"#fff",
                        color:selHt===h.id?"#fff":"#1e40af",cursor:"pointer"}}
                      onClick={()=>{setSelHt(h.id); setCustomHeight(""); applyVolume(sqft,h.ft);}}>
                      {h.label}
                    </button>
                  ))}
                </div>
                <label style={{display:"flex",gap:4,alignItems:"center",fontSize:11,color:"#1e40af",fontWeight:600}}>
                  Height
                  <input type="number" inputMode="decimal" min="0.1" step="any"
                    aria-label={`Height in feet for ${finding.cat}${finding.letter}`}
                    value={selHt?activeFt:customHeight}
                    onChange={e=>{const value=e.target.value; setSelHt(null); setCustomHeight(value); if(Number(value)>0) applyVolume(sqft,Number(value));}}
                    style={{width:76,minHeight:36,padding:"4px 6px",fontSize:16,border:"1px solid #bfdbfe",borderRadius:6,textAlign:"center"}}
                    placeholder="Height"/>
                  ft
                </label>
              </div>
              <VolumeEstimator finding={finding} footprints={volumeFootprints(job)} jobId={job?.id} onApply={patch=>{
                setSqft(patch.fumeSqft);setSelHt(null);setCustomHeight(String(patch.fumeHeightFt));
                const price=rate?Math.round(patch.fumeCubicFt*convFactor*rate)+(finding.evidence?repairLabor(finding):0):finding.primaryPrice;
                onUpdate({...finding,...patch,fumeSqftSource:"manual",primaryPrice:price,fumePriceFixed:true});
              }}/>
              {cubicFt>0&&(
                <div style={{fontSize:11,color:"#1e40af",fontWeight:700,marginTop:5}}>
                  📐 {formatVolume(cubicFt,fumeUnit)} {fumeUnit}{calcPrice>0?` → $${Number(calcPrice).toLocaleString()}`:""}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          {finding.primaryPrice>0&&<div style={{fontWeight:700,fontSize:15,color:"#0f172a"}}>${Number(finding.primaryPrice).toLocaleString()}{finding.priceUnit?<span style={{fontWeight:400,fontSize:11,color:"#64748b"}}> / {finding.priceUnit}</span>:null}</div>}
          {finding.hasSecondary&&finding.secPrice>0&&<div style={{fontSize:12,color:"#64748b"}}>Alt: ${Number(finding.secPrice).toLocaleString()}{finding.priceUnit?` / ${finding.priceUnit}`:""}</div>}
          <div style={{display:"flex",gap:6,marginTop:8,justifyContent:"flex-end"}}>
            {onEdit && <button style={S.btnSmOutline} onClick={onEdit}>Edit</button>}
            {onDelete && <button style={S.btnSm("#dc2626")} onClick={onDelete}>✕</button>}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── FUMIGATION VOLUME CALCULATOR ────────────────────────────────────────────
const FUME_RATE_KEY = 'termitescope_fume_rate_v1';
function loadFumeRate(){ try{ return parseFloat(localStorage.getItem(FUME_RATE_KEY))||0; }catch{ return 0; } }
function saveFumeRate(r){ try{ localStorage.setItem(FUME_RATE_KEY,String(r)); }catch{} }
const FUME_HEIGHT_LAST_KEY = 'termitescope_fume_last_height_v1';
function loadLastFumeHeight(){ try{ return parseFloat(localStorage.getItem(FUME_HEIGHT_LAST_KEY))||0; }catch{ return 0; } }
function saveLastFumeHeight(ft){ try{ if(ft) localStorage.setItem(FUME_HEIGHT_LAST_KEY,String(ft)); }catch{} }

export function FumeCalculator({finding, onChange, job, settings}) {
  const saved = loadFumeHeights() || DEFAULT_FUME_HEIGHTS;

  // Calculate diagramSqft first so we can use it as the initial sqft default.
  // No osmLevels multiplier here — user enters total structure height separately in the height picker.
  // Prefers the polygon that contains the map center (geocoded address) over the largest polygon.
  const diagramSqft = diagramFootprintSqft(job);

  const [heights, setHeights] = useState(saved);
  // Auto-select the height so volume/price calculate with no clicks: use the
  // finding's saved height, else the last height used (unless the inspector
  // entered a measured volume, which takes precedence).
  const [selectedHt, setSelectedHt] = useState(()=>{
    if(finding.fumeHeightFt){ const m=saved.find(h=>h.ft===finding.fumeHeightFt); return m?m.id:null; }
    if(finding.fumeMeasuredVal!=null && finding.fumeMeasuredVal!=="") return null;
    const m=saved.find(h=>h.ft===loadLastFumeHeight());
    return m?m.id:null;
  });
  // Auto-fill from diagram when creating a new finding (fumeSqft not yet set)
  const [sqftInput,setSqft] = useState(finding.fumeSqft||'');
  const [sqftSource,setSqftSource] = useState(followsDiagramArea(finding)?'diagram':'manual');
  const sqft=sqftSource==='diagram'&&diagramSqft>0?String(diagramSqft):sqftInput;
  const [customHt, setCustomHt] = useState(()=>finding.fumeHeightFt&&!saved.some(h=>h.ft===finding.fumeHeightFt)?String(finding.fumeHeightFt):"");
  const [showAddHt, setShowAddHt] = useState(false);
  const [newHtLabel, setNewHtLabel] = useState("");
  const [newHtFt, setNewHtFt] = useState("");
  const [savePermanent, setSavePermanent] = useState(true);
  // Rate priority: locked settings → saved on finding → settings profile → localStorage legacy
  const settingsRate = settings?.rates?.fumigation || 0;
  const fumeUnit = settings?.rates?.fumigationUnit || "÷1,000 cu ft";
  const rateLocked = !!(settings?.rates?.fumigationLocked);
  const [ratePerCuFt, setRatePerCuFt] = useState(()=> rateLocked ? settingsRate : (finding.fumeRate || settingsRate || loadFumeRate()));

  const activeFt = selectedHt ? (heights.find(h=>h.id===selectedHt)?.ft || 0) : (customHt ? Number(customHt) : 0);
  const sqftNum = Number(sqft) || 0;
  const cubicFt = sqftNum && activeFt ? Math.round(sqftNum * activeFt) : (Number(finding.fumeCubicFt)||0);

  const rate = rateLocked ? (parseFloat(settingsRate) || 0) : (parseFloat(ratePerCuFt) || 0);
  const convFactor = cuFtConversionFactor(fumeUnit);
  const isDiv1000 = convFactor === 0.001;
  const volumeInUnit = cubicFt * convFactor;          // volume expressed in the user's pricing unit
  const calculatedPrice = volumeInUnit && rate ? Math.round(volumeInUnit * rate) : 0;
  // Human-readable unit label for display
  const unitLabel = isDiv1000 ? "billing units (÷1,000)" : fumeUnit;

  useEffect(()=>{
    const stillReviewed=finding.fumeVolumeEstimate&&Number(sqft)===Number(finding.fumeSqft)&&activeFt===finding.fumeHeightFt;
    onChange({fumeSqft: sqft, fumeSqftSource:sqftSource, fumeHeightFt: activeFt, fumeCubicFt: cubicFt,
              fumeVolumeEstimate:stillReviewed?finding.fumeVolumeEstimate:null,
              ...(activeFt>0?{fumeMeasuredVal:""}:{}),fumeRate: rate, calculatedPrice});
  // eslint-disable-next-line
  },[sqft, sqftSource, selectedHt, customHt, ratePerCuFt]);

  function addCustomHeight() {
    if(!newHtFt || isNaN(Number(newHtFt))) return;
    const label = newHtLabel || `${newHtFt} ft`;
    const newH = {id:"fh_"+Date.now(), label, ft:Number(newHtFt), custom:true};
    const updated = [...heights, newH];
    setHeights(updated);
    if(savePermanent) saveFumeHeights(updated);
    setSelectedHt(newH.id);
    setNewHtLabel(""); setNewHtFt(""); setShowAddHt(false);
  }

  function removeHeight(id) {
    const updated = heights.filter(h=>h.id!==id);
    setHeights(updated);
    saveFumeHeights(updated);
    if(selectedHt===id) setSelectedHt(null);
  }

  return (
    <div style={{marginTop:14,padding:"14px 16px",background:"#eff6ff",borderRadius:10,border:"1px solid #bfdbfe"}}>
      <div style={{fontWeight:700,fontSize:13,color:"#1e40af",marginBottom:10}}>
        🏠 Fumigation Volume Calculator
      </div>
      <VolumeEstimator finding={finding} footprints={volumeFootprints(job)} jobId={job?.id} onApply={patch=>{
        setSqft(patch.fumeSqft);setSqftSource("manual");setSelectedHt(null);setCustomHt(String(patch.fumeHeightFt));
        onChange({...patch,fumeSqftSource:"manual",fumeRate:rate,calculatedPrice:rate?Math.round(patch.fumeCubicFt*convFactor*rate):0});
      }}/>

      {finding.fumeMeasuredVal!=null && finding.fumeMeasuredVal!=="" && (
        <div style={{marginBottom:10,padding:"8px 10px",background:"#dbeafe",border:"1px solid #93c5fd",borderRadius:8,fontSize:12,color:"#1e3a8a",fontWeight:600}}>
          📐 Inspector measured: {finding.fumeMeasuredVal} {finding.fumeMeasuredUnit||"cu ft"}
          {finding.fumeMeasuredUnit==="cu m" ? ` (${Number(finding.fumeCubicFt||0).toLocaleString()} cu ft)` : ""}
          . Leave the height unselected to price this measured volume, or enter sqft × height to recalculate.
        </div>
      )}

      {/* Sq Ft input */}
      <div style={{marginBottom:12}}>
        <label style={{...S.label,color:"#1e40af"}}>Structure Square Footage</label>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input
            style={{...S.input, maxWidth:160, fontWeight:700, fontSize:16}}
            type="number" inputMode="decimal"
            value={sqft}
            onChange={e=>{setSqft(e.target.value);setSqftSource("manual");}}
            onFocus={e=>{ if(e.target.value==="0") setSqft(""); }}
            placeholder={diagramSqft ? String(diagramSqft) : "e.g. 1850"}/>
          {diagramSqft>0 && Number(sqft)!==diagramSqft && (
            <button
              style={{...S.btnSmOutline,fontSize:11,color:"#1e40af",borderColor:"#bfdbfe"}}
              onClick={()=>{setSqft(String(diagramSqft));setSqftSource("diagram");}}
              title="Reset to sq ft from diagram polygon">
              ↺ {diagramSqft.toLocaleString()} from diagram
            </button>
          )}
          <span style={{fontSize:13,color:"#64748b"}}>sq ft</span>
        </div>
      </div>

      {/* Height preset buttons */}
      <div style={{marginBottom:10}}>
        <label style={{...S.label,color:"#1e40af"}}>Ceiling / Height</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
          {heights.map(h=>(
            <div key={h.id} style={{position:"relative",display:"inline-flex"}}>
              <button
                style={{
                  padding:"6px 14px", borderRadius:8, border:"none", cursor:"pointer",
                  fontWeight:700, fontSize:13,
                  background: selectedHt===h.id ? "#1e40af" : "#e0e7ff",
                  color: selectedHt===h.id ? "#fff" : "#1e40af",
                  transition:"all 0.15s"
                }}
                onClick={()=>{ const sel=selectedHt===h.id?null:h.id; setSelectedHt(sel); if(sel) saveLastFumeHeight(h.ft); setCustomHt(""); }}>
                {h.label}
              </button>
              {h.custom && (
                <button
                  style={{position:"absolute",top:-6,right:-6,width:16,height:16,borderRadius:"50%",
                    background:"#dc2626",color:"#fff",border:"none",cursor:"pointer",
                    fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}
                  onClick={()=>removeHeight(h.id)}>✕</button>
              )}
            </div>
          ))}
          {/* Other / custom */}
          <button
            style={{padding:"6px 14px",borderRadius:8,border:"1px dashed #93c5fd",cursor:"pointer",
              fontWeight:600,fontSize:13,background:"transparent",color:"#3b82f6"}}
            onClick={()=>{ setSelectedHt(null); setShowAddHt(!showAddHt); }}>
            + Other
          </button>
        </div>

        {/* Quick custom height inline */}
        {!selectedHt && !showAddHt && (
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input
              style={{...S.input,maxWidth:100}}
              type="number" inputMode="decimal"
              value={customHt}
              onFocus={e=>{ if(e.target.value==="0") setCustomHt(""); }}
              onChange={e=>setCustomHt(e.target.value)}
              placeholder="ft"/>
            <span style={{fontSize:12,color:"#64748b"}}>ft (one-time)</span>
          </div>
        )}

        {/* Add new preset */}
        {showAddHt && (
          <div style={{background:"#fff",borderRadius:8,padding:12,border:"1px solid #bfdbfe",marginTop:8}}>
            <div style={{fontWeight:600,fontSize:12,color:"#1e40af",marginBottom:8}}>Add New Height Button</div>
            <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
              <input style={{...S.input,maxWidth:100}} type="number" inputMode="decimal"
                value={newHtFt}
                onFocus={e=>{ if(e.target.value==="0") setNewHtFt(""); }}
                onChange={e=>setNewHtFt(e.target.value)} placeholder="Height ft"/>
              <input style={{...S.input,maxWidth:140}}
                value={newHtLabel} onChange={e=>setNewHtLabel(e.target.value)} placeholder="Label (optional)"/>
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:8}}>
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,cursor:"pointer"}}>
                <input type="checkbox" checked={savePermanent} onChange={e=>setSavePermanent(e.target.checked)}/>
                Save permanently to my height buttons
              </label>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button style={S.btn("#1e40af")} onClick={addCustomHeight}>Add</button>
              <button style={S.btnOutline} onClick={()=>setShowAddHt(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Rate input */}
      {cubicFt > 0 && (
        <div style={{marginBottom:10}}>
          <label style={{...S.label,color:"#1e40af",display:"flex",alignItems:"center",gap:6}}>
            Rate ($ / {isDiv1000 ? "1,000 cu ft" : fumeUnit})
            {rateLocked&&<span style={{fontSize:11,color:"#15803d",fontWeight:600,background:"#dcfce7",padding:"1px 6px",borderRadius:10}}>🔒 Locked</span>}
          </label>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:14,color:"#64748b"}}>$</span>
            <input
              style={{...S.input,maxWidth:120,fontWeight:700,background:rateLocked?"#f0fdf4":"",color:rateLocked?"#15803d":""}}
              type="number" inputMode="decimal" step="0.01"
              value={rateLocked ? settingsRate : (ratePerCuFt||"")}
              readOnly={rateLocked}
              onChange={rateLocked ? undefined : e=>{ setRatePerCuFt(e.target.value); saveFumeRate(parseFloat(e.target.value)||0); }}
              onFocus={rateLocked ? undefined : e=>{ if(e.target.value==="0") setRatePerCuFt(""); }}
              placeholder="e.g. 89"/>
            <span style={{fontSize:12,color:"#64748b"}}>per {isDiv1000 ? "1,000 cu ft" : fumeUnit}</span>
            {calculatedPrice > 0 && (
              <span style={{fontSize:13,fontWeight:700,color:"#1e40af",marginLeft:4}}>
                = ${calculatedPrice.toLocaleString()}
              </span>
            )}
          </div>
          {rateLocked&&<div style={{fontSize:11,color:"#15803d",marginTop:4}}>Rate set in Settings → Pricing. Edit there to change.</div>}
        </div>
      )}

      {/* Result */}
      <div style={{
        background: cubicFt ? "#1e40af" : "#e0e7ff",
        borderRadius:10, padding:"12px 16px",
        display:"flex", alignItems:"center", justifyContent:"space-between"
      }}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:cubicFt?"rgba(255,255,255,0.7)":"#93c5fd",textTransform:"uppercase",letterSpacing:1}}>
            Total Volume
          </div>
          <div style={{fontWeight:800, fontSize:cubicFt?24:18, color:cubicFt?"#fff":"#93c5fd"}}>
            {cubicFt ? Math.round(cubicFt).toLocaleString()+" cu ft" : "—"}
          </div>
          {calculatedPrice > 0 && (
            <div style={{fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.9)",marginTop:4}}>
              @ ${rate}/{isDiv1000?"1,000 cu ft":fumeUnit} = <span style={{fontSize:16}}>${calculatedPrice.toLocaleString()}</span>
            </div>
          )}
        </div>
        {cubicFt > 0 && (
          <div style={{textAlign:"right",fontSize:12,color:"rgba(255,255,255,0.8)"}}>
            <div>{sqftNum.toLocaleString()} sqft × {activeFt} ft</div>
            <div>= {Math.round(cubicFt).toLocaleString()} cu ft</div>
            {isDiv1000 && <div style={{fontSize:11,opacity:0.85}}>÷ 1,000 = {volumeInUnit.toFixed(2)} billing units</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// Inspector-facing fumigation volume entry (cu ft or cu m). Stores the canonical
// cubic-feet value the office prices; pricing/rate stays on the office side.
function InspectorVolume({ finding, onChange, job }) {
  const [val, setVal] = useState(finding.fumeMeasuredVal!==""&&finding.fumeMeasuredVal!=null?finding.fumeMeasuredVal:(finding.fumeCubicFt?String(finding.fumeMeasuredUnit==="cu m"?Number((finding.fumeCubicFt/CUFT_PER_M3).toFixed(2)):finding.fumeCubicFt):""));
  const [unit, setUnit] = useState(finding.fumeMeasuredUnit || "cu ft");
  const num = parseFloat(val) || 0;
  const toCuFt = (n,u) => u==="cu m" ? Math.round(n*CUFT_PER_M3) : Math.round(n);
  const apply = (v,u) => { const n=parseFloat(v)||0; onChange({ fumeMeasuredVal:v, fumeMeasuredUnit:u, fumeCubicFt:toCuFt(n,u), fumeSqft:"", fumeHeightFt:0, fumeVolumeEstimate:null }); };
  return (
    <div style={{marginTop:8,padding:"10px 12px",background:"#eff6ff",borderRadius:8,border:"1px solid #bfdbfe"}}>
      <div style={{fontSize:12,fontWeight:700,color:"#1e40af",marginBottom:6}}>🏠 Measured Fumigation Volume</div>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <input type="number" inputMode="decimal" value={val} placeholder="e.g. 32"
          onChange={e=>{ setVal(e.target.value); apply(e.target.value,unit); }}
          style={{width:110,padding:"6px 8px",border:"1px solid #bfdbfe",borderRadius:6,fontSize:13,fontWeight:700}}/>
        <select aria-label="Measured volume unit" value={unit} onChange={e=>{
          const next=e.target.value;const converted=num?(next==="cu m"?toCuFt(num,unit)/CUFT_PER_M3:toCuFt(num,unit)):"";
          setUnit(next);setVal(converted===""?"":String(Number(converted.toFixed(3))));
        }}
          style={{padding:"6px 8px",border:"1px solid #bfdbfe",borderRadius:6,fontSize:13,cursor:"pointer"}}>
          <option value="cu ft">cu ft</option>
          <option value="cu m">cu m</option>
        </select>
        {num>0 && unit==="cu m" && <span style={{fontSize:12,color:"#1e40af",fontWeight:600}}>= {toCuFt(num,unit).toLocaleString()} cu ft</span>}
      </div>
      <VolumeEstimator finding={finding} footprints={volumeFootprints(job)} jobId={job?.id} onApply={patch=>{
        setUnit("cu ft");setVal(String(patch.fumeCubicFt));onChange(patch);
      }}/>
      <div style={{fontSize:11,color:"#64748b",marginTop:6}}>Enter a measured volume or use height help to estimate it. The office applies the fumigation rate.</div>
    </div>
  );
}

// Dictation button (Web Speech API). Renders nothing where unsupported (e.g.
// some iOS browsers). Appends the recognized phrase via onText.
function MicButton({ onText, title }){
  const [listening,setListening]=useState(false);
  const recRef=useRef(null);
  const SR = typeof window!=='undefined' && (window.SpeechRecognition||window.webkitSpeechRecognition);
  if(!SR) return null;
  function toggle(){
    if(listening){ try{recRef.current&&recRef.current.stop();}catch{} return; }
    const rec=new SR();
    rec.lang='en-US'; rec.interimResults=false; rec.continuous=false; rec.maxAlternatives=1;
    rec.onresult=(e)=>{ const t=Array.from(e.results).map(r=>r[0].transcript).join(' ').trim(); if(t) onText(t); };
    rec.onend=()=>setListening(false);
    rec.onerror=()=>setListening(false);
    recRef.current=rec; setListening(true);
    try{ rec.start(); }catch{ setListening(false); }
  }
  return (
    <button type="button" onClick={toggle} title={title||"Dictate"}
      style={{border:"none",background:listening?"#dc2626":"#eef2ff",color:listening?"#fff":"#4338ca",borderRadius:6,padding:"3px 9px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
      {listening?"● Listening…":"🎤 Dictate"}
    </button>
  );
}

function FindingEditor({finding,markers,onSave,onCancel,isNew,job,settings,priceLocked}){
  const [f,setF]=useState({fumeSqft:"",fumeHeightFt:0,fumeCubicFt:0,photos:[],component:"",repairHours:"",paintRequired:isNew&&(finding.kind==="repair"||finding.cat===3||(finding.cat===2&&finding.letter==="B"))?true:"",...finding});
  const cat=WDO_CATEGORIES.find(c=>c.num===f.cat);
  const uf=(field)=>(e)=>setF(x=>({...x,[field]:e.target.value}));
  const appendF=(field,text)=>setF(x=>({...x,[field]:((x[field]||"").trim()?(x[field].trim()+" "):"")+text}));
  // Wood-repair hourly pricing: findings priced by hours × operator rate, except
  // the fumigation case (cat 2 non-B) which keeps the volume calculator.
  const repairRate = f.evidence&&f.cat===2?Number(f.woodRepairRate)||0:Number(settings?.rates?.repairHourly)||0;
  const isFumeFinding = isFumigationFinding(f);
  const labelRow=(text,field)=>(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
      <label style={{...S.label,marginBottom:0}}>{text}</label>
      <MicButton onText={t=>appendF(field,t)} title={`Dictate ${text}`}/>
    </div>
  );
  const linkedMarkers=(markers||[]).filter(m=>m.label.startsWith(String(f.cat)));
  return(
    <div style={{...S.findingCard,border:`2px solid ${cat.color}`,background:"#f8fafc",marginBottom:12}}>
      <div style={{display:"flex",gap:10,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        <div style={S.findingBadge(cat.color)}>{f.cat}{f.letter}</div>
        <strong style={{fontSize:14,color:cat.color}}>{cat.label} — {isNew?"New Finding":"Edit"}</strong>
        {!f.repairLocations&&linkedMarkers.length>0&&(
          <div style={{marginLeft:"auto"}}>
            <label style={{...S.label,marginBottom:2}}>Map Pin</label>
            <select style={{...S.select,width:120}} value={f.markerId||""} onChange={e=>setF(x=>({...x,markerId:e.target.value}))}>
              <option value="">None</option>
              {linkedMarkers.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{marginBottom:10}}>
        {labelRow("Finding","finding")}
        <textarea style={{...S.textarea,minHeight:60}} value={f.finding} onChange={uf("finding")}/>
      </div>
      {f.repairLocations?.length ? <div style={{background:'#0f172a',padding:14,borderRadius:10,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:'#e2e8f0',marginBottom:8}}>{f.repairLocations.length} location(s) · {f.repairHours} total hours</div>
        {f.repairLocations.map((l,index)=><div key={l.id} style={{marginBottom:12}}>
          <div style={{color:'#94a3b8',fontSize:12}}>Location {index+1}</div>
          <DiagramFindingDetails findings={[{...f,...l,id:l.id,primaryPrice:l.price,repairLocations:undefined,kind:'repair'}]} components={REPAIR_COMPONENTS} settings={settings} onChange={(id,patch)=>setF(current=>{
            const location=current.repairLocations.find(item=>item.id===id);
            const next=updateRepairEstimate({...current,...location,kind:'repair',primaryPrice:location.price,repairLocations:undefined},patch,settings);
            const locations=current.repairLocations.map(item=>item.id===id?{...item,...next,id:item.id,price:next.primaryPrice,photos:item.photos,estimateNeeded:Object.hasOwn(patch,'repairHours')?false:item.estimateNeeded,repairLocations:undefined}:item);
            return {...current,...Object.fromEntries(['finding','primaryRec','secondaryRec','hasSecondary'].filter(key=>Object.hasOwn(patch,key)).map(key=>[key,patch[key]])),repairLocations:locations,repairHours:locations.reduce((n,item)=>n+(Number(item.repairHours)||0),0),primaryPrice:locations.reduce((n,item)=>n+(Number(item.price)||0),0)};
          })}/>
        </div>)}
      </div> : <>
      <div style={{...S.grid2,marginBottom:10}} className="grid2-mobile">
        <div>
          <label style={S.label}>Repair / Replacement Component</label>
          <input style={S.input} list="repair-components" value={f.component||""} onChange={uf("component")} placeholder="e.g. Fascia board, Rafter tail, Sub-floor…"/>
          <datalist id="repair-components">{REPAIR_COMPONENTS.map(c=><option key={c} value={c}/>)}</datalist>
        </div>
        <div>
          <label style={S.label}>Repair Hours <span style={{fontWeight:400,color:"#94a3b8"}}>(crew estimate)</span></label>
          <input style={S.input} type="number" inputMode="decimal" min="0" step="0.25"
            value={f.repairHours??""}
            onChange={e=>setF(x=>updateRepairEstimate(x,{repairHours:e.target.value},settings))}
            placeholder="e.g. 1.5"/>
          {repairRate>0 && !isFumeFinding && Number(f.repairHours)>0 && (
            <div style={{fontSize:11,color:"#15803d",fontWeight:600,marginTop:4}}>🔨 {f.repairHours} hr × ${repairRate}/hr = ${(Math.round(Number(f.repairHours)*repairRate*100)/100).toLocaleString()}</div>
          )}
          {repairRate===0 && Number(f.repairHours)>0 && !isFumeFinding && (
            <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>Set a Wood Repair rate in Settings → Pricing to auto-price from hours.</div>
          )}
        </div>
      </div>
      {(f.cat===2||f.cat===3)&&<div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:12}}>
        <label style={S.label}>Repair scope<select aria-label="Repair scope" style={S.select} value={f.repairAction||''} onChange={e=>setF(x=>({...x,repairAction:e.target.value,kind:e.target.value?'repair':x.kind}))}><option value="">Select scope</option><option value="repair">Repair</option><option value="replace">Replace</option></select></label>
        <label style={S.label}>Painting required<select aria-label="Painting required" style={S.select} value={f.paintRequired===true?'yes':f.paintRequired===false?'no':''} onChange={e=>setF(x=>({...x,paintRequired:e.target.value===''?'':e.target.value==='yes'}))}><option value="">Not specified</option><option value="yes">Yes</option><option value="no">No</option></select></label>
      </div>}
      </>}
      {priceLocked ? (
        <div style={{marginBottom:10}}>{labelRow("Primary Recommendation","primaryRec")}<textarea style={{...S.textarea,minHeight:60}} value={f.primaryRec} onChange={uf("primaryRec")}/></div>
      ) : (
      <div style={{...S.grid2,marginBottom:10}} className="grid2-mobile">
        <div>{labelRow("Primary Recommendation","primaryRec")}<textarea style={{...S.textarea,minHeight:60}} value={f.primaryRec} onChange={uf("primaryRec")}/></div>
        <div>
          <label style={S.label}>Primary Price ($)</label>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input style={{...S.input,flex:1}} type="number" inputMode="decimal"
              value={f.primaryPrice}
              onFocus={e=>{ if(Number(e.target.value)===0) setF(x=>({...x,primaryPrice:""})); }}
              onBlur={e=>{ if(e.target.value===""||isNaN(Number(e.target.value))) setF(x=>({...x,primaryPrice:0})); }}
              onChange={uf("primaryPrice")}/>
            <span style={{color:"#64748b",fontSize:13}}>/</span>
            <input style={{...S.input,maxWidth:80}} value={f.priceUnit||""} onChange={uf("priceUnit")} placeholder="job"/>
          </div>
        </div>
      </div>
      )}
      <div style={{marginBottom:10}}>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,fontWeight:600,color:"#475569"}}>
          <input type="checkbox" checked={f.hasSecondary} onChange={e=>setF(x=>({...x,hasSecondary:e.target.checked}))}/>
          Include Secondary / Alternative Recommendation
        </label>
      </div>
      {f.hasSecondary&&(priceLocked ? (
        <div style={{marginBottom:10}}><label style={S.label}>Secondary Recommendation</label><textarea style={{...S.textarea,minHeight:60}} value={f.secondaryRec} onChange={uf("secondaryRec")}/></div>
      ) : (
        <div style={{...S.grid2,marginBottom:10}} className="grid2-mobile">
          <div><label style={S.label}>Secondary Recommendation</label><textarea style={{...S.textarea,minHeight:60}} value={f.secondaryRec} onChange={uf("secondaryRec")}/></div>
          <div><label style={S.label}>Secondary Price ($)</label>
            <input style={S.input} type="number" inputMode="decimal"
              value={f.secPrice}
              onFocus={e=>{ if(Number(e.target.value)===0) setF(x=>({...x,secPrice:""})); }}
              onBlur={e=>{ if(e.target.value===""||isNaN(Number(e.target.value))) setF(x=>({...x,secPrice:0})); }}
              onChange={uf("secPrice")}/>
          </div>
        </div>
      ))}
      <div style={{marginBottom:14}}>
        {labelRow("Inspector Notes (internal)","notes")}
        <input style={S.input} value={f.notes} onChange={uf("notes")} placeholder="Optional internal notes..."/>
      </div>
      {isFumigationFinding(f) && (priceLocked
        ? <InspectorVolume finding={f} job={job} onChange={vals=>setF(x=>({...x,...vals}))}/>
        : <FumeCalculator
            finding={f}
            job={job}
            settings={settings}
            onChange={vals=>setF(x=>({
              ...x, ...vals,
              // Always sync primary price to the calculated value
              primaryPrice: vals.calculatedPrice ? vals.calculatedPrice+(x.evidence?repairLabor(x):0) : x.primaryPrice
            }))}/>
      )}
      {f.evidence&&f.cat===2&&!priceLocked&&Number(f.woodRepairRate||0)!==Number(settings?.rates?.repairHourly||0)&&<button style={S.btnSmOutline} onClick={()=>setF(x=>{const base=Math.max(0,Number(x.primaryPrice)-repairLabor(x));const nx={...x,woodRepairRate:Number(settings?.rates?.repairHourly)||0,needsOfficePricing:false};return {...nx,primaryPrice:Math.round((base+repairLabor(nx))*100)/100};})}>Apply current office wood-repair rate (${Number(settings?.rates?.repairHourly)||0}/hour)</button>}
      <PhotoUpload
        photos={f.photos||[]}
        onChange={photosOrFn=>setF(x=>{const p=typeof photosOrFn==='function'?photosOrFn(x.photos||[]):photosOrFn;const ids=new Set(p.map(photo=>photo.id));const known=new Set((x.photos||[]).map(photo=>photo.id));return{...x,photos:p,...(x.repairLocations?{repairLocations:x.repairLocations.map((l,i)=>({...l,photos:[...(l.photos||[]).filter(photo=>ids.has(photo.id)).map(photo=>p.find(updated=>updated.id===photo.id)),...(i===0?p.filter(photo=>!known.has(photo.id)):[])]}))}:{})};})}
        label="Finding Photos"
      />
      <div style={{display:"flex",gap:8,marginTop:14}}>
        <button style={S.btn("#0f172a")} onClick={()=>onSave({...f,primaryPrice:Number(f.primaryPrice)||0,secPrice:Number(f.secPrice)||0,repairHours:(f.repairHours===""||f.repairHours==null)?"":(Number(f.repairHours)||0)})}>✓ Save Finding</button>
        <button style={S.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function CodebookModal({codebook,onSelect,onClose}){
  const [search,setSearch]=useState("");
  const filtered=codebook.filter(e=>e.name.toLowerCase().includes(search.toLowerCase())||e.finding.toLowerCase().includes(search.toLowerCase()));
  const grouped=WDO_CATEGORIES.map(cat=>({cat,entries:filtered.filter(e=>e.cat===cat.num)})).filter(g=>g.entries.length>0);
  return(
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{margin:0,fontSize:18}}>F&amp;R Codebook</h3>
          <button style={S.btnSmOutline} onClick={onClose}>✕</button>
        </div>
        <input style={{...S.input,marginBottom:14}} placeholder="Search entries..." value={search} onChange={e=>setSearch(e.target.value)}/>
        {grouped.map(g=>(
          <div key={g.cat.num} style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:g.cat.color,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{g.cat.num} — {g.cat.label}</div>
            {g.entries.map(e=>(
              <div key={e.id} style={{border:"1px solid #e2e8f0",borderRadius:8,padding:12,marginBottom:8,cursor:"pointer"}}
                onClick={()=>onSelect(e)}>
                <div style={{fontWeight:700,fontSize:13,color:"#1a1a2e",marginBottom:4}}>{e.name}</div>
                <div style={{fontSize:12,color:"#475569",marginBottom:4}}>{e.finding}</div>
                <div style={{fontSize:12,color:"#64748b"}}><strong>Rec:</strong> {e.primaryRec.slice(0,80)}{e.primaryRec.length>80?"…":""}</div>
                {e.defaultPrice>0&&<div style={{fontSize:12,fontWeight:700,color:g.cat.color,marginTop:4}}>${e.defaultPrice.toLocaleString()}{e.priceUnit?<span style={{fontWeight:400,color:"#64748b"}}> / {e.priceUnit}</span>:null}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CONTRACT ─────────────────────────────────────────────────────────────────
function ContractSection({job, updateJob, settings, isInspector, onPrepareSignature}){
  const allF=job.findings||[];
  const [signBusy,setSignBusy]=useState(false);
  const [signMsg,setSignMsg]=useState("");
  const [signErr,setSignErr]=useState("");
  const signLink = job.signatureRequest?.token ? `${window.location.origin}/sign?token=${job.signatureRequest.token}` : "";
  async function sendForSignature(){
    setSignErr(""); setSignMsg("");
    const to = job.propOwner?.email;
    if(!to){ setSignErr("Add the property owner's email in Job Info first."); return; }
    setSignBusy(true);
    try{
      if(!onPrepareSignature)throw Error('Sign in with an office account to send proposals.');
      await onPrepareSignature();
      const request=await createRemoteProposal(supabase,job,settings?.company);
      updateJob && updateJob(current=>String(current.id)===String(job.id)?{signatureRequest:request}:{});
      await emailRemoteProposal(supabase,request.token);
      updateJob && updateJob(current=>String(current.id)===String(job.id)?{signatureRequest:{...request,emailSent:true,sentAt:new Date().toISOString()}}:{});
      setSignMsg(`Proposal emailed to ${request.sentTo}.`);
    }catch(e){ setSignErr(String(e?.message||e)); }
    setSignBusy(false);
  }
  const sI1=allF.filter(f=>f.cat<=3).reduce((s,f)=>s+Number(f.primaryPrice||0),0);
  const sI2=allF.filter(f=>(f.cat<=3&&f.hasSecondary)).reduce((s,f)=>s+Number(f.secPrice||0),0);
  const sII1=allF.filter(f=>f.cat===4).reduce((s,f)=>s+Number(f.primaryPrice||0),0);
  const sII2=allF.filter(f=>(f.cat===4&&f.hasSecondary)).reduce((s,f)=>s+Number(f.secPrice||0),0);
  const gP=sI1+sII1; const gS=sI2+sII2;
  function updatePrice(id,field,val){
    if(updateJob) updateJob({findings:allF.map(f=>f.id===id?{...f,[field]:val}:f)});
  }
  function updateBidProgress(id,stage){
    if(updateJob) updateJob({findings:allF.map(f=>f.id===id?{...f,bidProgress:{...(f.bidProgress||{}),[stage]:!(f.bidProgress||{})[stage]}}:f)});
  }
  const secsByLabel=[
    {label:"SECTION I",cats:[1,2,3]},
    {label:"SECTION II",cats:[4]},
    {label:"FURTHER INSPECTION",cats:[5]},
  ];
  return(
    <div>
      <div style={S.card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <div style={S.cardTitle}>Contract / Proposal — Report #{job.reportNum}</div>
          <div style={{fontSize:11,color:"#94a3b8"}}>Prices editable inline</div>
        </div>
        <div style={{marginBottom:16,fontSize:13,color:"#475569"}}>{[job.address,job.city,job.state,job.zip].filter(Boolean).join(", ")}</div>
        {secsByLabel.map(sec=>{
          const sf=allF.filter(f=>sec.cats.includes(f.cat));
          if(!sf.length) return null;
          return(
            <div key={sec.label} style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8,borderBottom:"1px solid #e2e8f0",paddingBottom:4}}>{sec.label}</div>
              {sf.map((f,idx2)=>{
                const cat=WDO_CATEGORIES.find(c=>c.num===f.cat);
                return(
                  <div key={f.id} style={{background:idx2%2===0?"#f8fafc":"#fff",borderRadius:8,padding:"12px 14px",marginBottom:6,border:"1px solid #e2e8f0"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                      <span style={{flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,borderRadius:6,background:cat.color,color:"#fff",fontSize:12,fontWeight:800}}>{f.cat}{f.letter}</span>
                      <span style={{fontWeight:700,fontSize:14,color:"#0f172a"}}>{cat.label}</span>
                      <span style={{fontSize:11,color:"#94a3b8",marginLeft:"auto"}}>{sectionLabel(f.cat)}</span>
                    </div>
                    {(()=>{const sm=window.innerWidth<520; return(<>
                    {f.finding&&<div style={{fontSize:12,color:"#475569",marginBottom:8,paddingLeft:sm?0:40,lineHeight:1.5}}>{f.finding}</div>}
                    {f.primaryRec&&(
                      <div style={{display:"flex",alignItems:"flex-start",gap:12,paddingLeft:sm?0:40,marginBottom:f.hasSecondary?8:0,flexWrap:"wrap"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:11,fontWeight:700,color:cat.color,marginBottom:2}}>PRIMARY RECOMMENDATION</div>
                          <div style={{fontSize:12,color:"#1e293b",lineHeight:1.5}}>{f.primaryRec}</div>
                        </div>
                        <div style={{flexShrink:0,textAlign:"right",width:sm?"100%":"auto",minWidth:100}}>
                          <div style={{fontSize:11,fontWeight:600,color:"#64748b",marginBottom:3}}>Primary $</div>
                          <input style={{...S.input,textAlign:"right",maxWidth:sm?"100%":100,fontWeight:700,fontSize:14,padding:"4px 8px"}}
                            type="number" inputMode="decimal" value={f.primaryPrice}
                            onFocus={e=>{ if(Number(e.target.value)===0) updatePrice(f.id,"primaryPrice",""); }}
                            onBlur={e=>{ if(e.target.value===""||isNaN(Number(e.target.value))) updatePrice(f.id,"primaryPrice",0); }}
                            onChange={e=>updatePrice(f.id,"primaryPrice",e.target.value)}/>
                        </div>
                      </div>
                    )}
                    {f.hasSecondary&&f.secondaryRec&&(
                      <div style={{display:"flex",alignItems:"flex-start",gap:12,paddingLeft:sm?0:40,paddingTop:8,borderTop:"1px dashed #e2e8f0",marginTop:4,flexWrap:"wrap"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:11,fontWeight:700,color:"#64748b",marginBottom:2}}>SECONDARY / ALTERNATIVE</div>
                          <div style={{fontSize:12,color:"#475569",lineHeight:1.5}}>{f.secondaryRec}</div>
                        </div>
                        <div style={{flexShrink:0,textAlign:"right",width:sm?"100%":"auto",minWidth:100}}>
                          <div style={{fontSize:11,fontWeight:600,color:"#64748b",marginBottom:3}}>Secondary $</div>
                          <input style={{...S.input,textAlign:"right",maxWidth:sm?"100%":100,fontSize:14,padding:"4px 8px"}}
                            type="number" inputMode="decimal" value={f.secPrice}
                            onFocus={e=>{ if(Number(e.target.value)===0) updatePrice(f.id,"secPrice",""); }}
                            onBlur={e=>{ if(e.target.value===""||isNaN(Number(e.target.value))) updatePrice(f.id,"secPrice",0); }}
                            onChange={e=>updatePrice(f.id,"secPrice",e.target.value)}/>
                        </div>
                      </div>
                    )}
                    </>);})()}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{background:"#f1f5f9",borderRadius:10,padding:16,marginTop:4}}>
          {[["Section I Subtotal (Primary)",sI1],sI2>0?["Section I Subtotal (Secondary)",sI2,true]:null,sII1>0?["Section II Subtotal",sII1]:null,sII2>0?["Section II Subtotal (Secondary)",sII2,true]:null].filter(Boolean).map((row,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",color:row[2]?"#64748b":"#1a1a2e",fontWeight:row[2]?400:600}}>
              <span>{row[0]}</span><span>${Number(row[1]).toLocaleString()}</span>
            </div>
          ))}
          {job.reportType==="Separated Report" ? (
            <div style={{borderTop:"2px solid #cbd5e1",marginTop:10,paddingTop:10}}>
              <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:6}}>SEPARATED REPORT — authorize each section independently</div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}>
                <span style={{fontWeight:800,fontSize:15,color:"#0f172a"}}>Section I Total (Primary)</span>
                <span style={{fontWeight:800,fontSize:18,color:"#0f172a"}}>${Number(sI1).toLocaleString()}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0"}}>
                <span style={{fontWeight:800,fontSize:15,color:"#0f172a"}}>Section II Total (Primary)</span>
                <span style={{fontWeight:800,fontSize:18,color:"#0f172a"}}>${Number(sII1).toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <div style={{borderTop:"2px solid #cbd5e1",marginTop:10,paddingTop:10,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontWeight:800,fontSize:16,color:"#0f172a"}}>Grand Total (Primary)</span>
              <span style={{fontWeight:800,fontSize:20,color:"#0f172a"}}>${gP.toLocaleString()}</span>
            </div>
          )}
          {gS>0&&<div style={{display:"flex",justifyContent:"space-between",marginTop:4}}><span style={{color:"#64748b",fontSize:13}}>Grand Total (Secondary)</span><span style={{fontWeight:600,color:"#64748b"}}>${gS.toLocaleString()}</span></div>}
        </div>
      </div>
      {job.findings.some(f=>f.cat===5)&&(
        <div style={{...S.card,border:"1px solid #e0e7ff",background:"#eef2ff",padding:"10px 16px"}}>
          <div style={{fontSize:12,color:"#4338ca"}}>ℹ Further Inspection items are not included in the quote. Pricing determined after inspection of inaccessible areas.</div>
        </div>
      )}
      <div style={{...S.card,border:"1px solid #fcd34d",background:"#fffbeb"}}>
        <div style={{fontSize:12,color:"#92400e",fontWeight:600,marginBottom:6}}>ACKNOWLEDGEMENT</div>
        <div style={{fontSize:12,color:"#92400e",marginBottom:12}}>No work will be performed until a signed copy of this agreement has been received. This proposal is valid for 30 days from the date of inspection.</div>
        {/* E-Signature */}
        <div style={{borderTop:"1px solid #fde68a",paddingTop:12,marginTop:4}}>
          <div style={{fontWeight:700,fontSize:12,color:"#92400e",marginBottom:4}}>CLIENT SIGNATURE</div>
          <div style={{fontSize:11,color:"#b45309",marginBottom:6}}>By signing, client acknowledges receipt and agreement to the above inspection report and contract terms.</div>
          <ClientSignaturePanel
            signature={job.clientSignature||""}
            onChange={sig=>updateJob({clientSignature:sig})}
          />
          {job.clientSignature && (
            <div style={{fontSize:11,color:"#15803d",marginTop:6,fontWeight:600}}>✓ Signature captured — will be included in the PDF report</div>
          )}
        </div>
      </div>

      {/* Remote e-sign — email the owner a link to review & sign */}
      {!isInspector && (
      <div style={{...S.card,border:"1px solid #ddd6fe",background:"#faf5ff"}}>
        <div style={{fontWeight:800,fontSize:13,color:"#6d28d9",marginBottom:4}}>✍️ Remote Signature</div>
        {job.proposalAccepted ? (
          <div style={{fontSize:12.5,color:"#15803d",fontWeight:600}}>✓ Signed by {job.proposalSignedName||"the owner"}{job.proposalSignedAt?` on ${new Date(job.proposalSignedAt).toLocaleString()}`:""} — signature is included in the PDF report.</div>
        ) : (
          <>
            <div style={{fontSize:12,color:"#7c3aed",marginBottom:10}}>Email the property owner a secure link to review this proposal and sign to authorize the work.</div>
            {job.signatureRequest?.status==="pending" && (
              <div style={{fontSize:12,color:"#6d28d9",marginBottom:10,padding:"8px 12px",background:"#f3e8ff",borderRadius:8,border:"1px solid #e9d5ff"}}>
                {job.signatureRequest.emailSent===false?'Link ready for':'Sent to'} {job.signatureRequest.sentTo}{job.signatureRequest.sentAt?` · ${new Date(job.signatureRequest.sentAt).toLocaleString()}`:""}{job.signatureRequest.emailSent===false?' — email not sent. Copy the link below to send it manually.':' — awaiting signature.'}
              </div>
            )}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <button style={{...S.btn(job.propOwner?.email?"#7c3aed":"#94a3b8")}} onClick={sendForSignature} disabled={signBusy||!job.propOwner?.email}>
                {signBusy?"Sending…":(job.signatureRequest?.status==="pending"?"Resend to owner":"Send proposal to owner")}
              </button>
              {signLink && (
                <button style={S.btnOutline} onClick={()=>{ try{navigator.clipboard?.writeText(signLink);}catch{} setSignMsg("Link copied to clipboard."); }}>Copy link</button>
              )}
            </div>
            {!job.propOwner?.email && <div style={{fontSize:11,color:"#b45309",marginTop:6}}>Add the property owner's email in Job Info to enable this.</div>}
            {signMsg && <div style={{fontSize:12,color:"#15803d",marginTop:8,fontWeight:600}}>{signMsg}</div>}
            {signErr && <div style={{fontSize:12,color:"#dc2626",marginTop:8}}>{signErr}</div>}
            {signLink && <div style={{fontSize:11,color:"#94a3b8",marginTop:8,wordBreak:"break-all"}}>{signLink}</div>}
          </>
        )}
      </div>
      )}
      {/* Nav banner → PDF */}
      <div style={{...S.card,background:"linear-gradient(135deg,#1e3a5f,#1e40af)",border:"none",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#fff"}}>Contract ready</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",marginTop:2}}>Generate &amp; send the PDF report next</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...S.btnSmOutline,borderColor:"rgba(255,255,255,0.3)",color:"#fff",fontSize:12}}
            onClick={()=>window._tsSetTab&&window._tsSetTab("findings")}>← Findings</button>
          <button style={{...S.btn("#22d3ee"),color:"#0f172a",fontWeight:800,fontSize:13,padding:"9px 18px"}}
            onClick={()=>window._tsSetTab&&window._tsSetTab("report")}>→ PDF</button>
        </div>
      </div>
    </div>
  );
}
// ─── REPORT / PDF ─────────────────────────────────────────────────────────────
function ReportSection({job,settings,jsPdfLoaded,updateJob}){
  const [busy,      setBusy]      = useState(null); // null | "preview" | "download" | "email" | "resend" | "sms"
  const [error,     setError]     = useState("");
  const [emailSent, setEmailSent] = useState(false);
  // Candidate recipients drawn from the job's contacts, de-duplicated.
  const recipientOpts = Array.from(new Set([
    {label:"Ordered by",  email:job.orderedBy?.email},
    {label:"Property owner", email:job.propOwner?.email},
    {label:"Agent / party", email:job.partyOfInterest?.email},
  ].filter(r=>r.email).map(r=>JSON.stringify(r)))).map(s=>JSON.parse(s));
  const [picked, setPicked] = useState(()=>new Set(recipientOpts.map(r=>r.email)));
  const [extraEmail, setExtraEmail] = useState("");
  const toggle = (email)=> setPicked(p=>{ const n=new Set(p); n.has(email)?n.delete(email):n.add(email); return n; });
  const chosenRecipients = ()=>{
    const list = [...picked];
    const ex = extraEmail.trim();
    if(ex && /\S+@\S+\.\S+/.test(ex)) list.push(ex);
    return Array.from(new Set(list));
  };

  const checks=[
    {label:"Property address",     ok:!!job.address},
    {label:"Ordered By name",      ok:!!job.orderedBy.name},
    {label:"Inspector assigned",   ok:(job.inspectorIds||[]).length>0, warn:true},
    {label:"At least 1 finding",   ok:job.findings.length>0},
    {label:"Company name set",     ok:!!settings.company.name},
    {label:"PDF library loaded",   ok:jsPdfLoaded},
  ];
  const allOk   = checks.every(c=>c.ok);
  const hasEmail= !!job.orderedBy.email;
  const hasPhone= !!job.orderedBy.phone;
  const hasResend = !!(settings.resendApiKey || process.env.REACT_APP_RESEND_API_KEY);

  async function run(action){
    setError(""); setEmailSent(false); setBusy(action);
    try {
      if(action==="preview")  { await previewPDF(job,settings); }
      if(action==="download") { await downloadPDF(job,settings); }
      if(action==="email")    { await emailPDF(job,settings); }
      if(action==="sms") {
        const phone = job.orderedBy.phone?.replace(/\D/g,'') || '';
        const body  = buildSMSBody(job, settings);
        // Download PDF so inspector can manually attach, then open SMS
        await downloadPDF(job, settings);
        setTimeout(()=>{ window.location.href = `sms:${phone}?body=${body}`; }, 600);
      }
      if(action==="resend") {
        const recipients = chosenRecipients();
        if(!recipients.length){ setError("Select at least one recipient."); setBusy(null); return; }
        const {doc, fname} = generatePDF(await preparePDFPhotos(job,evidencePhoto), settings);
        // Convert to base64 for Resend attachment
        const b64 = btoa(
          Array.from(new Uint8Array(doc.output('arraybuffer')))
            .map(b=>String.fromCharCode(b)).join('')
        );
        // Try webhook first, then direct Resend API
        const webhookResult = await sendReportViaWebhook(job, settings, b64, fname, recipients);
        let result = webhookResult;
        if (!result) result = await sendReportViaResend(job, settings, b64, fname, recipients);
        if (result.ok) {
          setEmailSent(true);
          updateJob && updateJob({ reportSentAt: new Date().toISOString(), reportSentTo: result.to||recipients });
        }
        else { setError(result.error || 'Email failed.'); }
      }
    } catch(e){ setError("Error: "+e.message); }
    setBusy(null);
  }

  return(
    <div>
      {/* Nav banner ← Contract */}
      <div style={{...S.card,background:"linear-gradient(135deg,#1e3a5f,#1e40af)",border:"none",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#fff"}}>PDF Report</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",marginTop:2}}>Preview, download, or send the completed report</div>
        </div>
        <button style={{...S.btnSmOutline,borderColor:"rgba(255,255,255,0.3)",color:"#fff",fontSize:12}}
          onClick={()=>window._tsSetTab&&window._tsSetTab("contract")}>← Contract</button>
      </div>

      {/* Checklist */}
      <div style={S.card}>
        <div style={S.cardTitle}>Checklist</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
          {checks.map(c=>(
            <div key={c.label} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",fontSize:13}}>
              <span style={{fontSize:15,minWidth:20}}>{c.ok?"✅":c.warn?"⚠️":"❌"}</span>
              <span style={{color:c.ok?"#047857":c.warn?"#b45309":"#dc2626",fontWeight:c.ok?400:600}}>{c.label}</span>
            </div>
          ))}
        </div>
        {job.clientSignature && (
          <div style={{marginTop:8,padding:"6px 10px",background:"#f0fdf4",borderRadius:6,fontSize:12,color:"#15803d",border:"1px solid #86efac"}}>
            ✓ Client signature captured — included in PDF
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={S.card}>
        <div style={S.cardTitle}>Report Actions</div>
        {error&&<div style={{color:"#dc2626",fontSize:13,marginBottom:12,padding:"8px 12px",background:"#fef2f2",borderRadius:8}}>{error}</div>}
        {emailSent&&<div style={{color:"#15803d",fontSize:13,marginBottom:12,padding:"8px 12px",background:"#f0fdf4",borderRadius:8,border:"1px solid #86efac"}}>✓ Report emailed to {(job.reportSentTo||chosenRecipients()).join(", ")}</div>}
        {job.reportSentAt&&!emailSent&&<div style={{color:"#0369a1",fontSize:12,marginBottom:12,padding:"7px 12px",background:"#f0f9ff",borderRadius:8,border:"1px solid #bae6fd"}}>📧 Last emailed {new Date(job.reportSentAt).toLocaleString()}{job.reportSentTo?.length?` → ${job.reportSentTo.join(", ")}`:""}</div>}

        <div style={{display:"grid",gridTemplateColumns:window.innerWidth<520?"1fr":"1fr 1fr",gap:12,marginBottom:12}}>
          {/* Preview */}
          <div style={{border:"1px solid #e2e8f0",borderRadius:12,padding:16,textAlign:"center"}}>
            <div style={{fontSize:26,marginBottom:6}}>👁</div>
            <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Preview</div>
            <div style={{fontSize:11,color:"#64748b",marginBottom:12}}>Opens PDF in browser tab</div>
            <button style={{...S.btn("#0f172a"),width:"100%"}}
              onClick={()=>run("preview")} disabled={!jsPdfLoaded||busy==="preview"}>
              {busy==="preview"?"Opening…":"Preview PDF"}
            </button>
          </div>

          {/* Download */}
          <div style={{border:"1px solid #e2e8f0",borderRadius:12,padding:16,textAlign:"center"}}>
            <div style={{fontSize:26,marginBottom:6}}>💾</div>
            <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Download</div>
            <div style={{fontSize:11,color:"#64748b",marginBottom:12}}>Save PDF to your device</div>
            <button style={{...S.btn("#1e40af"),width:"100%"}}
              onClick={()=>run("download")} disabled={!jsPdfLoaded||busy==="download"}>
              {busy==="download"?"Saving…":"Download PDF"}
            </button>
          </div>
        </div>

        {/* Email row */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:12,padding:16,marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>✉️ Email Report</div>
          {/* Recipient chooser */}
          <div style={{marginBottom:12,padding:"10px 12px",background:"#f8fafc",borderRadius:8}}>
            <div style={{fontSize:11,fontWeight:700,color:"#64748b",marginBottom:6}}>Send to</div>
            {recipientOpts.length===0 && <div style={{fontSize:12,color:"#94a3b8",marginBottom:6}}>No contact emails on file — add one below or in Job Info.</div>}
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
              {recipientOpts.map(r=>(
                <label key={r.email} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#334155",background:picked.has(r.email)?"#e0f2fe":"#fff",border:`1px solid ${picked.has(r.email)?"#38bdf8":"#e2e8f0"}`,borderRadius:20,padding:"4px 10px",cursor:"pointer"}}>
                  <input type="checkbox" checked={picked.has(r.email)} onChange={()=>toggle(r.email)}/>
                  <span><strong>{r.label}</strong> · {r.email}</span>
                </label>
              ))}
            </div>
            <input style={{...S.input,fontSize:12}} value={extraEmail} onChange={e=>setExtraEmail(e.target.value)} placeholder="+ additional email (e.g. escrow@…)"/>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {/* mailto fallback — always available */}
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:11,color:"#64748b",marginBottom:8}}>
                {hasEmail ? `Downloads PDF & opens your email app — attach the PDF before sending` : "Add Ordered By email in Job Info first"}
              </div>
              <button style={{...S.btn(hasEmail?"#065f46":"#94a3b8"),width:"100%"}}
                onClick={()=>run("email")} disabled={!jsPdfLoaded||!hasEmail||!!busy}>
                {busy==="email"?"Opening…":"Open Email App"}
              </button>
            </div>
            {/* Resend direct send */}
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:11,color:"#64748b",marginBottom:8}}>
                {hasResend
                  ? (chosenRecipients().length ? `Sends the PDF to ${chosenRecipients().length} recipient${chosenRecipients().length>1?"s":""}` : "Select at least one recipient above")
                  : <span>Direct send via Resend — <span style={{color:"#1e40af",cursor:"pointer"}} onClick={()=>window._tsSetTab&&window._tsSetTab("settings")}>add API key in Settings</span></span>}
              </div>
              <button style={{...S.btn(hasResend&&chosenRecipients().length?"#7c3aed":"#94a3b8"),width:"100%"}}
                onClick={()=>run("resend")} disabled={!jsPdfLoaded||!hasResend||!chosenRecipients().length||!!busy}>
                {busy==="resend"?"Sending…":"Send via Resend ✉"}
              </button>
            </div>
          </div>
        </div>

        {/* SMS row */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:12,padding:16,marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:6}}>💬 SMS Notification</div>
          <div style={{fontSize:11,color:"#64748b",marginBottom:10}}>
            {hasPhone
              ? `Downloads PDF + opens SMS to ${job.orderedBy.phone} with report summary`
              : "Add Ordered By phone number in Job Info to enable SMS"}
          </div>
          <button style={{...S.btn(hasPhone?"#b45309":"#94a3b8")}}
            onClick={()=>run("sms")} disabled={!jsPdfLoaded||!hasPhone||!!busy}>
            {busy==="sms"?"Opening…":"Send SMS + Download PDF"}
          </button>
        </div>

        {!allOk&&(
          <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:8,padding:12,fontSize:12,color:"#92400e"}}>
            ⚠️ Some checklist items are incomplete — PDF will still generate but may have missing fields.
          </div>
        )}

        <div style={{marginTop:12,padding:"10px 14px",background:"#f8fafc",borderRadius:8,fontSize:11,color:"#64748b"}}>
          <strong>Included:</strong> WDO Inspection Report · Findings &amp; Recommendations · Contract/Proposal · Client Signature · Work Authorization (CA boilerplate)
        </div>
      </div>
    </div>
  );
}


// ─── NOTICE OF COMPLETION ─────────────────────────────────────────────────────
function NocSection({job, updateJob, settings}) {
  const u = (k,v) => updateJob({[k]:v});
  const allF = job.findings || [];
  const inspectors = settings?.inspectors || [];

  const primRows = allF.filter(f=>f.cat>=1&&f.cat<=5&&Number(f.primaryPrice||0)>0);
  const secRows  = allF.filter(f=>f.hasSecondary&&Number(f.secPrice||0)>0);

  const autoWork = primRows.reduce((s,f)=>s+Number(f.primaryPrice||0),0);
  const workCost = job.nocAutoSum!==false ? autoWork : Number(job.nocManualCost||0);
  const inspFee  = Number(job.nocInspectionFee||0);
  const otherFee = Number(job.nocOtherFee||0);
  const total    = workCost + inspFee + otherFee;

  const nocInspId = job.nocInspectorId || (inspectors[0]?.id||"");
  const selInsp   = inspectors.find(i=>i.id===nocInspId) || null;

  function colAmt(f, col) {
    const v = col==="primary" ? Number(f.primaryPrice||0) : Number(f.secPrice||0);
    return v>0 ? `$${v.toLocaleString()}` : "";
  }

  const CERT_OPTS = [
    {k:"none",   label:"None"},
    {k:"full",   label:"This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas."},
    {k:"except", label:"This is to certify that the property described herein is now free of evidence of active infestation or infection in the visible and accessible areas except as follows:"},
  ];
  const remarks = job.nocRemarks||"";
  const MAX_REMARKS = 700;

  const thSt = {padding:"8px 12px",textAlign:"left",fontWeight:700,fontSize:11,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.05em",borderBottom:"2px solid #e2e8f0",whiteSpace:"nowrap"};
  const tdSt = (i) => ({padding:"9px 12px",borderBottom:"1px solid #f1f5f9",background:i%2===0?"#fff":"#f8fafc"});

  function FindingTable({rows, priceField}) {
    if(!rows.length) return <div style={{fontSize:13,color:"#94a3b8",fontStyle:"italic",padding:"10px 0"}}>None</div>;
    return(
      <div style={{overflowX:"auto",borderRadius:8,border:"1px solid #e2e8f0",marginTop:4}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:"#f1f5f9"}}>
              {["Prefix","Section I","Section II","Further Insp.","Other"].map(h=>(
                <th key={h} style={thSt}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((f,i)=>{
              const cat=WDO_CATEGORIES.find(c=>c.num===f.cat);
              const pAmt = priceField==="sec" ? Number(f.secPrice||0) : Number(f.primaryPrice||0);
              const fmt  = pAmt>0?`$${pAmt.toLocaleString()}`:"";
              return(
                <tr key={f.id}>
                  <td style={{...tdSt(i),fontWeight:700,color:cat?.color||"#0f172a"}}>
                    {f.cat}{f.letter} — {cat?.label}
                  </td>
                  <td style={tdSt(i)}>{f.cat<=3?fmt:""}</td>
                  <td style={tdSt(i)}>{f.cat===4?fmt:""}</td>
                  <td style={tdSt(i)}>{f.cat===5?fmt:""}</td>
                  <td style={tdSt(i)}></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return(
    <div>
      {/* Header */}
      <div style={{...S.card,background:"linear-gradient(135deg,#14532d,#166534)",border:"none",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:"#fff"}}>Notice of Completion</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginTop:2}}>Standard Notice of Work Completed and Not Completed</div>
        </div>
        <button style={{...S.btnSmOutline,borderColor:"rgba(255,255,255,0.35)",color:"#fff",fontSize:12}}
          onClick={()=>window._tsSetTab&&window._tsSetTab("contract")}>← Contract</button>
      </div>

      {/* Completion details */}
      <div style={S.card}>
        <div style={S.cardTitle}>Completion Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div>
            <label style={S.label}>Date of Completion</label>
            <input type="date" style={S.input} value={job.nocCompletionDate||""}
              onChange={e=>u("nocCompletionDate",e.target.value)}/>
          </div>
          <div>
            <label style={S.label}>Completion Notice Sent To</label>
            <input style={S.input} value={job.nocSentTo||""}
              placeholder={job.orderedBy?.name||"Name / Company"}
              onChange={e=>u("nocSentTo",e.target.value)}/>
          </div>
        </div>
      </div>

      {/* Section 1 — Primary */}
      <div style={S.card}>
        <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:12}}>
          <div style={{width:34,height:34,borderRadius:"50%",background:"#1e40af",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:17,color:"#fff"}}>①</div>
          <div style={{fontSize:13,color:"#1e293b",lineHeight:1.55,fontWeight:500}}>
            Recommendations completed by this firm that are in accordance with the Structural Pest Control Board's Rules and Regulations:
          </div>
        </div>
        <FindingTable rows={primRows} priceField="primary"/>
      </div>

      {/* Section 2 — Secondary (only shown if any) */}
      {secRows.length>0&&(
        <div style={S.card}>
          <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:12}}>
            <div style={{width:34,height:34,borderRadius:"50%",background:"#6d28d9",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:17,color:"#fff"}}>②</div>
            <div style={{fontSize:13,color:"#1e293b",lineHeight:1.55,fontWeight:500}}>
              Recommendations completed by this firm that are considered secondary and substandard measures under Section 1992 of the Structural Pest Control Board's Rules and Regulations including persons requesting secondary measure:
            </div>
          </div>
          <FindingTable rows={secRows} priceField="sec"/>
        </div>
      )}

      {/* Cost of work completed */}
      <div style={S.card}>
        <div style={S.cardTitle}>Cost of Work Completed</div>
        <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"10px 16px",alignItems:"center",maxWidth:360}}>
          <span style={{fontSize:13,color:"#475569",fontWeight:600}}>Cost of Work</span>
          {job.nocAutoSum!==false
            ? <span style={{fontSize:15,fontWeight:700,color:"#0f172a"}}>${autoWork.toLocaleString()}</span>
            : <input type="number" style={{...S.input,maxWidth:120,textAlign:"right"}} value={job.nocManualCost||0}
                onChange={e=>u("nocManualCost",e.target.value)}/>
          }
          <label style={{fontSize:12,color:"#64748b",display:"flex",alignItems:"center",gap:6,gridColumn:"1/-1",marginBottom:2}}>
            <input type="checkbox" checked={job.nocAutoSum!==false}
              onChange={e=>u("nocAutoSum",e.target.checked)}/>
            Auto-sum from F&amp;R findings
          </label>
          <span style={{fontSize:13,color:"#475569",fontWeight:600}}>Inspection Fee</span>
          <input type="number" inputMode="decimal" style={{...S.input,maxWidth:120,textAlign:"right"}} value={inspFee||""}
            placeholder="0.00" onChange={e=>u("nocInspectionFee",Number(e.target.value)||0)}/>
          <span style={{fontSize:13,color:"#475569",fontWeight:600}}>Other</span>
          <input type="number" inputMode="decimal" style={{...S.input,maxWidth:120,textAlign:"right"}} value={otherFee||""}
            placeholder="0.00" onChange={e=>u("nocOtherFee",Number(e.target.value)||0)}/>
          <div style={{height:1,background:"#e2e8f0",gridColumn:"1/-1"}}/>
          <span style={{fontSize:14,fontWeight:700,color:"#0f172a"}}>Total</span>
          <span style={{fontSize:17,fontWeight:800,color:"#166534"}}>${total.toLocaleString()}</span>
        </div>
      </div>

      {/* Section 3 — Not completed */}
      <div style={S.card}>
        <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:12}}>
          <div style={{width:34,height:34,borderRadius:"50%",background:"#b45309",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:17,color:"#fff"}}>③</div>
          <div style={{fontSize:13,color:"#1e293b",lineHeight:1.55,fontWeight:500}}>
            (Optional) Recommendations <strong>NOT</strong> completed by this firm:
          </div>
        </div>
        <textarea style={{...S.textarea,minHeight:80}} value={job.nocNotCompleted||""}
          placeholder="Leave blank if all recommendations were completed…"
          onChange={e=>u("nocNotCompleted",e.target.value)}/>
      </div>

      {/* Section 4 — Certification */}
      <div style={S.card}>
        <div style={S.cardTitle}>4. Certification / Additional Remarks</div>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
          {CERT_OPTS.map(opt=>(
            <label key={opt.k} style={{display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer",padding:"10px 12px",borderRadius:8,border:`2px solid ${job.nocCertification===opt.k?"#166534":"#e2e8f0"}`,background:job.nocCertification===opt.k?"#f0fdf4":"#f8fafc"}}>
              <input type="radio" name="noc_cert" value={opt.k}
                checked={(job.nocCertification||"full")===opt.k}
                onChange={()=>u("nocCertification",opt.k)}
                style={{marginTop:2,flexShrink:0}}/>
              <span style={{fontSize:13,color:"#1e293b",lineHeight:1.55}}>{opt.label}</span>
            </label>
          ))}
        </div>
        {(job.nocCertification||"full")==="except"&&(
          <div style={{marginBottom:12}}>
            <label style={S.label}>Except as follows:</label>
            <input style={S.input} value={job.nocExceptText||""}
              placeholder="Describe the exception(s)…"
              onChange={e=>u("nocExceptText",e.target.value)}/>
          </div>
        )}
        <div>
          <label style={S.label}>Additional Remarks ({MAX_REMARKS - remarks.length} chars remaining)</label>
          <textarea style={{...S.textarea,minHeight:100}} maxLength={MAX_REMARKS}
            value={remarks} placeholder="Enter any additional remarks or certifications…"
            onChange={e=>u("nocRemarks",e.target.value)}/>
        </div>
      </div>

      {/* Inspector */}
      <div style={S.card}>
        <div style={S.cardTitle}>Inspected By</div>
        {inspectors.length>0?(
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14,border:"1px solid #e2e8f0",borderRadius:8,overflow:"hidden"}}>
            {inspectors.map((ins,i)=>(
              <button key={ins.id}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:(nocInspId===ins.id)?"#1e40af":"#fff",color:(nocInspId===ins.id)?"#fff":"#1e293b",border:"none",borderBottom:i<inspectors.length-1?"1px solid #e2e8f0":"none",cursor:"pointer",fontSize:13,fontWeight:(nocInspId===ins.id)?700:400,textAlign:"left"}}
                onClick={()=>u("nocInspectorId",ins.id)}>
                <span>{ins.name}</span>
                <span style={{fontSize:12,opacity:0.8}}>{ins.license}</span>
              </button>
            ))}
          </div>
        ):(
          <div style={{fontSize:13,color:"#94a3b8",fontStyle:"italic",marginBottom:12}}>No inspectors configured — add them in Settings.</div>
        )}
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer",marginBottom:10}}>
          <input type="checkbox" checked={job.nocIncludeSignature!==false}
            onChange={e=>u("nocIncludeSignature",e.target.checked)}/>
          Include Signature on PDF
        </label>
        {selInsp?.signature&&job.nocIncludeSignature!==false&&(
          <div style={{padding:8,background:"#f8fafc",borderRadius:8,border:"1px solid #e2e8f0",display:"inline-block"}}>
            <img src={selInsp.signature} alt="Signature" style={{maxHeight:60,maxWidth:200,display:"block"}}/>
          </div>
        )}
        <div style={{marginTop:12,padding:"10px 14px",background:"#f0fdf4",borderRadius:8,fontSize:12,color:"#166534",border:"1px solid #bbf7d0"}}>
          <div style={{marginBottom:10}}>✓ NOC data is saved with this job and included in the next PDF export.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button style={{...S.btn("#166534"),fontSize:12,padding:"7px 14px"}}
              onClick={()=>{
                const {doc}=generateNocOnlyPDF(job,settings);
                const blob=doc.output("blob");
                window.open(URL.createObjectURL(blob),"_blank");
              }}>👁 Preview NOC</button>
            <button style={{...S.btn("#1e40af"),fontSize:12,padding:"7px 14px"}}
              onClick={()=>{
                const {doc,fname}=generateNocOnlyPDF(job,settings);
                doc.save(fname);
              }}>⬇ Download NOC</button>
            <button style={{...S.btnSmOutline,fontSize:12,padding:"7px 14px",borderColor:"#166534",color:"#166534"}}
              onClick={()=>window._tsSetTab&&window._tsSetTab("report")}>→ Full Report PDF</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DISCLAIMERS TAB ─────────────────────────────────────────────────────────
function DisclaimersTab({disclaimers, setDisclaimers}) {
  const [editing, setEditing] = useState(null); // null | disclaimer object
  const [showNew, setShowNew] = useState(false);
  const [newDisc, setNewDisc] = useState({id:"",title:"",text:"",enabled:true});

  function toggle(id) {
    setDisclaimers(disclaimers.map(d=>d.id===id?{...d,enabled:!d.enabled}:d));
  }
  function remove(id) {
    setDisclaimers(disclaimers.filter(d=>d.id!==id));
  }
  function saveEdit() {
    setDisclaimers(disclaimers.map(d=>d.id===editing.id?editing:d));
    setEditing(null);
  }
  function addNew() {
    if(!newDisc.title.trim()||!newDisc.text.trim()) return;
    setDisclaimers([...disclaimers, {...newDisc, id:"disc_"+Date.now()}]);
    setNewDisc({id:"",title:"",text:"",enabled:true});
    setShowNew(false);
  }
  function resetToCA() {
    setDisclaimers([...CA_DEFAULT_DISCLAIMERS]);
  }

  const enabledCount = disclaimers.filter(d=>d.enabled).length;

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <div style={{fontWeight:700,fontSize:14}}>Report Disclaimers</div>
          <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{enabledCount} of {disclaimers.length} enabled — attached to every PDF</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...S.btnSmOutline,fontSize:11}} onClick={resetToCA}>↺ Reset CA Defaults</button>
          <button style={{...S.btn("#0f172a"),fontSize:12}} onClick={()=>setShowNew(true)}>+ Add Disclaimer</button>
        </div>
      </div>

      {/* Add new */}
      {showNew&&(
        <div style={{border:"1px solid #bfdbfe",borderRadius:10,padding:16,marginBottom:16,background:"#eff6ff"}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:"#1e40af"}}>New Disclaimer</div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Title</label>
            <input style={S.input} value={newDisc.title} onChange={e=>setNewDisc(d=>({...d,title:e.target.value}))} placeholder="e.g. Roof Exclusion"/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Disclaimer Text</label>
            <textarea style={{...S.textarea,minHeight:80}} value={newDisc.text} onChange={e=>setNewDisc(d=>({...d,text:e.target.value}))} placeholder="Enter disclaimer language..."/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={S.btn("#0f172a")} onClick={addNew}>Add</button>
            <button style={S.btnOutline} onClick={()=>setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Edit existing */}
      {editing&&(
        <div style={{border:"1px solid #fcd34d",borderRadius:10,padding:16,marginBottom:16,background:"#fffbeb"}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:"#92400e"}}>Edit Disclaimer</div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Title</label>
            <input style={S.input} value={editing.title} onChange={e=>setEditing(d=>({...d,title:e.target.value}))}/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Disclaimer Text</label>
            <textarea style={{...S.textarea,minHeight:100}} value={editing.text} onChange={e=>setEditing(d=>({...d,text:e.target.value}))}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={S.btn("#0f172a")} onClick={saveEdit}>Save</button>
            <button style={S.btnOutline} onClick={()=>setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {disclaimers.map(d=>(
          <div key={d.id} style={{
            border:`1px solid ${d.enabled?"#e2e8f0":"#f1f5f9"}`,
            borderRadius:10,padding:"12px 14px",
            background:d.enabled?"#fff":"#f8fafc",
            opacity:d.enabled?1:0.6,
            transition:"all 0.2s"
          }}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              {/* Toggle */}
              <div style={{paddingTop:2,cursor:"pointer"}} onClick={()=>toggle(d.id)}>
                <div style={{
                  width:38,height:20,borderRadius:10,
                  background:d.enabled?"#22d3ee":"#cbd5e1",
                  position:"relative",transition:"background 0.2s",flexShrink:0
                }}>
                  <div style={{
                    position:"absolute",top:2,
                    left:d.enabled?20:2,
                    width:16,height:16,borderRadius:8,
                    background:"#fff",transition:"left 0.2s",
                    boxShadow:"0 1px 3px rgba(0,0,0,0.2)"
                  }}/>
                </div>
              </div>
              {/* Content */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,color:"#1a1a2e",marginBottom:3}}>{d.title}</div>
                <div style={{fontSize:11,color:"#64748b",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                  {d.text}
                </div>
              </div>
              {/* Actions */}
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button style={{...S.btnSmOutline,fontSize:11}} onClick={()=>setEditing({...d})}>Edit</button>
                <button style={{...S.btnSm("#dc2626"),fontSize:11}} onClick={()=>remove(d.id)}>✕</button>
              </div>
            </div>
          </div>
        ))}
        {disclaimers.length===0&&(
          <div style={{textAlign:"center",padding:32,color:"#94a3b8",fontSize:13}}>
            No disclaimers. <button style={{background:"none",border:"none",color:"#22d3ee",cursor:"pointer",fontWeight:600}} onClick={resetToCA}>Load CA defaults</button>
          </div>
        )}
      </div>

      <div style={{marginTop:16,padding:"10px 14px",background:"#f0fdf4",borderRadius:8,fontSize:12,color:"#15803d",border:"1px solid #86efac"}}>
        ✓ Enabled disclaimers print on a dedicated page at the end of every PDF report
      </div>
    </div>
  );
}

// ─── SETTINGS MODAL ───────────────────────────────────────────────────────────

// ─── CONTACTS MODAL ───────────────────────────────────────────────────────────
function ContactsModal({contacts,setContacts,onClose,onPick}){
  const [search,setSearch]=useState("");
  const [editing,setEditing]=useState(null); // null | {} | contact
  const [importing,setImporting]=useState(false);
  const [typeFilter,setTypeFilter]=useState("All");
  const csvRef=useRef(null);

  const TYPES_ALL=["All",...CONTACT_TYPES];
  const filtered=contacts.filter(c=>{
    const q=search.toLowerCase();
    const matchQ=!q||(c.name||"").toLowerCase().includes(q)||(c.company||"").toLowerCase().includes(q)||(c.email||"").toLowerCase().includes(q)||(c.phone||"").toLowerCase().includes(q);
    const matchT=typeFilter==="All"||c.type===typeFilter;
    return matchQ&&matchT;
  }).sort((a,b)=>(a.name||"").localeCompare(b.name||""));

  function saveContact(c){
    if(!c.id) c={...c,id:Date.now().toString()};
    setContacts(prev=>prev.find(x=>x.id===c.id)?prev.map(x=>x.id===c.id?c:x):[...prev,c]);
    setEditing(null);
  }
  function deleteContact(id){ if(window.confirm("Delete this contact?")) setContacts(prev=>prev.filter(c=>c.id!==id)); }

  function importCSV(e){
    const file=e.target.files[0]; if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{
      const lines=ev.target.result.split(/\r?\n/).filter(Boolean);
      if(!lines.length)return;
      const headers=lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/^"|"$/g,""));
      const get=(row,keys)=>{ for(const k of keys){ const i=headers.indexOf(k); if(i>=0&&row[i]) return row[i].replace(/^"|"$/g,"").trim(); } return ""; };
      const imported=lines.slice(1).map(line=>{
        const row=line.split(",");
        const name=get(row,["name","full name","contact name","first name"]);
        if(!name)return null;
        return { id:Date.now().toString()+Math.random(), name, company:get(row,["company","brokerage","organization","firm"]), phone:formatPhone(get(row,["phone","mobile","cell","telephone","phone number"])), email:get(row,["email","e-mail","email address"]), type:get(row,["type","contact type","role"])||"Agent", notes:get(row,["notes","note","comments"]) };
      }).filter(Boolean);
      setContacts(prev=>{
        const existing=new Set(prev.map(c=>c.email+c.phone));
        const newOnes=imported.filter(c=>!existing.has(c.email+c.phone));
        alert(`Imported ${newOnes.length} new contacts (${imported.length-newOnes.length} duplicates skipped).`);
        return [...prev,...newOnes];
      });
      setImporting(false);
    };
    reader.readAsText(file);
    e.target.value="";
  }

  function exportCSV(){
    const header="Name,Company,Phone,Email,Type,Notes";
    const rows=contacts.map(c=>[c.name,c.company,c.phone,c.email,c.type,c.notes].map(v=>`"${(v||"").replace(/"/g,'""')}"`).join(","));
    const blob=new Blob([[header,...rows].join("\n")],{type:"text/csv"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="contacts.csv"; a.click();
  }

  if(editing!==null){
    return <ContactEditor contact={editing} onSave={saveContact} onCancel={()=>setEditing(null)}/>;
  }

  return(
    <div style={S.overlay}>
      <div style={{...S.modalLg,maxWidth:720}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{margin:0}}>👥 Contacts <span style={{fontSize:13,fontWeight:400,color:"#94a3b8"}}>({contacts.length})</span></h3>
          <button style={S.btnSmOutline} onClick={onClose}>✕</button>
        </div>

        {/* Toolbar */}
        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          <input style={{...S.input,flex:1,minWidth:160}} placeholder="Search name, company, email…" value={search} onChange={e=>setSearch(e.target.value)}/>
          <select style={{...S.select,width:"auto"}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
            {TYPES_ALL.map(t=><option key={t}>{t}</option>)}
          </select>
          <button style={S.btnSm("#0f172a")} onClick={()=>setEditing({id:"",name:"",company:"",phone:"",email:"",type:"Agent",notes:""})}>+ Add</button>
          <button style={S.btnSmOutline} onClick={()=>csvRef.current.click()} title="Import CSV">⬆ Import CSV</button>
          {contacts.length>0&&<button style={S.btnSmOutline} onClick={exportCSV} title="Export CSV">⬇ Export</button>}
          <input ref={csvRef} type="file" accept=".csv" style={{display:"none"}} onChange={importCSV}/>
        </div>

        {/* Pick mode banner */}
        {onPick&&(
          <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,color:"#1e40af",fontWeight:600}}>
            📇 Tap a contact below to fill in the form
          </div>
        )}

        {/* Type filter chips */}
        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:12}}>
          {TYPES_ALL.map(t=>(
            <button key={t} style={{...S.btnSm(typeFilter===t?"#1e40af":"#f1f5f9"),color:typeFilter===t?"#fff":"#475569",fontSize:11}}
              onClick={()=>setTypeFilter(t)}>{t}</button>
          ))}
        </div>

        {/* Contact list */}
        <div style={{maxHeight:420,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
          {filtered.length===0&&(
            <div style={{textAlign:"center",padding:40,color:"#94a3b8",fontSize:13}}>
              {contacts.length===0?"No contacts yet — add one or import a CSV":"No contacts match your search"}
            </div>
          )}
          {filtered.map(c=>(
            <div key={c.id} style={{background:"#f8fafc",borderRadius:8,padding:"10px 14px",border:"1px solid #e2e8f0",display:"flex",alignItems:"center",gap:12,cursor:onPick?"pointer":"default"}}
              onClick={onPick?()=>onPick(c):undefined}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"#1e40af",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,flexShrink:0}}>
                {(c.name||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,color:"#0f172a"}}>{c.name}</div>
                <div style={{fontSize:11,color:"#64748b"}}>{[c.company,c.type].filter(Boolean).join(" · ")}</div>
                <div style={{fontSize:11,color:"#94a3b8"}}>{[c.phone,c.email].filter(Boolean).join("  ·  ")}</div>
                {c.notes&&<div style={{fontSize:11,color:"#94a3b8",fontStyle:"italic",marginTop:2}}>{c.notes}</div>}
              </div>
              {!onPick&&(
                <div style={{display:"flex",gap:4}}>
                  <button style={{...S.btnSm("#f1f5f9"),color:"#475569",fontSize:11}} onClick={e=>{e.stopPropagation();setEditing(c);}}>Edit</button>
                  <button style={{...S.btnSm("#fee2e2"),color:"#dc2626",fontSize:11}} onClick={e=>{e.stopPropagation();deleteContact(c.id);}}>✕</button>
                </div>
              )}
              {onPick&&<div style={{color:"#1e40af",fontWeight:700,fontSize:13}}>→</div>}
            </div>
          ))}
        </div>

        <div style={{marginTop:14,display:"flex",justifyContent:"flex-end"}}>
          <button style={S.btnOutline} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function ContactEditor({contact,onSave,onCancel}){
  const [c,setC]=useState({...contact});
  const f=(field)=>(e)=>setC(x=>({...x,[field]:e.target.value}));
  return(
    <div style={S.overlay}>
      <div style={{...S.modalLg,maxWidth:520}}>
        <div style={{fontWeight:700,fontSize:15,marginBottom:16}}>{c.id?"Edit Contact":"New Contact"}</div>
        <div style={S.grid2}>
          <div><label style={S.label}>Full Name *</label><input style={S.input} value={c.name} onChange={f("name")} placeholder="Jane Smith" autoFocus/></div>
          <div><label style={S.label}>Type</label>
            <select style={S.select} value={c.type} onChange={f("type")}>
              {CONTACT_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div><label style={S.label}>Company / Brokerage</label><input style={S.input} value={c.company} onChange={f("company")} placeholder="RE/MAX, Coldwell…"/></div>
          <div><label style={S.label}>Phone</label><input style={S.input} type="tel" value={c.phone} onChange={e=>setC(x=>({...x,phone:formatPhone(e.target.value)}))} placeholder="(619) 555-0000"/></div>
          <div><label style={S.label}>Email</label><input style={S.input} type="email" value={c.email} onChange={f("email")} placeholder="jane@example.com"/></div>
          <div><label style={S.label}>Address</label><input style={S.input} value={c.address||""} onChange={f("address")} placeholder="Optional"/></div>
        </div>
        <div style={{marginTop:12}}><label style={S.label}>Notes</label><textarea style={{...S.textarea,minHeight:60}} value={c.notes} onChange={f("notes")} placeholder="Preferred contact method, referral source…"/></div>
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button style={S.btn("#0f172a")} onClick={()=>{ if(!c.name.trim()){alert("Name is required");return;} onSave(c); }}>Save Contact</button>
          <button style={S.btnOutline} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({settings,setSettings,onClose,initialTab="company",profile,profiles=[],onAccountsChanged,onSignOut}){
  const [tab,setTab]=useState(initialTab);
  const [s,setS]=useState({...settings,company:{...settings.company},rates:{...DEFAULT_RATES,...(settings.rates||{})},inspectors:[...settings.inspectors],codebook:[...settings.codebook],disclaimers:[...(settings.disclaimers||CA_DEFAULT_DISCLAIMERS)]});
  const [editingInsp,setEditingInsp]=useState(null);
  const [editingEntry,setEditingEntry]=useState(null);

  const [signingOut,setSigningOut]=useState(false);
  const [signOutError,setSignOutError]=useState("");
  async function signOut(){
    if(signingOut)return;
    setSigningOut(true);setSignOutError("");
    try{await onSignOut();onClose();}catch(error){setSignOutError(error.message||"Could not sign out. Please retry.");}finally{setSigningOut(false);}
  }
  function save(){setSettings({...s,reportStyle:normalizeReportStyle(s.reportStyle)});onClose();}
  function uc(field){return(e)=>setS(x=>({...x,company:{...x.company,[field]:e.target.value}}));}

  function saveInspector(ins){
    const exists=s.inspectors.find(i=>i.id===ins.id);
    if(exists)setS(x=>({...x,inspectors:x.inspectors.map(i=>i.id===ins.id?ins:i)}));
    else setS(x=>({...x,inspectors:[...x.inspectors,ins]}));
    setEditingInsp(null);
  }
  function deleteInspector(id){setS(x=>({...x,inspectors:x.inspectors.filter(i=>i.id!==id)}));}

  function saveEntry(e){
    const exists=s.codebook.find(x=>x.id===e.id);
    if(exists)setS(x=>({...x,codebook:x.codebook.map(cb=>cb.id===e.id?e:cb)}));
    else setS(x=>({...x,codebook:[...x.codebook,e]}));
    setEditingEntry(null);
  }
  function deleteEntry(id){setS(x=>({...x,codebook:x.codebook.filter(e=>e.id!==id)}));}

  const tabs=[{key:"company",label:"Company"},{key:"reportstyle",label:"Report Style"},{key:"pricing",label:"Pricing"},{key:"inspectors",label:"Inspectors"},{key:"codebook",label:"Codebook"},{key:"disclaimers",label:"Disclaimers"},{key:"apikeys",label:"API Keys"}];

  return(
    <div style={S.overlay}>
      <div style={S.modalLg}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{margin:0}}>Settings</h3>
          <button style={S.btnSmOutline} onClick={onClose}>✕</button>
        </div>
        <div style={{display:"flex",gap:4,background:"#f1f5f9",borderRadius:8,padding:3,marginBottom:20,flexWrap:"wrap"}}>
          {tabs.map(t=>(
            <button key={t.key} style={{...S.btnSm(tab===t.key?"#0f172a":"transparent"),color:tab===t.key?"#fff":"#475569",flex:"1 1 30%"}} onClick={()=>setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        {tab==="reportstyle"&&<ReportStyleSettings value={s.reportStyle} onChange={reportStyle=>setS(x=>({...x,reportStyle}))}/>}

        {tab==="pricing"&&(
          <div>
            <div style={{fontSize:13,color:"#64748b",marginBottom:16}}>
              Set your default service rates. These are used to auto-calculate job prices in the Findings &amp; Recommendations section. Lock a rate to enforce it across all jobs.
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[
                {key:"fumigation", label:"Drywood Termite Fumigation", unitKey:"fumigationUnit", lockedKey:"fumigationLocked", step:"0.01", placeholder:"e.g. 89",
                  unitOptions:["÷1,000 cu ft","cu ft","cu yd","cu m"],
                  formulaHints:{
                    "÷1,000 cu ft":"sqft × height ÷ 1,000 × rate  ← standard",
                    "cu ft":       "sqft × height × rate per cu ft",
                    "cu yd":       "sqft × height ÷ 27 × rate per cu yd",
                    "cu m":        "sqft × height × 0.0283 × rate per m³ (metric)",
                  }},
                {key:"localTreatment", label:"Local / Spot Treatment", unitKey:"localTreatmentUnit", lockedKey:"localTreatmentLocked", step:"1", placeholder:"e.g. 250",
                  unitOptions:["job","sq ft","linear ft"], formulaHints:{}},
                {key:"preventiveTreatment", label:"Preventive / Subterranean Treatment", unitKey:"preventiveTreatmentUnit", lockedKey:"preventiveTreatmentLocked", step:"1", placeholder:"e.g. 400",
                  unitOptions:["job","linear ft","sq ft"], formulaHints:{}},
                {key:"repairHourly", label:"Wood Repair (per hour)", unitKey:"repairHourlyUnit", lockedKey:"repairHourlyLocked", step:"1", placeholder:"e.g. 95",
                  unitOptions:["hour"], formulaHints:{hour:"repair hours × rate  (inspector enters hours per finding)"}},
              ].map(r=>{
                const rates = s.rates || {};
                const val = rates[r.key];
                // "÷1,000 cu ft" is the fumigation standard. Map the legacy
                // "m³ (÷1000)" name onto it for display; other units are left as-is.
                const rawUnit = rates[r.unitKey] ?? DEFAULT_RATES[r.unitKey];
                const unitVal = (r.key==="fumigation" && rawUnit==="m³ (÷1000)") ? "÷1,000 cu ft" : rawUnit;
                const isLocked = !!(rates[r.lockedKey]);
                const rateNum = parseFloat(val)||0;
                const convFactor = cuFtConversionFactor(unitVal);
                // Example price uses the right formula per unit type
                let exampleDesc = null, examplePrice = null;
                if (rateNum) {
                  if (unitVal==="job")           { exampleDesc="Flat rate"; examplePrice=rateNum; }
                  else if (unitVal==="sq ft")    { exampleDesc="Example: 2,000 sqft"; examplePrice=Math.round(2000*rateNum); }
                  else if (unitVal==="linear ft"){ exampleDesc="Example: 100 linear ft"; examplePrice=Math.round(100*rateNum); }
                  else { exampleDesc="Example: 2,000 sqft × 10 ft"; examplePrice=Math.round(2000*10*convFactor*rateNum); }
                }
                const hint = r.formulaHints?.[unitVal];
                return (
                <div key={r.key} style={{display:"flex",flexDirection:"column",gap:8,padding:"12px 16px",background:isLocked?"#f0fdf4":"#f8fafc",borderRadius:10,border:`1px solid ${isLocked?"#86efac":"#e2e8f0"}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:13,color:"#1e293b",display:"flex",alignItems:"center",gap:6}}>
                        {isLocked&&<span style={{fontSize:13}}>🔒</span>}
                        {r.label}
                      </div>
                      <div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>
                        {isLocked?"Rate locked — enforced on all jobs":"Default rate applied to new findings"}
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap"}}>
                      <span style={{color:"#475569",fontSize:14,fontWeight:700}}>$</span>
                      <input
                        style={{...S.input,width:100,fontWeight:700,fontSize:15,textAlign:"right"}}
                        type="number" inputMode="decimal" step={r.step}
                        placeholder={r.placeholder}
                        value={(val && val!==0) ? val : ""}
                        onChange={e=>setS(x=>({...x,rates:{...DEFAULT_RATES,...(x.rates||{}),[r.key]:parseFloat(e.target.value)||0}}))}/>
                      <span style={{color:"#64748b",fontSize:12}}>/</span>
                      <select
                        style={{...S.input,width:"auto",fontSize:12,padding:"4px 6px"}}
                        value={unitVal}
                        onChange={e=>setS(x=>({...x,rates:{...DEFAULT_RATES,...(x.rates||{}),[r.unitKey]:e.target.value}}))}>
                        {r.unitOptions.map(u=><option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                  {hint&&<div style={{fontSize:11,color:"#1e40af",background:"#eff6ff",padding:"5px 8px",borderRadius:6,fontFamily:"monospace"}}>{hint}</div>}
                  {examplePrice!=null&&<div style={{fontSize:11,color:"#475569"}}>{exampleDesc} → <strong>${Number(examplePrice).toLocaleString()}</strong></div>}
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                    <input
                      type="checkbox"
                      checked={isLocked}
                      onChange={e=>setS(x=>({...x,rates:{...DEFAULT_RATES,...(x.rates||{}),[r.lockedKey]:e.target.checked}}))}
                      style={{width:16,height:16,cursor:"pointer"}}/>
                    <span style={{fontSize:12,color:isLocked?"#15803d":"#475569",fontWeight:isLocked?600:400}}>
                      {isLocked?"🔒 Rate locked — cannot be overridden per-job":"Lock rate (prevent per-job overrides)"}
                    </span>
                  </label>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {tab==="company"&&(
          <div>
            <div style={S.grid2}>
              <div><label style={S.label}>Company Name</label><input style={S.input} value={s.company.name} onChange={uc("name")}/></div>
              <div><label style={S.label}>Company License #</label><input style={S.input} value={s.company.license} onChange={uc("license")} placeholder="PR0000"/></div>
              <div><label style={S.label}>Branch Office #</label><input style={S.input} value={s.company.branchOffice} onChange={uc("branchOffice")}/></div>
              <div><label style={S.label}>Phone</label><input style={S.input} value={s.company.phone} type="tel" autoComplete="tel" placeholder="(619) 555-0000" onChange={e=>setS(x=>({...x,company:{...x.company,phone:formatPhone(e.target.value)}}))}/></div>
              <div><label style={S.label}>Email</label><input style={S.input} value={s.company.email} onChange={uc("email")}/></div>
              <div><label style={S.label}>Website</label><input style={S.input} value={s.company.website} onChange={uc("website")}/></div>
            </div>
            <div style={{marginTop:12}}><label style={S.label}>Address</label><input style={S.input} value={s.company.address} onChange={uc("address")}/></div>
            <div style={{...S.grid3,marginTop:12}}>
              <div><label style={S.label}>City</label><input style={S.input} value={s.company.city} onChange={uc("city")}/></div>
              <div><label style={S.label}>State</label><input style={S.input} value={s.company.state} onChange={uc("state")}/></div>
              <div><label style={S.label}>Zip</label><input style={S.input} value={s.company.zip} onChange={uc("zip")}/></div>
            </div>
            <div style={{marginTop:14}}>
              <label style={S.label}>Company Logo</label>
              <input type="file" accept="image/*" style={{marginTop:4,fontSize:12}}
                onChange={e=>{
                  const f=e.target.files[0];if(!f)return;
                  const r=new FileReader();
                  r.onload=ev=>setS(x=>({...x,company:{...x.company,logo:ev.target.result}}));
                  r.readAsDataURL(f);
                }}/>
              {s.company.logo&&<img src={s.company.logo} alt="logo" style={{marginTop:8,maxHeight:60,borderRadius:4}}/>}
            </div>
          </div>
        )}

        {tab==="inspectors"&&(profile?<InspectorRoster legacyInspectors={s.inspectors} profiles={profiles} isAdmin={profile.role==='admin'} currentUserId={profile.id}
          onRosterChange={inspectors=>{setS(x=>({...x,inspectors}));setSettings(x=>({...x,inspectors}));}}
          onAccountsChange={onAccountsChanged} createAccount={sbCreateInspector}
          renderEditor={props=><InspectorEditor {...props}/>}/>:          <div>
            {editingInsp?(
              <InspectorEditor inspector={editingInsp} onSave={saveInspector} onCancel={()=>setEditingInsp(null)}/>
            ):(
              <>
                <button style={S.btn("#0f172a")} onClick={()=>setEditingInsp({id:Date.now().toString(),name:"",license:"",signature:""})}>+ New Inspector</button>
                <div style={{marginTop:14}}>
                  {s.inspectors.map(ins=>(
                    <div key={ins.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid #f1f5f9"}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:14}}>{ins.name}</div>
                        <div style={{fontSize:12,color:"#64748b"}}>License: {ins.license}</div>
                      </div>
                      <button style={S.btnSmOutline} onClick={()=>setEditingInsp({...ins})}>Edit</button>
                      <button style={S.btnSm("#dc2626")} onClick={()=>deleteInspector(ins.id)}>✕</button>
                    </div>
                  ))}
                  {!s.inspectors.length&&<div style={{color:"#94a3b8",marginTop:16,fontSize:13}}>No inspectors added yet.</div>}
                </div>
              </>
            )}
          </div>
)}

        {tab==="codebook"&&(
          <div>
            {editingEntry?(
              <CodebookEditor entry={editingEntry} onSave={saveEntry} onCancel={()=>setEditingEntry(null)}/>
            ):(
              <>
                <button style={S.btn("#0f172a")} onClick={()=>setEditingEntry({id:Date.now().toString(),cat:2,name:"",finding:"",primaryRec:"",secondaryRec:"",defaultPrice:0,priceUnit:""})}>+ Add Entry</button>
                <div style={{marginTop:14}}>
                  {WDO_CATEGORIES.map(cat=>{
                    const entries=s.codebook.filter(e=>e.cat===cat.num);
                    if(!entries.length)return null;
                    return(
                      <div key={cat.num} style={{marginBottom:16}}>
                        <div style={{fontSize:12,fontWeight:700,color:cat.color,textTransform:"uppercase",marginBottom:8}}>{cat.num} — {cat.label}</div>
                        {entries.map(e=>(
                          <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #f1f5f9"}}>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:13}}>{e.name}</div>
                              <div style={{fontSize:11,color:"#64748b"}}>{e.finding.slice(0,60)}{e.finding.length>60?"…":""}</div>
                            </div>
                            {e.defaultPrice>0&&<span style={{fontWeight:700,color:cat.color}}>${e.defaultPrice.toLocaleString()}{e.priceUnit?<span style={{fontWeight:400,fontSize:11,color:"#64748b"}}> / {e.priceUnit}</span>:null}</span>}
                            <button style={S.btnSmOutline} onClick={()=>setEditingEntry({...e})}>Edit</button>
                            <button style={S.btnSm("#dc2626")} onClick={()=>deleteEntry(e.id)}>✕</button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}



        {tab==="disclaimers"&&(
          <DisclaimersTab disclaimers={s.disclaimers||[]} setDisclaimers={d=>setS(x=>({...x,disclaimers:d}))}/>
        )}

        {tab==="apikeys"&&(
          <div>
            <div style={{fontSize:13,color:"#64748b",marginBottom:16,lineHeight:1.6}}>
              API keys for optional integrations. Keys are stored locally in your browser.
            </div>

            {/* Mapbox */}
            <div style={{marginBottom:20,padding:"14px 16px",background:"#f8fafc",borderRadius:10,border:"1px solid #e2e8f0"}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>Mapbox API Key</div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:10}}>Enables address autocomplete, satellite map, and geocoding. Required for map features.</div>
              <input style={S.input} type="password" value={s.mapboxKey||""} onChange={e=>setS(x=>({...x,mapboxKey:e.target.value}))} placeholder="pk.eyJ1…"/>
              <div style={{fontSize:11,color:"#94a3b8",marginTop:6}}>Get a free key at <a href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noreferrer" style={{color:"#1e40af"}}>mapbox.com</a> — free tier: 50,000 map loads/month</div>
            </div>

            {/* Resend email */}
            <div style={{marginBottom:20,padding:"14px 16px",background:"#f8fafc",borderRadius:10,border:"1px solid #e2e8f0"}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>Resend API Key <span style={{fontWeight:400,fontSize:11,color:"#94a3b8"}}>(for direct email delivery)</span></div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:10}}>Enables "Send via Resend" on the PDF report screen — sends the PDF as an email attachment directly from the app.</div>
              <input style={S.input} type="password" value={s.resendApiKey||""} onChange={e=>setS(x=>({...x,resendApiKey:e.target.value}))} placeholder="re_…"/>
              <div style={{fontSize:11,color:"#94a3b8",marginTop:6}}>Get a free key at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" style={{color:"#1e40af"}}>resend.com</a> — free tier: 3,000 emails/month</div>
            </div>

            {/* Email webhook (optional) */}
            <div style={{marginBottom:20,padding:"14px 16px",background:"#f8fafc",borderRadius:10,border:"1px solid #e2e8f0"}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>Email Webhook URL <span style={{fontWeight:400,fontSize:11,color:"#94a3b8"}}>(optional — for Supabase Edge Functions)</span></div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:10}}>Advanced: URL of a Supabase Edge Function or other backend endpoint that handles email sending. Takes priority over the Resend API key.</div>
              <input style={S.input} type="url" value={s.emailWebhookUrl||""} onChange={e=>setS(x=>({...x,emailWebhookUrl:e.target.value}))} placeholder="https://your-project.supabase.co/functions/v1/send-report"/>
            </div>

            <div style={{padding:"10px 14px",background:"#eff6ff",borderRadius:8,fontSize:11,color:"#1e40af",border:"1px solid #bfdbfe"}}>
              ℹ Keys are stored in your browser's local storage and synced to your Supabase account if signed in. Never share your API keys.
            </div>
          </div>
        )}

        {signOutError&&<p role="alert" style={{color:"#b91c1c",marginTop:16}}>{signOutError}</p>}
        <div style={{marginTop:24,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <button style={S.btn("#22d3ee")} disabled={signingOut} onClick={save}>Save Settings</button>
          <button style={S.btnOutline} disabled={signingOut} onClick={onClose}>Cancel</button>
          {onSignOut&&<button type="button" style={{...S.btnOutline,marginLeft:"auto",minHeight:44,color:"#b91c1c",borderColor:"#fecaca",fontWeight:700}} disabled={signingOut} onClick={signOut}>{signingOut?"Signing out…":"Sign out"}</button>}
        </div>
      </div>
    </div>
  );
}

function InspectorEditor({inspector,onSave,onCancel,paymentEditor,disabled=false,saving=false}){
  const [ins,setIns]=useState({...inspector});
  const [sigMode,setSigMode]=useState("draw"); // upload | draw
  const sigCanvasRef=useRef(null);
  const [sigDrawing,setSigDrawing]=useState(false);
  const sigFileRef=useRef(null);

  function getSigPos(e){
    const c=sigCanvasRef.current;
    const r=c.getBoundingClientRect();
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    // Scale from CSS display size → internal canvas resolution
    const scaleX = c.width  / r.width;
    const scaleY = c.height / r.height;
    return [(cx-r.left)*scaleX, (cy-r.top)*scaleY];
  }
  function sigDown(e){
    e.preventDefault();
    const c=sigCanvasRef.current;
    const ctx=c.getContext("2d");
    const [x,y]=getSigPos(e);
    ctx.beginPath();ctx.moveTo(x,y);
    setSigDrawing(true);
  }
  function sigMove(e){
    e.preventDefault();
    if(!sigDrawing)return;
    const c=sigCanvasRef.current;
    const ctx=c.getContext("2d");
    const [x,y]=getSigPos(e);
    ctx.lineTo(x,y);
    ctx.strokeStyle="#1a1a2e";ctx.lineWidth=2;ctx.lineCap="round";
    ctx.stroke();
  }
  function sigUp(e){
    e.preventDefault();
    setSigDrawing(false);
    const c=sigCanvasRef.current;
    setIns(x=>({...x,signature:c.toDataURL("image/png")}));
  }
  function clearSig(){
    const c=sigCanvasRef.current;
    if(c)c.getContext("2d").clearRect(0,0,c.width,c.height);
    setIns(x=>({...x,signature:""}));
  }

  return(
    <div>
      <div style={{fontWeight:700,fontSize:15,marginBottom:14}}>{inspector.name?"Edit Inspector":"New Inspector"}</div>
      <div style={S.grid2}>
        <div><label style={S.label}>Inspector Name</label>
          <input style={S.input} value={ins.name} onChange={e=>setIns(x=>({...x,name:e.target.value}))} placeholder="Full name"/>
        </div>
        <div><label style={S.label}>License # (FR#)</label>
          <input style={S.input} value={ins.license} onChange={e=>setIns(x=>({...x,license:e.target.value}))} placeholder="FR00000"/>
        </div>
      </div>

      <div style={{marginTop:12}}><label style={S.label}>Login email (optional until account creation)</label><input aria-label="Inspector email" disabled={!!ins.userId} style={S.input} type="email" value={ins.email||''} onChange={e=>setIns(x=>({...x,email:e.target.value}))} placeholder="inspector@example.com"/></div>
      {paymentEditor}
      {/* Signature */}
      <div style={{marginTop:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <label style={S.label}>Signature</label>
          <div style={{display:"flex",gap:4,background:"#f1f5f9",borderRadius:8,padding:3}}>
            {[["upload","📁 Upload"],["draw","✍ Draw"]].map(([k,l])=>(
              <button key={k} style={{...S.btnSm(sigMode===k?"#0f172a":"transparent"),color:sigMode===k?"#fff":"#475569",fontSize:12}}
                onClick={()=>setSigMode(k)}>{l}</button>
            ))}
          </div>
        </div>

        {sigMode==="upload"&&(
          <div>
            <input ref={sigFileRef} type="file" accept="image/*" style={{display:"none"}}
              onChange={e=>{
                const f=e.target.files[0];if(!f)return;
                const r=new FileReader();
                r.onload=ev=>setIns(x=>({...x,signature:ev.target.result}));
                r.readAsDataURL(f);
              }}/>
            {ins.signature?(
              <div style={{position:"relative",display:"inline-block"}}>
                <img src={ins.signature} alt="sig" style={{maxHeight:70,maxWidth:"100%",border:"1px solid #e2e8f0",borderRadius:6,background:"#fff",padding:6}}/>
                <button style={{position:"absolute",top:-8,right:-8,...S.btnSm("#dc2626"),width:22,height:22,padding:0,fontSize:13,borderRadius:"50%"}}
                  onClick={()=>setIns(x=>({...x,signature:""}))}>✕</button>
              </div>
            ):(
              <div style={{border:"2px dashed #cbd5e1",borderRadius:8,padding:24,textAlign:"center",cursor:"pointer",background:"#f8fafc"}}
                onClick={()=>sigFileRef.current.click()}>
                <div style={{fontSize:24,marginBottom:6}}>✍️</div>
                <div style={{fontSize:13,color:"#64748b"}}>Click to upload signature image</div>
                <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>PNG or JPG, transparent background works best</div>
              </div>
            )}
          </div>
        )}

        {sigMode==="draw"&&(
          <div>
            <div style={{border:"1px solid #e2e8f0",borderRadius:8,overflow:"hidden",background:"#fff",touchAction:"none"}}>
              <canvas ref={sigCanvasRef} width={460} height={120}
                style={{width:"100%",display:"block",cursor:"crosshair"}}
                onMouseDown={sigDown} onMouseMove={sigMove} onMouseUp={sigUp} onMouseLeave={sigUp}
                onTouchStart={sigDown} onTouchMove={sigMove} onTouchEnd={sigUp}/>
            </div>
            <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center"}}>
              <span style={{fontSize:11,color:"#94a3b8",flex:1}}>Draw your signature above</span>
              <button style={{...S.btnSmOutline,fontSize:11}} onClick={clearSig}>Clear</button>
              <button style={{...S.btnSm("#0f172a"),fontSize:11}} onClick={()=>{
                const c=sigCanvasRef.current;
                if(c)setIns(x=>({...x,signature:c.toDataURL("image/png")}));
                setSigMode("upload");
              }}>Save Signature</button>
            </div>
          </div>
        )}
      </div>

      <div style={{marginTop:20,display:"flex",gap:10}}>
        <button disabled={disabled} style={S.btn("#0f172a")} onClick={()=>{
          const finalIns = sigMode==="draw" && sigCanvasRef.current
            ? {...ins, signature: sigCanvasRef.current.toDataURL("image/png")}
            : ins;
          onSave(finalIns);
        }}>Save Inspector</button>
        <button disabled={saving} style={S.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function CodebookEditor({entry,onSave,onCancel}){
  const [e,setE]=useState({...entry});
  return(
    <div>
      <div style={{fontWeight:700,fontSize:15,marginBottom:14}}>Codebook Entry</div>
      <div style={S.grid2}>
        <div>
          <label style={S.label}>Category</label>
          <select style={S.select} value={e.cat} onChange={ev=>setE(x=>({...x,cat:Number(ev.target.value)}))}>
            {WDO_CATEGORIES.map(c=><option key={c.num} value={c.num}>{c.num} — {c.label}</option>)}
          </select>
        </div>
        <div><label style={S.label}>Entry Name / Code</label><input style={S.input} value={e.name} onChange={ev=>setE(x=>({...x,name:ev.target.value}))} placeholder="2A-Drywood"/></div>
      </div>
      <div style={{marginTop:12}}><label style={S.label}>Finding Text</label><textarea style={{...S.textarea,minHeight:60}} value={e.finding} onChange={ev=>setE(x=>({...x,finding:ev.target.value}))}/></div>
      <div style={{...S.grid2,marginTop:12}}>
        <div><label style={S.label}>Primary Recommendation</label><textarea style={{...S.textarea,minHeight:60}} value={e.primaryRec} onChange={ev=>setE(x=>({...x,primaryRec:ev.target.value}))}/></div>
        <div>
          <label style={S.label}>Default Price ($)</label>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input style={{...S.input,flex:1}} type="number" inputMode="decimal" value={e.defaultPrice}
              onFocus={ev=>{ if(Number(ev.target.value)===0) setE(x=>({...x,defaultPrice:""})); }}
              onBlur={ev=>{ if(ev.target.value===""||isNaN(Number(ev.target.value))) setE(x=>({...x,defaultPrice:0})); }}
              onChange={ev=>setE(x=>({...x,defaultPrice:ev.target.value}))}/>
            <span style={{color:"#64748b",fontSize:13}}>/</span>
            <input style={{...S.input,maxWidth:80}} value={e.priceUnit||""}
              onChange={ev=>setE(x=>({...x,priceUnit:ev.target.value}))}
              placeholder="job"/>
          </div>
        </div>
      </div>
      <div style={{marginTop:12}}><label style={S.label}>Secondary Recommendation (optional)</label><textarea style={{...S.textarea,minHeight:60}} value={e.secondaryRec} onChange={ev=>setE(x=>({...x,secondaryRec:ev.target.value}))}/></div>
      <div style={{marginTop:16,display:"flex",gap:10}}>
        <button style={S.btn("#0f172a")} onClick={()=>onSave(e)}>Save Entry</button>
        <button style={S.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── JOB HISTORY MODAL ────────────────────────────────────────────────────────
function JobHistoryModal({ jobs, currentJobId, onOpen, onDelete, onClose, onUpdateJob }) {
  const [search, setSearch] = useState("");
  const mob = window.innerWidth < 520;
  const filtered = jobs.filter(j => {
    const q = search.toLowerCase();
    return !q || (j.address||"").toLowerCase().includes(q) ||
      (j.reportNum||"").toLowerCase().includes(q) ||
      (j.orderedBy?.name||"").toLowerCase().includes(q);
  });
  // Sort newest first by date then by id
  const sorted = [...filtered].sort((a,b)=>(b.id||"").localeCompare(a.id||""));

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9000,display:"flex",alignItems:mob?"flex-end":"flex-start",justifyContent:"center",...(mob?{}:{paddingTop:60})}} onClick={onClose}>
      <div style={{background:"#1e293b",borderRadius:mob?"16px 16px 0 0":12,width:"100%",maxWidth:mob?"100%":600,maxHeight:mob?"88svh":"80vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{padding:"18px 20px",borderBottom:"1px solid #334155",display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18,fontWeight:700,color:"#f1f5f9",flex:1}}>📋 Job History</span>
          <button style={{...S.btn("#0f172a"),padding:"6px 12px",fontSize:12}} onClick={onClose}>Close</button>
        </div>
        {/* Search */}
        <div style={{padding:"12px 20px",borderBottom:"1px solid #1e293b"}}>
          <input
            style={{...S.input,width:"100%"}}
            placeholder="Search by address, report #, or client…"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            autoFocus
          />
        </div>
        {/* List */}
        <div style={{overflowY:"auto",flex:1,padding:"8px 0"}}>
          {sorted.length===0 && (
            <div style={{padding:"32px 20px",textAlign:"center",color:"#64748b",fontSize:14}}>
              {search ? "No jobs match your search." : "No saved jobs yet. Your jobs will appear here automatically."}
            </div>
          )}
          {sorted.map(j=>{
            const isCurrent = j.id===currentJobId;
            const findingCount = (j.findings||[]).length;
            const displayAddr = j.address ? `${j.address}${j.city?", "+j.city:""}` : "No address";
            return (
              <div key={j.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:"1px solid #1e293b",background:isCurrent?"rgba(34,211,238,0.06)":"transparent"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                    {isCurrent&&<span style={{background:"#22d3ee",color:"#0f172a",borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700}}>CURRENT</span>}
                    <span style={{fontWeight:600,color:"#f1f5f9",fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayAddr}</span>
                  </div>
                  <div style={{fontSize:12,color:"#94a3b8",display:"flex",gap:12,flexWrap:"wrap"}}>
                    <span>Report #{j.reportNum||"—"}</span>
                    <span>{j.date||"No date"}</span>
                    {j.orderedBy?.name&&<span>Client: {j.orderedBy.name}</span>}
                    {findingCount>0&&<span style={{color:"#22d3ee"}}>{findingCount} finding{findingCount!==1?"s":""}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:3,flexShrink:0,alignItems:"flex-end",marginRight:6}}>
                  {(()=>{
                    const autoP={
                      FP:!!(j.address&&j.orderedBy?.name),
                      DM:!!((j.drawings||[]).length>0||j.mapBg),
                      FR:(j.findings||[]).length>0,
                      CT:(j.findings||[]).some(f=>f.primaryPrice>0),
                    };
                    const sp2=j.stageProgress||{};
                    return ["FP","DM","FR","CT","CS","NC"].map(s=>{
                      const isAuto=s in autoP;
                      const checked=isAuto?autoP[s]:sp2[s];
                      return(
                        <div key={s}
                          style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:isAuto?"default":"pointer",WebkitTapHighlightColor:"transparent"}}
                          onClick={isAuto?undefined:e=>{e.stopPropagation();if(onUpdateJob)onUpdateJob(j.id,{stageProgress:{...sp2,[s]:!checked}});}}>
                          <div style={{fontSize:10,color:checked?"#22d3ee":"#94a3b8",fontWeight:700,lineHeight:1,letterSpacing:"0.02em"}}>{s}</div>
                          <div style={{width:28,height:28,border:"2px solid",borderColor:checked?"#22d3ee":"rgba(255,255,255,0.25)",borderRadius:6,background:checked?"#22d3ee":"rgba(255,255,255,0.05)",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                            {checked&&<span style={{color:"#0f172a",fontSize:14,fontWeight:900,lineHeight:1}}>✓</span>}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  {!isCurrent&&(
                    <button style={{...S.btn("#0ea5e9"),padding:"6px 14px",fontSize:12}} onClick={()=>onOpen(j)}>Open</button>
                  )}
                  <button
                    style={{...S.btn("#dc2626"),padding:"6px 10px",fontSize:12}}
                    onClick={()=>{ if(!isCurrent||window.confirm("Delete the current job? You'll lose unsaved changes.")) onDelete(j.id).catch(error=>window.alert(error.message)); }}
                    title="Delete job"
                  >🗑</button>
                </div>
              </div>
            );
          })}
        </div>
        {sorted.length>0&&(
          <div style={{padding:"10px 20px",borderTop:"1px solid #334155",fontSize:12,color:"#64748b"}}>
            {sorted.length} job{sorted.length!==1?"s":""} saved locally
          </div>
        )}
      </div>
    </div>
  );
}

// ─── JOBS DASHBOARD ───────────────────────────────────────────────────────────
// ─── INSPECTOR DASHBOARD ─────────────────────────────────────────────────────
// Read-only view of the jobs assigned to one inspector plus a month calendar of
// their upcoming inspections. No owner/admin controls (no New Job, escrow, NOC,
// order-by, flags, bulk actions, invoicing, or delete).
// Renders a job's diagram exactly as drawn: satellite (or graph paper) background
// with the traced shapes and WDO finding markers composited on top.
function DiagramThumb({ job }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const CW = CANVAS_W, CH = job.canvasH || CANVAS_H;
    canvas.width = CW; canvas.height = CH;
    const ctx = canvas.getContext("2d");
    const paint = (bg) => {
      ctx.clearRect(0, 0, CW, CH);
      if (bg) {
        const rot = ((job.mapRotation || 0) * Math.PI) / 180;
        if (rot !== 0) { ctx.save(); ctx.translate(CW/2, CH/2); ctx.rotate(rot);
          const s = Math.abs(Math.cos(rot)) + Math.abs(Math.sin(rot));
          ctx.drawImage(bg, -CW*s/2, -CH*s/2, CW*s, CH*s); ctx.restore();
        } else ctx.drawImage(bg, 0, 0, CW, CH);
      } else {
        ctx.fillStyle = "#f7f9fc"; ctx.fillRect(0, 0, CW, CH);
        ctx.strokeStyle = "rgba(140,170,210,0.5)"; ctx.lineWidth = 1;
        for (let x = 0; x <= CW; x += 50) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CH); ctx.stroke(); }
        for (let y = 0; y <= CH; y += 50) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CW, y); ctx.stroke(); }
      }
      // Traced shapes — white halo under a dark line so they read over any imagery.
      (job.drawings || []).forEach(d => {
        if (!d.pts || d.pts.length < 2) return;
        const trace = () => { ctx.beginPath(); ctx.moveTo(d.pts[0][0], d.pts[0][1]);
          for (let i = 1; i < d.pts.length; i++) ctx.lineTo(d.pts[i][0], d.pts[i][1]);
          if (d.closed) ctx.closePath(); };
        ctx.setLineDash(d.style === "dashed" ? [10,6] : d.style === "dotted" ? [3,6] : []);
        trace(); ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = 5; ctx.stroke();
        trace(); ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 2.5; ctx.stroke();
        ctx.setLineDash([]);
      });
      // WDO finding markers (e.g. "2A", "3B") with a white halo.
      (job.markers || []).forEach(m => {
        if (!m || m.x == null) return;
        const fs = m.fontSize || 16, lh = fs * 1.25;
        ctx.font = `bold ${fs}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.lineJoin = "round";
        const parts = String(m.label || "").split("+");
        ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = 3.5;
        parts.forEach((p, i) => ctx.strokeText(p, m.x, m.y + (i - (parts.length-1)/2) * lh));
        ctx.fillStyle = m.type === "area" ? "#0f172a" : "#dc2626";
        parts.forEach((p, i) => ctx.fillText(p, m.x, m.y + (i - (parts.length-1)/2) * lh));
      });
    };
    if (job.mapBg) { const img = new Image(); img.onload = () => paint(img); img.onerror = () => paint(null); img.src = job.mapBg; }
    else paint(null);
  }, [job]);
  return <canvas ref={ref} style={{ width: "100%", height: "auto", display: "block", borderRadius: 8, border: "1px solid #334155" }} />;
}

// Shared read-first job detail modal used by both the inspector and operator views.
function JobSnapshot({ job:j, onClose, onOpen, workReadOnly, workNote, onSetWork, assignPeople, onAssign, colorFor, startTab, startLabel }) {
  const pill = {background:"#1e293b",border:"1px solid #334155",borderRadius:999,padding:"3px 10px",color:"#cbd5e1",fontSize:12,fontWeight:600};
  const lbl = {fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:0.5,marginBottom:5};
  const autoP={FP:!!(j.address&&j.orderedBy?.name),DM:!!((j.drawings||[]).length>0||j.mapBg),FR:(j.findings||[]).length>0,CT:(j.findings||[]).some(f=>f.primaryPrice>0)};
  const sp=j.stageProgress||{};
  const cats=(j.findings||[]);
  const total=cats.reduce((s,f)=>s+(Number(f.primaryPrice)||0),0);
  const addrFull=[j.address,j.city,j.state,j.zip].filter(Boolean).join(", ");
  const dir=`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addrFull||j.address||"")}`;
  const os=jobOverallStatus(j.workStatus);
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:2100,background:"rgba(15,23,42,0.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#0f172a",border:"1px solid #334155",borderRadius:16,maxWidth:520,width:"100%",maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 70px rgba(0,0,0,0.6)"}}>
        <div style={{borderBottom:"1px solid #334155",padding:"14px 18px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:800,fontSize:16,color:"#f1f5f9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.address||"No address"}</div>
            <div style={{fontSize:12,color:"#94a3b8"}}>{[j.city,j.county,j.state].filter(Boolean).join(" · ")||"—"}</div>
            <span style={{display:"inline-block",marginTop:6,fontSize:11,fontWeight:800,color:os.color,background:os.color+"22",border:`1px solid ${os.color}`,borderRadius:999,padding:"2px 10px"}}>{os.label}</span>
            {j.inspectionSubmitted && <span style={{display:"inline-block",marginTop:6,marginLeft:6,fontSize:11,fontWeight:800,color:"#0f172a",background:"#34d399",borderRadius:999,padding:"2px 10px"}}>✓ Submitted{j.inspectionSubmittedAt?` · ${new Date(j.inspectionSubmittedAt).toLocaleDateString()}`:""}</span>}
            {j.reportSentAt && <span style={{display:"inline-block",marginTop:6,marginLeft:6,fontSize:11,fontWeight:800,color:"#0369a1",background:"#e0f2fe",border:"1px solid #7dd3fc",borderRadius:999,padding:"2px 10px"}}>📧 Emailed · {new Date(j.reportSentAt).toLocaleDateString()}</span>}
            {j.proposalAccepted && <span style={{display:"inline-block",marginTop:6,marginLeft:6,fontSize:11,fontWeight:800,color:"#7c3aed",background:"#f3e8ff",border:"1px solid #d8b4fe",borderRadius:999,padding:"2px 10px"}}>✍️ Signed{j.proposalSignedAt?` · ${new Date(j.proposalSignedAt).toLocaleDateString()}`:""}</span>}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:"#94a3b8",lineHeight:1,flexShrink:0}}>×</button>
        </div>
        <div style={{padding:18,overflowY:"auto",display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            <span style={pill}>📅 {j.date||"—"}{j.time?` · ${j.time}`:""}</span>
            <span style={pill}>Report #{j.reportNum||"—"}</span>
            <span style={pill}>{j.reportType||"—"}</span>
          </div>
          {assignPeople && (
            <div>
              <div style={lbl}>Assigned inspector</div>
              <select value={j.assignedTo||""} onChange={e=>onAssign&&onAssign(e.target.value||null)}
                style={{width:"100%",background:"#0f172a",color:colorFor?colorFor(j.assignedTo):"#e2e8f0",border:`1px solid ${colorFor?colorFor(j.assignedTo):"#334155"}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                <option value="" style={{color:"#fff"}}>— Unassigned —</option>
                {assignPeople.map(p=><option key={p.id} value={p.id} style={{color:"#fff"}}>{p.full_name||p.email||"(unnamed)"}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={lbl}>Report steps</div>
            <div style={{display:"flex",gap:4}}>
              {["FP","DM","FR","CT","CS","NC"].map(s=>{ const on=(s in autoP)?autoP[s]:!!sp[s]; return <span key={s} title={s} style={{fontSize:10,fontWeight:700,color:on?"#0f172a":"#475569",background:on?"#22d3ee":"transparent",border:`1px solid ${on?"#22d3ee":"#334155"}`,borderRadius:4,padding:"2px 6px"}}>{s}</span>; })}
            </div>
          </div>
          <div style={{background:"#111c30",border:"1px solid #334155",borderRadius:10,padding:"12px 14px"}}>
            <div style={{...lbl,marginBottom:8}}>Work status{workNote||""}</div>
            <WorkStatus value={j.workStatus} onChange={onSetWork} readOnly={workReadOnly}/>
          </div>
          {(j.mapBg || (j.drawings||[]).length>0 || (j.markers||[]).length>0) && <div><div style={lbl}>Diagram</div><DiagramThumb job={j}/></div>}
          {(j.orderedBy?.name||j.orderedBy?.phone) && (
            <div><div style={lbl}>Ordered by</div>
              <div style={{fontSize:13,color:"#e2e8f0"}}>{j.orderedBy?.name}{j.orderedBy?.company?` · ${j.orderedBy.company}`:""}</div>
              {j.orderedBy?.phone && <a href={`tel:${j.orderedBy.phone}`} style={{fontSize:13,color:"#22d3ee",textDecoration:"none"}}>📞 {j.orderedBy.phone}</a>}
            </div>
          )}
          <div>
            <div style={lbl}>Findings ({cats.length}){total>0?` · $${total.toLocaleString()}`:""}</div>
            {cats.length===0 ? <div style={{fontSize:12,color:"#64748b"}}>None recorded yet.</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {cats.slice(0,8).map((f,i)=>{ const c=WDO_CATEGORIES.find(x=>x.num===f.cat); const ph=f.photos||[]; return (
                  <div key={i} style={{display:"flex",flexDirection:"column",gap:4}}>
                    <div style={{display:"flex",gap:8,alignItems:"center",fontSize:12,color:"#cbd5e1"}}>
                      <span style={{fontWeight:800,color:c?.color||"#94a3b8",background:"#1e293b",border:"1px solid #334155",borderRadius:4,padding:"1px 6px",flexShrink:0}}>{f.cat}{f.letter}</span>
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c?.label||""}{f.component?` · 🔧 ${f.component}`:""}{(f.repairHours!==""&&f.repairHours!=null)?` · ⏱ ${f.repairHours}h`:""}{f.primaryPrice>0?` — $${Number(f.primaryPrice).toLocaleString()}`:""}</span>
                    </div>
                    {ph.length>0 && (
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",paddingLeft:30}}>
                        {ph.slice(0,6).map(p=>(
                          <img key={p.id} src={p.data} alt="" onClick={()=>window.open(p.data,'_blank')}
                            style={{width:46,height:36,objectFit:"cover",borderRadius:4,border:"1px solid #334155",cursor:"pointer"}}/>
                        ))}
                        {ph.length>6 && <span style={{fontSize:11,color:"#64748b",alignSelf:"center"}}>+{ph.length-6}</span>}
                      </div>
                    )}
                  </div>
                ); })}
                {cats.length>8 && <div style={{fontSize:11,color:"#64748b"}}>+{cats.length-8} more</div>}
              </div>
            )}
          </div>
          {j.notes && <div><div style={lbl}>Notes</div><div style={{fontSize:12,color:"#cbd5e1",whiteSpace:"pre-wrap"}}>{j.notes}</div></div>}
        </div>
        <div style={{borderTop:"1px solid #334155",padding:"12px 18px",display:"flex",gap:8,alignItems:"center"}}>
          {j.address && <a href={dir} target="_blank" rel="noreferrer" style={{...S.btn("#334155"),textDecoration:"none",fontSize:13,padding:"9px 14px"}}>🧭 Directions</a>}
          <button onClick={()=>{ onClose(); onOpen(j, startTab); }} style={{...S.btn("#0ea5e9"),fontSize:13,fontWeight:800,padding:"9px 16px",marginLeft:"auto"}}>{startLabel||"Open full job →"}</button>
        </div>
      </div>
    </div>
  );
}

const INSPECTOR_COLORS = ["#22d3ee","#f59e0b","#a78bfa","#34d399","#f472b6","#60a5fa","#fb7185","#facc15","#4ade80","#c084fc"];

// Build a Google Maps multi-stop route from a day's jobs. Orders by
// nearest-neighbour when map coords exist, else by scheduled time.
function buildDayRoute(dayJobs){
  const withAddr = (dayJobs||[]).filter(j=>j.address);
  if(!withAddr.length) return null;
  let ordered;
  if(withAddr.every(j=>j.mapCenter&&j.mapCenter.lat)){
    const remaining=[...withAddr]; ordered=[];
    let curLat=remaining[0].mapCenter.lat, curLng=remaining[0].mapCenter.lng;
    while(remaining.length){
      let bi=0,bd=Infinity;
      for(let i=0;i<remaining.length;i++){
        const mc=remaining[i].mapCenter;
        const d=Math.hypot(mc.lat-curLat, mc.lng-curLng);
        if(d<bd){bd=d;bi=i;}
      }
      const nx=remaining.splice(bi,1)[0]; ordered.push(nx); curLat=nx.mapCenter.lat; curLng=nx.mapCenter.lng;
    }
  } else {
    ordered=[...withAddr].sort((a,b)=>String(a.time||"").localeCompare(String(b.time||"")));
  }
  const enc=j=>encodeURIComponent([j.address,j.city,j.state,j.zip].filter(Boolean).join(", "));
  const dest=enc(ordered[ordered.length-1]);
  const wp=ordered.slice(0,-1).map(enc).join("|");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}${wp?`&waypoints=${wp}`:""}`;
}

function OperatorDashboard({ jobs, people, settings, onOpen, onGoJobs, onNew, onAssign }){
  const now = new Date();
  const pad = n=>String(n).padStart(2,"0");
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
  const active = (jobs||[]);
  const mob = window.innerWidth < 720;
  const money = n=>"$"+Number(n||0).toLocaleString();
  const findingsTotal = j=>(j.findings||[]).reduce((s,f)=>s+(Number(f.primaryPrice)||0),0);
  const inNextWeek = ds=>{ if(!ds) return false; const d=(new Date(ds+"T00:00:00")-new Date(todayStr+"T00:00:00"))/86400000; return d>=0 && d<7; };

  // Status buckets from the headline work status + workflow flags.
  const statusDefs = [
    { key:"Awaiting", color:"#64748b" },
    { key:"Scheduled", color:"#0ea5e9" },
    { key:"In progress", color:"#f59e0b" },
    { key:"Submitted", color:"#34d399" },
    { key:"Signed", color:"#a78bfa" },
    { key:"Complete", color:"#22c55e" },
  ];
  const counts = Object.fromEntries(statusDefs.map(s=>[s.key,0]));
  active.forEach(j=>{
    if(j.proposalAccepted) counts.Signed++;
    if(j.inspectionSubmitted) counts.Submitted++;
    const os=jobOverallStatus(j.workStatus).label;
    if(counts[os]!=null && os!=="Awaiting") counts[os]++;
    else if(os==="Awaiting" && !j.inspectionSubmitted && !j.proposalAccepted) counts.Awaiting++;
  });

  const totalQuoted = active.reduce((s,j)=>s+findingsTotal(j),0);
  const signedRevenue = active.filter(j=>j.proposalAccepted).reduce((s,j)=>s+findingsTotal(j),0);
  const outstanding = totalQuoted - signedRevenue;
  const proposalsSent = active.filter(j=>j.signatureRequest||j.proposalAccepted).length;
  const proposalsSigned = active.filter(j=>j.proposalAccepted).length;
  const reportsEmailed = active.filter(j=>j.reportSentAt).length;

  const weekJobs = active.filter(j=>inNextWeek(j.date)).sort((a,b)=>String((a.date||"")+(a.time||"")).localeCompare(String((b.date||"")+(b.time||""))));
  const todayJobs = active.filter(j=>j.date===todayStr);
  const unassigned = active.filter(j=>!j.assignedTo && j.date && j.date>=todayStr);
  const overdue = active.filter(j=>j.date && j.date<todayStr && !j.inspectionSubmitted && !j.proposalAccepted);
  const nameOf = id => people.find(p=>p.id===id)?.full_name || people.find(p=>p.id===id)?.email || "—";

  // Per-inspector workload for the coming week.
  const workload = people.map(p=>({ name:p.full_name||p.email, id:p.id, count:weekJobs.filter(j=>j.assignedTo===p.id).length }));
  const unassignedWeek = weekJobs.filter(j=>!j.assignedTo).length;

  const card = {background:"#1e293b",border:"1px solid #334155",borderRadius:12,padding:16};
  const lbl = {fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8};
  const todayRoute = buildDayRoute(todayJobs.filter(j=>j.assignedTo));

  const JobLine = ({j})=>{ const os=jobOverallStatus(j.workStatus); return (
    <div onClick={()=>onOpen(j)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,background:"#0f172a",border:"1px solid #334155",cursor:"pointer"}}>
      <span style={{fontSize:12,fontWeight:700,color:"#22d3ee",minWidth:64}}>{j.date? j.date.slice(5) : "—"}{j.time?` ${j.time}`:""}</span>
      <span style={{flex:1,minWidth:0,fontSize:13,color:"#f1f5f9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.address||`#${j.reportNum}`}</span>
      {j.assignedTo ? <span style={{fontSize:11,color:"#94a3b8"}}>{nameOf(j.assignedTo)}</span> : <span style={{fontSize:11,fontWeight:700,color:"#f59e0b"}}>Unassigned</span>}
      <span style={{fontSize:10,fontWeight:800,color:os.color,background:os.color+"22",border:`1px solid ${os.color}`,borderRadius:999,padding:"1px 8px"}}>{os.label}</span>
    </div>
  ); };

  return (
    <div style={{padding:16,minHeight:"calc(100vh - 60px)",background:"#0f172a"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:20}}>📊</span>
        <span style={{fontSize:16,fontWeight:800,color:"#f1f5f9"}}>Office Dashboard</span>
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button style={{...S.btn("#0ea5e9"),fontSize:12,padding:"6px 12px"}} onClick={onNew}>＋ New Job</button>
          <button style={{...S.btn("#334155"),fontSize:12,padding:"6px 12px"}} onClick={onGoJobs}>All Jobs →</button>
        </div>
      </div>

      {/* Status tiles */}
      <div style={{display:"grid",gridTemplateColumns:mob?"repeat(2,1fr)":"repeat(6,1fr)",gap:10,marginBottom:14}}>
        {statusDefs.map(s=>(
          <div key={s.key} onClick={onGoJobs} style={{...card,padding:14,cursor:"pointer",borderColor:s.color+"66"}}>
            <div style={{fontSize:26,fontWeight:800,color:s.color}}>{counts[s.key]}</div>
            <div style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>{s.key}</div>
          </div>
        ))}
      </div>

      {/* Revenue pipeline */}
      <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"2fr 1fr",gap:14,marginBottom:14}}>
        <div style={card}>
          <div style={lbl}>Revenue pipeline</div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            <div><div style={{fontSize:22,fontWeight:800,color:"#f1f5f9"}}>{money(totalQuoted)}</div><div style={{fontSize:11,color:"#94a3b8"}}>Total quoted</div></div>
            <div><div style={{fontSize:22,fontWeight:800,color:"#a78bfa"}}>{money(signedRevenue)}</div><div style={{fontSize:11,color:"#94a3b8"}}>Signed / authorized</div></div>
            <div><div style={{fontSize:22,fontWeight:800,color:"#f59e0b"}}>{money(outstanding)}</div><div style={{fontSize:11,color:"#94a3b8"}}>Outstanding</div></div>
          </div>
          {totalQuoted>0 && (
            <div style={{marginTop:12,height:8,borderRadius:6,background:"#0f172a",overflow:"hidden"}}>
              <div style={{width:`${Math.round(signedRevenue/totalQuoted*100)}%`,height:"100%",background:"#a78bfa"}}/>
            </div>
          )}
        </div>
        <div style={card}>
          <div style={lbl}>Proposals & reports</div>
          <div style={{fontSize:13,color:"#cbd5e1",display:"flex",flexDirection:"column",gap:6}}>
            <div>✍️ Proposals signed: <strong style={{color:"#a78bfa"}}>{proposalsSigned}</strong> / {proposalsSent} sent</div>
            <div>📧 Reports emailed: <strong style={{color:"#22d3ee"}}>{reportsEmailed}</strong></div>
          </div>
        </div>
      </div>

      {/* Attention: unassigned & overdue */}
      {(unassigned.length>0 || overdue.length>0) && (
        <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:14,marginBottom:14}}>
          {unassigned.length>0 && (
            <div style={{...card,borderColor:"#b45309"}}>
              <div style={{...lbl,color:"#f59e0b"}}>⚠️ Unassigned ({unassigned.length})</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {unassigned.slice(0,5).map(j=>(
                  <div key={j.id} style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span onClick={()=>onOpen(j)} style={{flex:1,minWidth:0,fontSize:12,color:"#f1f5f9",cursor:"pointer",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.date?.slice(5)} · {j.address||`#${j.reportNum}`}</span>
                    <select defaultValue="" onChange={e=>{ if(e.target.value) onAssign(j, e.target.value); }} style={{background:"#0f172a",color:"#e2e8f0",border:"1px solid #334155",borderRadius:6,fontSize:11,padding:"3px 6px"}}>
                      <option value="">Assign…</option>
                      {people.map(p=><option key={p.id} value={p.id}>{p.full_name||p.email}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          {overdue.length>0 && (
            <div style={{...card,borderColor:"#dc2626"}}>
              <div style={{...lbl,color:"#f87171"}}>⏰ Overdue ({overdue.length})</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {overdue.slice(0,5).map(j=><JobLine key={j.id} j={j}/>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* This week + workload + route */}
      <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"2fr 1fr",gap:14}}>
        <div style={card}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={lbl}>Next 7 days ({weekJobs.length})</div>
            {todayRoute && <a href={todayRoute} target="_blank" rel="noreferrer" style={{...S.btn("#334155"),textDecoration:"none",fontSize:11,padding:"4px 10px"}}>🧭 Plan today's route</a>}
          </div>
          {weekJobs.length===0 ? <div style={{fontSize:13,color:"#64748b"}}>No jobs scheduled this week.</div> : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>{weekJobs.slice(0,10).map(j=><JobLine key={j.id} j={j}/>)}</div>
          )}
        </div>
        <div style={card}>
          <div style={lbl}>Inspector workload (7d)</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {workload.length===0 && <div style={{fontSize:12,color:"#64748b"}}>No inspectors yet.</div>}
            {workload.map(w=>(
              <div key={w.id} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#cbd5e1"}}>
                <span>{w.name}</span><span style={{fontWeight:800,color:"#f1f5f9"}}>{w.count}</span>
              </div>
            ))}
            {unassignedWeek>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#f59e0b",fontWeight:700,borderTop:"1px solid #334155",paddingTop:6,marginTop:2}}><span>Unassigned</span><span>{unassignedWeek}</span></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function InspectorDashboard({ jobs, settings, onOpen, firmMode, people, lockedId, onPatchJob }) {
  // In firm mode assignment is by login (job.assignedTo → a profile id); in
  // legacy mode it's the local inspector record (job.inspectorIds).
  const inspectors = firmMode ? (people||[]) : (people && people.length ? people : (settings?.inspectors||[]).map(i=>({id:i.id,full_name:i.name,license:i.license})));
  const [inspId, setInspId] = useState(lockedId || inspectors[0]?.id || "");
  useEffect(()=>{
    if(lockedId){setInspId(lockedId);return;}
    setInspId(current=>inspectors.some(p=>p.id===current)?current:inspectors[0]?.id||"");
  },[lockedId,people,firmMode,settings?.inspectors]);
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selDay, setSelDay] = useState(null); // 'YYYY-MM-DD' currently focused day
  const [snap, setSnap]   = useState(null);   // job shown in the snapshot modal
  const mob = window.innerWidth < 720;

  const pad = n => String(n).padStart(2, "0");
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
  const shortType = t => ({"Complete Report":"Comp","Limited Report":"Ltd","Supplemental Report":"Supp","Reinspection":"Reinsp","Corrected Report":"Corr","Separated Report":"Sep"}[t] || (t||"").slice(0,4));
  const fmtDay = ds => { const [y,m,d]=ds.split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"}); };

  const insp = inspectors.find(i => i.id === inspId);
  const matchJob = j => firmMode ? (j.assignedTo === inspId) : (j.inspectorIds||[]).includes(inspId);
  const myJobs = jobs
    .filter(matchJob)
    .sort((a,b)=> String(a.date||"").localeCompare(String(b.date||"")));
  const upcoming = myJobs.filter(j => (j.date||"") >= todayStr);

  const byDate = {};
  myJobs.forEach(j => { if(j.date){ (byDate[j.date] = byDate[j.date] || []).push(j); } });

  const first = new Date(view.y, view.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(view.y, view.m+1, 0).getDate();
  const cells = [];
  for(let i=0;i<startDow;i++) cells.push(null);
  for(let d=1; d<=daysInMonth; d++) cells.push(d);
  while(cells.length % 7 !== 0) cells.push(null);
  const monthLabel = first.toLocaleString("en-US",{month:"long",year:"numeric"});
  const shiftMonth = delta => { setSelDay(null); setView(v => { const d=new Date(v.y,v.m+delta,1); return {y:d.getFullYear(),m:d.getMonth()}; }); };
  const cellDate = d => `${view.y}-${pad(view.m+1)}-${pad(d)}`;
  const gotoToday = () => { setView({y:now.getFullYear(),m:now.getMonth()}); setSelDay(todayStr); };

  const card = {background:"#1e293b",border:"1px solid #334155",borderRadius:12,padding:14};
  const th = {padding:"8px 10px",fontWeight:700,fontSize:11,color:"#94a3b8",textAlign:"left",borderBottom:"2px solid #334155",whiteSpace:"nowrap"};
  const td = {padding:"8px 10px",fontSize:12,color:"#e2e8f0",borderBottom:"1px solid rgba(51,65,85,0.5)",whiteSpace:"nowrap",overflow:"hidden",maxWidth:200,textOverflow:"ellipsis"};
  const pill = {background:"#1e293b",border:"1px solid #334155",borderRadius:999,padding:"3px 10px",color:"#cbd5e1",fontSize:12,fontWeight:600};
  const lbl = {fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:0.5,marginBottom:5};

  // Small reusable job card used in the side panel.
  const JobCard = ({ j }) => {
    const os = jobOverallStatus(j.workStatus);
    return (
      <div onClick={()=>setSnap(j)} style={{background:"#0f172a",border:"1px solid #334155",borderRadius:8,padding:"8px 10px",cursor:"pointer"}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
          <span style={{fontSize:12,fontWeight:700,color:"#22d3ee"}}>{j.date||"—"}{j.time?` · ${j.time}`:""}</span>
          <span style={{fontSize:11,color:"#64748b"}}>#{j.reportNum} · {shortType(j.reportType)}</span>
        </div>
        <div style={{fontSize:13,color:"#f1f5f9",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.address||"No address"}</div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:3,flexWrap:"wrap"}}>
          <span style={{fontSize:10,fontWeight:800,color:os.color,background:os.color+"22",border:`1px solid ${os.color}`,borderRadius:999,padding:"1px 8px"}}>{os.label}</span>
          {j.inspectionSubmitted && <span style={{fontSize:10,fontWeight:800,color:"#0f172a",background:"#34d399",borderRadius:999,padding:"1px 8px"}}>✓ Submitted</span>}
          <WorkStatusChips ws={j.workStatus}/>
        </div>
        {j.address && (
          <div style={{display:"flex",gap:6,marginTop:8}}>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([j.address,j.city,j.state,j.zip].filter(Boolean).join(", "))}`}
              target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
              style={{...S.btn("#334155"),textDecoration:"none",fontSize:12,padding:"6px 10px",flex:1,textAlign:"center"}}>🧭 Navigate</a>
            <button onClick={e=>{e.stopPropagation(); onOpen(j,"map");}}
              style={{...S.btn("#0ea5e9"),fontSize:12,padding:"6px 10px",flex:1,fontWeight:800}}>▶ Start</button>
          </div>
        )}
      </div>
    );
  };

  const panelJobs = selDay ? (byDate[selDay]||[]) : upcoming;

  return (
    <div style={{padding:16,minHeight:"calc(100vh - 60px)",background:"#0f172a"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:20}}>🗓</span>
        <span style={{fontSize:15,fontWeight:800,color:"#f1f5f9"}}>{lockedId ? "My Schedule" : "Inspector Schedule"}</span>
        {lockedId ? (
          <span style={{marginLeft:"auto",fontSize:13,color:"#e2e8f0",fontWeight:700}}>{insp?.full_name||"Me"}</span>
        ) : inspectors.length>0 ? (
          <select value={inspId} onChange={e=>{setInspId(e.target.value);setSelDay(null);}}
            style={{background:"#1e293b",border:"1px solid #334155",color:"#f1f5f9",borderRadius:6,padding:"6px 12px",fontSize:13,cursor:"pointer",marginLeft:"auto"}}>
            {inspectors.map(i=><option key={i.id} value={i.id}>{i.full_name||i.email||"(unnamed)"}</option>)}
          </select>
        ) : (
          <span style={{marginLeft:"auto",fontSize:13,color:"#94a3b8"}}>{firmMode?"No inspector logins yet — create them in Supabase.":"Add inspectors in Settings ⚙ to use this view."}</span>
        )}
      </div>

      {inspId && (
        <>
          <div style={{color:"#94a3b8",fontSize:13,marginBottom:14}}>
            {insp?.full_name}{insp?.license?` · Lic. ${insp.license}`:""} — <span style={{color:"#22d3ee",fontWeight:700}}>{upcoming.length}</span> upcoming · {myJobs.length} total assigned
          </div>

          <div style={{display:"flex",gap:16,flexDirection:mob?"column":"row",alignItems:"stretch"}}>
            <div style={{...card, flex:mob?"none":2}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:8}}>
                <button onClick={()=>shiftMonth(-1)} style={{...S.btn("#334155"),padding:"4px 14px",fontSize:16}}>‹</button>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontWeight:800,fontSize:15,color:"#f1f5f9"}}>{monthLabel}</span>
                  <button onClick={gotoToday} style={{...S.btn("#0ea5e9"),padding:"3px 10px",fontSize:11,fontWeight:700}}>Today</button>
                </div>
                <button onClick={()=>shiftMonth(1)} style={{...S.btn("#334155"),padding:"4px 14px",fontSize:16}}>›</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
                  <div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#64748b",padding:"2px 0"}}>{mob?d[0]:d}</div>
                ))}
                {cells.map((d,i)=>{
                  if(d===null) return <div key={i}/>;
                  const ds = cellDate(d);
                  const dayJobs = byDate[ds]||[];
                  const isToday = ds===todayStr;
                  const isSel = ds===selDay;
                  const cap = mob?1:3;
                  return (
                    <div key={i} onClick={()=>setSelDay(ds)}
                      style={{minHeight:mob?52:84,borderRadius:8,cursor:"pointer",padding:4,overflow:"hidden",
                        border:`1px solid ${isSel?"#22d3ee":isToday?"#0e7490":"#334155"}`,
                        background:isSel?"rgba(34,211,238,0.16)":isToday?"rgba(34,211,238,0.06)":"#0f172a",
                        ...(isSel?{boxShadow:"0 0 0 1px #22d3ee"}:{})}}>
                      <div style={{fontSize:11,fontWeight:700,color:isToday?"#22d3ee":"#94a3b8",marginBottom:2,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                        <span>{d}</span>{dayJobs.length>0 && <span style={{width:6,height:6,borderRadius:6,background:"#0ea5e9",display:"inline-block"}}/>}
                      </div>
                      {dayJobs.slice(0,cap).map(j=>(
                        <div key={j.id} onClick={e=>{e.stopPropagation(); setSnap(j);}} title={[j.address,j.city].filter(Boolean).join(", ")}
                          style={{fontSize:10,background:"#0ea5e9",color:"#fff",borderRadius:4,padding:"1px 4px",marginBottom:2,cursor:"pointer",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {j.address||`#${j.reportNum}`}
                        </div>
                      ))}
                      {dayJobs.length>cap && <div style={{fontSize:9,color:"#64748b"}}>+{dayJobs.length-cap} more</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{fontSize:11,color:"#64748b",marginTop:10,textAlign:"center"}}>Tap a day to see its jobs · tap a job for details</div>
            </div>

            <div style={{...card, flex:mob?"none":1, display:"flex",flexDirection:"column",maxHeight:mob?"none":540}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8}}>
                <div style={{fontWeight:800,fontSize:14,color:"#f1f5f9"}}>{selDay ? fmtDay(selDay) : "Upcoming"}</div>
                {selDay && <button onClick={()=>setSelDay(null)} style={{background:"none",border:"none",color:"#22d3ee",fontSize:12,cursor:"pointer",fontWeight:700}}>Upcoming ›</button>}
              </div>
              {panelJobs.length===0 && <div style={{color:"#64748b",fontSize:13}}>{selDay?"No jobs scheduled this day.":"No upcoming jobs."}</div>}
              <div style={{display:"flex",flexDirection:"column",gap:8,overflowY:"auto"}}>
                {panelJobs.map(j=><JobCard key={j.id} j={j}/>)}
              </div>
            </div>
          </div>

          <div style={{...card, marginTop:16, padding:0, overflow:"hidden"}}>
            <div style={{padding:"12px 14px",fontWeight:800,fontSize:14,color:"#f1f5f9",borderBottom:"1px solid #334155"}}>All Assigned Jobs</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={th}>Report</th><th style={th}>Type</th><th style={th}>Steps</th>
                  <th style={th}>Property Address</th><th style={th}>County</th><th style={th}>Insp.</th><th style={th}>Work status</th>
                </tr></thead>
                <tbody>
                  {myJobs.length===0 && <tr><td colSpan={7} style={{...td,textAlign:"center",padding:30,color:"#475569",maxWidth:"none"}}>No jobs assigned to this inspector.</td></tr>}
                  {myJobs.map((j,idx)=>{
                    const autoP={FP:!!(j.address&&j.orderedBy?.name),DM:!!((j.drawings||[]).length>0||j.mapBg),FR:(j.findings||[]).length>0,CT:(j.findings||[]).some(f=>f.primaryPrice>0)};
                    const sp=j.stageProgress||{};
                    return (
                      <tr key={j.id} onClick={()=>setSnap(j)} style={{background:idx%2===0?"#0f172a":"rgba(30,41,59,0.4)",cursor:"pointer"}}>
                        <td style={{...td,fontWeight:700,color:"#f1f5f9"}}>{j.reportNum||"—"}</td>
                        <td style={td}>{shortType(j.reportType)}</td>
                        <td style={td}>
                          <div style={{display:"flex",gap:3}}>
                            {["FP","DM","FR","CT","CS","NC"].map(s=>{
                              const on = (s in autoP) ? autoP[s] : !!sp[s];
                              return <span key={s} title={s} style={{fontSize:9,fontWeight:700,color:on?"#0f172a":"#475569",background:on?"#22d3ee":"transparent",border:`1px solid ${on?"#22d3ee":"#334155"}`,borderRadius:3,padding:"1px 3px"}}>{s}</span>;
                            })}
                          </div>
                        </td>
                        <td style={td}>{[j.address,j.city].filter(Boolean).join(", ")||"—"}</td>
                        <td style={td}>{j.county||"—"}</td>
                        <td style={td}>{j.date||"—"}</td>
                        <td style={{...td,maxWidth:"none"}}>{(()=>{const o=jobOverallStatus(j.workStatus);return <span style={{fontSize:10,fontWeight:800,color:o.color,background:o.color+"22",border:`1px solid ${o.color}`,borderRadius:999,padding:"1px 8px"}}>{o.label}</span>;})()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {snap && <JobSnapshot job={snap} onClose={()=>setSnap(null)} onOpen={onOpen}
        startTab={lockedId?"map":undefined} startLabel={lockedId?"▶ Start inspection":undefined}
        workReadOnly={!!lockedId} workNote={lockedId?" (updated by the office)":""}
        onSetWork={ws=>{ setSnap({...snap,workStatus:ws}); onPatchJob?.(snap.id,{workStatus:ws}); }}/>}
    </div>
  );
}

// ─── OPERATOR TEAM CALENDAR ──────────────────────────────────────────────────
function OperatorCalendar({ jobs, people, onOpen, onAssign, onPatchJob }) {
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selDay, setSelDay] = useState(null);
  const [snap, setSnap] = useState(null);
  const [hidden, setHidden] = useState(()=>new Set());
  const mob = window.innerWidth < 820;

  const pad = n => String(n).padStart(2,"0");
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
  const colorFor = id => { if(!id) return "#64748b"; const i=people.findIndex(p=>p.id===id); return i>=0?INSPECTOR_COLORS[i%INSPECTOR_COLORS.length]:"#64748b"; };
  const nameFor = id => { if(!id) return "Unassigned"; const p=people.find(x=>x.id===id); return p?(p.full_name||p.email||"(unnamed)"):"Unknown"; };
  const countFor = id => jobs.filter(j=>(j.assignedTo||"")===(id||"")).length;

  const shown = jobs.filter(j=>!hidden.has(j.assignedTo||""));
  const byDate = {};
  shown.forEach(j=>{ if(j.date){ (byDate[j.date]=byDate[j.date]||[]).push(j); } });

  const first = new Date(view.y, view.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(view.y, view.m+1, 0).getDate();
  const cells = [];
  for(let i=0;i<startDow;i++) cells.push(null);
  for(let d=1; d<=daysInMonth; d++) cells.push(d);
  while(cells.length % 7 !== 0) cells.push(null);
  const monthLabel = first.toLocaleString("en-US",{month:"long",year:"numeric"});
  const shiftMonth = delta => { setSelDay(null); setView(v=>{ const d=new Date(v.y,v.m+delta,1); return {y:d.getFullYear(),m:d.getMonth()}; }); };
  const cellDate = d => `${view.y}-${pad(view.m+1)}-${pad(d)}`;
  const fmtDay = ds => { const [y,m,d]=ds.split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"}); };
  const toggle = id => setHidden(h=>{ const n=new Set(h); const k=id||""; n.has(k)?n.delete(k):n.add(k); return n; });

  const card = {background:"#1e293b",border:"1px solid #334155",borderRadius:12,padding:14};
  const legendItems = [...people, {id:"", full_name:"Unassigned"}];
  const panelJobs = (selDay ? (byDate[selDay]||[]) : shown.filter(j=>(j.date||"")>=todayStr)).slice().sort((a,b)=>String(a.date||"").localeCompare(String(b.date||"")));

  return (
    <div style={{padding:16,minHeight:"calc(100vh - 60px)",background:"#0f172a"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <span style={{fontSize:20}}>📅</span>
        <span style={{fontSize:15,fontWeight:800,color:"#f1f5f9"}}>Team Calendar</span>
        <span style={{fontSize:12,color:"#94a3b8",marginLeft:"auto"}}>{shown.length} shown · {jobs.length} total</span>
      </div>

      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
        {legendItems.map(p=>{
          const c=colorFor(p.id); const off=hidden.has(p.id||""); const n=countFor(p.id);
          return (
            <button key={p.id||"un"} onClick={()=>toggle(p.id)} title={off?"Show":"Hide"}
              style={{display:"inline-flex",alignItems:"center",gap:6,background:off?"transparent":c+"22",border:`1px solid ${off?"#334155":c}`,color:off?"#64748b":"#e2e8f0",borderRadius:999,padding:"4px 10px",fontSize:12,fontWeight:700,cursor:"pointer",textDecoration:off?"line-through":"none"}}>
              <span style={{width:10,height:10,borderRadius:10,background:c,display:"inline-block"}}/>
              {p.full_name||p.email} <span style={{color:"#94a3b8",fontWeight:600}}>{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",gap:16,flexDirection:mob?"column":"row",alignItems:"stretch"}}>
        <div style={{...card, flex:mob?"none":3}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button onClick={()=>shiftMonth(-1)} style={{...S.btn("#334155"),padding:"4px 14px",fontSize:16}}>‹</button>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontWeight:800,fontSize:15,color:"#f1f5f9"}}>{monthLabel}</span>
              <button onClick={()=>{ setView({y:now.getFullYear(),m:now.getMonth()}); setSelDay(todayStr); }} style={{...S.btn("#0ea5e9"),padding:"3px 10px",fontSize:11,fontWeight:700}}>Today</button>
            </div>
            <button onClick={()=>shiftMonth(1)} style={{...S.btn("#334155"),padding:"4px 14px",fontSize:16}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#64748b",padding:"2px 0"}}>{mob?d[0]:d}</div>)}
            {cells.map((d,i)=>{
              if(d===null) return <div key={i}/>;
              const ds=cellDate(d); const dayJobs=byDate[ds]||[]; const isToday=ds===todayStr; const isSel=ds===selDay; const cap=mob?2:4;
              return (
                <div key={i} onClick={()=>setSelDay(ds)} style={{minHeight:mob?58:96,borderRadius:8,cursor:"pointer",padding:4,overflow:"hidden",border:`1px solid ${isSel?"#22d3ee":isToday?"#0e7490":"#334155"}`,background:isSel?"rgba(34,211,238,0.14)":isToday?"rgba(34,211,238,0.05)":"#0f172a"}}>
                  <div style={{fontSize:11,fontWeight:700,color:isToday?"#22d3ee":"#94a3b8",marginBottom:2}}>{d}</div>
                  {dayJobs.slice(0,cap).map(j=>{ const c=colorFor(j.assignedTo); return (
                    <div key={j.id} onClick={e=>{e.stopPropagation(); setSnap(j);}} title={`${j.address||("#"+j.reportNum)} — ${nameFor(j.assignedTo)}`}
                      style={{fontSize:10,background:c,color:"#0b1220",fontWeight:700,borderRadius:4,padding:"1px 4px",marginBottom:2,cursor:"pointer",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {j.address||`#${j.reportNum}`}
                    </div>
                  ); })}
                  {dayJobs.length>cap && <div style={{fontSize:9,color:"#64748b"}}>+{dayJobs.length-cap} more</div>}
                </div>
              );
            })}
          </div>
          <div style={{fontSize:11,color:"#64748b",marginTop:10,textAlign:"center"}}>Colors = inspector · tap a day to list · tap a job to view / reassign</div>
        </div>

        <div style={{...card, flex:mob?"none":1.2, display:"flex",flexDirection:"column",maxHeight:mob?"none":560}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontWeight:800,fontSize:14,color:"#f1f5f9"}}>{selDay?fmtDay(selDay):"Upcoming"}</div>
            {selDay && <button onClick={()=>setSelDay(null)} style={{background:"none",border:"none",color:"#22d3ee",fontSize:12,cursor:"pointer",fontWeight:700}}>Upcoming ›</button>}
          </div>
          {panelJobs.length===0 && <div style={{color:"#64748b",fontSize:13}}>{selDay?"No jobs this day.":"No upcoming jobs."}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:8,overflowY:"auto"}}>
            {panelJobs.map(j=>{ const c=colorFor(j.assignedTo); return (
              <div key={j.id} onClick={()=>setSnap(j)} style={{background:"#0f172a",border:"1px solid #334155",borderLeft:`3px solid ${c}`,borderRadius:8,padding:"8px 10px",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#22d3ee"}}>{j.date||"—"}{j.time?` · ${j.time}`:""}</span>
                  <span style={{fontSize:11,fontWeight:700,color:c}}>{nameFor(j.assignedTo)}</span>
                </div>
                <div style={{fontSize:13,color:"#f1f5f9",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.address||"No address"}</div>
                <div style={{fontSize:11,color:"#94a3b8"}}>{[j.city,j.county].filter(Boolean).join(" · ")||"—"}</div>
              </div>
            ); })}
          </div>
        </div>
      </div>

      {snap && <JobSnapshot job={snap} onClose={()=>setSnap(null)} onOpen={onOpen}
        workReadOnly={false}
        onSetWork={ws=>{ setSnap({...snap,workStatus:ws}); onPatchJob?.(snap.id,{workStatus:ws}); }}
        assignPeople={people} colorFor={colorFor}
        onAssign={uid=>{ onAssign?.(snap, uid); setSnap({...snap, assignedTo:uid||null}); }}/>}
    </div>
  );
}

function JobsDashboard({ jobs, currentJobId, settings, onOpen, onNew, onUpdateJob, onDelete, firmMode, people, onAssign }) {
  const [search, setSearch] = useState("");
  const [deletingId,setDeletingId]=useState(null);
  const [deleteError,setDeleteError]=useState("");
  const [filterState, setFilterState] = useState("All");
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  const states = [...new Set(jobs.map(j => j.state).filter(Boolean))].sort();
  const inspectors = settings?.inspectors || [];

  function inspName(ids) {
    if (!ids?.length) return "";
    const insp = inspectors.find(i => i.id === ids[0]);
    return insp ? (insp.name||"").split(" ").map((w,i)=>i===0?w:w[0]+".").join(" ") : "";
  }

  function shortType(t) {
    const map = {"Complete Report":"Comp","Limited Report":"Ltd","Supplemental Report":"Supp","Reinspection":"Reinsp","Corrected Report":"Corr","Separated Report":"Sep"};
    return map[t] || (t||"").slice(0,4);
  }

  let filtered = jobs;
  if (filterState !== "All") filtered = filtered.filter(j => j.state === filterState);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(j =>
      (j.address||"").toLowerCase().includes(q) ||
      (j.reportNum||"").toLowerCase().includes(q) ||
      (j.orderedBy?.name||"").toLowerCase().includes(q) ||
      (j.county||"").toLowerCase().includes(q) ||
      (j.city||"").toLowerCase().includes(q)
    );
  }
  filtered = [...filtered].sort((a, b) => {
    const av = sortBy === "orderedBy" ? (a.orderedBy?.name||"") : (a[sortBy]||"");
    const bv = sortBy === "orderedBy" ? (b.orderedBy?.name||"") : (b[sortBy]||"");
    const cmp = String(av).localeCompare(String(bv), undefined, {numeric: true});
    return sortDir === "asc" ? cmp : -cmp;
  });

  async function deleteJob(j){
    if(deletingId)return;
    if(!window.confirm(`Are you sure you want to delete this job?\n\n${j.address||('Report #'+(j.reportNum||'—'))}\n\nThis removes the inspection and its saved report data. This cannot be undone.`))return;
    setDeleteError("");setDeletingId(j.id);
    try{await onDelete(j.id);}
    catch(error){setDeleteError(error.message||"Could not delete this job. Please retry.");}
    finally{setDeletingId(null);}
  }
  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  }

  const th = {padding:"8px 10px",fontWeight:700,fontSize:11,color:"#94a3b8",textAlign:"left",borderBottom:"2px solid #334155",whiteSpace:"nowrap",background:"#1e293b",position:"sticky",top:0,zIndex:1};
  const td = {padding:"7px 10px",fontSize:12,color:"#e2e8f0",borderBottom:"1px solid rgba(51,65,85,0.5)",whiteSpace:"nowrap",overflow:"hidden",maxWidth:160,textOverflow:"ellipsis"};
  function SortHdr({col, label}) {
    return <th style={{...th,cursor:"pointer",userSelect:"none"}} onClick={()=>handleSort(col)}>{label}{sortBy===col?(sortDir==="asc"?" ↑":" ↓"):""}</th>;
  }

  return (
    <div style={{padding:"16px",minHeight:"calc(100vh - 60px)",background:"#0f172a"}}>
      {/* Controls */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <span style={{fontSize:13,color:"#94a3b8",fontWeight:600,whiteSpace:"nowrap"}}>Currently viewing reports for:</span>
        <select value={filterState} onChange={e=>setFilterState(e.target.value)}
          style={{background:"#1e293b",border:"1px solid #334155",color:"#f1f5f9",borderRadius:6,padding:"5px 10px",fontSize:13,cursor:"pointer"}}>
          <option value="All">All</option>
          {states.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <input style={{flex:1,minWidth:180,background:"#1e293b",border:"1px solid #334155",color:"#f1f5f9",borderRadius:6,padding:"6px 12px",fontSize:13}}
          placeholder="Search address, report #, client, county…"
          value={search} onChange={e=>setSearch(e.target.value)}/>
        <button style={{...S.btn("#0ea5e9"),padding:"7px 16px",fontSize:13,fontWeight:600,flexShrink:0}} onClick={onNew}>＋ New Job</button>
      </div>

      {deleteError&&<p role="alert" style={{color:"#fecaca",padding:12,marginBottom:12,border:"1px solid #ef4444",borderRadius:8}}>{deleteError}</p>}
      {/* Table */}
      <div className="ts-jobs-table" style={{overflowX:"auto",borderRadius:10,border:"1px solid #1e293b",maxHeight:"calc(100vh - 160px)",overflowY:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",background:"#0f172a"}}>
          <thead>
            <tr>
              <th style={th}>Actions</th>
              <SortHdr col="reportNum" label="Report ▾"/>
              <SortHdr col="reportType" label="Type"/>
              <th style={th}>Flag</th>
              <th style={th}>Progress</th>
              <SortHdr col="invoice" label="Invc"/>
              <SortHdr col="address" label="Property Address"/>
              <SortHdr col="county" label="County"/>
              <SortHdr col="date" label="Insp."/>
              <th style={th}>Assigned</th>
              <th style={th}>NOC</th>
              <SortHdr col="escrow" label="Escrow#"/>
              <SortHdr col="orderedBy" label="Order By"/>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={13} style={{...td,textAlign:"center",padding:40,color:"#475569",maxWidth:"none"}}>
                {search || filterState !== "All" ? "No jobs match your search." : "No jobs yet — click + New Job to get started."}
              </td></tr>
            )}
            {filtered.map((j, idx) => {
              const isCurrent = j.id === currentJobId;
              const sp = j.stageProgress || {};
              const addr = [j.address, j.city].filter(Boolean).join(", ");
              const rowBg = isCurrent ? "rgba(34,211,238,0.07)" : idx%2===0 ? "#0f172a" : "rgba(30,41,59,0.4)";
              return (
                <tr key={j.id} style={{background:rowBg,cursor:"pointer"}} onClick={()=>onOpen(j)}>
                  <td style={td} onClick={e=>e.stopPropagation()}>
                    <select aria-label={`Job actions ${j.address||j.reportNum||j.id}`} title="Job actions" value="" disabled={deletingId!==null}
                      onChange={e=>{const action=e.target.value;if(action==="open")onOpen(j);else if(action==="delete")deleteJob(j);}}
                      style={{width:36,height:36,padding:0,textAlign:"center",appearance:"none",WebkitAppearance:"none",background:"#1e293b",border:"1px solid #475569",borderRadius:6,color:"#e2e8f0",fontSize:20,cursor:"pointer"}}>
                      <option value="" disabled>⋯</option>
                      <option value="open">Open job</option>
                      {onDelete&&<option value="delete">Delete job</option>}
                    </select>
                  </td>
                  <td style={{...td,fontWeight:700,color:"#f1f5f9"}}>{j.reportNum||"—"}</td>
                  <td style={td}>{shortType(j.reportType)}</td>
                  <td style={td} onClick={e=>e.stopPropagation()}>
                    <button style={{background:"none",border:"none",cursor:"pointer",padding:0,fontSize:15,color:j.flag?"#fbbf24":"#334155"}}
                      title={j.flag?"Remove flag":"Flag this job"} onClick={()=>onUpdateJob(j.id,{flag:!j.flag})}>⚑</button>
                  </td>
                  <td style={td} onClick={e=>e.stopPropagation()}>
                    <div style={{display:"flex",gap:2,alignItems:"flex-end"}}>
                      {(()=>{
                        const autoP={
                          FP:!!(j.address&&j.orderedBy?.name),
                          DM:!!((j.drawings||[]).length>0||j.mapBg),
                          FR:(j.findings||[]).length>0,
                          CT:(j.findings||[]).some(f=>f.primaryPrice>0),
                        };
                        return ["FP","DM","FR","CT","CS","NC"].map(s=>{
                          const isAuto=s in autoP;
                          const checked=isAuto?autoP[s]:sp[s];
                          return(
                            <div key={s} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,cursor:"pointer",WebkitTapHighlightColor:"transparent"}} onClick={e=>{e.stopPropagation();const TAB_MAP={FP:"job",DM:"map",FR:"findings",CT:"contract",CS:"job",NC:"report"};onOpen(j,TAB_MAP[s]);}}>
                              <div style={{fontSize:9,color:checked?"#22d3ee":"#94a3b8",fontWeight:700,lineHeight:1}}>{s}</div>
                              <div style={{width:22,height:22,border:"2px solid",borderColor:checked?"#22d3ee":"rgba(255,255,255,0.2)",borderRadius:4,background:checked?"#22d3ee":"rgba(255,255,255,0.04)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {checked&&<span style={{color:"#0f172a",fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </td>
                  <td style={td}>{j.invoice||""}</td>
                  <td style={{...td,maxWidth:200}}>{addr||"—"}</td>
                  <td style={td}>{j.county||""}</td>
                  <td style={td}>{j.date||""}</td>
                  <td style={td} onClick={e=>e.stopPropagation()}>
                    {firmMode ? (
                      <select value={j.assignedTo||""} onChange={e=>onAssign&&onAssign(j, e.target.value||null)}
                        style={{background:"#1e293b",color:"#e2e8f0",border:"1px solid #334155",borderRadius:6,padding:"3px 6px",fontSize:11,maxWidth:130,cursor:"pointer"}}>
                        <option value="">— Unassigned —</option>
                        {(people||[]).map(p=><option key={p.id} value={p.id}>{p.full_name||p.email||"(unnamed)"}</option>)}
                      </select>
                    ) : inspName(j.inspectorIds)}
                  </td>
                  <td style={td} onClick={e=>e.stopPropagation()}>
                    <input type="checkbox" checked={!!j.noc} onChange={()=>onUpdateJob(j.id,{noc:!j.noc})} title="Notice of Completion"/>
                  </td>
                  <td style={td}>{j.escrow||""}</td>
                  <td style={td}>{j.orderedBy?.name||""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{marginTop:8,fontSize:12,color:"#64748b",display:"flex",gap:16}}>
        <span>Showing <strong style={{color:"#94a3b8"}}>{filtered.length > 0 ? `1 to ${filtered.length}` : "0"}</strong> of <strong style={{color:"#94a3b8"}}>{jobs.length}</strong> {jobs.length===1?"Report":"Reports"}</span>
      </div>
    </div>
  );
}

// Named export for verification of the actual report generator.
export {generatePDF,newJob,DEFAULT_COMPANY,MapSection,NotificationBell,JobsDashboard,SettingsModal,FindingsSection,ContractSection};
